/* ============================================================================
   PARSER FALLBACK (HORS-LIGNE, GRATUIT)
   ----------------------------------------------------------------------------
   Utilisé uniquement en repli si l'Edge Function (IA) est indisponible.
   Il NE consomme PAS de quota et n'appelle aucune API : analyse locale par
   mots-clés. Le résultat respecte le même schéma que la réponse de l'IA, donc
   le moteur de rendu ne fait aucune différence entre les deux sources.
   ========================================================================== */

const SPORT_KEYWORDS = {
  // « gardien volant » / « quinto » : termes futsal-only (salve futsal F20 —
  // un récit futsal sans le mot « futsal » partait au handball via « pivot »,
  // mot ambigu, et « gardien volant » seul partait au… badminton via
  // « volant »). Futsal PREMIER du registre : ses marqueurs sûrs gagnent.
  futsal: ["futsal", "gardien volant", "quinto"],
  // « check-ball » / « non-charge » : termes 3x3-only (salve 3x3 T20 — un
  // récit de check-ball sans « basket » ni « 3x3 » partait au FOOTBALL, et
  // la garde acceptait la scène : toutes les règles du mauvais sport étaient
  // satisfaites). Mesuré : 4 prompts au corpus, tous basket3x3.
  basket3x3: ["check-ball", "check ball", "checkball", "zone de non-charge", "non-charge"],
  basketball: ["basket", "basketball", "nba", "pick and roll"],
  rugby: ["rugby", "en-but", "ouvreur", "mêlée", "melee", "essai"],
  // Basket AVANT handball : « pivot » est aussi un poste de basket — les
  // marqueurs sûrs (lay-up, trailer, double-pas) doivent gagner sur lui.
  basketball: ["lay-up", "layup", "lay up", "double-pas", "double pas", "trailer", "3 points", "trois points", "lancer franc", "lancers francs"],
  handball: ["handball", "hand", "hb", "pivot", "demi-centre", "demi centre", "tir en suspension"],
  // Football AVANT badminton : « dégagement » (bad) vole les récits de corner
  // sans le mot « football » — « corner » et « coup franc » ancrent le foot.
  football: ["football", "foot", "soccer", "corner", "coup franc", "surface de réparation"],
  volleyball: ["volley", "volleyball", "vb", "passeur-pointu", "service flottant", "passe haute", "réceptionneur", "receptionneur", "manchette"],
  badminton: ["badminton", "bad", "volant", "dégagement", "amorti", "smash bad"],
  tennis_de_table: ["tennis de table", "tt", "ping-pong", "ping pong", "ping", "pongiste", "raquette de table"],
  tennis: ["tennis", "service-volée", "fond de court", "coup droit", "revers"],
  ultimate: ["ultimate", "frisbee", "disque", "stack"],
  gymnastique: ["gymnastique", "gymnaste", "gym", "praticable", "enchaînement", "enchainement", "acrosport", "roulade", "rondade", "salto", "atr", "trampoline", "tremplin", "poutre", "pareur", "voltigeur"],
  tir_a_l_arc: ["tir à l'arc", "tir a l'arc", "archerie", "sarbacane"],
  petanque: ["pétanque", "petanque", "cochonnet", "boules"],
  athletisme: ["athlétisme", "athletisme", "4x100", "course de relais", "sprint", "course de vitesse", "montante descendante", "montante-descendante"],
};

/* MOTS PARTAGÉS par plusieurs sports : consultés SEULEMENT si aucun mot-clé
   propre n'a répondu (cf. detectSportCore). Le sport indiqué est le repli
   HISTORIQUE du mot, celui qui s'appliquait quand il figurait dans la table
   ci-dessus — un texte qui ne dit que « smash » continue donc de donner du
   volley, exactement comme avant. */
const SPORT_KEYWORDS_SHARED = {
  volleyball: ["smash"],
};

const EXERCISE_KEYWORDS = [
  "situation d'apprentissage", "exercice", "atelier", "couloir", "plot", "plots",
  "consigne", "consignes", "rotation", "observateur", "passeur", "élève", "eleve",
  "but pour l'élève", "critère", "critere", "réussite", "reussite", "groupes de",
  "rôles", "roles", "disposition", "contre-attaque", "contre attaque",
  "montée de balle", "montee de balle", "surnombre", "supériorité", "superiorite",
];

const FORMATIONS = {
  "4-4-2": [4, 4, 2], "4-3-3": [4, 3, 3], "5-3-2": [5, 3, 2],
  "3-5-2": [3, 5, 2], "4-2-3-1": [4, 2, 3, 1], "3-4-3": [3, 4, 3],
};

const ACTION_KEYWORDS = {
  passe: ["passe", "donne", "transmet", "sert", "décale", "decale", "remise"],
  centre: ["centre", "centrer", "cross"],
  tir: ["tir", "frappe", "shoot", "tire"],
  dribble: ["dribble", "dribbler", "élimine", "elimine"],
  course: ["course", "appel", "suivre", "suit", "déborde", "deborde", "roll", "monte", "perce", "courir"],
  ecran: ["écran", "ecran", "screen", "bloc"],
};

const ACTION_COLORS = {
  passe: "#e74c3c", centre: "#f39c12", tir: "#9b59b6",
  dribble: "#1abc9c", course: "#3498db", ecran: "#95a5a6",
};

/* Matche un mot-clé sur des FRONTIÈRES DE MOTS (évite les faux positifs par
   sous-chaîne : « essai » dans « nécessaire », « hand » dans « handicap »,
   « tir » dans « sortir »…). Tient compte des lettres accentuées françaises. */
const WORD = "0-9a-zà-ÿœ";
function matchKeyword(t, kw) {
  const esc = kw.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`(^|[^${WORD}])${esc}([^${WORD}]|$)`, "i").test(t);
}
function keywordIndex(t, kw) {
  const esc = kw.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const m = new RegExp(`(?:^|[^${WORD}])(${esc})(?:[^${WORD}]|$)`, "i").exec(t);
  return m ? m.index : -1;
}

function detectSport(text) {
  return detectSportCore(text.toLowerCase()) || "football";
}
/* Détection en deux temps : d'abord les NOMS de sport explicites (fiables),
   puis seulement les termes d'action (qui peuvent être ambigus d'un sport à
   l'autre : « réception », « smash »…). */
const SPORT_NAMES = {
  gymnastique: ["gymnastique", "gymnaste", "gym", "praticable", "acrosport", "trampoline"],
  tir_a_l_arc: ["tir à l'arc", "tir a l'arc", "archerie"],
  petanque: ["pétanque", "petanque", "cochonnet"],
  athletisme: ["athlétisme", "athletisme", "course de relais", "course de vitesse", "montante descendante", "montante-descendante"],
  volleyball: ["volley", "volley-ball", "volleyball"],
  badminton: ["badminton"],
  tennis_de_table: ["tennis de table", "ping-pong", "ping pong", "pongiste"],
  tennis: ["tennis"],
  basket3x3: ["basket 3x3", "basket3x3", "3x3", "3 x 3"],
  basketball: ["basket", "basket-ball", "basketball"],
  handball: ["handball", "hand-ball"],
  futsal: ["futsal"],
  rugby: ["rugby"],
  ultimate: ["ultimate", "frisbee"],
  football: ["football", "soccer"],
};
/* JEUX TRADITIONNELS / PRÉ-SPORTIFS — pas de terrain dédié : sport "other"
   (aire neutre), dégradation propre. Liste FERMÉE de noms de jeux, sans
   collision avec le vocabulaire des sports supportés (« jeu du drapeau »
   complet — jamais « drapeau » seul, qui est aussi celui du corner). Testée
   APRÈS les noms de sports explicites (un sport nommé prime toujours) et
   AVANT les mots d'action ambigus (« ballon » ne doit plus donner football). */
const TRADITIONAL_GAMES = [
  "jeu du béret", "béret", "beret", "balle au prisonnier", "balle aux prisonniers", "ballon prisonnier",
  "épervier", "epervier", "gagne-terrain", "gagne terrain", "thèque", "theque",
  "poules, renards, vipères", "poules renards vipères", "poules-renards-vipères",
  "déménageurs", "demenageurs", "rivière aux crocodiles", "riviere aux crocodiles",
  "jeu du drapeau", "ballon-château", "ballon château", "ballon chateau", "balle assise",
  "chat perché", "chat perche", "accroche-décroche", "accroche-decroche",
];
/* LA TABLE DÉPARTAGE LE TENNIS DE TABLE DU TENNIS.
   Le tennis de table perdait son sport de TROIS façons, toutes mesurées sur le
   corpus (8 prompts), et toutes avec la même conséquence : la scène partait sur
   un COURT DE TENNIS, en herbe, et la garde l'acceptait — les deux sports sont
   des raquettes, ses règles passaient. Un défaut FAUX ET SERVI.
     • « Tennis de tableRégularité en coup droit » — sans séparateur, le mot-clé
       COMPOSÉ ne mord plus (`keywordIndex` exige une frontière), et « tennis »,
       lui, mord toujours : il gagne ;
     • « tenis de table » — la faute de frappe courante ; plus aucun NOM ne mord,
       le repli par mots-clés voit « coup droit » et conclut au tennis ;
     • « deux joueurs de part et d'autre de la table » — l'activité n'est jamais
       nommée, même repli, même conclusion.
   Le discriminant est la TABLE DE JEU, avec ses deux homonymes écartés : la
   « table de marque » (le pupitre du secrétaire, présent au basket et au
   handball) et la « table de saut » (gymnastique).
   Mesuré avant écriture : 8 basculements sur 1155, 8 CORRECTS, 0 fautif. */
const TABLE_TT = /t[eé]n+is\s*-?\s*de\s*-?\s*table|\bping.?pong\b|\bpongistes?\b|\bdemi.?tables?\b|\b(?:de|sur|autour de|au bout de|le long de|d['’]autre de|derri[èe]re)\s+la\s+table\b|\btables?\b(?!\s*(?:de\s*(?:marque|saut|match))\b)(?=[^.!?]{0,80}\b(?:raquette|balle|service|revers|coup droit|filet)\b)/i;

function detectSportCore(t) {
  // Le sport nommé LE PLUS TÔT dans le texte gagne (à égalité : l'ordre du
  // registre départage) : « Tennis de table : … montante-descendante sur 3
  // tables » est du tennis de table — « montante-descendante » (nom athlé)
  // volait le sport à cause du seul ordre d'itération.
  let best = null;
  for (const [sport, words] of Object.entries(SPORT_NAMES))
    for (const w of words) {
      const i = keywordIndex(t, w);
      if (i >= 0 && (!best || i < best.i)) best = { sport, i };
    }
  if (best) {
    // « 3x3 » RAFFINE le basket (même sport, format différent) : « basket …
    // en 3x3 » reste du basket3x3 même si « basket » apparaît plus tôt.
    if (best.sport === "basketball" && SPORT_NAMES.basket3x3.some((w) => matchKeyword(t, w))) return "basket3x3";
    // …et la TABLE raffine le tennis, exactement de la même façon.
    if (best.sport === "tennis" && TABLE_TT.test(t)) return "tennis_de_table";
    return best.sport;
  }
  if (TRADITIONAL_GAMES.some((w) => matchKeyword(t, w))) return "other";
  // « en sprint » est une MANIÈRE (« reculent en sprint », « reviennent en
  // sprint ») — pas un marqueur d'athlétisme. Seul le nom compte (« sprint de
  // 30 m », « ateliers sprint ») : la locution est neutralisée avant le repli
  // par mots-clés (les NOMS de sport explicites ci-dessus ont déjà eu la main).
  /* « ESSAI » EST AUSSI UNE FAUTE D'ORTHOGRAPHE, et l'une des plus courantes du
     français : « le défenseur ESSAI de récupérer » pour « essaie ». Comme
     `essai` est un mot-clé RUGBY et que le rugby est testé avant le football,
     un « exercisse de foot ball : … un defenseur qui essai de recuperé le
     balon » partait sur un TERRAIN DE RUGBY (salve de lancement nº3, prompts
     pauvres). Le nom se reconnaît à ce qui le suit : « marquer un essai »,
     « la ligne d'essai », « un essai de pénalité » — jamais un INFINITIF.
     Neutralisé comme « en sprint » juste au-dessus, et pour la même raison.
     Mesuré : 0 occurrence de la forme verbale sur les 1155 prompts du corpus
     (les 27 « essai » y sont tous le nom) — le correctif ne peut rien y
     déplacer ; il vise la population que le corpus sous-représente, celle qui
     écrit vite. */
  const tKw = t.replace(/\ben sprint\w*/g, " ")
    .replace(/\bessais?\s+d[e']\s*(\w+(?:er|ir|re))\b/gi, " $1 ");
  for (const [sport, words] of Object.entries(SPORT_KEYWORDS))
    if (words.some((w) => matchKeyword(tKw, w))) {
      // « en-but » est AUSSI le vocabulaire de l'ULTIMATE (salve ultimate
      // U20 : un récit de stack avec « en-but » mais sans « ultimate/disque »
      // était jugé aux règles du RUGBY — sa passe vers l'avant, LÉGALE à
      // l'ultimate, le faisait rejeter). Quand le rugby n'a gagné que par
      // « en-but » (aucun ancrage rugby/essai/mêlée/ouvreur) et qu'un
      // marqueur ultimate est présent, l'ultimate reprend la main.
      // Mesuré : 0 flip sur les 50 prompts « en-but » du corpus — gratuit.
      if (sport === "rugby" && !/rugby|essai|m[êe]l[ée]e|ouvreur/.test(tKw)
        && /stack|coupeur|disque|frisbee|marqueur[^.;]{0,20}compte/.test(tKw)) return "ultimate";
      // Même arbitrage qu'au-dessus : « coup droit » et « revers » sont du
      // vocabulaire de tennis, mais s'il y a une TABLE de jeu c'est du tennis
      // de table — c'est par ici que passaient la faute de frappe « tenis » et
      // les textes qui ne nomment pas l'activité.
      if (sport === "tennis" && TABLE_TT.test(tKw)) return "tennis_de_table";
      return sport;
    }
  // DERNIER RECOURS : les mots PARTAGÉS par plusieurs sports (« smash » se
  // joue au volley, au badminton, au tennis et au tennis de table). Ils ne
  // tranchent que si RIEN d'autre n'a parlé — sinon, listés dans un sport
  // précis, ils volaient la détection à un mot EXCLUSIF d'un autre : une
  // consigne orale de badminton (« un dégagement… un amorti… chercher le
  // volant… tu conclus par un smash ») partait en VOLLEY et se dessinait sur
  // un terrain de volley (salve raquettes, R04). L'ordre du registre, lui,
  // reste inchangé pour tous les autres mots (les priorités documentées
  // ci-dessus — basket avant handball, football avant badminton — tiennent).
  for (const [sport, words] of Object.entries(SPORT_KEYWORDS_SHARED))
    if (words.some((w) => matchKeyword(tKw, w))) return sport;
  // …et si RIEN n'a parlé, une table de jeu suffit : « tenis de table » (faute
  // de frappe) ne mord aucun nom et peut n'être accompagné d'aucun mot de
  // raquette. Placé en DERNIER recours, il ne vole donc rien à personne.
  if (TABLE_TT.test(tKw)) return "tennis_de_table";
  return null;
}
/* Renvoie un sport UNIQUEMENT s'il est explicitement nommé (sinon null).
   Utilisé côté client pour corriger le sport quel que soit le retour de l'IA. */
export function guessSportExplicit(text) {
  return detectSportCore(String(text || "").toLowerCase());
}
/* ULTIMATE — préférences de terrain lues dans le texte :
   - les marques « brick » (détail réglementaire) ne s'affichent que si demandées ;
   - la zone d'en-but est déjà dessinée par le terrain : on ne garde la zone
     éventuellement ajoutée par l'IA que si le texte PRÉCISE une position dedans. */
export function wantsBrick(text) {
  return /\bbricks?\b|marques? de brique|points? de brique/i.test(String(text || ""));
}
/* STADE (piste en anneau) — le fond athlétisme par défaut est une LIGNE DROITE
   à 4 couloirs : parfait pour un sprint ou un atelier, faux pour tout ce qui
   TOURNE. On bascule sur l'anneau UNIQUEMENT quand le texte l'exige — tour de
   piste, virage, demi-fond, distances qui ne tiennent pas en ligne droite
   (200 m et plus), relais 4×100. Volontairement EXCLUS : le 100 m et les
   sprints courts (ligne droite, c'est leur réalité), « relais 4×40 » (atelier
   scolaire, 👎 PROD40) et le mot « anneau » seul (c'est aussi l'engin de
   lancer, salve v40 A02). Mesuré : 0 bascule sur les 1106 scènes du corpus —
   aucune scène existante ne change de tracé. */
export function wantsStade(text) {
  const t = String(text || "");
  // Un « relais en 3 ateliers » est un ATELIER de transmission monté sur un
  // bout de terrain, pas une course sur piste : le stade y serait faux (👎
  // PROD40, PROD111, et A03 de la salve v40). Un relais TOUT COURT, lui, se
  // court sur la piste — y compris le 4×40 scolaire, tracé « sur une portion
  // réduite de la piste » (référentiel EPS).
  if (/atelier/i.test(t)) return false;
  return /tours? de piste|autour de la piste|piste d['’]athl[ée]tisme|\bvirages?\b|\brelais\b|\bt[ée]moin\b|zone de transmission|\b(?:200|400|800|1000|1500|3000)\s*m(?:[èe]tres?)?\b|demi.?fond|\bsteeple\b/i
    .test(t);
}
/* Géométrie de PISTE — repères en coordonnées de scène (0-100), miroir du
   tracé `TERRAINS.athletisme` en mode stade : la bande de couloirs occupe 16 %
   de la hauteur en haut ET en bas, la pelouse est au centre.
   `LANE_BAS` est l'axe des couloirs de la ligne droite avant (celle où l'on
   court les sprints et les relais courts), `LANE_HAUT` la ligne droite
   opposée. */
export const PISTE = { LANE_BAS: 92, LANE_HAUT: 8, VIRAGE_D: 94, VIRAGE_G: 6 };
export function endZonePrecised(text) {
  // Garde la zone d'en-but de l'IA seulement si une POSITION précise y est
  // donnée (au fond, en coin, côté…) — pas pour un simple « réceptionne dans
  // la zone d'en-but » (qui décrit juste le point marqué, déjà tracé).
  return /(au fond|au centre|en coin|dans le coin|c[ôo]t[ée] (?:droit|gauche))[^.]*?(en.?but|zone de marque)|(fond|coin|centre) de (?:la zone|l['’]en.?but)/i.test(String(text || ""));
}
/* Assainit une scène ULTIMATE (quelle que soit sa source — IA brute ou analyse) :
   - terrain : brick à la demande ; la zone d'en-but étant déjà dessinée, on
     retire la zone redondante ajoutée par l'IA sauf si une position y est précisée ;
   - on retire les DÉFENSEURS non demandés (l'IA en invente parfois) ;
   - NO TRAVELING : le PORTEUR du disque ne se déplace pas — on supprime ses
     actions course/dribble (le suivi du porteur passe au receveur à chaque passe). */
export function sanitizeUltimateScene(data, text) {
  if (!data || data.sport !== "ultimate") return data;
  data.terrainOpts = { ...(data.terrainOpts || {}), brick: wantsBrick(text) };
  if (!endZonePrecised(text) && Array.isArray(data.zones))
    data.zones = data.zones.filter((z) => !/en.?but|end.?zone|zone de marque/i.test(z.label || ""));

  const t = String(text || "").toLowerCase();
  const players = Array.isArray(data.players) ? data.players : [];
  const actions = Array.isArray(data.actions) ? data.actions : [];
  const removed = new Set();
  if (!/d[ée]fenseur|d[ée]fense|adversaire|marqueur|marquage|opposant|\bcontre\b/.test(t))
    for (const p of players) {
      // Un rôle OFFENSIF explicite (receveur, lanceur, en attente…) n'est pas un
      // défenseur fantôme, même en équipe B : les drills à COLONNES DE RÔLES
      // colorent la 2e colonne en B (lanceurs/receveurs) — on ne la vide pas.
      const offensif = /receveur|lanceur|passeur|attente|soutien|stack|handler|cutter/i.test(p.role || "");
      if ((p.team === "B" && !offensif) || /d[ée]fenseur|marqueur/i.test(p.role || "")) removed.add(p.id);
    }

  const ordered = actions.slice().sort((a, b) => (a.order ?? 1e9) - (b.order ?? 1e9));
  const firstHolder = (ordered.find((a) => (a.type === "passe" || a.type === "centre") && a.from) || {}).from || (players[0] || {}).id || null;
  let holder = firstHolder;
  const drop = new Set();
  for (const a of ordered) {
    if ((a.type === "course" || a.type === "dribble") && a.from === holder) drop.add(a);
    else if ((a.type === "passe" || a.type === "centre") && a.to) holder = a.to;
  }
  data.players = players.filter((p) => !removed.has(p.id));
  data.actions = actions.filter((a) => !drop.has(a) && !removed.has(a.from) && !removed.has(a.to));

  // Le DISQUE doit être visible au départ, sur le porteur. On normalise tout
  // « ballon » en « disque », et on en ajoute un sur le porteur s'il manque.
  data.markers = (Array.isArray(data.markers) ? data.markers : [])
    .map((m) => (/ballon|ball|balle/i.test(m.type || "") ? { ...m, type: "disque" } : m));
  const carrier = data.players.find((p) => p.id === firstHolder) || data.players[0];
  if (carrier && !data.markers.some((m) => (m.type || "") === "disque"))
    data.markers.push({ type: "disque", x: clampN(Math.round(carrier.x + 4), 4, 96), y: carrier.y });
  return data;
}
const NET_SPORTS = ["badminton", "tennis", "tennis_de_table", "volleyball"];
function frenchNum(w) {
  const m = { un: 1, une: 1, deux: 2, trois: 3, quatre: 4, cinq: 5, six: 6, sept: 7 };
  return m[w] != null ? m[w] : parseInt(w, 10);
}
function detectKind(text) {
  const t = text.toLowerCase();
  return EXERCISE_KEYWORDS.some((w) => matchKeyword(t, w)) ? "exercise" : "match";
}
function detectFormation(text) {
  const m = text.match(/\b(\d(?:-\d){1,3})\b/);
  if (m && FORMATIONS[m[1]]) return m[1];
  // Forme COMPACTE (« 442 », « 433 ») : re-tirettée si elle nomme une
  // formation connue.
  const c = text.match(/\b(\d{3,4})\b/);
  if (c) { const k = c[1].split("").join("-"); if (FORMATIONS[k]) return k; }
  return null;
}

function cleanAfterColon(s) { const i = s.indexOf(":"); return (i !== -1 ? s.slice(i + 1) : s).trim(); }
function splitItems(s) { return s.split(/,|\bpuis\b|\bet\b/).map((x) => x.trim()).filter((x) => x.length > 2); }

function extractPedagogy(text) {
  const peda = { objectif: "", organisation: [], criteres_reussite: [], erreurs: [] };
  const chunks = text.split(/[\n.;]+/).map((s) => s.trim()).filter((s) => s.length > 3);
  chunks.forEach((c) => {
    const l = c.toLowerCase();
    if (!peda.objectif && (l.includes("but pour l'élève") || l.includes("but pour l'eleve") || l.startsWith("objectif"))) {
      peda.objectif = cleanAfterColon(c);
    } else if (l.includes("erreur")) {
      splitItems(cleanAfterColon(c)).forEach((it) => peda.erreurs.push(it));
    } else if (l.includes("pour réussir") || l.includes("pour reussir") || l.includes("tu as réussi") ||
               l.includes("tu as reussi") || l.includes("critère") || l.includes("critere")) {
      splitItems(cleanAfterColon(c)).forEach((it) => peda.criteres_reussite.push(it));
    } else if (l.includes("groupe") || l.includes("disposition") || l.includes("rotation") ||
               l.includes("rôle") || l.includes("role") || l.includes("couloir") || l.includes("plot") ||
               l.includes("organisation") || l.includes("min")) {
      peda.organisation.push(cleanAfterColon(c));
    }
  });
  if (!peda.objectif && chunks.length) peda.objectif = chunks[0];
  return peda;
}

function extractDim(text) {
  const m2 = text.match(/(\d+)\s*m\b[^\d]{0,20}?(\d+)\s*m\b/i);
  if (m2) return m2[1] + "m x " + m2[2] + "m";
  const m1 = text.match(/(\d+)\s*m\b/i);
  return m1 ? m1[1] + "m" : "";
}

function buildExerciseLayout(text) {
  // Terrain en PAYSAGE : couloirs orientés dans le sens de la LONGUEUR (en x),
  // empilés sur la hauteur (en y). Progression de gauche -> droite.
  const dim = extractDim(text) || "20m x 5m";
  const cm = text.toLowerCase().match(/(\d+|deux|trois|quatre|cinq|six)\s*couloirs?/);
  const lanes = Math.max(1, Math.min(cm ? frenchNumWord(cm[1]) : 2, 4));
  const zones = [], markers = [], players = [], actions = [];
  const laneH = 22, gap = (100 - lanes * laneH) / (lanes + 1);
  const x = 10, w = 80;
  let pid = 1, order = 1;
  for (let i = 0; i < lanes; i++) {
    const y = Math.round(gap + i * (laneH + gap)), cy = y + laneH / 2;
    zones.push({ x, y, w, h: laneH, label: "Couloir " + (i + 1), dim, style: "dashed" });
    [[x, y], [x + w, y], [x, y + laneH], [x + w, y + laneH]].forEach((p) =>
      markers.push({ type: "plot", x: Math.round(p[0]), y: Math.round(p[1]) }));
    const passeur = { id: "P" + pid++, role: "Passeur", x: x + 8, y: Math.round(cy), color: "#0072B2", team: "A" };
    const receveur = { id: "P" + pid++, role: "Receveur", x: x + w - 8, y: Math.round(cy), color: "#0072B2", team: "A" };
    players.push(passeur, receveur);
    players.push({ id: "P" + pid++, role: "Observateur", x: x + w / 2, y: Math.round(y - gap * 0.4), color: "#F2C200", team: "OBS" });
    markers.push({ type: "ballon", x: x + 14, y: Math.round(cy) });
    actions.push({ from: passeur.id, to: receveur.id, type: "passe", label: "passe", style: "solid", order: order++ });
    actions.push({ from: passeur.id, to: receveur.id, type: "course", label: "suit le ballon", style: "dashed", order: order++ });
  }
  return { zones, markers, players, actions };
}

function buildFootballFormation(formationKey) {
  // Terrain en PAYSAGE : gardien à GAUCHE, lignes vers la DROITE, joueurs
  // étalés en largeur (y). Chaque joueur reçoit un rôle lisible + des "tags"
  // (ligne, poste, côté) pour résoudre les acteurs nommés dans le texte.
  const lines = FORMATIONS[formationKey];
  const players = [{ id: "P1", num: 1, role: "Gardien", x: 6, y: 50, color: "#F2C200", team: "A", tags: ["gardien"] }];
  let idx = 2;
  const total = lines.length;
  lines.forEach((count, li) => {
    const x = 22 + (li / Math.max(1, total - 1)) * 66;
    const isDef = li === 0, isAtt = li === total - 1;
    for (let i = 0; i < count; i++) {
      const y = Math.round(((i + 1) / (count + 1)) * 100);
      // Vue tactique (attaque vers la droite) : côté DROIT en bas (y grand),
      // côté GAUCHE en haut (y petit).
      const side = count >= 2 ? (i === 0 ? "gauche" : (i === count - 1 ? "droit" : null)) : null;
      let role = "Joueur", tags = [];
      if (isDef) {
        if (side && count >= 4) { role = "Latéral " + (side === "droit" ? "D" : "G"); tags = ["defenseur", "lateral", side]; }
        else { role = "Déf. central"; tags = ["defenseur", "central"]; }
      } else if (isAtt) {
        if (side && count >= 3) { role = "Ailier " + (side === "droit" ? "D" : "G"); tags = ["attaquant", "ailier", side]; }
        else { role = "Avant-centre"; tags = ["attaquant", "avant-centre", "central"]; }
      } else {
        // Ligne de milieu : à 4+ joueurs, les excentrés sont des ailiers ; à 3
        // joueurs (4-3-3…), un SEUL milieu central (l'axial) — les autres sont
        // des milieux gauche/droit, pour que « le milieu central » vise l'axe.
        if (side && count >= 4) { role = "Ailier " + (side === "droit" ? "D" : "G"); tags = ["milieu", "ailier", side]; }
        else if (side) { role = "Milieu " + (side === "droit" ? "D" : "G"); tags = ["milieu", side]; }
        else { role = "Milieu central"; tags = ["milieu", "central"]; }
      }
      players.push({ id: "P" + idx, num: idx, role, x: Math.round(x), y, color: "#0072B2", team: "A", tags });
      idx++;
    }
  });
  return players;
}

/* FUTSAL : 5 joueurs (gardien + 4 de champ), pas 11. Système lu dans le texte
   (« 2-2 », « 3-1 », « 4-0 ») sinon losange 1-2-1 (fixo, deux ailes, pivot).
   Convention alignée sur le football : on défend le but GAUCHE (gardien à gauche). */
function buildFutsalFormation(text) {
  const t = c0(text);
  const gk = { id: "P1", num: 1, role: "Gardien", x: 6, y: 50, color: "#0072B2", team: "A", tags: ["gardien"] };
  let outfield;
  if (/\b2\s*-\s*2\b/.test(t)) outfield = [["Défenseur", 28, 30], ["Défenseur", 28, 70], ["Attaquant", 64, 30], ["Attaquant", 64, 70]];
  else if (/\b3\s*-\s*1\b/.test(t)) outfield = [["Défenseur", 28, 28], ["Fixo", 32, 50], ["Défenseur", 28, 72], ["Pivot", 74, 50]];
  else if (/\b4\s*-\s*0\b/.test(t)) outfield = [["Aile G", 40, 22], ["Fixo", 34, 50], ["Aile D", 40, 78], ["Pivot", 68, 50]];
  else outfield = [["Fixo", 26, 50], ["Aile gauche", 52, 25], ["Aile droite", 52, 75], ["Pivot", 78, 50]];   // 1-2-1
  return [gk, ...outfield.map(([role, x, y], i) => ({ id: "P" + (i + 2), num: i + 2, role, x, y, color: "#0072B2", team: "A" }))];
}

function buildGenericPlayers(n) {
  const players = [];
  for (let i = 0; i < n; i++)
    players.push({
      id: "P" + (i + 1), num: i + 1, role: "Joueur " + (i + 1),
      x: Math.round(((i + 1) / (n + 1)) * 100), y: 30 + (i % 2) * 30,
      color: "#0072B2", team: "A", tags: [],
    });
  return players;
}

/* ---- Chaîne d'actions « A donne à B qui déborde et centre pour C qui marque »
   Modèle DIRIGÉ PAR LES VERBES : on lit la phrase de gauche à droite en suivant
   le PORTEUR du ballon. Chaque verbe agit sur le sujet courant (dernier acteur
   nommé avant le verbe) : passe/centre transmettent au prochain acteur, remise
   renvoie au porteur précédent (jeu en une-deux), dribble/course déplacent le
   sujet, écran pose un bloc, tir/frappe conclut au but. Plusieurs verbes par
   segment sont donc gérés (« conduit, élimine puis frappe »). */
const PLAY_ACTORS = [
  [/gardien|portier/, ["gardien"]],
  [/ailier droit|ailier d\b|aile droite/, ["ailier", "droit"]],
  [/ailier gauche|ailier g\b|aile gauche/, ["ailier", "gauche"]],
  [/ailier|aili[èe]re|winger/, ["ailier"]],
  [/avant.?centre|buteur|\bpointe\b|attaquant de pointe|finisseur|num[ée]ro 9|\bn[°o] ?9\b|pivot|int[ée]rieur/, ["avant-centre"]],
  [/lat[ée]ral droit|arri[èe]re droit/, ["lateral", "droit"]],
  [/lat[ée]ral gauche|arri[èe]re gauche/, ["lateral", "gauche"]],
  [/lat[ée]ral|arri[èe]re lat[ée]ral/, ["lateral"]],
  [/d[ée]fenseur central|d[ée]fenseur axial|stoppeur/, ["defenseur", "central"]],
  [/d[ée]fenseur|arri[èe]re central|arri[èe]re/, ["defenseur"]],
  [/milieu (?:central|axial|relayeur|r[ée]cup[ée]rateur|d[ée]fensif|offensif)|sentinelle|num[ée]ro 6|num[ée]ro 8/, ["milieu", "central"]],
  [/milieu|relayeur|meneur(?: de jeu)?|demi\b|num[ée]ro 10/, ["milieu"]],
  [/second(?:e)? attaquant|deuxi[èe]me attaquant|soutien|attaquant|avant\b/, ["attaquant"]],
];
function findPlayActors(t) {
  const events = [], taken = [];
  const free = (s, e) => !taken.some(([a, b]) => s < b && e > a);
  for (const [re, tags] of PLAY_ACTORS) {
    const r = new RegExp(re.source, "g"); let m;
    while ((m = r.exec(t))) { const s = m.index, e = s + m[0].length; if (!free(s, e)) continue; taken.push([s, e]); events.push({ pos: s, tags, num: null }); }
  }
  // Numéros : « le 10 », « n°7 », « numéro 9 », « au 8 »… mais PAS un chiffre de
  // formation (« 4-3-3 ») détecté par le tiret-chiffre suivant.
  const rn = /(?:n[°o]\s*|num[ée]ro\s+|joueur\s+|\ble\s+|\bla\s+|\bau\s+|\bdu\s+|pour le\s+|l['']\s*)(\d{1,2})\b(?!\s*[-/]\s*\d)/gi; let m;
  while ((m = rn.exec(t))) { const s = m.index, e = s + m[0].length; if (!free(s, e)) continue; taken.push([s, e]); events.push({ pos: s, tags: [], num: parseInt(m[1], 10) }); }
  return events.sort((a, b) => a.pos - b.pos);
}
function resolvePlayActor(ev, players, used) {
  if (ev.num != null) {
    return players.find((p) => p.num === ev.num && !used.has(p.id)) || players.find((p) => p.num === ev.num) || null;
  }
  if (!ev.tags.length) return null;
  let best = null, score = 0;
  for (const p of players) {
    const tags = p.tags || [];
    const matched = tags.filter((tg) => ev.tags.includes(tg)).length;
    if (!matched) continue;
    // Départage : privilégie un joueur dont l'identité colle au plus près (moins
    // de tags superflus) — « le milieu » vise un milieu central plutôt qu'un
    // milieu excentré, ce qui le distingue d'« ailier gauche ».
    const s = matched - (used.has(p.id) ? 0.5 : 0) - 0.15 * (tags.length - matched);
    if (s > score) { score = s; best = p; }
  }
  return score > 0 ? best : null;
}
/* Acteurs résolus en joueurs, dans l'ordre du texte. Un même rôle/numéro cité
   plusieurs fois renvoie le MÊME joueur (indispensable pour la remise A→B→A). */
function resolveMentions(t, players) {
  const cache = new Map(), used = new Set(), out = [];
  for (const ev of findPlayActors(t)) {
    const key = ev.num != null ? "n" + ev.num : ev.tags.join("+");
    let p = cache.get(key);
    if (!p) { p = resolvePlayActor(ev, players, used); if (p) { cache.set(key, p); used.add(p.id); } }
    if (p) out.push({ pos: ev.pos, p });
  }
  return out;
}
/* Verbes classés par catégorie. Ordre = priorité en cas d'occurrence ambiguë.
   Le scan impose une frontière de mot À GAUCHE (évite « marque » dans
   « démarque », « passe » dans « repasse »…). */
const VERB_CATS = [
  ["centre",  /centre[rz]?(?!\s+(?:du|de|terrain|adverse|de jeu))|cross|d[ée]pose le ballon/],
  ["remise",  /remise|remet|redonne|rend le ballon|talonne|une.?deux/],
  ["passe",   /passe[rz]?|donne|transmet|sert|d[ée]cale|adresse|glisse|trouve|oriente[rz]?|bascule|renverse|joue (?:avec|vers|en remise)/],
  ["dribble", /dribble[rz]?|conduit|conduire|[ée]limine|crochet|fixe[rz]?|perce|transperce|p[ée]n[éèe]tr/],
  ["screen",  /[ée]cran|\bbloc\b|screen/],
  ["run",     /appel(?:le)?|d[ée]borde[rz]?|\bcourt\b|courir|enchaîne|d[ée]marque|\bfile\b|\bmonte\b|d[ée]croche|\bcoupe\b|plonge|d[ée]double|prend (?:la profondeur|l'espace)|attaque l'espace|se projette/],
  ["shoot",   /tire|\btir\b|frappe[rz]?|conclut|conclure|reprise|reprend|marque[rz]?|cadre|smash|claquette|lobe|fusille|ajuste/],
];
function findVerbs(t) {
  const out = [];
  // Le trait d'union compte comme une lettre ici : protège les composés
  // (« avant-centre » ne doit pas livrer un faux verbe « centre »).
  const isWord = (ch) => ch != null && new RegExp(`[${WORD}-]`, "i").test(ch);
  for (const [cat, re] of VERB_CATS) {
    const r = new RegExp(re.source, "gi"); let m;
    while ((m = r.exec(t))) { if (isWord(t[m.index - 1])) continue; out.push({ pos: m.index, cat }); }
  }
  out.sort((a, b) => a.pos - b.pos);     // tri stable : priorité = ordre des catégories
  const kept = [];
  for (const v of out) if (!kept.some((k) => Math.abs(k.pos - v.pos) < 2)) kept.push(v);
  return kept;
}
const STEP_LABEL = { passe: "Passe", centre: "Centre", tir: "Tir", dribble: "Conduite", course: "Déplacement", ecran: "Écran" };
function buildPlayChain(text, players, sport) {
  // Sports à cage CENTRALE : la conclusion vise ENTRE les poteaux (tracés
  // render.js : y 40-60), pas la position latérale du tireur — l'avant-centre
  // d'un 4-4-2 reprenait le centre à y=33, à côté du cadre (règle « tir à côté
  // du cadre », salve handball nº2). Rugby : l'en-but fait toute la largeur.
  const aimLo = ["handball", "futsal", "football", "basketball", "basket3x3"].includes(sport) ? 42 : 10;
  const aimHi = aimLo === 42 ? 58 : 90;
  const t = text.toLowerCase();
  const mentions = resolveMentions(t, players);
  if (!mentions.length) return null;
  const verbs = findVerbs(t);

  // Position locale courante (miroir du suivi temporel du moteur de rendu).
  const pos = {}; players.forEach((p) => { pos[p.id] = { x: +p.x || 0, y: +p.y || 0 }; });
  const lastBefore = (q) => { let r = null; for (const mn of mentions) { if (mn.pos < q) r = mn.p; else break; } return r; };
  const firstAfter = (q) => { for (const mn of mentions) if (mn.pos > q) return mn.p; return null; };

  let holder = mentions[0].p, prev = null;
  const actions = []; let ord = 1;
  const moveTo = (subj, type, targetId) => {
    const c = pos[subj.id]; if (!c) return;
    const ty = targetId && pos[targetId] ? pos[targetId].y : null;
    const tox = clampN(Math.round(c.x + 18), 6, 92);
    const toy = clampN(Math.round(c.y + (ty != null ? (ty - c.y) * 0.5 : 0)), 8, 92);
    actions.push({ from: subj.id, tox, toy, type, order: ord++, label: STEP_LABEL[type] });
    pos[subj.id] = { x: tox, y: toy };
  };

  for (const v of verbs) {
    const subj = lastBefore(v.pos) || holder;
    if (!subj) continue;
    if (v.cat === "passe" || v.cat === "centre") {
      const tgt = firstAfter(v.pos);
      if (!tgt || tgt.id === subj.id) continue;
      actions.push({ from: subj.id, to: tgt.id, type: v.cat, order: ord++, label: STEP_LABEL[v.cat] });
      prev = holder; holder = tgt;
    } else if (v.cat === "remise") {
      const back = firstAfter(v.pos) || prev || holder;
      if (!back || back.id === subj.id) continue;
      actions.push({ from: subj.id, to: back.id, type: "passe", order: ord++, label: "Remise" });
      prev = holder; holder = back;
    } else if (v.cat === "dribble") {
      moveTo(subj, "dribble", (firstAfter(v.pos) || {}).id);
    } else if (v.cat === "run") {
      moveTo(subj, "course", (firstAfter(v.pos) || {}).id);
    } else if (v.cat === "screen") {
      const tgt = firstAfter(v.pos), c = pos[subj.id];
      const d = tgt && pos[tgt.id] ? pos[tgt.id] : { x: c.x + 10, y: c.y };
      const ex = clampN(Math.round((c.x + d.x) / 2), 6, 92), ey = clampN(Math.round((c.y + d.y) / 2), 8, 92);
      actions.push({ from: subj.id, tox: ex, toy: ey, type: "ecran", order: ord++, label: "Écran" });
      pos[subj.id] = { x: ex, y: ey };
    } else if (v.cat === "shoot") {
      const sh = holder || subj;
      const prev = actions[actions.length - 1];
      // « reprend et marque » = un seul tir : pas de tir consécutif redondant.
      if (prev && prev.type === "tir" && prev.from === sh.id) continue;
      actions.push({ from: sh.id, tox: 97, toy: clampN(pos[sh.id].y, aimLo, aimHi), type: "tir", order: ord++, label: "Tir" });
    }
  }

  // Aucun verbe exploitable mais ≥2 acteurs : relie les acteurs en passes.
  if (!actions.length && mentions.length >= 2) {
    const seen = new Set();
    const uniq = mentions.filter((mn) => (seen.has(mn.p.id) ? false : seen.add(mn.p.id)));
    for (let i = 0; i < uniq.length - 1; i++)
      actions.push({ from: uniq[i].p.id, to: uniq[i + 1].p.id, type: "passe", order: ord++, label: "Passe" });
  }
  // Finition implicite : conclusion évoquée après le dernier acteur, mais aucun tir tracé.
  if (actions.length && !actions.some((a) => a.type === "tir")) {
    const lastPos = mentions[mentions.length - 1].pos;
    if (/marque|but\b|conclut|score|cadre|finition|au fond/.test(t.slice(lastPos))) {
      const sh = holder || mentions[mentions.length - 1].p;
      actions.push({ from: sh.id, tox: 97, toy: clampN(pos[sh.id].y, aimLo, aimHi), type: "tir", order: ord++, label: "Tir" });
    }
  }
  return actions.length ? actions : null;
}

/* Acteurs nommés par LETTRE (« joueur A … joueur E ») dans l'ordre d'apparition,
   avec un indice de côté/couloir lu autour de leur 1re mention. La détection
   s'appuie d'abord sur « joueur X » (fiable), puis sur « à/vers/pour X ». */
function detectNamedActors(t) {
  const seen = new Map();
  const add = (L, pos) => { L = L.toLowerCase(); if (!seen.has(L)) seen.set(L, pos); };
  let m;
  const r1 = /\bjoueur\s+([a-h])\b/gi; while ((m = r1.exec(t))) add(m[1], m.index);
  const r2 = /(?:^|[^0-9a-zà-ÿœ])(?:à|vers|pour|au)\s+([a-h])\b/gi; while ((m = r2.exec(t))) add(m[1], m.index);
  const arr = [...seen.entries()].map(([letter, pos]) => ({ letter, pos })).sort((a, b) => a.pos - b.pos);
  for (const a of arr) {
    const win = t.slice(Math.max(0, a.pos - 6), a.pos + 80);
    a.side = /couloir droit|c[ôo]t[ée] droit|[àa] droite/.test(win) ? "d"
      : /couloir gauche|c[ôo]t[ée] gauche|[àa] gauche/.test(win) ? "g"
      : /centre|axe|axial/.test(win) ? "c" : null;
    a.deep = /zone de marque|en.?but|profondeur|derni[èe]re ligne|fond/.test(win);
    a.back = /d[ée]fense|d[ée]fensive|r[ée]cup[èe]re|derri[èe]re/.test(win);
  }
  return arr;
}
function countNamedActors(t) { return detectNamedActors(c0(t)).length; }

/* Phase de jeu décrite avec des acteurs nommés (A→B→C…). On CRÉE un joueur par
   acteur (placé selon les indices côté/profondeur) et on construit une chaîne
   SÉQUENTIELLE robuste : chaque receveur fait un APPEL vers l'avant puis reçoit
   la passe (suivi temporel -> la passe arrive à la nouvelle position). Le
   dernier reçoit dans la zone. Plus fiable que le moteur de verbes sur un texte
   multi-phrases (« zone de marque » ne crée pas de faux tirs, ordre respecté). */
function buildNamedPlay(text, sport) {
  const defs = detectNamedActors(c0(text));
  if (defs.length < 2) return null;
  const n = defs.length;
  const sideY = { g: 26, c: 50, d: 74 };
  let altUp = true;
  const players = defs.map((d, i) => {
    let y = d.side ? sideY[d.side] : (altUp ? 34 : 66);
    if (!d.side) altUp = !altUp;
    // Progression régulière vers le but selon l'ordre ; le 1er recule s'il
    // récupère « en défense », le DERNIER va au fond s'il finit « dans la zone »
    // (on n'empile pas tous les « en profondeur » à droite).
    let x = clampN(Math.round(16 + (i / Math.max(1, n - 1)) * 64), 8, 88);
    if (i === 0 && d.back) x = Math.min(x, 14);
    if (i === n - 1 && d.deep) x = 88;
    return { id: "P" + (i + 1), num: i + 1, role: "Joueur " + d.letter.toUpperCase(), team: "A", color: "#0072B2", x, y };
  });
  const markers = [{ type: sport === "ultimate" ? "ballon" : "ballon", x: clampN(players[0].x + 5, 6, 96), y: players[0].y }];
  const actions = []; let ord = 1;
  for (let i = 1; i < players.length; i++) {
    const recv = players[i], giver = players[i - 1], last = i === players.length - 1;
    // Appel : le receveur se projette vers l'avant (vers le but, à droite).
    const tox = clampN(recv.x + (last ? 8 : 12), 8, 95);
    actions.push({ from: recv.id, tox, toy: recv.y, type: "course", order: ord++, label: (last ? "Course dans la zone : " : "Appel : ") + recv.role });
    // Passe du porteur vers le receveur (arrive à sa position avancée).
    actions.push({ from: giver.id, to: recv.id, type: "passe", order: ord++, label: `${giver.role} → ${recv.role}` });
  }
  const steps = [];
  for (let i = 1; i < players.length; i++) {
    steps.push(`${players[i - 1].role} → ${players[i].role}` + (i === players.length - 1 ? " (réception dans la zone)" : ""));
  }
  // Synthèse pour le panneau « Ce que j'ai compris » (contrat invasion) :
  // P1..Pn -> A1..An ; chaque maillon = appel du receveur puis passe.
  const phase = [];
  for (let i = 1; i < n; i++) {
    phase.push({ acteur: "A" + (i + 1), action: "course", cible: i === n - 1 ? "but" : null, note: (i === n - 1 ? "Course dans la zone : " : "Appel : ") + players[i].role });
    phase.push({ acteur: "A" + i, action: "passe", cible: "A" + (i + 1), note: `${players[i - 1].role} → ${players[i].role}` });
  }
  const analyse = {
    version: 1, famille: "invasion",
    effectif: { attaquants: n, defenseurs: 0, gardien: false },
    depart: null,
    phase,
  };
  return { players, markers, actions, steps, analyse };
}

/* ALIMENTATION À SENS UNIQUE — un joueur ENVOIE, l'autre se déplace et ne
   renvoie pas : panier, robot, partenaire qui alimente, service répété. C'est
   l'EXCEPTION ; un échange de raquette ALTERNE les frappeurs, et c'est la
   règle.

   Prédicat PARTAGÉ entre le moteur (`buildRacketRally`, qui alterne sauf ici)
   et le juge (dérogation de la règle `rallye`, src/fidelity.js) — juge et
   moteur doivent dire la même chose, sinon l'un fabrique exactement ce que
   l'autre reproche. C'est ce qui s'était produit : `rallye` était le PREMIER
   défaut du repli (169 signalements sur le corpus routé, presque le double du
   suivant), et il venait du gabarit d'échange lui-même, qui faisait frapper A
   à chaque coup pendant que B se contentait de courir.

   Un verbe d'ENVOI ne suffit pas à conclure : il faut que RIEN ne dise que
   l'autre renvoie (« le relanceur cherche à retourner croisé » est un
   échange, malgré « un serveur »). Mesuré sur les 85 scènes du corpus qui
   portaient le défaut : 10 alimentations, 75 échanges, et les 10 sont de vrais
   exercices à sens unique (robot, panier, service seul). */
const FEED_ONE_WAY = /\benvoie|\balimente|\bdistribue|\blance\b|panier de|casier|multi.?(?:balles?|volants?)|nourri|sert (?:\d+|cinq|trois|quatre|dix) fois|chaque [ée]l[èe]ve sert|services? successifs?|t'envoie|vous envoie|leur envoie|un serveur|le serveur\b|son service|sert depuis/i;
const FEED_RETURNED = /renvoie|renvoient|remet|remettre|relance|[ée]change|rallye|se renvoient|coop[ée]r|adversaire (?:relance|renvoie|joue)|chacun (?:frappe|renvoie|joue)|match|point|duel/i;
export function isOneWayFeed(text) {
  const t = String(text || "");
  return FEED_ONE_WAY.test(t) && !FEED_RETURNED.test(t);
}

/* Échange raquette/filet : A à gauche, B à droite. Reproduit un motif
   « gauche-droite N fois puis M fois du même côté » (contre-pied). */
function buildRacketRally(text, sport) {
  const t = text.toLowerCase();
  const A = { id: "A", num: 1, role: "Joueur A", x: 16, y: 50, color: "#0072B2", team: "A" };
  const B = { id: "B", num: 2, role: "Joueur B", x: 84, y: 50, color: "#D55E00", team: "B" };
  const players = [A, B], actions = [], markers = [{ type: "ballon", x: 22, y: 50 }];
  const counts = []; const re = /(\d+|un|une|deux|trois|quatre|cinq|six|sept)\s*fois/g; let mm;
  while ((mm = re.exec(t))) counts.push(Math.max(1, Math.min(6, frenchNum(mm[1]))));
  const hasCP = /contre.?pied/.test(t);  // le motif contre-pied n'apparaît QUE s'il est demandé
  const alt = counts[0] || 3;            // alternances gauche-droite
  const same = hasCP ? (counts[1] || 2) : 0; // répétitions même côté (contre-pied)
  const TOP = 22, BOT = 78;              // côtés de B (gauche = haut, droite = bas en paysage)
  let order = 1;
  let side = (t.indexOf("droite") >= 0 && (t.indexOf("gauche") < 0 || t.indexOf("droite") < t.indexOf("gauche"))) ? 1 : 0;
  /* REPLACEMENT ENTRE LES FRAPPES (mesure 08/08 : sur 45 scènes de filet du lot
     doré où un replacement est demandé, le REPLI n'en dessinait AUCUN dans
     49 % des cas — B courait chercher la balle et restait planté là où il
     l'avait prise). Tant que c'était vrai, refuser une scène d'IA pour
     `replacement` revenait à retomber une fois sur deux sur le même défaut.
     GATE : seulement si le texte le demande — même déclencheur que la règle
     `replacement` de src/fidelity.js, pour que juge et moteur disent la même
     chose. Sinon on ajouterait 3 à 6 flèches à TOUS les rallyes, au prix de la
     lisibilité, pour une consigne que l'enseignant n'a pas donnée.
     BASE : celle du sport (précision enseignant du 08/08) — au TENNIS le
     joueur de fond revient au centre DERRIÈRE sa ligne (x≈92 côté B) ; au
     badminton c'est le centre de son demi-court (x≈73) ; au tennis de table il
     reste à l'extérieur de la table (x≈84). Toujours sur l'axe (y=50). */
  const wantsReplacement = /replac|revenir (?:vers|au|à) (?:le |la )?(?:centre|milieu)|retour au (?:centre|milieu)|revien(?:s|t|nent) (?:vers |au |à )?(?:le |la )?(?:centre|milieu)|y revenir|revenir (?:au|sur|à) (?:son |leur |le |la )?(?:plot|rep[èe]re|marque|base)/i.test(text)
    // DÉROGATION, la même que le juge et que finish() : un travail annoncé SUR
    // PLACE ne se replace pas (sinon on contredit la consigne de l'enseignant).
    && !/sur place|sans (?:se )?d[ée]plac|sans bouger|statique|immobile|pieds fixes/i.test(text);
  const BASE_X = sport === "tennis" ? 92 : sport === "tennis_de_table" ? 84 : 73;
  const BASE_A = 100 - BASE_X;           // la base de A est le miroir de celle de B
  /* ALTERNANCE DES FRAPPEURS — le défaut nº 1 du repli, et il était ICI. Ce
     gabarit est celui de l'ÉCHANGE (son titre le dit) : A frappait pourtant
     TOUTES les balles, B se contentant de courir dessous. « l'adversaire n'a
     jamais touché le volant » — 169 signalements sur le corpus routé, et des
     👎 sans appel (« cela n'a aucun sens », « L'échange pour créer… »).
     Sauf ALIMENTATION à sens unique (robot, panier, service seul), où c'est le
     dispositif : le frappeur alterne désormais, et le receveur est celui qui
     court puis se replace. */
  const alterne = !isOneWayFeed(text);
  let k = 0;                             // index de frappe, continu jusqu'au contre-pied
  const frappeurDe = (i) => (alterne && i % 2) ? "B" : "A";
  for (let i = 0; i < alt; i++, k++) {
    const frappeur = frappeurDe(k), receveur = frappeur === "A" ? "B" : "A";
    const ty = side ? BOT : TOP;
    // La cible est dans le camp du RECEVEUR : côté B (x≈72-78) quand A frappe,
    // côté A (x≈22-28) quand B renvoie — miroir exact.
    const tx = frappeur === "A" ? 72 + (i % 2) * 6 : 28 - (i % 2) * 6;
    actions.push({ from: frappeur, tox: tx, toy: ty, type: "renvoi", style: "solid", order: order++, label: side ? "Frappe côté droit" : "Frappe côté gauche" });
    // Le receveur se place DERRIÈRE le volant, du côté de son camp — et à 7
    // unités, pas 3. À 3, sa pastille d'arrivée et le fantôme de retombée se
    // CHEVAUCHENT au rendu : deux ronds l'un sur l'autre, illisibles (vu à
    // l'œil sur PROD4 ; aucun banc ne le signale, les fantômes ne sont pas des
    // joueurs pour le contrôle de superposition). Le défaut préexistait côté B,
    // l'alternance le rendait visible des deux côtés.
    actions.push({ from: receveur, tox: frappeur === "A" ? tx + 7 : tx - 7, toy: ty, type: "course", style: "dashed" });
    // …puis retour à la base AVANT la frappe suivante (pas après la dernière :
    // le point est fini, et une flèche de plus n'apprend rien).
    if (wantsReplacement && i < alt - 1)
      actions.push({ from: receveur, tox: receveur === "B" ? BASE_X : BASE_A, toy: 50, type: "course", style: "dashed", order: order++, label: "Replacement" });
    side = side ? 0 : 1;
  }
  const wrong = side ? TOP : BOT;        // contre-pied : on vise le côté que l'autre vient de quitter
  for (let j = 0; j < same; j++, k++) {
    const frappeur = frappeurDe(k);
    actions.push({ from: frappeur, tox: frappeur === "A" ? 74 : 26, toy: wrong, type: "tir", style: "solid", order: order++, label: "Contre-pied" });
  }
  return { players, markers, actions };
}

/* ---- ENTRER DANS L'ÉCHANGE COMME DOMINANT (service) ------------------------
   Chaîne pédagogique complète, en 5 temps : le SERVICE écarte le relanceur ->
   celui-ci part EN RETARD (départ décalé s'il est annoncé « du couloir opposé »)
   -> sa remise est FAIBLE (courte, au centre) -> le serveur se replace dessus
   -> et CONCLUT le point dans l'espace libre. Le rally générique montrait un
   échange neutre : ni la mise en difficulté, ni le point du serveur. */
function buildDominantServe(text, sport) {
  const t = c0(text || "");
  // ── Ce que le texte impose (le reste est le motif par défaut) ──────────────
  // 1) PROFONDEUR DU SERVICE : court (juste au-dessus du filet) ou long (fond).
  const shortServe = /(?:sert|servir|service|services)[^.;]{0,20}\bcourts?\b|\bcourts?\b[^.;]{0,20}(?:au|sur le|par.dessus le)\s*filet|service au filet/.test(t);
  // 2) DÉPART DU RELANCEUR : décalé (couloir opposé) et/ou en FOND de court.
  const offset = /couloir oppos[ée]|c[ôo]t[ée] oppos[ée]|d[ée]cal[ée]|mauvais c[ôo]t[ée]|du c[ôo]t[ée] inverse/.test(t);
  const fromBack = /couloir de fond|fond de (?:court|terrain)|en fond de court|ligne de fond|depuis le fond|part du fond/.test(t);
  // Le service file en DIAGONALE dans le camp de B : au FOND (service long) ou
  // juste derrière le filet (service court) — c'est lui qui crée le retard.
  // GÉOMÉTRIE RÉGLEMENTAIRE (render.js) : filet en 50, lignes de service courtes
  // en 34 et 66, lignes de fond en 6 et 94, ligne centrale en y=50. Les CARRÉS
  // DE SERVICE sont donc x∈[6,34] à gauche et x∈[66,94] à droite.
  // Un service COURT retombe juste derrière la ligne de service court adverse —
  // jamais au milieu du court (la « rivière » : service illégal au badminton).
  const SERVE = shortServe ? { x: 68, y: 62 } : { x: 89, y: 69 };
  // Le SERVEUR engage depuis son carré de service, donc DERRIÈRE sa ligne de
  // service court (x < 34) — court ou long ne change que son recul dans le carré.
  const A = { id: "A", num: 1, role: "Joueur A", x: shortServe ? 30 : 26, y: 32, color: "#0072B2", team: "A" };
  // Le RELANCEUR se tient dans le carré de service DIAGONAL (celui que vise le
  // service) — c'est la règle. Ce qui varie, c'est son placement DANS ce carré :
  //  · « couloir de fond » : bon côté, mais au fond -> il arrive en retard ;
  //  · « couloir opposé »  : mauvais côté (l'erreur travaillée) ;
  //  · sinon               : couverture normale, à mi-court.
  const B = { id: "B", num: 2, role: "Joueur B", team: "B", color: "#D55E00",
    x: fromBack ? 90 : offset ? 84 : 74,
    y: offset ? (SERVE.y > 50 ? 12 : 88) : fromBack ? SERVE.y : 58 };
  // Remise FAIBLE : courte et centrale, dans le camp du serveur — c'est elle qui
  // offre la balle de conclusion.
  const REMISE = { x: 31, y: 55 };
  // Frappe gagnante : l'ESPACE LIBRE, c'est-à-dire l'inverse de là où le service
  // a fixé B — au FOND si B a été attiré au filet, court s'il est resté derrière ;
  // et toujours dans le couloir opposé au point de chute.
  const WIN = { x: shortServe ? 88 : 62, y: SERVE.y > 50 ? 20 : 80 };
  const players = [A, B];
  const markers = [{ type: "ballon", x: A.x + 4, y: A.y }];
  const actions = [
    { from: "A", tox: SERVE.x, toy: SERVE.y, type: "renvoi", order: 1,
      label: shortServe ? "Service court, juste au-dessus du filet" : "Service long, loin de B" },
    { from: "B", tox: SERVE.x, toy: SERVE.y, type: "course", order: 2,
      label: offset ? "Départ du couloir opposé : B traverse"
        : fromBack ? "Parti du fond : B arrive en retard" : "B se déplace sur le volant" },
    { from: "B", tox: REMISE.x, toy: REMISE.y, type: "renvoi", order: 3, label: "Remise en retard (balle facile)" },
    { from: "A", tox: REMISE.x + 2, toy: REMISE.y, type: "course", order: 4, label: "Le serveur enchaîne sur la balle" },
    { from: "A", tox: WIN.x, toy: WIN.y, type: "tir", order: 5,
      label: shortServe ? "2e coup gagnant dans le fond libéré" : "Frappe gagnante dans l'espace libre" },
  ];
  return { players, markers, actions, sport };
}

/* ---- Échange raquette SÉQUENCÉ --------------------------------------------
   Suit les FRAPPES décrites (service, poussette, top-spin, bloc, attaque…)
   dans l'ordre du texte, avec leurs acteurs (« le joueur B remet… ») et les
   zones visées. Convention vue de dessus (joueurs droitiers) : le REVERS de A
   (à gauche, face au jeu) est en HAUT, celui de B (à droite) en BAS — la
   « diagonale revers » relie donc haut-gauche et bas-droite. Renvoie null si
   moins de deux frappes reconnues (repli sur le motif buildRacketRally). */
/* ════════════════════════════════════════════════════════════════════════════
   LEXIQUE DES COUPS (sports de filet) — SOURCE UNIQUE
   Chaque coup décrit :
     • re     reconnaissance dans le texte ;
     • type   flèche EPS : "renvoi" (échange) ou "tir" (frappe gagnante) ;
     • name   libellé canonique affiché ;
     • depth  zone d'arrivée PAR DÉFAUT si le texte ne la précise pas
              (avant = au filet · median = mi-court · fond) ;
     • fixed  profondeur IMPOSÉE (ex. l'amorti reste au filet même décrit « long ») ;
     • sports activités concernées — DESCRIPTIF (la reconnaissance reste pour
              l'instant indépendante du sport ; champ prévu pour un futur cadrage).
   L'ORDRE compte : la 1re entrée qui matche gagne (du plus SPÉCIFIQUE au plus
   générique). Pour enrichir une activité : compléter/ajouter une entrée ICI
   (puis une assertion au corpus). ════════════════════════════════════════════ */
const STROKE_LEXICON = [
  { re: /poussette|pouss[eé]/,                       type: "renvoi", name: "Poussette",    depth: "avant", sports: ["tennis_de_table", "badminton", "tennis"] },
  { re: /top.?spin|\blift\b/,                        type: "tir",    name: "Top-spin",                     sports: ["tennis_de_table", "tennis"] },
  { re: /sma[sc]h/,                                  type: "tir",    name: "Smash",        depth: "avant", sports: ["badminton", "volleyball", "tennis"] },
  { re: /\bkill\b/,                                  type: "tir",    name: "Kill",         depth: "avant", sports: ["badminton", "volleyball"] },
  { re: /acc[ée]l[èe]r/,                             type: "tir",    name: "Accélération",                 sports: ["tennis_de_table"] },
  { re: /vol[ée]e/,                                  type: "tir",    name: "Volée",                        sports: ["tennis"] },
  { re: /amorti/,                                    type: "renvoi", name: "Amorti",       depth: "avant", fixed: true, sports: ["badminton", "tennis"] },
  { re: /d[ée]gag(?:ement|ent|e)(?!s)|\blob\b|\bclear\b/, type: "renvoi", name: "Dégagement", depth: "fond", sports: ["badminton", "tennis"] },
  { re: /\bbloc\b|bloque/,                           type: "renvoi", name: "Bloc",                         sports: ["volleyball", "tennis_de_table"] },
  // « contre » = contre/bloc actif (TT, tennis, volley). GARDÉ contre l'effectif
  // « N contre M » (chiffre/nombre attenant) et les composés « contre-attaque/pied ».
  { re: /\bcontre[rz]?\b(?!-)(?!\s*(?:\d|une?\b|deux\b|trois\b|quatre\b|cinq\b|six\b|sept\b|huit\b|le\b|la\b|les\b|l['’]|du\b|des\b|au\b|aux\b|son\b|sa\b|ses\b|adversaire|joueur|[A-D]\b))/, type: "renvoi", name: "Contre", sports: ["tennis_de_table", "tennis", "volleyball"] },
  { re: /ser?vice|\bsert\b|engage\b|mises? en jeu/,  type: "renvoi", name: "Service",      depth: "fond",  sports: ["badminton", "tennis", "volleyball", "tennis_de_table"] },
  { re: /r[ée]ception\b/,                            type: "renvoi", name: "Réception",                    sports: ["volleyball"] },
  { re: /remise|remet\b|renvoie|retourne\b/,         type: "renvoi", name: "Remise",                       sports: ["badminton", "tennis", "tennis_de_table"] },
  { re: /attaque/,                                   type: "tir",    name: "Attaque",                      sports: ["volleyball"] },
  { re: /manchett?e/,                                type: "renvoi", name: "Manchette",                    sports: ["volleyball"] },
  { re: /touche de balle|passe haute/,               type: "renvoi", name: "Touche",                       sports: ["volleyball"] },
  { re: /jouer? (?:le|la) ball/,                     type: "tir",    name: "Frappe",                       sports: ["volleyball", "badminton", "tennis", "tennis_de_table"] },
  { re: /envoie|\benvoi\b/,                          type: "renvoi", name: "Envoi",                        sports: ["volleyball", "badminton"] },
  { re: /\bpasse\b/,                                 type: "renvoi", name: "Passe",                        sports: ["volleyball"] },
  { re: /frappe\b/,                                  type: "tir",    name: "Frappe",                       sports: ["badminton", "tennis", "tennis_de_table", "volleyball"] },
];
// Formes DÉRIVÉES (compat, ordre préservé) : tuple consommé par la tokenisation
// des coups, et table des profondeurs par défaut consommée par depthOf().
const STROKE_DEFS = STROKE_LEXICON.map((s) => [s.re, s.type, s.name]);
const STROKE_DEPTH = {};
for (const s of STROKE_LEXICON) if (s.depth && !(s.name in STROKE_DEPTH)) STROKE_DEPTH[s.name] = { depth: s.depth, fixed: !!s.fixed };
/* « frappe » est une frappe d'ATTAQUE seulement en usage verbal : « après
   chaque frappe », « la frappe » (noms) ne créent pas d'action. */
function isNounFrappe(body, idx) {
  return /(?:chaque|cette|toute|la|une|sa)\s+$/.test(body.slice(Math.max(0, idx - 10), idx));
}
/* « travail du service », « compte les services réussis » : mention du service
   comme THÈME de la séance ou CRITÈRE de réussite, pas une frappe à dessiner.
   Étendu (salve réception) aux mentions-thèmes de la RÉCEPTION/MANCHETTE
   (« travail de la précision de réception ») et au COMPTE suivi d'un
   changement de rôles (« 8 services puis rotation des rôles »). */
function isNounService(body, idx) {
  const before = body.slice(Math.max(0, idx - 26), idx);
  if (/(?:travail\w*|exercice|atelier|s[ée]ance|apprentissage|progresser|pr[ée]cision|qualit[ée]|direction|choix)\s+(?:du|de la|de l['’]|de|des|en|d['’])\s*$/.test(before)) return true;
  if (/(?:compt\w+|not[ée]\w*|valid\w+|observ\w+|r[ée]ussite\s+(?:du|des)|nombre\s+d['’])\s*(?:les|le|la|l['’]|ses|ces)?\s*$/.test(before)) return true;
  // « Rotation toutes les 8 attaques » : le compte du CHANGEMENT de rôles.
  if (/rotation\s+(?:toutes?\s+les?\s+)?(?:\d+|deux|trois|quatre|cinq|six|sept|huit|neuf|dix)\s+$/.test(before)) return true;
  return /(?:\d+|deux|trois|quatre|cinq|six|sept|huit|neuf|dix)\s+$/.test(before)
    && /^(?:services?|r[ée]ceptions?|manchett?es?|attaques?|smash?s?)\s*(?:,|puis|et)\s*(?:on\s+)?(?:rotation|chang\w*|tourne|inverse|permut\w*)/.test(body.slice(idx));
}
function buildRacketSequence(text, sport, pre) {
  // Les consignes / récapitulatifs ne décrivent pas l'échange (ils le
  // paraphrasent), et la ligne de TITRE peut citer des coups sans être une
  // frappe : on ne lit que la description de la situation.
  // bodyRaw conserve la CASSE (les joueurs « A », « B », « C », « D » du
  // double s'identifient par leur majuscule) ; body est sa version minuscule,
  // aux MÊMES positions (les index des regex sont interchangeables).
  const bodyRaw = String(text || "")
    .replace(/^\s*(titre|sport|niveau|objectif)\s*:[^\n]*/gim, "")
    .split(/(?:consignes?|donc|[ée]l[ée]ments [àa] repr[ée]senter|crit[èe]res?(?: de r[ée]ussite)?)\s*:/i)[0];
  const body = bodyRaw.toLowerCase();
  // ── VOLLEY exercice MULTI-TOUCHES COOPÉRATIF (chacun se fait N touches) ──
  // « réception basse POUR SOI, puis passe dix doigts POUR SOI vers l'avant,
  // puis passe tendue au fond de l'adversaire ; celui-ci RÉPÈTE à l'identique » :
  // chaque joueur enchaîne N touches POUR LUI-MÊME avant d'envoyer par-dessus le
  // filet (≠ du cas saisie ci-dessous : ici l'adversaire RENVOIE). Orthographe
  // tolérée (« pour sois même »).
  if (sport === "volleyball" && !pre) {
    const soi = /pour\s+soi(?:s)?(?:[- ]?m[êe]me)?|pour\s+(?:lui|elle)[- ]?m[êe]me/i.test(body);
    const soiCount = (body.match(/pour\s+soi(?:s)?/gi) || []).length;
    const renvoiFinal = /adversaire|par-?dessus|au fond|dans (?:le|son) camp|de l['’]autre c[ôo]t[ée]/i.test(body);
    const repete = /r[ée]p[èe]te|[àa] l['’]identique|ainsi de suite|chacun (?:son|leur)/i.test(body);
    if (soi && soiCount >= 2 && renvoiFinal && repete) {
      const N = clampN(soiCount + 1, 3, 5);          // 2 touches pour soi + 1 envoi = 3
      const players = [
        { id: "A", num: 1, role: "Joueur A", x: 8, y: 50, color: "#0072B2", team: "A" },
        { id: "B", num: 2, role: "Joueur B", x: 90, y: 50, color: "#D55E00", team: "B" },
      ];
      const actions = []; let ord = 1;
      const startX = 8, frontX = 42;
      // N-1 touches POUR SOI en progressant vers le filet (dont la 1re = réception).
      for (let i = 1; i < N; i++) {
        const x = Math.round(startX + ((frontX - startX) * i) / (N - 1));
        actions.push({ from: "A", tox: x, toy: 50, type: "course", order: ord++,
          label: i === 1 ? "Réception pour soi" : i === N - 1 ? "Passe (10 doigts) pour soi" : "Touche pour soi" });
      }
      // Dernière touche : passe TENDUE par-dessus le filet, au FOND adverse.
      actions.push({ from: "A", tox: 90, toy: 50, type: "renvoi", order: ord++, label: "Passe tendue au fond adverse" });
      // L'adversaire RÉPÈTE : symétrique (une touche indicative pour signifier
      // qu'il enchaîne à son tour la même séquence).
      actions.push({ from: "B", tox: 66, toy: 50, type: "course", order: ord++, label: "Répète à l'identique" });
      return {
        players, markers: [{ type: "ballon", x: startX + 4, y: 50 }],
        actions,
        steps: [`Chaque joueur enchaîne ${N} touches pour lui-même`, "Réception basse puis passe (10 doigts) vers l'avant", "Passe tendue au fond du camp adverse ; l'autre répète"],
        analyse: { version: 1, famille: "filet", format: "simple", coups: [
          { acteur: "A", coup: "reception", profondeur: null, direction: null, repetitions: 1 },
          { acteur: "A", coup: "passe", profondeur: "avant", direction: null, repetitions: N - 2 },
          { acteur: "A", coup: "passe", profondeur: "fond", direction: null, repetitions: 1 },
          { acteur: "B", coup: "reception", profondeur: null, direction: null, repetitions: 1 },
        ] },
      };
    }
  }
  // ── VOLLEY exercice MULTITOUCHE / coïncidence (1v1 coopératif) ──────────
  // « A se fait N passes pour lui[-]même » + « B bloque/attrape/réceptionne » :
  // ce n'est pas un échange (en match, A ne pourrait pas se passer la balle
  // à lui-même) mais un EXERCICE — A progresse en multitouche depuis la ligne
  // de fond, puis envoie chez B qui se déplace pour ATTRAPER la balle (saisie,
  // pas un contre/bloc qui renverrait). On gère ce cas en amont du rallye.
  if (sport === "volleyball" && !pre) {
    const NUMW = { deux: 2, trois: 3, quatre: 4, cinq: 5, six: 6 };
    const auto = body.match(/(\d+|deux|trois|quatre|cinq|six)\s*(?:auto.?passes?|passes?\s+(?:pour|à)\s+lui[- ]?m[êe]me|passes?\s+pour\s+soi)/)
      || body.match(/(?:auto.?passes?|passes?\s+(?:pour|à)\s+lui[- ]?m[êe]me|passes?\s+pour\s+soi).{0,40}?(\d+|deux|trois|quatre|cinq|six)/);
    const saisit = /\bbloque\b|\battrape\b|\bsaisit\b|\br[ée]ceptionne\b|\bcapte\b/.test(body);
    if (auto && saisit) {
      const N = clampN(NUMW[auto[1]] != null ? NUMW[auto[1]] : parseInt(auto[1], 10) || 3, 2, 6);
      const players = [
        { id: "A", num: 1, role: "Joueur A", x: 8, y: 50, color: "#0072B2", team: "A" },
        { id: "B", num: 2, role: "Joueur B", x: 78, y: 50, color: "#D55E00", team: "B" },
      ];
      const actions = []; let ord = 1;
      // 1) Auto-passes : progression LIGNE DE FOND -> ZONE AVANT (fantômes
      //    numérotés via les ghosts d'arrivée des courses).
      const startX = 8, frontX = 44;
      for (let i = 1; i <= N; i++) {
        const x = Math.round(startX + ((frontX - startX) * i) / N);
        actions.push({ from: "A", tox: x, toy: 50, type: "course", order: ord++, label: i === 1 ? `Auto-passes ×${N}` : null });
      }
      // 2) Passe par-dessus le filet vers la ZONE ARRIÈRE de B.
      const landX = 84, landY = 50;
      actions.push({ from: "A", tox: landX, toy: landY, type: "renvoi", order: ord++, label: "Passe vers la zone arrière" });
      // 3) Anticipation / coïncidence : B se déplace au point de chute et SAISIT.
      actions.push({ from: "B", tox: landX, toy: landY, type: "course", order: ord++, label: "Anticipation / saisie" });
      const markers = [
        { type: "ballon", x: startX + 4, y: 50 },                              // ballon de départ (au porteur A)
        { type: "ballon", x: landX, y: landY },                                // ballon SAISI au point de chute (la balle s'arrête)
      ];
      return {
        players, markers, actions,
        steps: [`A se fait ${N} auto-passes en progressant vers la zone avant`, "Passe par-dessus le filet vers la zone arrière de B", "B anticipe le point de chute et attrape la balle"],
        analyse: { version: 1, famille: "filet", format: "simple", coups: [
          { acteur: "A", coup: "passe", profondeur: null, direction: null, repetitions: N },
          { acteur: "A", coup: "passe", profondeur: "fond", direction: null, repetitions: 1 },
          { acteur: "B", coup: "reception", profondeur: null, direction: null, repetitions: 1 },
        ] },
      };
    }
  }
  // ── VOLLEY : DRILL DE DÉFENSE (attaquant adverse -> défenseur(s)) ─────────
  // « l'attaquant adverse smashe (depuis une caisse), le(s) défenseur(s) en
  // zones 1 et 5 relèvent en manchette vers le passeur / un cerceau en zone
  // 3 » : le smash TRAVERSE, la défense RESTE dans le camp (relance vers
  // l'avant). Sans gabarit, ce motif tombait sur la réception de service
  // canonique (serveur inventé) ou un rallye traversant. Gate étroite :
  // volley, défenseur(s)/libéro décrits + un frappeur adverse
  // (attaquant/entraîneur qui smashe), PAS de réception ni de « N contre M »
  // ni de contreurs (le contre + couverture relève de la famille defense).
  if (sport === "volleyball") {
    const hasDef = /d[ée]fenseurs?|lib[ée]ro/.test(body);
    const hasFeeder = /attaquant|ataquant|entra[iî]neur|enseignant|sma[sc]h/.test(body);
    const noRecep = !/r[ée]ception\b|r[ée]ceptionn?eur|contreur/.test(body);
    const noDuel2 = !/\b(?:\d|un|une|deux|trois|quatre|cinq|six)\s*(?:contre|vs|[cv]\.?)\s*(?:\d|un|une|deux|trois|quatre|cinq|six)\b/.test(body);
    if (hasDef && hasFeeder && noRecep && noDuel2 && /d[ée]fen[sc]/.test(body)) {
      const players = [], markers = [], actions = []; let ord = 1;
      const coach = /entra[iî]neur|enseignant/.test(body);
      players.push({ id: "AT", num: 1, role: coach ? "Entraîneur" : "Attaquant", x: 44, y: 50, color: "#0072B2", team: "A" });
      markers.push({ type: "ballon", x: 47, y: 46 });
      // Défenseurs : compte lu (« deux défenseurs », fautif accepté), libéro =
      // 1 ; zones arrière 1 et 5 = les coins, zone 6 = le centre.
      const libero = /lib[ée]ro/.test(body);
      const dM = body.match(/\b(un|deux|trois|[123])\s*d[ée]fenseurs?/);
      const nDef = libero ? 1 : dM ? ({ un: 1, deux: 2, trois: 3 }[dM[1]] || parseInt(dM[1], 10)) : 1;
      const spots = nDef >= 3 ? [{ x: 84, y: 22 }, { x: 84, y: 50 }, { x: 84, y: 78 }]
        : nDef === 2 ? [{ x: 84, y: 26 }, { x: 84, y: 74 }]
        : [{ x: 82, y: /zone\s*5/.test(body) ? 70 : /zone\s*1\b/.test(body) ? 30 : 50 }];
      spots.forEach((s, i) => players.push({ id: "DF" + (i + 1), num: i + 1, role: libero ? "Libéro" : "Défenseur", x: s.x, y: s.y, color: "#D55E00", team: "B" }));
      // Cible de la relance : le PASSEUR décrit, sinon un cerceau/point de
      // chute en zone avant (zone 2-3).
      const setter = /pas{1,2}eur/.test(body);
      if (setter) players.push({ id: "PS", num: nDef + 1, role: "Passeur", x: 58, y: 46, color: "#D55E00", team: "B" });
      const hoop = /cerceau|cible/.test(body);
      if (hoop) markers.push({ type: "cerceau", x: 56, y: 54 });
      const relTo = setter ? { to: "PS" } : hoop ? { tox: 56, toy: 54 } : { tox: 58, toy: 50 };
      // Déplacement de LECTURE (« se déplace vers la ligne de côté ») avant la
      // défense — smash alors croisé vers ce côté.
      const reads = /se d[ée]place[^.;]{0,30}ligne de c[ôo]t[ée]/.test(body);
      const cross2 = /crois[ée]/.test(body);
      const repM2 = body.match(/(\d+)\s*(?:balles?|ballons?|sma[sc]h[ée]?s?|attaques?|frappes?)/);
      spots.forEach((s, i) => {
        if (i >= 2) return;                                    // 2 flèches max (lisibilité)
        let dy = s.y;
        if (reads && i === 0) {
          dy = Math.min(86, s.y + 14);
          actions.push({ from: "DF1", tox: s.x, toy: dy, type: "course", order: ord++, label: "Se déplace (lecture)" });
        }
        actions.push({ from: "AT", tox: s.x - 4, toy: dy, type: "tir", order: ord++,
          label: (cross2 ? "Smash croisé" : coach ? "Frappe" : "Smash") + (i === 0 && repM2 ? ` (×${repM2[1]})` : "") });
        actions.push({ from: "DF" + (i + 1), ...relTo, type: "renvoi", order: ord++, label: libero ? "Manchette basse" : "Manchette défensive" });
      });
      return {
        players, markers, actions,
        steps: [
          coach ? "L'entraîneur frappe vers les défenseurs" : "L'attaquant adverse smashe",
          reads ? "Lire l'élan et se déplacer avant la frappe" : "Jambes fléchies, plateforme orientée",
          setter ? "Relever la balle jouable vers le passeur" : hoop ? "Faire retomber la défense dans le cerceau (zone 3)" : "Relever la balle vers la zone avant",
        ],
        analyse: { version: 1, famille: "filet", format: "equipes",
          effectif: { gauche: 1, droite: nDef + (setter ? 1 : 0) },
          coups: [
            { acteur: "adversaire", coup: "attaque", profondeur: "fond", direction: cross2 ? "croise" : null, repetitions: repM2 ? Math.min(20, +repM2[1]) : 1 },
            { acteur: "A", coup: "reception", profondeur: "avant", direction: null, repetitions: 1 },
          ] },
      };
    }
  }
  // ── VOLLEY : DRILL PASSEUR -> ATTAQUANT (travail de l'attaque) ────────────
  // « le passeur monte la balle, l'attaquant smashe (croisé / le long de la
  // ligne) » : passeur et attaquant sont PARTENAIRES — la passe haute reste
  // dans le camp, SEUL le smash traverse le filet. Le rallye alterné rendait la
  // passe par-dessus le filet (passeur adverse) et le bloc « equipes »
  // inventait service + réception. Gate étroite : volley, une frappe d'attaque
  // ET un passeur/une passe haute décrits, PAS de réception ni de « N contre
  // M » ni de bloc/contre adverse ; un service n'est admis que via un
  // LANCEUR/engageur (drill alimenté d'en face). Fonctionne pour la route
  // ANALYSE (pre : directions/répétitions des coups) comme pour la route locale.
  if (sport === "volleyball") {
    const hasAtk = /sma[sc]h|attaqu|frapp/.test(body);
    const hasSet = /pas{1,2}eurs?\b|passe haute\b|monte[rz]?\s+(?:la|le|une|des|\d+\s*)?\s*(?:balles?|ballons?|passe)/.test(body);
    const noOpp = !/r[ée]ception|manchett?e|\bbloc\b|d[ée]fens/.test(body);
    const noDuel = !/\b(?:\d|un|une|deux|trois|quatre|cinq|six)\s*(?:contre|vs|[cv]\.?)\s*(?:\d|un|une|deux|trois|quatre|cinq|six)\b/.test(body);
    const svcOk = !/ser?vice|\bsert\b/.test(body) || /lanceur|engage/.test(body);
    if (hasAtk && hasSet && noOpp && noDuel && svcOk) {
      // Côté de l'attaquant : zone 4 (avant-gauche, y bas) par défaut ; zone 2
      // ou « côté droit » -> avant-droit (y haut). Convention : gauche = haut.
      const side2 = (/zone\s*2\b/.test(body) && !/zone\s*4\b/.test(body)) || /c[ôo]t[ée] droit/.test(body);
      const atkY = side2 ? 74 : 26;
      const pY = atkY <= 50 ? 56 : 44;
      const elan = /[ée]lan|appuis|derri[èe]re la ligne des 3/.test(body);
      const players = [
        { id: "P", num: 1, role: "Passeur", x: 42, y: pY, color: "#0072B2", team: "A" },
        { id: "K", num: 2, role: "Attaquant", x: elan ? 24 : 34, y: atkY, color: "#0072B2", team: "A" },
      ];
      const markers = [{ type: "ballon", x: 45, y: pY }];
      const actions = []; let ord = 1;
      // Lanceur/engageur d'en face qui alimente le passeur.
      if (/lanceur|engage/.test(body)) {
        players.push({ id: "L", num: 1, role: "Lanceur", x: 70, y: 50, color: "#D55E00", team: "B" });
        markers[0] = { type: "ballon", x: 66, y: 50 };
        actions.push({ from: "L", tox: 46, toy: pY, type: "renvoi", order: ord++, label: "Engagement" });
      }
      // Ramasseur / récupérateur en face (hors trajectoire).
      const ramM = body.match(/ramasseur|r[ée]cup[ée]rateur/);
      if (ramM) players.push({ id: "R", num: 2, role: ramM[0].startsWith("ram") ? "Ramasseur" : "Récupérateur", x: 80, y: 50 + (atkY <= 50 ? 6 : -6), color: "#D55E00", team: "B" });
      // Contreur(s) adverse(s) au filet, face à l'attaquant — leur COMPTE est
      // lu (« les deux contreurs sautent ensemble »), et leurs déplacements
      // décrits sont dessinés (« se décalent le long du filet », « se décale
      // vers l'axe ») — le choix de direction devient lisible.
      if (/contreurs?/.test(body)) {
        const cM = body.match(/\b(deux|trois|[23])\s+contreurs?|les\s+(deux|trois)\s+contreurs?/);
        const nCtr = cM ? ({ deux: 2, trois: 3 }[cM[1] || cM[2]] || parseInt(cM[1] || cM[2], 10) || 1) : 1;
        const cy = atkY <= 50 ? 30 : 70;
        for (let i = 0; i < Math.min(nCtr, 3); i++) {
          const yy = clampN(cy + (atkY <= 50 ? i * 12 : -i * 12), 14, 86);
          players.push({ id: "X" + (i ? i + 1 : ""), num: 3 + i, role: "Contreur", x: 54, y: yy, color: "#D55E00", team: "B" });
          // « se déplacent le long du filet » : les contreurs convergent vers
          // le point d'attaque avant le saut.
          if (i > 0 && /se d[ée]placent[^.;]{0,40}filet|glissent|convergent/.test(body))
            actions.push({ from: "X" + (i + 1), tox: 54, toy: clampN(cy + (atkY <= 50 ? 6 : -6), 14, 86), type: "course", order: ord++, label: i === 1 ? "Se déplacent le long du filet" : null });
        }
        if (/d[ée]cal\w+\s+vers\s+l['’]axe/.test(body))
          actions.push({ from: "X", tox: 54, toy: atkY <= 50 ? 44 : 56, type: "course", order: ord++, label: "Se décale vers l'axe" });
      }
      // Cerceaux d'appuis sous la course d'élan.
      if (/cerceaux?/.test(body)) for (const cx of [25, 31, 37]) markers.push({ type: "cerceau", x: cx, y: atkY });
      // Tapis-cibles aux coins du fond du camp adverse.
      const tapis = /tapis|matelas/.test(body);
      if (tapis) { markers.push({ type: "cible", x: 86, y: 16 }); markers.push({ type: "cible", x: 86, y: 84 }); }
      // Passe haute (répétitions « N ballons » en libellé), élan, smash(s).
      const repM = body.match(/(\d+)\s*(?:ballons?|balons?|balles?)/);
      actions.push({ from: "P", tox: 41, toy: atkY + (atkY <= 50 ? 4 : -4), type: "renvoi", order: ord++, label: "Passe haute" + (repM ? ` (×${repM[1]})` : "") });
      if (elan) actions.push({ from: "K", tox: 38, toy: atkY, type: "course", order: ord++, label: /appuis/.test(body) ? "Élan 3 appuis" : "Course d'élan" });
      // Directions d'attaque : structurées (analyse) sinon lues du texte —
      // « alterne croisée puis ligne » = DEUX flèches en éventail.
      const dirs = [];
      const addDir = (key) => { const e = dirs.find((d) => d.key === key); if (e) e.n++; else dirs.push({ key, n: 1 }); };
      const preAtk = pre ? pre.shots.filter((s) => s.type === "tir") : [];
      if (preAtk.length) for (const s of preAtk) {
        const seg = s.seg || "";
        addDir(/d[ée]crois|parall/.test(seg) ? "ligne" : /crois|diagonal/.test(seg) ? "croise" : "def");
      } else {
        const lineRe = /d[ée]crois\w*|parall[èe]\w*|le long de (?:la )?ligne/;
        const stripped = body.replace(new RegExp(lineRe.source, "g"), "");
        if (/crois[ée]?|diagonal/.test(stripped)) addDir("croise");
        if (lineRe.test(body)) addDir("ligne");
        if (!dirs.length) addDir("def");
      }
      if (dirs.length > 2) dirs.length = 2;
      for (const d of dirs) {
        const to = d.key === "croise" ? { x: tapis ? 86 : 81, y: tapis ? (atkY <= 50 ? 84 : 16) : (atkY <= 50 ? 72 : 28) }
          : d.key === "ligne" ? { x: tapis ? 86 : 84, y: tapis ? (atkY <= 50 ? 16 : 84) : (atkY <= 50 ? 22 : 78) }
          : { x: tapis ? 86 : 79, y: tapis ? (atkY <= 50 ? 84 : 16) : (atkY <= 50 ? 62 : 38) };
        const lab = d.key === "croise" ? "Smash croisé" : d.key === "ligne" ? "Smash décroisé" : "Smash";
        actions.push({ from: "K", tox: to.x, toy: to.y, type: "tir", order: ord++, label: lab + (d.n > 1 ? ` (×${d.n})` : "") });
      }
      const coups = [{ acteur: "A", coup: "passe", profondeur: "avant", direction: null, repetitions: repM ? Math.min(20, +repM[1]) : 1 }];
      for (const d of dirs) coups.push({ acteur: "B", coup: "attaque", profondeur: null, direction: d.key === "croise" ? "croise" : d.key === "ligne" ? "parallele" : null, repetitions: d.n });
      return {
        players, markers, actions,
        steps: [
          /lanceur|engage/.test(body) ? "Le lanceur engage vers le passeur" : null,
          "Passe haute du passeur devant l'attaquant",
          elan ? "Course d'élan, saut et frappe au sommet" : "Saut et frappe au sommet",
          dirs.length > 1 ? "Alterner smash croisé et smash le long de la ligne" : "Smash dans le camp adverse",
        ].filter(Boolean),
        analyse: { version: 1, famille: "filet", format: "equipes",
          effectif: { gauche: 2, droite: players.filter((p) => p.team === "B").length },
          coups },
      };
    }
  }
  const G = sport === "tennis_de_table"
    ? { ax: 8, bx: 92, hitAx: 36, hitBx: 64, shortAx: 44, shortBx: 56, top: 32, bot: 68 }
    : { ax: 14, bx: 86, hitAx: 30, hitBx: 70, shortAx: 42, shortBx: 58, top: 26, bot: 74 };
  // 1) Événements (frappes, échanges comptés, déplacements) dans l'ordre du texte.
  const evs = [];
  STROKE_DEFS.forEach(([re, type, label], di) => {
    const r = new RegExp(re.source, "g"); let m;
    while ((m = r.exec(body))) {
      if (label === "Frappe" && isNounFrappe(body, m.index)) continue;
      if (["Service", "Réception", "Manchette", "Attaque", "Smash"].includes(label) && isNounService(body, m.index)) continue;
      evs.push({ pos: m.index, kind: "stroke", type, label, di });
    }
  });
  // Échange compté : « 10 échanges », « série de 10 », « 10 coups/balles »…
  // (nom OU verbe). Un seul événement « exchange », au comptage lu globalement.
  { const NUMW = { deux: 2, trois: 3, quatre: 4, cinq: 5, six: 6, sept: 7, huit: 8, neuf: 9, dix: 10 };
    const numEx = (w) => (NUMW[w] != null ? NUMW[w] : (parseInt(w, 10) || 3));
    const cm = body.match(/(\d+|deux|trois|quatre|cinq|six|sept|huit|neuf|dix)\s*(?:[ée]changes?|coups?|balles?|renvois?|fois)/)
      || body.match(/s[ée]rie de\s*(\d+|deux|trois|quatre|cinq|six|sept|huit|neuf|dix)/);
    const count = clampN(cm ? numEx(cm[1]) : 3, 2, 5);
    // On ancre l'échange sur le COMPTAGE (« 10 échanges ») plutôt que sur un
    // « l'échange » introductif (« engage l'échange »), pour ne pas voler le
    // segment du service ni mal cibler la zone.
    const m = cm || /(?:s[ée]rie|[ée]changent|maintenir l['’ ]?[ée]change|l['’ ]?[ée]change (?:se poursuit|se prolonge|continue|s['’ ]installe|se construit|se d[ée]roule|se met en place))/.exec(body);
    if (m) evs.push({ pos: m.index, kind: "exchange", count, di: 50 });
    // RALLYE DE DÉGAGÉS décrit sans comptage (« échange en dégagé long entre
    // joueur 1 et 2 », « ils se renvoient en fond », « dégagés longs ») : on
    // l'anime comme un échange soutenu (4 frappes), ALTERNÉ entre les deux —
    // sinon une seule frappe était détectée et on retombait sur un drill à sens
    // unique (le receveur ne renvoyait jamais).
    else if (!/engage l['’ ]?[ée]change/.test(body)) {
      // « dégagés longs » (PLURIEL = rallye) déclenche ; « dégage long » (verbe
      // singulier = UN coup) est désormais lu comme un Dégagement par le lexique.
      const bm = /[ée]change(?:nt)?\s+(?:en\s+|de\s+)?d[ée]gag[ée]|d[ée]gag[ée]s\s+longs?|se renvoient|[ée]changes?\s+(?:long|en fond|de fond)|\brall(?:ye|y)\b/.exec(body);
      if (bm) evs.push({ pos: bm.index, kind: "exchange", count: 4, di: 50, rally: true });
    } }
  { const r = /se d[ée]place|d[ée]placement lat[ée]ral|se d[ée]porte/g; let m;
    while ((m = r.exec(body))) evs.push({ pos: m.index, kind: "move", dir: "side", di: 60 }); }
  // « monte la balle / monte une passe haute / monte 10 ballons » = geste de
  // PASSE du passeur, pas une montée au filet du joueur.
  { const r = /se pr[ée]cipite|monte au filet|\bmonte\b(?!\s+(?:la|le|une|des|\d+\s*)?\s*(?:balles?|ballons?|passe))|remonte|vers l'avant|vers le filet|au filet|\bavance\b/g; let m;
    while ((m = r.exec(body))) evs.push({ pos: m.index, kind: "move", dir: "fwd", di: 61 }); }
  evs.sort((a, b) => a.pos - b.pos || a.di - b.di);
  // « remet en poussette » = UNE frappe : dans une fenêtre courte, la plus
  // spécifique (premier rang du lexique) gagne.
  const kept = [];
  for (const e of evs) {
    const last = kept[kept.length - 1];
    if (last && e.pos - last.pos <= 12 && last.kind === e.kind) {
      // « engage un dégagement long », « sert court »… : l'engagement EST un
      // service — cette nature prime (placement serveur/receveur), la qualité
      // du coup (long/court/croisé) reste lue dans le segment (profondeur).
      if (last.label === "Service" || e.label === "Service") {
        if (last.label !== "Service") kept[kept.length - 1] = { ...e, pos: last.pos, type: "renvoi", label: "Service" };
      }
      else if (e.di < last.di) kept[kept.length - 1] = e;
    }
    else kept.push(e);
  }
  // ── VOLLEY : hygiène de CHRONOLOGIE réception/service ────────────────────
  // 1) Mention-THÈME de la réception en tête de texte (« volley : réception en
  //    mouvement — … ») : pas une frappe — retirée dès qu'un VRAI geste de
  //    réception (réception/manchette) est décrit plus loin. Les tournures
  //    actives (« fait une réception ») restent des frappes.
  // 2) Réception décrite AVANT le service qui la déclenche (« … réceptionne en
  //    manchette le service déclenché … ») : le SERVICE repasse en tête — une
  //    réception n'existe pas avant la mise en jeu.
  if (sport === "volleyball" && !pre) {
    const strokes = () => kept.filter((e) => e.kind === "stroke");
    const isRec = (e) => e.label === "Réception" || e.label === "Manchette";
    const s0 = strokes()[0];
    if (s0 && s0.label === "Réception" && s0.pos < 60
      && !/(?:fait|joue|effectue|r[ée]alise|r[ée]ussit)\s+(?:une|la|sa)\s*$/.test(body.slice(0, s0.pos))
      && strokes().some((e) => e !== s0 && isRec(e)))
      kept.splice(kept.indexOf(s0), 1);
    const s1 = strokes()[0];
    const svc = strokes().find((e) => e.label === "Service");
    if (s1 && isRec(s1) && svc && kept.indexOf(svc) > kept.indexOf(s1)) {
      const si = kept.indexOf(s1);
      const [svcEv] = kept.splice(kept.indexOf(svc), 1);
      kept.splice(si, 0, svcEv);
    }
  }
  // Un RALLYE DE DÉGAGÉS explicite (marqué `rally`) constitue à lui seul une
  // séquence ; sinon il faut au moins 2 frappes décrites (les motifs « N fois /
  // contre-pied » restent gérés par buildRacketRally, sans frappes nommées).
  {
    const nonMove = kept.filter((e) => e.kind !== "move");
    if (!pre && !nonMove.some((e) => e.kind === "exchange" && e.rally) && nonMove.length < 2) return null;
  }

  // 2) Acteur d'un événement : le joueur le plus proche AVANT lui. On lit les
  // mentions sur bodyRaw (CASSE conservée) : « joueur A », mais aussi « A »/« B »
  // SEULS en majuscule (« B remonte en amorti ») — le « a » article minuscule
  // n'en est pas un. « joueur 1/2 » accepté également.
  const mentions = [];
  { const r = /\b(?:joueur\s+)?([AB])\b|\bjoueur\s*([12])\b/g; let m;
    while ((m = r.exec(bodyRaw))) mentions.push({ pos: m.index, who: m[1] || (m[2] === "1" ? "A" : "B") }); }
  const actorBefore = (p) => { let w = null; for (const mn of mentions) { if (mn.pos < p) w = mn.who; else break; } return w; };
  const other = (w) => (w === "A" ? "B" : "A");

  // SYNTHÈSE de l'analyse comprise (panneau « Ce que j'ai compris ») : ce que
  // le moteur LOCAL a extrait du texte, au même contrat que l'analyse IA —
  // le panneau de correction fonctionne donc aussi en repli hors-ligne.
  const COUP_KEY = { "Service": "service", "Remise": "remise", "Dégagement": "degagement", "Amorti": "amorti", "Bloc": "bloc", "Poussette": "poussette", "Manchette": "manchette", "Échange": "echange", "Smash": "smash", "Kill": "kill", "Top-spin": "topspin", "Volée": "attaque", "Accélération": "attaque", "Attaque": "attaque", "Touche": "passe", "Envoi": "remise", "Passe": "passe", "Réception": "reception", "Frappe": "attaque" };
  const synthFilet = (list, format) => {
    const coups = [];
    for (const s of list) {
      const seg = s.seg || "";
      const c = {
        acteur: s.who === "OPP" ? "adversaire" : (s.who || null),
        coup: COUP_KEY[s.name] || "remise",
        profondeur: /\bfond\b|\blong(?:ue)?\b|profond/.test(seg) ? "fond" : /court|filet/.test(seg) ? "avant" : /mi-?court|m[ée]dian/.test(seg) ? "median" : null,
        direction: /\bcrois/.test(seg) ? "croise" : /d[ée]crois|parall/.test(seg) ? "parallele" : /\bgauche\b/.test(seg) ? "gauche" : /\bdroite?\b/.test(seg) ? "droite" : /centre|milieu|axe/.test(seg) ? "centre" : null,
        repetitions: 1,
      };
      const prev = coups[coups.length - 1];
      if (prev && prev.coup === "echange" && c.coup === "echange") prev.repetitions++;
      else coups.push(c);
    }
    return { version: 1, famille: "filet", format, coups };
  };

  // ── RALLYE 2D (badminton, tennis, volley) ───────────────────────────────
  // Modèle de COURT à zones : un coup atterrit dans une zone de l'adversaire
  // (fond/médian/avant × gauche/centre/droite) — jamais la « rivière » au filet
  // pour un service — et le RECEVEUR se déplace là où le volant/la balle
  // retombe (sa « position 2 », fantôme). Cela enchaîne naturellement
  // service long → fond, amorti → avant, montée au filet, smash.
  if (sport === "badminton" || sport === "tennis" || sport === "volleyball") {
    // Profondeur visée par un coup (commune simple / double).
    const depthOf = (name, seg) => {
      const def = STROKE_DEPTH[name];
      if (def && def.fixed) return def.depth;                      // amorti = drop, toujours près du filet
      // La profondeur EXPLICITE du texte prime (« dans le fond », « long », « court »…).
      if (/\bfond\b|\blong(?:ue)?s?\b|profond|derri[èe]re/.test(seg)) return "fond";
      if (/court(?:e)?|pr[èe]s du filet|au filet|\bavant\b/.test(seg)) return "avant";
      if (/mi-?court|m[ée]dian|mi-?terrain|\bau t\b|sur le t\b/.test(seg)) return "median";
      // Défaut selon le coup (issu du lexique) ; sinon service au fond, reste mi-court.
      if (def) return def.depth;
      return name === "Service" ? "fond" : "median";
    };
    // Zone d'arrivée LÉGALE d'un service (x, côté receveur) : au badminton,
    // toujours DERRIÈRE la ligne de service courte (jamais la « rivière » au
    // filet, interdite) ; au tennis, DANS le carré de service ; au volley, en
    // profondeur. `side` = camp du receveur ("L" gauche / "R" droite).
    const serveX = (side, depth) => {
      const t = sport === "tennis" ? { avant: 42, median: 36, fond: 30 }   // carré de service : ~32->50
        : sport === "volleyball" ? { avant: 30, fond: 10 }
        : { avant: 29, fond: 11 };       // badminton : ligne courte à 34/66, fond double à 6/94
      const v = t[depth] != null ? t[depth] : t.fond;
      return side === "L" ? v : 100 - v;
    };

    // ── VOLLEY en ÉQUIPES (« 1 contre 2 » : réception -> passe -> attaque) ──
    // Le volley enchaîne plusieurs touches DANS la même équipe avant de
    // franchir le filet — impossible dans le rallye alterné. Équipe de gauche
    // = service (le « joueur seul ») ; à droite : C réceptionne au fond, D
    // passe au filet, et le réceptionneur MONTE attaquer.
    const effM = sport === "volleyball" ? body.match(/\b([1-6])\s*(?:contre|c\.?|vs|x)\s*([1-6])\b/) : null;
    let effV = pre && pre.format === "equipes" ? pre.effectif
      : (effM ? { gauche: +effM[1], droite: +effM[2] } : null);
    if (sport === "volleyball" && effV && effV.gauche >= 1 && effV.droite >= 1 && effV.gauche + effV.droite > 2) {
      if (effV.droite < effV.gauche) effV = { gauche: effV.droite, droite: effV.gauche };   // le serveur isolé à gauche
      // CONTREURS chiffrés (« deux contreurs sautent au filet », « face au
      // contre à deux ») : l'équipe d'en face aligne au moins N joueurs au
      // filet — les slots « Bloqueur » prennent le rôle « Contreur ».
      const ctrM = body.match(/\b(deux|trois|[23])\s+contreurs?\b|contre\s+[àa]\s+(deux|trois|[23])\b/);
      const ctrWant = ctrM ? ({ deux: 2, trois: 3 }[ctrM[1] || ctrM[2]] || parseInt(ctrM[1] || ctrM[2], 10)) : 0;
      const nL = clampN(Math.max(clampN(effV.gauche, 1, 6), ctrWant ? ctrWant + 1 : 0), 1, 6);
      // TRAVAIL DE RÉCEPTION — « N réceptionneurs » chiffrés par le texte SANS
      // attaque décrite : l'effectif IA oublie souvent le passeur (« 3
      // réceptionneurs + un passeur » déclaré droite:3). On sert la ligne
      // arrière chiffrée (+ passeur si décrit) ; orthographe tolérée
      // (« receptioneurs »). Inactif dès qu'une attaque est décrite.
      const recM = body.match(/\b(deux|trois|quatre|[234])\b\s+(?:colonn?es?\s+d(?:e\s|'|’)\s*)?r[ée]cept?ionn?eurs?/);
      const recWant = recM ? ({ deux: 2, trois: 3, quatre: 4 }[recM[1]] || parseInt(recM[1], 10)) : 0;
      const atkInText = /attaqu|sma[sc]h|\bkill\b|frappe|marqu|conclu|renvoi|trois touches|3 touches/.test(body);
      const nR = (recWant >= 2 && !atkInText)
        ? clampN(Math.max(clampN(effV.droite, 1, 6), recWant + (/pas{1,2}eur/.test(body) ? 1 : 0)), 1, 6)
        : clampN(effV.droite, 1, 6);
      // FORMATIONS RÉALISTES (filet vertical à x=50). GAUCHE = service (serveur au fond +
      // base : bloqueurs au filet, défenseurs arrière) ; DROITE = réception (réceptionneurs
      // au fond, passeur + attaquant(s) au filet). Numérotation 1..N PAR ÉQUIPE (aucun doublon
      // intra-équipe, aucun joueur sans numéro). Acteurs : réception = C, passe = D, attaque = E.
      const RIGHT = [
        { id: "C", role: "Réceptionneur", x: 80, y: 46 }, { id: "D", role: "Passeur", x: 58, y: 50 },
        { id: "E", role: "Attaquant", x: 58, y: 74 }, { id: "F", role: "Attaquant", x: 58, y: 26 },
        { id: "H", role: "Réceptionneur", x: 85, y: 72 }, { id: "I", role: "Réceptionneur", x: 85, y: 22 },
      ];
      const LEFT = [
        { id: "A", role: "Serveur", x: 5, y: 50 }, { id: "LB1", role: "Bloqueur", x: 44, y: 32 },
        { id: "LB2", role: "Bloqueur", x: 44, y: 68 }, { id: "LD1", role: "Défenseur", x: 22, y: 28 },
        { id: "LD2", role: "Défenseur", x: 22, y: 72 }, { id: "LB3", role: "Bloqueur", x: 44, y: 50 },
      ];
      const players = [], pos = {};
      LEFT.slice(0, nL).forEach((p, i) => { players.push({ id: p.id, num: i + 1, role: p.role, x: p.x, y: p.y, color: "#0072B2", team: "A" }); pos[p.id] = { x: p.x, y: p.y }; });
      RIGHT.slice(0, nR).forEach((p, i) => { players.push({ id: p.id, num: i + 1, role: p.role, x: p.x, y: p.y, color: "#D55E00", team: "B" }); pos[p.id] = { x: p.x, y: p.y }; });
      // Ligne arrière chiffrée : les postes « Attaquant » du gabarit canonique
      // redeviennent des RÉCEPTIONNEURS (ligne arrière) jusqu'au compte demandé.
      if (recWant >= 2 && !atkInText) {
        // Emplacements de ligne arrière encore LIBRES (un joueur H/I du gabarit
        // peut déjà occuper le sien).
        const backs = [{ x: 85, y: 72 }, { x: 85, y: 22 }, { x: 88, y: 50 }]
          .filter((b) => !players.some((p) => Math.hypot(p.x - b.x, p.y - b.y) < 6));
        let have = players.filter((p) => p.team === "B" && p.role === "Réceptionneur").length;
        for (const p of players) {
          if (have >= recWant) break;
          if (p.team === "B" && p.role === "Attaquant") {
            const b = backs.shift() || { x: 85, y: 50 };
            p.role = "Réceptionneur"; p.x = b.x; p.y = b.y; pos[p.id] = { x: b.x, y: b.y };
            have++;
          }
        }
      }
      // Le LIBÉRO nommé par le texte : c'est le réceptionneur travaillé.
      if (/lib[ée]ro/.test(body)) { const c = players.find((p) => p.id === "C"); if (c) c.role = "Libéro"; }
      // Les N premiers Bloqueurs deviennent les CONTREURS décrits — ancrés FACE
      // à l'attaquant travaillé (E) quand il existe : le contre se forme du
      // côté de l'attaque, pas symétriquement.
      if (ctrWant) {
        const ctrs = players.filter((pp) => pp.role === "Bloqueur").slice(0, ctrWant);
        ctrs.forEach((p) => { p.role = "Contreur"; });
        const eAtt = players.find((pp) => pp.id === "E");
        if (eAtt && ctrs.length >= 2)
          ctrs.forEach((p, i) => { p.y = Math.round(clampN(eAtt.y + (i - (ctrs.length - 1) / 2) * 14, 14, 86)); });
      }

      // Terrain DIVISÉ (« en deux dans la profondeur, en trois dans la
      // largeur ») : lignes de zone en pointillés (zones de taille nulle).
      const zonesV = [];
      const wordN = { deux: 2, trois: 3, quatre: 4 };
      const dP = body.match(/en\s+(deux|trois|quatre|\d)\s+dans la profondeur/);
      const dL = body.match(/en\s+(deux|trois|quatre|\d)\s+dans la largeur/);
      const nProf = dP ? (wordN[dP[1]] || +dP[1]) : 0;
      const nLarg = dL ? (wordN[dL[1]] || +dL[1]) : 0;
      if (nProf > 1) for (let k = 1; k < nProf; k++) {
        const off = Math.round((50 / nProf) * k);
        zonesV.push({ x: 50 - off, y: 2, w: 0, h: 96, style: "dashed" });
        zonesV.push({ x: 50 + off, y: 2, w: 0, h: 96, style: "dashed" });
      }
      if (nLarg > 1) for (let k = 1; k < nLarg; k++)
        zonesV.push({ x: 2, y: Math.round((100 / nLarg) * k), w: 96, h: 0, style: "dashed" });

      // Coups : structurés (analyse) ou extraits du texte.
      const shotsV = pre ? pre.shots.map((s) => ({ ...s })) : [];
      if (!pre) for (let i = 0; i < kept.length; i++) {
        const e = kept[i];
        if (e.kind !== "stroke") continue;
        const seg = body.slice(e.pos, Math.min(kept[i + 1] ? kept[i + 1].pos : e.pos + 100, e.pos + 100));
        shotsV.push({ type: e.type, name: e.label, seg, who: null });
      }
      if (!shotsV.length) return null;
      const svcAll = shotsV.filter((s) => s.name === "Service").length;
      const svc0 = shotsV[0] && shotsV[0].name === "Service" ? shotsV[0] : null;
      if (svc0) shotsV.shift();

      const actions = []; let ord = 1;
      const repM = body.match(/(\d+)\s*(?:mises? en jeu|services?|engagements?)/);
      const reps0 = Math.max(1, Math.min(10, pre ? svcAll : (repM ? +repM[1] : 1)));
      const synthV = [{ acteur: "A", coup: "service", profondeur: "fond", direction: null, repetitions: reps0 }];
      // 1) MISE EN JEU depuis derrière la ligne de fond -> zone ARRIÈRE adverse.
      //    Côté NOMMÉ (« sert côté gauche », « le réceptionneur excentré côté
      //    gauche », direction du service dans l'analyse) : le service atterrit
      //    de ce côté — convention : gauche = haut (y bas), droite = bas.
      const sideM = body.match(/(?:sert|service|envoi)[^.;]{0,60}?c[ôo]t[ée]\s+(gauche|droite?)|r[ée]ceptionn?eur[^.;]{0,50}?c[ôo]t[ée]\s+(gauche|droite?)/);
      const svcSide = svc0 && /\bgauche\b/.test(svc0.seg || "") ? "gauche"
        : svc0 && /\bdroite?\b/.test(svc0.seg || "") ? "droite"
        : sideM ? (sideM[1] || sideM[2]) : null;
      const land = { x: 88, y: svcSide === "gauche" ? 24 : /^droit/.test(svcSide || "") ? 68 : 44 };
      actions.push({ from: "A", tox: land.x, toy: land.y, type: "renvoi", order: ord++, label: "Mise en jeu → zone arrière" + (reps0 > 1 ? ` (×${reps0})` : "") });
      if (Math.hypot(pos.C.x - land.x, pos.C.y - land.y) > 7)
        actions.push({ from: "C", tox: land.x, toy: land.y, type: "course", order: ord++, label: "Déplacement sur le ballon" });
      pos.C = { ...land };
      // 2) STRUCTURE CANONIQUE réception -> passe (au filet) -> ATTAQUE conclue.
      // On NE COMPTE PAS les mots-touches du texte (« réception EN MANCHETTE » est
      // UNE seule touche, pas deux ; « passe haute » n'en ajoute pas une 3e) : la
      // séquence volley est fixe, seuls ses PARAMÈTRES sont lus (passe parallèle ?,
      // attaque croisée ?, intitulé de la frappe).
      const setShot = shotsV.find((s) => ["Passe", "Touche", "Envoi", "Remise"].includes(s.name));
      const atkShot = shotsV.find((s) => s.type === "tir");
      // DIRECTION DE LA PASSE (passeur) — lue sur le coup de passe OU le texte.
      const setParallel = /d[ée]crois|parall/.test((setShot && setShot.seg) || "") || /d[ée]crois|parall|le long de (?:la )?ligne/.test(body);
      // DIRECTION D'ATTAQUE : « décroisée » / « le long de la ligne » / « parallèle » = LE LONG
      // DE LA LIGNE (toy = y de l'attaquant) — PRIORITAIRE car « décroisée » contient « crois ».
      // « croisée » / « diagonale » = vers l'AUTRE côté. On NE lit la direction QUE sur le coup
      // d'attaque (seg) ET sur LA PROPOSITION DE L'ATTAQUE dans le texte (du dernier mot-clé de
      // frappe à la fin) — jamais sur la passe : « passe parallèle … attaque croisée » ne doit
      // pas rendre l'attaque « décroisée » (le « parallèle » qualifie la PASSE).
      const atkM = body.match(/(?:attaque|smash|kill|claquette|fusille|conclut|reprise|reprend)\b[\s\S]*$/);
      const atkBody = atkM ? atkM[0] : body;
      const atkSeg = (atkShot && atkShot.seg) || "";
      const parallel = /d[ée]crois|parall|le long de (?:la )?ligne/.test(atkBody) || /d[ée]crois|parall/.test(atkSeg);
      const cross = !parallel && (/crois|diagonal/.test(atkBody) || /crois|diagonal/.test(atkSeg));
      const highSet = (setShot && setShot.name === "Touche") || /passe haute/.test(body);
      const spot = { x: 56, y: (pos.D ? pos.D.y : 34) <= 50 ? 62 : 26 };
      const atkLabel = (atkShot && atkShot.name && !/^attaque$/i.test(atkShot.name)) ? atkShot.name
        : (cross ? "Attaque croisée" : parallel ? "Attaque décroisée" : "Attaque");
      const atkTo = { x: 24, y: pos.A.y <= 50 ? 70 : 30 };
      // TRAVAIL DE RÉCEPTION — séquence qui S'ARRÊTE AU PASSEUR (« manchette
      // vers le passeur », « passeur cible », « rattrapage à deux mains », ligne
      // arrière chiffrée) SANS attaque décrite : ni montée ni attaque
      // synthétisées — le texte fait foi. La passe du passeur n'est gardée que
      // si elle est décrite ET que sa cible (E) est restée un attaquant.
      // Orthographe tolérée (« paseur »). Inactif dès qu'une attaque existe.
      const endAtSetter = !atkShot && !atkInText && nR > 1
        && (recWant >= 2
          || /(?:vers|sur|pour|jusqu'au|o[ùu] se trouve)\s+(?:le\s+|la\s+|son\s+)?pas{1,2}eur|pas{1,2}eur\s+cible|rattrap\w+\s+[àa]\s+deux\s+mains|(?:bloque|attrape|capte)\s+l[ae]\s+ball?on|dans\s+les\s+(?:bras|mains)\s+du\s+pas{1,2}eur/.test(body));
      if (endAtSetter) {
        actions.push({ from: "C", to: "D", type: "renvoi", order: ord++, label: "Réception" });
        synthV.push({ acteur: "C", coup: "reception", profondeur: null, direction: null, repetitions: 1 });
        const setDescribed = shotsV.some((s) => ["Passe", "Touche"].includes(s.name)) || /passe haute|ench[aî]+n\w+ une passe/.test(body);
        const eAtk = players.find((p) => p.id === "E" && p.role === "Attaquant");
        if (nR >= 3 && setDescribed && eAtk) {
          actions.push({ from: "D", to: "E", type: "renvoi", order: ord++, label: highSet ? "Passe haute" : "Passe du passeur" });
          synthV.push({ acteur: "D", coup: "passe", profondeur: null, direction: setParallel ? "parallele" : null, repetitions: 1 });
        }
      } else if (nR >= 3) {
        // ≥ 3 à droite (3c3, 4c4…) : réception C -> passe haute D -> ATTAQUE par l'attaquant E posté
        // au filet. Le réceptionneur NE monte PAS : la frappe vient d'un joueur distinct.
        actions.push({ from: "C", to: "D", type: "renvoi", order: ord++, label: "Réception" });
        synthV.push({ acteur: "C", coup: "reception", profondeur: null, direction: null, repetitions: 1 });
        actions.push({ from: "D", to: "E", type: "renvoi", order: ord++, label: highSet ? "Passe haute" : "Passe pour l'attaquant" });
        synthV.push({ acteur: "D", coup: "passe", profondeur: "avant", direction: setParallel ? "parallele" : "droite", repetitions: 1 });
        // croisée -> vers l'autre côté ; décroisée/parallèle -> le long de la ligne (même y que E).
        const atkY = cross ? (pos.E.y <= 50 ? 74 : 26) : pos.E.y;
        actions.push({ from: "E", tox: 22, toy: atkY, type: "tir", order: ord++, label: atkLabel });
        synthV.push({ acteur: "E", coup: "attaque", profondeur: null, direction: cross ? "croise" : parallel ? "parallele" : null, repetitions: 1 });
      } else {
        if (nR > 1) {
          // Réception (C) -> passe du passeur au filet (D) -> le réceptionneur MONTE (réduit à 2).
          actions.push({ from: "C", to: "D", type: "renvoi", order: ord++, label: "Réception" });
          synthV.push({ acteur: "C", coup: "reception", profondeur: null, direction: null, repetitions: 1 });
          actions.push({ from: "D", tox: spot.x, toy: spot.y, type: "renvoi", order: ord++, label: setParallel ? "Passe parallèle au filet" : highSet ? "Passe haute" : "Passe au filet" });
          synthV.push({ acteur: "D", coup: "passe", profondeur: null, direction: setParallel ? "parallele" : null, repetitions: 1 });
          actions.push({ from: "C", tox: spot.x, toy: spot.y, type: "course", order: ord++, label: "Montée du réceptionneur" });
        } else {
          // 1 seul receveur : il réceptionne et se place pour attaquer (auto-passe).
          actions.push({ from: "C", tox: spot.x, toy: spot.y, type: "course", order: ord++, label: "Réception et montée au filet" });
          synthV.push({ acteur: "C", coup: "reception", profondeur: null, direction: null, repetitions: 1 });
        }
        pos.C = { ...spot };
        actions.push({ from: "C", tox: atkTo.x, toy: atkTo.y, type: "tir", order: ord++, label: atkLabel });
        synthV.push({ acteur: "C", coup: "attaque", profondeur: null, direction: cross ? "croise" : null, repetitions: 1 });
      }
      return {
        players,
        markers: [{ type: "ballon", x: 6, y: 50 }],   // ballon derrière la ligne de fond (engagement)
        zones: zonesV,
        actions,
        analyse: { version: 1, famille: "filet", format: "equipes", effectif: { gauche: nL, droite: nR }, coups: synthV },
      };
    }

    // ── DOUBLE (2 contre 2, badminton / tennis) ───────────────────────────
    // A et B sont PARTENAIRES (équipe de gauche) ; C et D les adversaires.
    // Chaque coup atterrit chez l'équipe adverse, et c'est le joueur qui joue
    // le coup SUIVANT (désigné par le texte, sinon le plus proche du volant)
    // qui se déplace dessus — « B partenaire de A recule et smash » : c'est
    // bien B, le partenaire du serveur, qui attaque la 3e touche.
    const isDouble = (sport === "badminton" || sport === "tennis") &&
      (pre ? pre.format === "double" : /\bdoubles?\b|deux contre deux|2\s*(?:contre|vs|x)\s*2/.test(body));
    if (isDouble) {
      const teamOf = { A: "L", B: "L", C: "R", D: "R" };
      const mates = { A: "B", B: "A", C: "D", D: "C" };
      const members = { L: ["A", "B"], R: ["C", "D"] };
      const otherT = (t) => (t === "L" ? "R" : "L");
      const depthX2 = { L: { fond: 10, median: 26, avant: 40 }, R: { avant: 60, median: 74, fond: 90 } };
      const players = [
        { id: "A", num: 1, role: "Joueur A", x: 26, y: 38, color: "#0072B2", team: "A" },
        { id: "B", num: 2, role: "Joueur B", x: 26, y: 62, color: "#0072B2", team: "A" },
        { id: "C", num: 1, role: "Joueur C", x: 74, y: 38, color: "#D55E00", team: "B" },
        { id: "D", num: 2, role: "Joueur D", x: 74, y: 62, color: "#D55E00", team: "B" },
      ];
      const P = {}; players.forEach((p) => { P[p.id] = p; });
      const pos = { A: { x: 26, y: 38 }, B: { x: 26, y: 62 }, C: { x: 74, y: 38 }, D: { x: 74, y: 62 } };

      // Acteurs : « A », « joueur C », « B partenaire de A » (l'acteur est B ;
      // la mention « partenaire de A » désigne d'ailleurs le même joueur) et
      // « l'adversaire / le receveur » (équipe d'en face, joueur le plus proche).
      // Les lettres se lisent sur bodyRaw : seules les MAJUSCULES comptent
      // (le « a » de liaison et « double » ne déclenchent rien).
      const men2 = [];
      { const r = /partenaire\s+(?:de\s+)?([A-D])\b|\b(?:joueur\s+)?([A-D])\b/g; let m;
        while ((m = r.exec(bodyRaw))) men2.push({ pos: m.index, who: m[1] ? mates[m[1]] : m[2] }); }
      { const r = /adversaires?|receveur|relanceur/g; let m;
        while ((m = r.exec(body))) men2.push({ pos: m.index, who: "OPP" }); }
      men2.sort((a, b) => a.pos - b.pos);
      const actor2 = (p) => { let w = null; for (const mn of men2) { if (mn.pos < p) w = mn.who; else break; } return w; };
      const nearest = (t, pt) => members[t].reduce((best, id) =>
        (Math.hypot(pos[id].x - pt.x, pos[id].y - pt.y) < Math.hypot(pos[best].x - pt.x, pos[best].y - pt.y) ? id : best), members[t][0]);

      const shots2 = pre ? pre.shots.map((s) => ({ ...s })) : [];
      if (!pre) for (let i = 0; i < kept.length; i++) {
        const e = kept[i];
        if (e.kind === "move") continue;
        const seg = body.slice(e.pos, Math.min(kept[i + 1] ? kept[i + 1].pos : e.pos + 100, e.pos + 100));
        if (e.kind === "exchange") for (let b = 0; b < e.count; b++) shots2.push({ type: "renvoi", name: "Échange", seg, who: null });
        else shots2.push({ type: e.type, name: e.label, seg, who: actor2(e.pos) });
      }
      if (shots2.length < 2) return null;

      const lat2 = (seg, who) => {
        if (/\bcrois/.test(seg)) return pos[who].y <= 50 ? 74 : 26;
        if (/d[ée]crois|parall|long de (?:la )?ligne/.test(seg)) return pos[who].y;
        if (/\bdroite?\b/.test(seg)) return 74;
        if (/\bgauche\b/.test(seg)) return 26;
        if (/centre|milieu|axe/.test(seg)) return 50;
        return null;
      };

      // SERVICE : serveur derrière sa ligne de service, en diagonale de la
      // zone visée, son partenaire couvre l'autre demi-court derrière lui ;
      // le receveur attend dans le carré diagonalement opposé, son partenaire
      // au fond de l'autre demi-court.
      const srv = shots2[0].who && shots2[0].who !== "OPP" ? shots2[0].who : "A";
      if (shots2[0].name === "Service") {
        const sT = teamOf[srv], rT = otherT(sT);
        const ty0 = lat2(shots2[0].seg, srv);
        const srvY = ty0 == null ? 38 : (ty0 <= 50 ? 62 : 38);
        // Serveur DERRIÈRE sa ligne de service (jamais dessus), receveur
        // derrière la sienne (bad : lignes courtes · tennis : lignes de fond).
        const lineX = sport === "badminton" ? { L: 28, R: 72 } : { L: 12, R: 88 };
        const backX = { L: 16, R: 84 };
        const setP = (id, x, y) => { pos[id] = { x, y }; P[id].x = x; P[id].y = y; };
        setP(srv, lineX[sT], srvY);
        setP(mates[srv], backX[sT] + (sT === "L" ? 4 : -4), 100 - srvY);
        const rcv = members[rT][srvY <= 50 ? 1 : 0];     // carré diagonal au serveur
        setP(rcv, lineX[rT], 100 - srvY);
        setP(mates[rcv], backX[rT], srvY);
      }

      const ballWord = sport === "badminton" ? "le volant" : "la balle";
      const actions = []; let ord = 1, jit = 0, prevHitter = null, prevLand = null;
      for (let i = 0; i < shots2.length; i++) {
        const s = shots2[i];
        let who = s.who;
        // Acteur implicite (ou mention périmée / « adversaire ») : l'échange
        // alterne entre les équipes, le plus proche du volant s'en charge.
        if (!who || who === "OPP" || who === prevHitter)
          who = !prevHitter ? srv : nearest(otherT(teamOf[prevHitter]), prevLand || pos[prevHitter]);
        const oppT = otherT(teamOf[who]);
        let depth = depthOf(s.name, s.seg);
        const isServe = s.name === "Service";
        // En DOUBLE au badminton, le service est COURT par défaut (juste
        // derrière la ligne de service adverse) — long seulement si demandé.
        if (isServe && sport === "badminton" && !/\blongs?\b|fond|profond/.test(s.seg)) depth = "avant";
        let y = lat2(s.seg, who);
        if (s.type === "tir") {
          const [m1, m2] = members[oppT];
          // « dans l'intervalle / entre les deux adversaires / dans le trou » :
          // viser le MILIEU entre les deux adversaires (le trou), et non l'espace
          // libre à l'opposé du barycentre (comportement par défaut ci-dessous).
          if (/intervalle|entre (?:les deux|eux|les adversaires)|dans le trou|au milieu/.test(s.seg))
            y = clampN(Math.round((pos[m1].y + pos[m2].y) / 2), 18, 82);
          // Finition : viser l'espace LIBRE, à l'opposé du barycentre adverse.
          else if (y == null)
            y = (pos[m1].y + pos[m2].y) / 2 <= 50 ? 74 : 26;
          if (depth === "avant") depth = "median";
        } else if (y == null) {
          // SERVICE : il atterrit DANS le carré diagonal (jamais sur la ligne
          // centrale) ; sinon, milieu de court par défaut.
          // La LARGEUR de la retombée est la DIRECTION du coup. Par défaut
          // elle valait 50 — plein axe, pour toutes les frappes sauf celles
          // nommées « Échange » : le schéma sortait alors sur une seule ligne
          // horizontale (joueurs en y=50, retombées en 46/50/54), et le
          // déplacement latéral que l'exercice enseigne devenait invisible.
          // C'est le reproche exact de quatre 👎 archivés (PROD19, PROD37,
          // PROD50, PROD54) et le défaut « scène à plat » de la salve
          // raquettes. On ALTERNE désormais les côtés, plus sagement que
          // l'échange (30/70 contre 26/74) : la balle change de côté à chaque
          // coup, l'adversaire traverse son camp pour la jouer.
          y = isServe ? ((pos[who] ? pos[who].y : 38) <= 50 ? 62 : 38)
            : s.name === "Échange" ? (jit % 2 ? 26 : 74) : (jit % 2 ? 30 : 70);
        }
        // Un service atterrit dans une zone LÉGALE (derrière la ligne courte
        // au bad, dans le carré au tennis) — sans éventail, la cible est nette.
        const x = isServe ? serveX(oppT, depth)
          : (depthX2[oppT][depth] != null ? depthX2[oppT][depth] : depthX2[oppT].median);
        const tox = clampN(x + (isServe ? 0 : (jit % 2 ? 3 : -3)), oppT === "L" ? 6 : 54, oppT === "L" ? 46 : 94);
        const toy = clampN(y + [0, 4, -4][jit % 3], 12, 88); jit++;
        const zoneTag = depth === "fond" ? "fond" : depth === "avant" ? "filet" : "";
        actions.push({ from: who, tox, toy, type: s.type, order: ord++, label: s.name + (zoneTag ? " → " + zoneTag : "") });
        prevLand = { x: tox, y: toy };
        if (i < shots2.length - 1) {
          // Qui jouera le coup suivant ? (désigné par le texte s'il est dans la
          // bonne équipe, sinon le plus proche) -> il se déplace sur le volant.
          let nxt = shots2[i + 1].who;
          if (!nxt || nxt === "OPP" || teamOf[nxt] !== oppT) nxt = nearest(oppT, prevLand);
          if (Math.hypot(pos[nxt].x - tox, pos[nxt].y - toy) > 7)
            actions.push({ from: nxt, tox, toy, type: "course", order: ord++, label: "Déplacement sur " + ballWord });
          pos[nxt] = { x: tox, y: toy };
        }
        prevHitter = who;
      }
      const p0 = P[srv];
      return {
        players,
        markers: [{ type: "ballon", x: clampN(p0.x + (teamOf[srv] === "L" ? 4 : -4), 4, 96), y: p0.y }],
        actions,
        analyse: synthFilet(shots2, "double"),
      };
    }

    const depthX = { A: { fond: 10, median: 26, avant: 40 }, B: { avant: 60, median: 74, fond: 90 } };
    const foreY = (w) => (w === "A" ? 74 : 26);            // coin coup droit (droitier)
    // RALLYE DE FOND (dégagés longs) : les deux joueurs sont POSTÉS AU FOND de
    // leur court (couloir du fond), pour que l'échange se lise fond-à-fond (long)
    // et non au milieu. Sinon départ neutre au centre.
    const backRally = (sport === "badminton" || sport === "tennis")
      && /d[ée]gag[ée]s? longs?|[ée]change\s+(?:en\s+|de\s+)?d[ée]gag|jeu de fond|fond de (?:court|terrain)|\ben fond\b|volants? longs?/.test(body);
    const ax0 = backRally ? 13 : 26, bx0 = backRally ? 87 : 74;
    const players = [
      { id: "A", num: 1, role: "Joueur A", x: ax0, y: 50, color: "#0072B2", team: "A" },
      { id: "B", num: 2, role: "Joueur B", x: bx0, y: 50, color: "#D55E00", team: "B" },
    ];
    const pos = { A: { x: ax0, y: 50 }, B: { x: bx0, y: 50 } };
    const latOf = (opp, seg, who) => {
      if (/en face|face [àa] lui|devant lui/.test(seg)) return pos[who].y;   // envoi parallèle
      if (/coup droit/.test(seg)) return foreY(opp);
      if (/revers/.test(seg)) return foreY(opp) === 74 ? 26 : 74;
      if (/\bcrois/.test(seg)) return pos[who].y <= 50 ? 74 : 26;
      if (/d[ée]crois|parall|long de (?:la )?ligne/.test(seg)) return pos[who].y;
      if (/\bdroit(?:e)?\b/.test(seg)) return 74;
      if (/\bgauche\b/.test(seg)) return 26;
      if (/centre|milieu|axe/.test(seg)) return 50;
      return null;
    };
    // Coups dans l'ordre (les échanges comptés sont dépliés ; on ignore les
    // « move » : le déplacement vers la balle est généré automatiquement).
    const shots = pre ? pre.shots.map((s) => ({ ...s })) : [];
    if (!pre) for (let i = 0; i < kept.length; i++) {
      const e = kept[i];
      if (e.kind === "move") continue;
      const seg = body.slice(e.pos, Math.min(kept[i + 1] ? kept[i + 1].pos : e.pos + 100, e.pos + 100));
      if (e.kind === "exchange") for (let b = 0; b < e.count; b++) shots.push({ type: "renvoi", name: "Échange", seg, who: null });
      else shots.push({ type: e.type, name: e.label, seg, who: actorBefore(e.pos) });
    }
    if (shots.length < 2) return null;
    let striker = shots[0].who || "A", ord = 1, jit = 0;
    // SERVICE : le serveur part de SON CARRÉ de service (en diagonale de la
    // zone visée), juste derrière sa ligne de service — jamais sur la ligne
    // médiane — et le volant est posé DEVANT lui. Le receveur attend dans le
    // carré diagonalement opposé (celui où arrive le service).
    if (shots[0].name === "Service") {
      const srv = striker, rcv = other(srv);
      const ty0 = latOf(rcv, shots[0].seg, srv);
      // Serveur DÉCALÉ d'un côté derrière sa ligne de fond -> diagonale FRANCHE
      // vers le carré opposé (gauche du milieu -> carré droit, et inversement).
      const srvY = ty0 == null ? 32 : (ty0 <= 50 ? 68 : 32);
      // tennis : le court est INSÉRÉ (lignes de fond ~10/90) ; serveur ET
      // receveur se placent DANS LA MARGE, derrière leur ligne de fond.
      // Volley comme tennis : le court est inséré (lignes de fond en 10/90) et
      // le service se joue DERRIÈRE la ligne, dans la zone libre — x=12 était
      // dans le court (service illégal). Le tennis de table garde son recul.
      const lineX = sport === "badminton" ? { A: 28, B: 72 }
        : (sport === "tennis" || sport === "volleyball") ? { A: 5, B: 95 } : { A: 12, B: 88 };
      const baseR = sport === "tennis" ? { A: 5, B: 95 } : { A: 28, B: 72 };
      pos[srv] = { x: lineX[srv], y: srvY };
      pos[rcv] = { x: baseR[rcv], y: ty0 == null ? 100 - srvY : (ty0 <= 50 ? 32 : 68) };
      players[srv === "A" ? 0 : 1].x = pos[srv].x; players[srv === "A" ? 0 : 1].y = pos[srv].y;
      players[rcv === "A" ? 0 : 1].x = pos[rcv].x; players[rcv === "A" ? 0 : 1].y = pos[rcv].y;
    }
    const markers = [];
    // Départ matérialisé par un PLOT (« il part d'un plot décalé… ») : le
    // receveur du premier envoi attend DÉCALÉ de la zone visée, un plot marque
    // ce départ — son déplacement vers le ballon devient explicite.
    if (/\bplots?\b/.test(body) && shots[0].name !== "Service") {
      const who0 = shots[0].who || striker, rcv0 = other(who0);
      let ty0 = latOf(rcv0, shots[0].seg, who0);
      if (ty0 == null) ty0 = 50;
      pos[rcv0] = { x: pos[rcv0].x, y: clampN(ty0 + (ty0 <= 50 ? -16 : 16), 12, 88) };
      players[rcv0 === "A" ? 0 : 1].y = pos[rcv0].y;
      markers.push({ type: "plot", x: clampN(pos[rcv0].x + (rcv0 === "A" ? -4 : 4), 4, 96), y: pos[rcv0].y - 5 });
    }
    const actions = [];
    // Événements à RENDRE dans l'ordre du texte : COUPS et MONTÉES. (L'ancien
    // chemin 2D ignorait les déplacements et alternait aveuglément les frappeurs,
    // d'où l'absence de montée au filet ET de retour du receveur.)
    //  - une MONTÉE « réserve » le coup suivant à celui qui monte (service-volée) ;
    //  - si un joueur frapperait DEUX FOIS de suite, l'adversaire REMET d'abord
    //    (retour du receveur) — au filet, la balle revient au volleyeur.
    const events = [];
    if (pre) for (const s of pre.shots) events.push({ kind: "stroke", type: s.type, name: s.name, seg: s.seg, who: s.who });
    else for (let i = 0; i < kept.length; i++) {
      const e = kept[i];
      const seg = body.slice(e.pos, Math.min(kept[i + 1] ? kept[i + 1].pos : e.pos + 100, e.pos + 100));
      if (e.kind === "move") events.push({ kind: "move", dir: e.dir, who: actorBefore(e.pos) });
      else if (e.kind === "exchange") for (let b = 0; b < e.count; b++) events.push({ kind: "stroke", type: "renvoi", name: "Échange", seg, who: null });
      else events.push({ kind: "stroke", type: e.type, name: e.label, seg, who: actorBefore(e.pos) });
    }

    let prevHitter = null, pendingMover = null, lastLand = null, lastServer = null;
    // On ne déplace QUE la position de travail : le rendu suit les pastilles via
    // les actions « course » (players[] reste aux positions de DÉPART).
    const setPos = (w, x, y) => { pos[w] = { x, y }; };
    const moveToBall = (who) => {
      if (!lastLand) return;
      // Au tennis, un coup joué APRÈS REBOND se prend DERRIÈRE le rebond (la
      // balle repart vers le fond) : le joueur se place ~2 m en retrait, vers SA
      // ligne de fond. (Une volée n'appelle pas moveToBall : on reste sur la
      // balle.) Au badminton/volley, pas de rebond : on va sur la balle.
      const tx = sport === "tennis" ? clampN(lastLand.x + (who === "A" ? -8 : 8), 6, 94) : lastLand.x;
      const ty = lastLand.y;
      if (Math.hypot(pos[who].x - tx, pos[who].y - ty) > 8) {
        const label = sport === "tennis" ? "Déplacement derrière le rebond" : "Déplacement sur la balle";
        actions.push({ from: who, tox: tx, toy: ty, type: "course", order: ord++, label });
        setPos(who, tx, ty);
      }
    };
    let lastStrokeName = null;
    const emitStroke = (who, name, type, seg, target) => {
      lastStrokeName = name;
      const opp = other(who);
      let depth = depthOf(name, seg);
      const isServe = name === "Service";
      let y = latOf(opp, seg, who), tox, toy;
      if (target) { tox = target.x; toy = target.y; }
      else {
        if (type === "tir") { if (y == null) y = pos[opp].y <= 50 ? 74 : 26; if (depth === "avant") depth = "median"; }
        // Service DIAGONAL vers le carré opposé : « au T » près de la ligne
        // médiane (centre), sinon plein carré (large).
        else if (isServe && y == null) y = /\bau t\b|sur le t\b/.test(seg)
          ? (pos[who].y <= 50 ? 56 : 44) : (pos[who].y <= 50 ? 68 : 32);
        // CHANTIER REPLI (👎 PROD95, « le service doit arriver dans le carré
        // de service ») : une direction « centre » venue de l'analyse posait
        // la chute SUR l'axe (y=50) — ni dans un carré ni dans l'autre. Au
        // service, la RÈGLE prime sur la direction ambiguë : « centre » se
        // lit « au T », juste DANS le carré diagonal.
        if (isServe && y != null && Math.abs(y - 50) < 4) y = pos[who].y <= 50 ? 56 : 44;
        // Un échange en DÉGAGÉ LONG se joue DROIT, DEEP-TO-DEEP, SUR le joueur
        // (sa position), et non dans un coin ouvert.
        // Les AUTRES coups (amorti, smash, remise…) valaient 50 — plein axe :
        // avec deux joueurs de simple posés eux aussi en y=50, le schéma
        // sortait sur UNE SEULE LIGNE horizontale (retombées en 46/50/54), et
        // le déplacement latéral que l'exercice enseigne devenait invisible.
        // Quatre 👎 archivés disent exactement cela (PROD19, PROD37, PROD50,
        // PROD54), et la salve raquettes l'a revu à l'œil. On ALTERNE les
        // côtés : la balle change de camp latéral à chaque coup, l'adversaire
        // traverse pour la jouer — `moveToBall` dessine alors sa course.
        else if (y == null) y = name === "Échange" ? (depth === "fond" ? pos[opp].y : foreY(opp)) : (jit % 2 ? 30 : 70);
        const x = isServe ? serveX(opp === "A" ? "L" : "R", depth)
          : (depthX[opp][depth] != null ? depthX[opp][depth] : depthX[opp].median);
        tox = clampN(x + (isServe ? 0 : (jit % 2 ? 3 : -3)), opp === "A" ? 6 : 54, opp === "A" ? 46 : 94);
        toy = clampN(y + [0, 4, -4][jit % 3], 12, 88); jit++;
      }
      const zoneTag = depth === "fond" ? "fond" : depth === "avant" ? "filet" : "";
      actions.push({ from: who, tox, toy, type, order: ord++, label: name + (zoneTag ? " → " + zoneTag : "") });
      lastLand = { x: tox, y: toy }; prevHitter = who;
    };
    for (const ev of events) {
      if (ev.kind === "move") {
        const mover = ev.who || pendingMover || prevHitter || striker;
        let tox = pos[mover].x, toy = pos[mover].y;
        // Le volant/la balle est-il DANS le camp du mover (= un coup à jouer) ?
        const ownBall = lastLand && ((mover === "A" && lastLand.x < 50) || (mover === "B" && lastLand.x > 50));
        if (ev.dir === "fwd") {
          if (ownBall) {
            // Monter pour JOUER : on va SUR le volant (badminton, pas de rebond),
            // ou DERRIÈRE le rebond au tennis — sinon on attaque dans le vide.
            tox = sport === "tennis" ? clampN(lastLand.x + (mover === "A" ? -8 : 8), 6, 94) : lastLand.x;
            toy = lastLand.y;
          } else {
            tox = mover === "A" ? 44 : 56;                                            // montée anticipée vers le filet
          }
        } else {
          toy = pos[mover].y <= 50 ? 74 : 26;                                         // latéral
        }
        if (Math.abs(tox - pos[mover].x) >= 2 || Math.abs(toy - pos[mover].y) >= 2) {
          actions.push({ from: mover, tox, toy, type: "course", order: ord++, label: ev.dir === "fwd" ? (sport === "tennis_de_table" ? "Avance près de la table" : "Montée au filet") : "Déplacement" });
          setPos(mover, tox, toy);
        }
        pendingMover = mover;
        continue;
      }
      let who = ev.who || pendingMover || (prevHitter ? other(prevHitter) : striker);
      // SÉRIE DE SERVICES (« 5 services courts puis 5 services longs ») : un
      // service n'est JAMAIS un retour — un service consécutif à un autre est
      // servi par le MÊME joueur (drill de service), sans remise intercalée.
      const serviceSeries = ev.name === "Service" && lastStrokeName === "Service" && !ev.who;
      if (serviceSeries && prevHitter) who = prevHitter;
      // VOLLEY : une RÉCEPTION/MANCHETTE répond au service — jamais jouée par
      // le serveur. Sans acteur explicite elle revient au RECEVEUR, même s'il
      // vient de toucher la balle (série : chaque réception répond à un nouveau
      // service du même serveur) — sans « Remise » intercalée ni déplacement.
      const receptionDrill = sport === "volleyball" && !ev.who && lastServer
        && (ev.name === "Réception" || ev.name === "Manchette");
      if (receptionDrill) who = other(lastServer);
      // Pas de double frappe : l'adversaire REMET d'abord (retour du receveur).
      if (prevHitter && who === prevHitter && !serviceSeries && !receptionDrill) {
        const ret = other(who), atNet = who === pendingMover;
        moveToBall(ret);
        // Au filet, le retour vient À PORTÉE du volleyeur ; sinon retour normal.
        emitStroke(ret, "Remise", "renvoi", "", atNet ? { x: clampN(pos[who].x + (who === "A" ? -3 : 3), 6, 94), y: pos[who].y } : null);
      }
      // Le frappeur va sur la balle reçue (sauf s'il est posté au filet pour
      // volleyer). En drill de réception, le receveur ne se déplace que si la
      // balle est DANS SON CAMP (jamais une traversée du filet pour « aller
      // chercher » sa propre manchette précédente).
      const ballInOwnCamp = lastLand && ((who === "A" && lastLand.x < 50) || (who === "B" && lastLand.x > 50));
      if (who !== pendingMover && !serviceSeries && (!receptionDrill || ballInOwnCamp)) moveToBall(who);
      // VOLLEY : manchette/réception jouée VERS SON PROPRE CAMP (« vers la zone
      // avant de son camp ») : la touche RESTE du même côté du filet (zone du
      // passeur) au lieu de traverser comme un renvoi.
      const keepSide = sport === "volleyball" && (ev.name === "Réception" || ev.name === "Manchette")
        && /(?:vers|dans)\s+(?:la\s+)?(?:zone\s+)?avant\s+de\s+son\s+(?:camp|terrain)|dans\s+son\s+(?:propre\s+)?camp/.test(ev.seg || "");
      emitStroke(who, ev.name, ev.type, ev.seg, keepSide ? { x: who === "A" ? 42 : 58, y: 50 } : undefined);
      if (ev.name === "Service") lastServer = who;
      striker = other(who);
      pendingMover = null;
    }
    // REBOND (tennis) : la balle rebondit dans le camp adverse avant d'être
    // rejouée — SAUF si le coup suivant est une VOLÉE (interceptée avant le bond).
    // (Pas de rebond au badminton/volley : le volant/la balle ne touche pas le sol.)
    if (sport === "tennis") {
      const balls = actions.filter((a) => a.type === "renvoi" || a.type === "tir");
      balls.forEach((a, i) => {
        const nxt = balls[i + 1];
        if (nxt && /vol[ée]e/i.test(nxt.label || "")) return;
        markers.push({ type: "rebond", x: a.tox, y: a.toy });
      });
    }
    // Balle posée DEVANT le serveur.
    const p0 = players[(shots[0].who || "A") === "A" ? 0 : 1];
    markers.push({ type: "ballon", x: clampN(p0.x + (p0.id === "A" ? 4 : -4), 4, 96), y: p0.y });
    return { players, markers, actions, analyse: synthFilet(shots, "simple") };
  }

  const reversTheme = /revers/.test(body);
  const cdTheme = /coup droit/.test(body) && !reversTheme;
  // Placement de départ : neutre au centre, ou au COIN du coup droit (resp.
  // revers) quand l'échange est croisé sur ce côté — la diagonale est alors nette
  // et le « replacement au centre » devient lisible.
  const pos = reversTheme ? { A: { x: G.ax, y: G.top }, B: { x: G.bx, y: G.bot } }
    : cdTheme ? { A: { x: G.ax, y: G.bot }, B: { x: G.bx, y: G.top } }
    : { A: { x: G.ax, y: 50 }, B: { x: G.bx, y: 50 } };
  const players = [
    { id: "A", num: 1, role: "Joueur A", x: pos.A.x, y: pos.A.y, color: "#0072B2", team: "A" },
    { id: "B", num: 2, role: "Joueur B", x: pos.B.x, y: pos.B.y, color: "#D55E00", team: "B" },
  ];
  // Zone visée CHEZ L'ADVERSAIRE : revers de A en haut, revers de B en bas.
  const sideY = (opp, side) => (side === "revers" ? (opp === "A" ? G.top : G.bot) : (opp === "A" ? G.bot : G.top));
  const lastTarget = { A: "revers", B: "revers" };
  const actions = [];
  let striker = null, ord = 1, k = 0, lastLand = null;
  const synthShots = [];   // trace des coups joués (synthèse de l'analyse)
  const bounces = [];      // rebonds (tennis de table) : repères discrets
  const playShot = (who, seg, type, baseLabel) => {
    synthShots.push({ name: baseLabel, seg, who });
    const opp = other(who);
    const isServe = baseLabel === "Service";
    // PIVOT / jeu de jambes : pour une frappe d'attaque jouée de SON coup droit
    // (resp. revers) — et non « vers le coup droit DE l'adversaire » (cible) — le
    // frappeur se REPLACE DERRIÈRE LA BALLE REÇUE (lastLand) pour l'attaquer ;
    // l'attaque part ainsi de la balle, pas d'un coin fixe.
    const strokeWing = type === "tir"
      ? (/\bcoup droit\b/.test(seg) && !/coup droit\s+(?:de\b|du\b|d'|adverse)/.test(seg) ? "cd"
        : /\b(?:de |en )revers\b/.test(seg) ? "revers" : null)
      : null;
    // Camp du frappeur : la balle reçue est-elle dans SA moitié (un coup à jouer) ?
    const incoming = lastLand && ((who === "A" && lastLand.x < 50) || (who === "B" && lastLand.x > 50));
    if (strokeWing && lastLand) {
      // On joue la balle APRÈS le rebond, en restant DERRIÈRE sa table : le
      // pivot se replace au niveau (y) de la balle, du côté qui permet le coup,
      // SANS monter sur la table.
      const baseX = who === "A" ? G.ax + 2 : G.bx - 2;
      const prepY = clampN(lastLand.y + (strokeWing === "cd" ? (who === "A" ? -14 : 14) : (who === "A" ? 14 : -14)), 10, 90);
      if (Math.hypot(pos[who].x - baseX, pos[who].y - prepY) > 6) {
        actions.push({ from: who, tox: baseX, toy: prepY, type: "course", order: ord++, label: "Pivot " + (strokeWing === "cd" ? "coup droit" : "revers") });
        pos[who] = { x: baseX, y: prepY };
      }
    }
    // Le frappeur ne se replace pas tout seul quand aucun déplacement n'est
    // décrit (séquence IA, ou texte court « service ... puis retour ... ») : son
    // retour partait alors d'un coin sans rapport avec la trajectoire reçue (ex.
    // service au coup droit alors que le receveur est placé côté revers). Quand il
    // est du CÔTÉ OPPOSÉ de la table par rapport à la balle tombée dans son camp,
    // on l'aligne DERRIÈRE sa table sur cette balle : le retour part alors de la
    // trajectoire du coup précédent. Condition stricte (côté opposé ET écart franc)
    // pour ne pas parasiter un échange déjà aligné ni un déplacement déjà décrit.
    else if (!isServe && incoming
        && (pos[who].y - 50) * (lastLand.y - 50) < 0 && Math.abs(pos[who].y - lastLand.y) > 20) {
      const baseX = who === "A" ? G.ax + 2 : G.bx - 2;
      const prepY = clampN(lastLand.y, 10, 90);
      if (Math.hypot(pos[who].x - baseX, pos[who].y - prepY) > 6) {
        actions.push({ from: who, tox: baseX, toy: prepY, type: "course", order: ord++, label: "Replacement sur la balle" });
        pos[who] = { x: baseX, y: prepY };
      }
    }
    let ty, tag;
    // Origine RÉELLE du coup : la balle reçue (lastLand) si elle est dans le camp
    // du frappeur, sinon sa position — sert aux directions croisé/parallèle.
    const fromY = incoming && lastLand ? lastLand.y : pos[who].y;
    // Aile (cd/revers) correspondant à une hauteur y chez `opp` (mémoire lastTarget).
    const wingAt = (y) => (opp === "A" ? (y <= 50 ? "revers" : "cd") : (y <= 50 ? "cd" : "revers"));
    if (/zone libre|contre.?pied|oppos[ée]/.test(seg)) { ty = pos[opp].y <= 50 ? G.bot : G.top; tag = "zone libre"; }
    else if (/coup droit/.test(seg)) { ty = sideY(opp, "cd"); lastTarget[opp] = "cd"; tag = "coup droit"; }
    else if (/revers/.test(seg)) { ty = sideY(opp, "revers"); lastTarget[opp] = "revers"; tag = "revers"; }
    // DIRECTIONS de table : « décroisé / parallèle / le long de la ligne » = la
    // balle file DROIT (même côté que son origine) ; « croisé / en diagonale » =
    // elle TRAVERSE (côté opposé). Testées AVANT « croisé » : « décroisé »
    // contient « crois ». « au centre » = milieu de table.
    else if (/d[ée]crois|parall[èe]l|long de (?:la )?ligne/.test(seg)) { ty = fromY <= 50 ? G.top : G.bot; lastTarget[opp] = wingAt(ty); tag = "ligne"; }
    else if (/crois[ée]?|diagonal/.test(seg)) { ty = fromY <= 50 ? G.bot : G.top; lastTarget[opp] = wingAt(ty); tag = "croisé"; }
    else if (/centre|milieu|axe/.test(seg)) { ty = 50; tag = "centre"; }
    else if (/\bdroit(?:e)?\b|fond droit|c[ôo]t[ée] droit/.test(seg)) { ty = sideY(opp, "cd"); lastTarget[opp] = "cd"; tag = "droite"; }
    else if (/\bgauche\b|fond gauche|c[ôo]t[ée] gauche/.test(seg)) { ty = sideY(opp, "revers"); lastTarget[opp] = "revers"; tag = "gauche"; }
    else { ty = sideY(opp, lastTarget[opp]); tag = ""; }
    let tx = opp === "A" ? G.hitAx : G.hitBx;
    if (/\bcourte?s?\b/.test(seg)) { tx = opp === "A" ? G.shortAx : G.shortBx; if (!tag) tag = "court"; }
    else if (/\blong(?:ue)?s?\b|fond de (?:table|court)/.test(seg)) { tx = opp === "A" ? G.hitAx - 7 : G.hitBx + 7; if (!tag) tag = "long"; }
    // Éventail des impacts : les balles répétées dans une même zone ne se
    // superposent pas (lisibilité). PAS d'éventail sur le SERVICE : il doit
    // tomber NET dans le camp receveur (sinon il se retrouve sur le filet).
    if (!isServe) { tx += [-5, 0, 5][k % 3]; ty = clampN(ty + [3, -3, 0][k % 3], 12, 88); k++; }
    actions.push({ from: who, tox: tx, toy: ty, type, order: ord++, label: baseLabel + (tag ? " → " + tag : "") });
    // REBOND (tennis de table) : en échange, la balle ne rebondit QUE dans le
    // camp ADVERSE ; au SERVICE seulement, elle rebondit AUSSI sur sa PROPRE
    // demi-table d'abord (rebond sur les deux côtés).
    if (sport === "tennis_de_table") {
      if (isServe) {
        const S = pos[who], ox = who === "A" ? G.hitAx : G.hitBx;
        const oy = S.y + (ty - S.y) * ((ox - S.x) / ((tx - S.x) || 1));
        bounces.push({ type: "rebond", x: ox, y: Math.round(clampN(oy, 12, 88)) });
      }
      bounces.push({ type: "rebond", x: tx, y: ty });
    }
    lastLand = { x: tx, y: ty };   // la balle est désormais dans le camp du receveur
    striker = who;
  };
  // Un joueur ne frappe pas DEUX FOIS de suite : si l'acteur nommé est le même
  // que le dernier frappeur (ex. l'échange finit sur A, puis « A accélère »),
  // l'adversaire REMET d'abord — sinon il manque un coup dans le rallye.
  // EXCEPTION série de services (« 5 services courts puis 5 longs ») : un
  // service n'est JAMAIS un retour — un service consécutif à un autre est servi
  // par le MÊME joueur (drill), sans remise adverse intercalée.
  let lastStrokeName = null;
  const strokeAction = (who, seg, type, baseLabel, explicit) => {
    if (baseLabel === "Service" && lastStrokeName === "Service" && !explicit && striker) {
      lastStrokeName = baseLabel;
      playShot(striker, seg, type, baseLabel);
      return;
    }
    if (striker && who === striker && !(baseLabel === "Service" && lastStrokeName === "Service"))
      playShot(other(who), "revers", "renvoi", "Remise");
    lastStrokeName = baseLabel;
    playShot(who, seg, type, baseLabel);
  };
  if (pre) {
    // Séquence STRUCTURÉE (analyse IA) : on déroule les coups directement —
    // acteur explicite sinon alternance naturelle de l'échange.
    for (const s of pre.shots) {
      const who = s.who === "A" || s.who === "B" ? s.who : other(striker || "B");
      strokeAction(who, s.seg, s.type, s.name, s.who === "A" || s.who === "B");
    }
  } else for (let i = 0; i < kept.length; i++) {
    const e = kept[i];
    const segEnd = Math.min(kept[i + 1] ? kept[i + 1].pos : e.pos + 90, e.pos + 90);
    const seg = body.slice(e.pos, segEnd);
    if (e.kind === "move") {
      const who = actorBefore(e.pos) || other(striker || "B");
      let tox = pos[who].x, toy = pos[who].y;
      // Montée vers l'avant : on s'approche du filet (centre) SANS le franchir.
      if (e.dir === "fwd") tox = who === "A" ? 44 : 56;
      else {
        // « vers sa droite » : la droite de A (face au jeu) est en BAS, celle de B en HAUT.
        const right = /droite/.test(seg), left = /gauche/.test(seg);
        if (right) toy = who === "A" ? G.bot : G.top;
        else if (left) toy = who === "A" ? G.top : G.bot;
        else toy = pos[who].y <= 50 ? G.bot : G.top;
      }
      // Déplacement nul (déjà à destination) : on n'ajoute pas de flèche
      // (évite les doublons « se précipite » + « vers l'avant » + « au filet »).
      if (Math.abs(tox - pos[who].x) < 2 && Math.abs(toy - pos[who].y) < 2) continue;
      actions.push({ from: who, tox, toy, type: "course", order: ord++, label: e.dir === "fwd" ? (sport === "tennis_de_table" ? "Avance près de la table" : "Montée au filet") : "Déplacement" });
      pos[who] = { x: tox, y: toy };
      continue;
    }
    if (e.kind === "exchange") {
      let who = other(striker || "B");     // l'échange repart de l'autre joueur
      for (let b = 0; b < e.count; b++) { strokeAction(who, seg, "renvoi", "Échange"); who = other(who); }
      continue;
    }
    const explicit = actorBefore(e.pos);
    strokeAction(explicit || other(striker || "B"), seg, e.type, e.label, !!explicit);
  }
  // Replacement au centre après les frappes (si demandé) : flèche de retour
  // pour chaque joueur posté hors du centre.
  if (/se replac|replacement|retour au centre|revenir au centre|se replacer/.test(c0(text))) {
    ["A", "B"].forEach((w) => {
      if (Math.abs(pos[w].y - 50) >= 8) actions.push({ from: w, tox: pos[w].x, toy: 50, type: "course", order: ord++, label: "Replacement au centre" });
    });
  }
  if (actions.length > 14) actions.length = 14;
  const first = actions.find((a) => a.type !== "course");
  const markers = first
    ? [{ type: "ballon", x: first.from === "A" ? G.ax + 5 : G.bx - 5, y: pos[first.from] ? players[first.from === "A" ? 0 : 1].y : 50 }]
    : [];
  markers.push(...bounces);
  return { players, markers, actions, steps: actions.map((a) => a.label), analyse: synthFilet(synthShots, "simple") };
}

/* Adaptateur ARCHITECTURE V2 (« l'IA comprend, le code dessine ») : dessine
   une séquence raquette à partir de COUPS STRUCTURÉS déjà validés (analyse
   IA) au lieu d'extraire les événements du texte — même moteur de placement.
   shots : [{ type, name, seg, who }] · format : "simple" | "double".
   Voir docs/architecture-v2-analyse-structuree.md. */
export function racketFromAnalysis(text, sport, shots, format, effectif) {
  return buildRacketSequence(text, sport, { shots, format, effectif });
}

/* DRILL À DISTRIBUTEURS (badminton / tennis, format « equipes » 1 contre N) —
   un JOUEUR SEUL défend tout le terrain ; en face, 2-3 DISTRIBUTEURS alternent
   les envois (amorti / dégagement…) pour le faire se déplacer de l'avant au
   fond. Le joueur seul se déplace sur chaque volant et le REJOUE vers le
   distributeur suivant. (Le rallye 1c1 et le double 2c2 ne savent pas dessiner
   cette asymétrie : le repli local la réduisait à un simple.) */
/* ─────────── PANIER DE BALLES — enseignant distributeur (sports de filet) ────
   « L'enseignant, panier de balles/ballons/volants, distribue ; l'élève
   enchaîne <coups> » : l'ENSEIGNANT (avec son panier) est en face et alimente,
   l'ÉLÈVE enchaîne les coups de l'enchaînement — distribution numérotée vers
   des points variés, déplacement de l'élève, frappe (coup nommé), replacement.
   File d'élèves supportée (« trois élèves en colonne, chacun leur tour »). */
export function feederBasketSignal(text) {
  const t = c0(text || "");
  const basket = /paniers? de (?:balles?|ballons?|volants?)|casiers? de (?:balles?|ballons?|volants?)|seau de balles?|distribution au panier|distribue au panier|au panier de/.test(t);
  // Verbes de DISTRIBUTION à bornes de mots : « l'élève RENVOIE » ou « RELANCE »
  // ne doivent pas déclencher (le défenseur d'un drill d'attaque n'est pas un
  // panier de balles).
  const teacher = /enseignant|professeur|\bprof\b|entra[îi]neur|\bcoach\b/.test(t)
    && /\bdistribue?n?t?\b|\bdistribution\b|\benvoie?n?t?\b|\blancent?\b|\blance\b|\balimente?n?t?\b|met en jeu/.test(t);
  return basket || teacher;
}
export function buildFeederBasket(text, sport) {
  const t = c0(text || "");
  const tt = sport === "tennis_de_table";
  // Géométrie : élève à GAUCHE, enseignant EN FACE (au bout de la table en TT,
  // au filet s'il y est décrit, sinon à mi-camp).
  const G = tt ? { ax: 10, fx: 90, land: { avant: 42, fond: 14, mid: 28 }, hitB: 62 }
    : { ax: 14, fx: /au filet/.test(t) ? 60 : 76, land: { avant: 40, fond: 10, mid: 26 }, hitB: 64 };

  // COUPS DE L'ÉLÈVE, dans l'ordre du texte : lexique partagé + compléments
  // locaux (coup droit / revers — trop ambigus pour le lexique du rally).
  // « retourne dans la colonne/file » est un RETOUR EN FILE, pas une frappe
  // (le lexique lisait « retourne » comme une remise).
  const tScan = t.replace(/retourne?r?\s[^.;]{0,30}?(?:colonne|file|rang)/g, " ");
  const strokes = [];
  STROKE_DEFS.forEach(([re, type, name], di) => {
    const r = new RegExp(re.source, "g"); let m;
    while ((m = r.exec(tScan))) strokes.push({ pos: m.index, di, type, name });
  });
  [[/coup droit/g, "renvoi", "Coup droit"], [/\brevers\b/g, "renvoi", "Revers"]].forEach(([re, type, name], i) => {
    let m; while ((m = re.exec(tScan))) strokes.push({ pos: m.index, di: 100 + i, type, name });
  });
  strokes.sort((a, b) => a.pos - b.pos || a.di - b.di);
  // Dédoublonnage par fenêtre (« remet en poussette » = UNE frappe) et retrait
  // des coups de L'ENSEIGNANT (envoi/service/distribution : la distribution est
  // dessinée par le gabarit lui-même).
  let kept = [];
  for (const s of strokes) {
    if (/^(Envoi|Service|Distribution)$/.test(s.name)) continue;
    const last = kept[kept.length - 1];
    // Fenêtre de dédoublonnage (« remet en poussette » = UNE frappe) — SAUF si
    // un connecteur d'enchaînement sépare les deux (« volée PUIS smash »).
    if (last && s.pos - last.pos <= 12 && !/\bpuis\b|,|\bet\b|ensuite|enfin/.test(t.slice(last.pos, s.pos))) continue;
    kept.push(s);
  }
  // « Frappe » GÉNÉRIQUE parasite (« chaque frappe », « après la frappe ») :
  // ignorée dès qu'au moins un coup NOMMÉ est présent.
  if (kept.some((s) => s.name !== "Frappe")) kept = kept.filter((s) => s.name !== "Frappe");
  // « N frappes — filet, fond, filet, fond » : séquence de profondeurs SANS
  // coups nommés -> une frappe par profondeur listée.
  if (kept.length <= 1 && /frappes?/.test(t)) {
    const seq = [...t.matchAll(/\b(filet|fond)\b/g)].map((m) => m[1]);
    if (seq.length >= 2) {
      kept.length = 0;
      seq.slice(0, 4).forEach((d) => kept.push({ type: "renvoi", name: d === "filet" ? "Frappe au filet" : "Frappe du fond", depth: d === "filet" ? "avant" : "fond" }));
    }
  }
  if (!kept.length) kept.push({ type: "renvoi", name: "Frappe" });
  const seq = kept.slice(0, 4);
  // GESTE D'ATTAQUE décrit (« chaque attaquant frappe », « smash en zone 4 ») :
  // la frappe générique de l'élève est une vraie ATTAQUE (flèche de tir), pas
  // une remise neutre.
  if (/attaquant|attaque|smash/.test(t)) for (const s of seq) if (s.name === "Frappe") { s.type = "tir"; s.name = "Attaque"; }

  // ÉLÈVE(S) : file éventuelle (« trois élèves en colonne, chacun leur tour »).
  // CHANTIER REPLI (👎 PROD106, « il n'y a pas 3 élèves ») : un effectif
  // CHIFFRÉ (« trois élèves partent du fond… les deux autres aident ») se
  // dessine même sans mot de file — l'enseignant compte ses élèves sur le
  // schéma avant tout.
  const file = /colonne|en file|chacun (?:son|leur) tour/.test(t);
  // …ET LE COMPTAGE NE CONNAISSAIT QUE LE MOT « ÉLÈVES ». L'enseignant écrit
  // aussi « deux défenseurs », « 2 joueurs derrière », « trois receveurs » :
  // SEPT 👎 sur ce seul gabarit, tous au volley, dont « le nombre de joueur
  // demandé (ici 2 en défense) est souvent zappé, il n'y en a qu'un ! » et
  // « Encore un joueur au lieu de 3 !! inutilisable !! ».
  const fm = t.match(/(\d+|deux|trois|quatre)\s*(?:[ée]l[èe]ves?|joueurs?|d[ée]fenseurs?|receveurs?|r[ée]ceptionneurs?|attaquants?|relanceurs?)/);
  const nEl = fm ? clampN(frenchNumWord(fm[1]), 1, 4) : (file ? 3 : 1);
  // …ET ILS JOUENT, ILS N'ATTENDENT PAS. Le correctif de PROD106 rangeait les
  // élèves supplémentaires en « En attente » AU BORD du terrain : c'est juste
  // pour une COLONNE (chacun son tour), et faux dès que le texte les fait
  // jouer ENSEMBLE — « deux défenseurs côte à côte au fond », « trois élèves
  // en réception ». Le 👎 « Où sont les 3 élèves en réception !! » visait
  // exactement ça : les pions existaient, mais hors du jeu.
  // Hors file, ils sont donc SUR le terrain, à la profondeur de l'élève de
  // référence et étalés en largeur — c'est le « côte à côte » du texte.
  const ROLES = [[/d[ée]fenseur/, "Défenseur"], [/r[ée]ceptionneur|receveur/, "Réceptionneur"],
    [/attaquant/, "Attaquant"], [/relanceur/, "Relanceur"]];
  const roleN = (fm && (ROLES.find(([re]) => re.test(fm[0])) || [])[1]) || "Élève";
  const ensemble = nEl > 1 && !file;
  const players = [{ id: "A", num: 1, role: ensemble ? roleN : "Élève", x: G.ax + 6, y: 50, color: "#0072B2", team: "A" }];
  for (let j = 1; j < nEl; j++) {
    if (ensemble) players.push({ id: "A" + (j + 1), num: j + 1, role: roleN, x: G.ax + 6,
      y: clampN(50 + ((j + 1) >> 1) * 18 * (j % 2 ? 1 : -1), 12, 88), color: "#0072B2", team: "A" });
    else players.push({ id: "W" + j, role: "En attente", x: Math.max(3, G.ax - 4), y: 50 + j * 12, color: "#0072B2", team: "A" });
  }
  // ENSEIGNANT en face, panier (casier) et ballon à côté.
  players.push({ id: "E", role: /entra[îi]neur/.test(t) ? "Entraîneur" : "Enseignant", x: G.fx, y: 50, color: "#D55E00", team: "B" });
  const markers = [
    { type: "casier", x: Math.min(96, G.fx + 6), y: 62 },
    { type: "ballon", x: Math.max(3, G.fx - 4), y: 50 },
  ];

  // ALTERNANCE latérale : coups latéralisés (coup droit/revers, alterné) ou
  // plusieurs coups de même profondeur -> les distributions balaient les côtés.
  const lateral = /altern|pivot|c[ôo]t[ée]|coins|droite|gauche/.test(t);
  // CHANTIER REPLI (👎 PROD106, « distribution trop longue ») : « balles
  // COURTES / qui tombent devant / derrière le filet » au niveau du TEXTE fixe
  // la profondeur des distributions quand le coup n'en porte pas — la balle
  // tombe devant l'élève, qui doit AVANCER : c'est l'objet de l'exercice.
  const dText = /balles? courtes?|volants? courts?|tombe (?:devant|pr[èe]s du filet)|derri[èe]re le filet|devant (?:soi|lui|elle|l'[ée]l[èe]ve)/.test(t) ? "avant" : null;
  const depthOf = (s) => s.depth || (STROKE_DEPTH[s.name] ? STROKE_DEPTH[s.name].depth : null) || dText;
  const actions = []; let ord = 1, side = 0;
  const posA = { x: G.ax + 6, y: 50 };
  seq.forEach((s, i) => {
    const d = depthOf(s);
    const lx = d === "avant" ? G.land.avant : d === "fond" ? G.land.fond : G.land.mid;
    const ly = lateral ? (side ? 72 : 28) : clampN(44 + (i % 2) * 12, 28, 72);
    side ^= 1;
    // 1) Distribution de l'enseignant vers le point de frappe.
    actions.push({ from: "E", tox: lx, toy: ly, type: "renvoi", order: ord++, label: i === 0 ? "Distribution" : null });
    // 2) Déplacement de l'élève si le point est loin.
    if (Math.hypot(posA.x - lx, posA.y - ly) > 7) {
      actions.push({ from: "A", tox: lx, toy: ly, type: "course", order: ord++, label: i === 0 ? "Déplacement" : null });
      posA.x = lx; posA.y = ly;
    }
    // 3) Frappe de l'élève (coup nommé) vers le camp de l'enseignant.
    actions.push({ from: "A", tox: G.hitB, toy: side ? 62 : 38, type: s.type, order: ord++, label: s.name });
  });
  // Replacement au centre (une fois, en fin d'enchaînement).
  if (/se replace|replacement|revient au centre/.test(t)) {
    actions.push({ from: "A", tox: G.ax + 6, toy: 50, type: "course", order: ord++, label: "Replacement au centre" });
    posA.x = G.ax + 6; posA.y = 50;
  }

  const OBJ = sport === "volleyball" ? "ballons" : sport === "badminton" ? "volants" : "balles";
  return {
    players, markers, actions,
    steps: [
      `L'enseignant distribue depuis le panier de ${OBJ}`,
      `L'élève enchaîne : ${seq.map((s) => s.name.toLowerCase()).join(" puis ")}`,
      ...(nEl > 1 ? [ensemble ? `Les ${nEl} joueurs sont en place ensemble` : "Chacun son tour, puis retour dans la file"] : []),
    ],
    title: `Panier de ${OBJ} — enchaînement`,
  };
}

export function buildRacketFeederDrill(text, sport, shots, effectif) {
  const e = effectif || {};
  const g = parseInt(e.gauche, 10) || 1, d = parseInt(e.droite, 10) || 2;
  const nFeed = clampN(Math.max(g, d), 2, 3);
  if (Math.min(g, d) !== 1) return null;
  // Joueur seul à GAUCHE (lecture gauche -> droite), distributeurs en face.
  const players = [{ id: "A", num: 1, role: "Joueur seul", x: 14, y: 50, color: "#0072B2", team: "A" }];
  const F = nFeed === 2 ? [[74, 30], [74, 70]] : [[74, 26], [74, 74], [86, 50]];
  F.forEach(([x, y], i) => players.push({ id: "F" + (i + 1), num: i + 1, role: "Distributeur", x, y, color: "#D55E00", team: "B" }));
  // Zones visées chez le joueur seul, selon la nature/profondeur du coup.
  const landOf = (name, seg) => {
    const s = (name + " " + (seg || "")).toLowerCase();
    if (/amorti|court|avant|filet/.test(s)) return { x: 40, tag: "filet" };
    if (/d[ée]gag|fond|long|profond|lob|clear/.test(s)) return { x: 10, tag: "fond" };
    return { x: 26, tag: "" };
  };
  const posA = { x: 14, y: 50 };
  const actions = []; let ord = 1, fi = 0, side = 0, last = null, firstWho = null;
  for (const s of (shots || []).slice(0, 8)) {
    const cur = s.who === "A" ? "solo" : s.who ? "feeder" : (last === "solo" ? "feeder" : "solo");
    if (cur === "solo") {
      // Renvoi du joueur seul vers le distributeur qui jouera le coup suivant
      // (derrière la ligne de service courte : un service y reste légal).
      actions.push({ from: "A", tox: 70, toy: F[fi][1], type: s.type, order: ord++, label: s.name });
      if (!firstWho) firstWho = "A";
    } else {
      const land = landOf(s.name, s.seg);
      const y = side ? 72 : 28; side ^= 1;   // alterner les côtés = faire courir le joueur seul
      actions.push({ from: "F" + (fi + 1), tox: land.x, toy: y, type: s.type, order: ord++, label: s.name + (land.tag ? " → " + land.tag : "") });
      if (Math.hypot(posA.x - land.x, posA.y - y) > 7) {
        actions.push({ from: "A", tox: land.x, toy: y, type: "course", order: ord++, label: "Déplacement sur le volant" });
        posA.x = land.x; posA.y = y;
      }
      if (!firstWho) firstWho = "F" + (fi + 1);
      fi = (fi + 1) % nFeed;
    }
    last = cur;
  }
  if (actions.length < 2) return null;
  const p0 = players.find((p) => p.id === firstWho) || players[0];
  return { players, markers: [{ type: "ballon", x: clampN(p0.x + (p0.team === "A" ? 4 : -4), 4, 96), y: p0.y }], actions };
}

/* ---- Famille ATELIER : ateliers NOMMÉS (« Atelier 1 : … Atelier 2 : … ») ----
   Quand le texte énumère plusieurs ateliers distincts, on les matérialise
   TOUS : une zone titrée par atelier (grille), un plot, un groupe d'élèves et
   le sens de travail — au lieu de ne dessiner que le premier reconnu. */
function extractNamedAteliers(t) {
  const out = [];
  // Séparateur OPTIONNEL (« Atelier 1 : sprint », « Atelier 1 course… ») et
  // capture PARESSEUSE bornée par l'atelier suivant : plusieurs ateliers sur
  // une même ligne ne s'avalent pas. Le nom reste ENTIER (la détection du
  // type lit tout ; seuls l'étiquette et l'analyse tronquent).
  const r = /ateliers?\s*(?:n[°o]\s*)?(\d+)\s*[:.\-–]?\s*([^\n;]+?)(?=\s*ateliers?\s*(?:n[°o]\s*)?\d|[\n;]|$)/gi;
  let m;
  while ((m = r.exec(t))) out.push({ n: +m[1], nom: m[2].trim().replace(/\s+/g, " ") });
  out.sort((a, b) => a.n - b.n);
  return out;
}
/* Agrès de gymnastique reconnus dans le nom d'un atelier -> [regex, kind, label].
   Ordre du plus SPÉCIFIQUE au plus générique (« barres asymétriques » avant
   « barre fixe » ; « tapis » en dernier, car beaucoup d'agrès en comportent un). */
const GYM_AGRES = [
  [/plan\s*inclin[ée]/, "plan_incline", "Plan incliné"],
  [/barres?\s*asym[ée]triques?/, "barres_asym", "Barres asymétriques"],
  // « parallèles » APRÈS « asymétriques » (les deux commencent par « barres »)
  // et AVANT « barre fixe ». Ce ne sont pas des variantes de style : les barres
  // asymétriques sont à DEUX HAUTEURS différentes, les parallèles à la MÊME —
  // l'IA substituait l'une à l'autre (salve gym nº1, G03), ce qui n'est pas une
  // dégradation mais un contresens d'agrès.
  [/barres?\s*parall[èe]les?/, "barres_par", "Barres parallèles"],
  [/barre\s*fixe/, "barre_fixe", "Barre fixe"],
  [/poutre/, "poutre", "Poutre"],
  // « cheval » AVANT « tremplin » : un « saut de cheval avec tremplin » a pour
  // agrès PRINCIPAL le cheval/table (le tremplin n'est que la planche d'appel) —
  // sinon le mot « tremplin » capterait l'atelier en mini-trampoline.
  [/cheval|saut\s*de\s*cheval|table\s*de\s*saut|saut\s*(?:de|sur)\s*(?:la\s*)?table/, "cheval", "Table de saut"],
  [/mini.?tramp|trampoline|tremplin/, "trampoline", "Mini-trampoline"],
  [/mur d'escalade|espalier|mur de gym/, "mur", "Mur"],   // « contre le mur » (drill de balle) n'est PAS un agrès -> géré ailleurs
  [/tapis|matelas/, "tapis", "Tapis"],
];
/* Gestes gymniques (sans agrès nommé) qui suffisent à reconnaître un atelier de
   GYM — et l'empêchent de tomber dans le rendu athlétisme : « course d'élan »
   précédant un salto ne doit PAS être pris pour un sprint. */
const GYM_SKILL_RE = /roulade|[ée]quilibre|arabesque|salto|\batr\b|appui\s*tendu\s*renvers|rondade|\broue\b|saut\s*de\s*mains|souplesse|saut\s*group|saut\s*(?:en\s*)?extension|saut\s*tendu|gymniqu/;

export function ateliersFromList(items, rotation) {
  const n = items.length;
  const cols = n <= 3 ? 1 : 2;
  const rows = Math.ceil(n / cols);
  const zones = [], markers = [], players = [], actions = [];
  // En 1 colonne, on RÉTRÉCIT un peu la largeur pour libérer une VOIE à droite
  // où passent les arcs de rotation (sans traverser le contenu des ateliers).
  const W = cols === 1 ? 80 : 42;
  const slotH = 88 / rows;
  const H = Math.min(34, slotH - 6);
  items.forEach((it, i) => {
    const c = i % cols, r = Math.floor(i / cols);
    const x = cols === 1 ? 8 : 6 + c * 48;
    const y = Math.round(6 + r * slotH + (slotH - H) / 2);
    const num = it.n || i + 1;
    const nom = it.nom.toLowerCase();
    const cy = Math.round(y + H / 2);
    const id = "G" + (i + 1);
    const zone = { x, y, w: W, h: H, label: `Atelier ${num} — ${it.nom.slice(0, 44)}`, style: "dashed" };
    zones.push(zone);

    // MINI-SCÈNE selon le type d'atelier (déduit du nom) : aires de réception
    // RECONNAISSABLES — sable (longueur), tapis (hauteur), aire de lancer —
    // et LIGNES matérialisées (départ, arrivée, chrono, limite de lancer)
    // via des zones de largeur nulle (trait vertical étiqueté).
    const vline = (lx, label) => zones.push({ x: lx, y: y + 3, w: 0, h: H - 6, style: "solid", label });
    // Agrès SPÉCIFIQUES gym (le « tapis » est partagé avec l'athlétisme — saut
    // en hauteur — donc il ne DÉCLENCHE pas à lui seul la branche gym).
    const agresGym = GYM_AGRES.filter(([re, k]) => k !== "tapis" && re.test(nom));
    if (agresGym.length || GYM_SKILL_RE.test(nom)) {
      // ── ATELIER de GYMNASTIQUE : groupe + AGRÈS + aire de réception ──
      // Prioritaire sur l'athlétisme (« course d'élan » d'un salto n'est pas un
      // sprint). Le geste se travaille SUR l'agrès nommé, ou au sol (tapis).
      const gx = x + 7;
      players.push({ id, role: "Groupe " + num, team: "A", color: "#0072B2", x: gx, y: cy });
      // « mur » n'est PAS dans GYM_AGRES (sinon il capterait « passe contre le mur ») :
      // ici on est déjà en contexte GYM (skill/agrès reconnu), donc « ATR contre le mur »
      // matérialise bien l'agrès mur.
      const main = agresGym[0] || (/\bmur\b/.test(nom) ? [/\bmur\b/, "mur", "Mur"] : null);
      // Aire de réception matelassée pour les éléments aériens / sorties.
      const needsMat = !!main
        && /salto|sortie|r[ée]ception|saut\s*(?:en\s*)?extension|saut\s*tendu|saut\s*de\s*mains/.test(nom);
      if (main) {
        const apX = Math.round(x + W * (needsMat ? 0.5 : 0.6)), aw = Math.round(W * 0.28), ah = H - 12;
        markers.push({ type: "agres", kind: main[1], x: apX, y: cy, w: aw, h: ah, label: main[2] });
        // SAUT DE CHEVAL « avec tremplin » : tremplin (mini-tremplin de gym) JUSTE
        // DEVANT le cheval. NB : « planche d'appel » est un terme d'ATHLÉTISME
        // (saut en longueur) — surtout pas un tremplin — donc on ne le déclenche
        // que sur « tremplin ».
        const tremplin = main[1] === "cheval" && /tremplin/.test(nom);
        if (tremplin) {
          const tw = Math.max(5, Math.round(aw * 0.5));
          markers.push({ type: "agres", kind: "tremplin", x: apX - Math.round(aw / 2) - Math.round(tw / 2) - 1, y: cy, w: tw, h: Math.round(ah * 0.55), label: "Tremplin" });
        }
        actions.push({ fromx: gx + 6, fromy: cy, tox: apX - Math.round(aw / 2) - (tremplin ? Math.max(5, Math.round(aw * 0.5)) : 2), toy: cy, type: "course" });
        if (needsMat) {
          const mw = Math.round(W * 0.2);
          zones.push({ x: x + W - 3 - mw, y: y + 4, w: mw, h: H - 8, fill: "tapis", style: "solid", label: "Tapis" });
        }
      } else {
        // Agrès « tapis » explicite, ou geste au sol sans agrès : aire matelassée.
        zones.push({ x: Math.round(x + W * 0.42), y: y + 4, w: Math.round(W * 0.46), h: H - 8, fill: "tapis", style: "solid", label: "Tapis" });
        actions.push({ fromx: gx + 6, fromy: cy, tox: Math.round(x + W * 0.52), toy: cy, type: "course", style: "dashed" });
      }
    } else if (/hauteur/.test(nom)) {
      // Barre VUE DE DESSUS : court sur la longueur du tapis, juste à GAUCHE de
      // son bord gauche (on la franchit puis on retombe sur le tapis derrière).
      const matX = Math.round(x + W * 0.62);
      players.push({ id, role: "Sauteur", team: "A", color: "#0072B2", x: x + 6, y: y + H - 6 });
      actions.push({ from: id, tox: matX - 5, toy: cy, type: "course" });                       // élan jusqu'à l'appel
      markers.push({ type: "barre", x: matX - 1, y: cy, h: H - 6 });                            // barre = longueur du tapis, juste au bord gauche
      zones.push({ x: matX, y: y + 3, w: Math.round(W * 0.34), h: H - 6, fill: "tapis", style: "solid", label: "Tapis" });
    } else if (/longueur|multi-?bonds?|triple/.test(nom)) {
      // Planche d'appel PRÈS du sable en longueur, reculée en multi-bond/triple.
      const triple = /multi-?bonds?|triple/.test(nom);
      const sableX = Math.round(x + W * 0.64);
      const plancheX = Math.round(x + W * (triple ? 0.4 : 0.6));
      players.push({ id, role: "Sauteur", team: "A", color: "#0072B2", x: x + 6, y: cy });
      actions.push({ from: id, tox: plancheX - 2, toy: cy, type: "course" });                     // élan
      markers.push({ type: "planche", x: plancheX, y: cy });                                      // planche d'appel
      actions.push({ fromx: plancheX + 1, fromy: cy, tox: Math.round(x + W * 0.8), toy: cy, type: "course", style: "dashed" }); // saut / bonds
      zones.push({ x: sableX, y: y + 3, w: Math.round(W * 0.32), h: H - 6, fill: "sable", style: "solid", label: "Sable" });
    } else if (/lancer|vortex|javelot|poids|m[ée]decine|anneau|disque/.test(nom)) {
      const limitX = Math.round(x + W * 0.3);
      players.push({ id, role: "Lanceur", team: "A", color: "#0072B2", x: Math.round(x + W * 0.2), y: cy });
      markers.push({ type: "ballon", x: Math.round(x + W * 0.2) + 4, y: cy - 4 });                // engin
      vline(limitX, "Ligne de lancer");                                                           // limite à ne pas franchir
      zones.push({ x: Math.round(x + W * 0.45), y: y + 3, w: Math.round(W * 0.5), h: H - 6, fill: "aire", style: "dashed", label: "Aire de lancer" });
      markers.push({ type: "cible", x: Math.round(x + W * 0.7), y: cy });
      actions.push({ from: id, tox: Math.round(x + W * 0.7), toy: cy, type: "tir" });             // trajectoire
      // Plots REPÈRES de distance (« un plot tous les 10 mètres ») répartis le
      // long de l'aire de lancer pour mesurer la performance.
      if (/\bplots?\b/.test(nom)) {
        const a0 = x + W * 0.46, a1 = x + W * 0.93, count = 5;
        for (let p = 1; p <= count; p++)
          markers.push({ type: "plot", x: Math.round(a0 + (a1 - a0) * (p / (count + 1))), y: Math.round(cy - 7) });
      }
      // ZONES DE PERFORMANCE cotées (« zones à 10, 15 et 20 m ») : lignes
      // graduées dans l'aire de lancer, cote sur chacune.
      const zs = nom.match(/zones? [àa] ([\d ,et]+) m/);
      if (zs) {
        const dists = (zs[1].match(/\d+/g) || []).map(Number).filter((v) => v > 0).slice(0, 4);
        const dmax = Math.max(...dists);
        for (const d of dists) {
          const zx = Math.round(x + W * (0.45 + 0.48 * (d / dmax)));
          zones.push({ x: zx, y: y + 5, w: 0, h: H - 10, style: "dashed", label: `${d} m` });
        }
      }
    } else if (/\bhaies?\b/.test(nom)) {
      // COURSE DE HAIES : couloir Départ -> Arrivée, coureur, et N haies (barres en
      // travers) placées aux distances CUMULÉES si elles sont données (« 10 m + 6 m
      // + 6 m… » -> haies à 10, 16, 22 m…), sinon réparties régulièrement.
      const x0 = x + 6, x1 = x + W - 6;
      vline(x0, "Départ"); vline(x1, "Arrivée");
      players.push({ id, role: "Coureur", team: "A", color: "#0072B2", x: x0 + 3, y: cy });
      actions.push({ from: id, tox: x1 - 2, toy: cy, type: "course" });
      const nMatch = it.nom.match(/(\d+)\s*haies?/i);
      const nH = clampN(nMatch ? +nMatch[1] : 3, 1, 6);
      const segs = (it.nom.match(/\d+\s*m\b/g) || []).map((s) => parseInt(s, 10)).filter((v) => v > 0);
      const hurdleX = [];
      if (segs.length >= 2) {
        const cum = []; segs.reduce((a, b) => { const v = a + b; cum.push(v); return v; }, 0);
        const total = cum[cum.length - 1];
        const take = Math.min(nH, cum.length - 1);          // dernière borne = ligne d'arrivée
        for (let k = 0; k < take; k++) hurdleX.push(x0 + (cum[k] / total) * (x1 - x0));
        for (let k = take; k < nH; k++) hurdleX.push(x0 + ((k + 1) / (nH + 1)) * (x1 - x0));
        zone.dim = total + " m";
      } else {
        for (let k = 1; k <= nH; k++) hurdleX.push(x0 + (k / (nH + 1)) * (x1 - x0));
        const dm = it.nom.match(/(\d+)\s*m\b/); if (dm) zone.dim = dm[1] + " m";
      }
      hurdleX.forEach((hx) => markers.push({ type: "haie", x: Math.round(hx), y: cy }));
    } else if (/lay-?up|\btirs?\b|shoot|panier|frappe|tir\s+(?:en|[àa]\s|de\s)/.test(nom)) {
      // Atelier de TIR (lay-up, tir à mi-distance, tir en suspension, tir au but…) :
      // tireur, ballon, CIBLE (but/panier) à droite, flèche de tir. Évite que
      // « tir EN COURSE » tombe dans la branche sprint (couloir Départ/Arrivée).
      const tx = Math.round(x + W * 0.8);
      players.push({ id, role: "Tireur", team: "A", color: "#0072B2", x: x + 7, y: cy });
      markers.push({ type: "ballon", x: x + 11, y: cy - 4 });
      markers.push({ type: "cible", x: tx, y: cy });
      actions.push({ from: id, tox: tx, toy: cy, type: "tir" });
    } else if (/sprint|course|vitesse|montante|relais|navette/.test(nom)) {
      const lance = /d[ée]parts?\s+lanc[ée]/.test(nom);
      const duo = /montante|descendante|duel|deux coureurs|2 coureurs/.test(nom);
      const x0 = x + 6, x1 = x + W - 6;
      let xc = null;
      vline(x0, "Départ");
      // Plots-REPÈRES de mesure explicites (« plots 16-19-22-25-28 m ») : on les
      // place À L'ÉCHELLE de leurs distances (et on en dessine le bon NOMBRE), au
      // lieu de trois plots fixes.
      const plotRun = (nom.match(/plots?\D*([\d][\d\s,–-]*)/) || [])[1];
      const plotNums = plotRun ? (plotRun.match(/\d+/g) || []).map(Number).filter((v) => v > 0 && v < 300) : [];
      if (lance) {
        // Départ LANCÉ : ligne d'élan, ligne où démarre le CHRONO, puis des
        // REPÈRES de mesure (pas de ligne d'arrivée : on mesure la distance).
        xc = Math.round(x + W * 0.4);
        vline(xc, "Chrono");
        if (plotNums.length >= 2) {
          const dmin = Math.min(...plotNums), dmax = Math.max(...plotNums);
          plotNums.forEach((d) => {
            const frac = dmax > dmin ? (d - dmin) / (dmax - dmin) : 0.5;
            markers.push({ type: "plot", x: Math.round(xc + 4 + frac * ((x1 - 2) - (xc + 4))), y: y + H - 4 });
          });
        } else {
          for (let k = 1; k <= 3; k++) markers.push({ type: "plot", x: Math.round(xc + (k * (x1 - xc)) / 3) - 1, y: y + H - 4 });
        }
      } else {
        vline(x1, "Arrivée");
      }
      // Montante/descendante : au moins DEUX coureurs qui se font la course.
      const lanes = duo ? [y + Math.round(H * 0.33), y + Math.round(H * 0.67)] : [cy];
      lanes.forEach((ly, k) => {
        const pid = k ? id + "b" : id;
        players.push({ id: pid, role: "Coureur" + (duo ? " " + (k + 1) : ""), team: "A", color: "#0072B2", x: x0 + 3, y: ly });
        actions.push({ from: pid, tox: x1 - 2, toy: ly, type: "course" });
      });
      const dm = it.nom.match(/(\d+)\s*m(?:[èe]tres?)?\b/);
      // Plots cotés : l'atelier est coté par le repère le plus LOIN (ex. 28 m).
      // Sinon, départ lancé : la distance cote la ZONE D'ÉLAN (Départ -> Chrono),
      // segment de mesure dédié ; à défaut elle cote la course entière.
      if (plotNums.length >= 2) zone.dim = Math.max(...plotNums) + " m";
      else if (dm && lance) zones.push({ x: x0, y: y + H - 6, w: xc - x0, h: 0, style: "dashed", dim: dm[1] + " m" });
      else if (dm) zone.dim = dm[1] + " m";
      if (/starter|observat|chronom/.test(nom))
        players.push({ id: id + "s", role: "Starter", team: "OBS", color: "#F2C200", x: x + W - 4, y: y + 6 });
    } else if (/pyramide|acrosport|porteur|voltigeur/.test(nom)) {
      // ACROSPORT : figure à trois — porteur (base), voltigeur (au-dessus), pareur
      // (à côté), sur un tapis. (Vue de dessus : voltigeur légèrement « au-dessus ».)
      const fx = Math.round(x + W * 0.45);
      zones.push({ x: Math.round(x + W * 0.28), y: y + 4, w: Math.round(W * 0.42), h: H - 8, fill: "tapis", style: "solid", label: "Tapis" });
      players.push({ id: id + "p", role: "Porteur", team: "A", color: "#0072B2", x: fx, y: cy + 7 });
      players.push({ id: id + "v", role: "Voltigeur", team: "A", color: "#0072B2", x: fx, y: cy - 7 });
      players.push({ id: id + "a", role: "Pareur", team: "OBS", color: "#F2C200", x: Math.round(x + W * 0.72), y: cy });
    } else if (/[ée]toile|d[ée]placement|appuis|navette de plot|plots? aux coins|chandelle au/.test(nom)) {
      // Travail de DÉPLACEMENT : un joueur à la base + N plots à atteindre, une course
      // vers chacun. « étoile / coins / appuis / autour » -> plots EN ANNEAU autour du
      // joueur ; sinon -> plots alignés. Le NOMBRE de plots est lu (« 4 plots »).
      const star = /[ée]toile|coins?|appuis|autour/.test(nom);
      const nm = nom.match(/(\d+)\s*plots?/);
      const nP = clampN(nm ? parseInt(nm[1], 10) : (star ? 4 : 3), 2, 6);
      const ccx = Math.round(x + W * (star ? 0.5 : 0.18)), ccy = cy;
      players.push({ id, role: "Joueur", team: "A", color: "#0072B2", x: ccx, y: ccy });
      const pts = [];
      if (star) for (let k = 0; k < nP; k++) {
        const ang = -Math.PI / 2 + (k / nP) * 2 * Math.PI;
        pts.push([Math.round(clampN(ccx + W * 0.34 * Math.cos(ang), x + 3, x + W - 3)), Math.round(clampN(ccy + (H / 2 - 4) * Math.sin(ang), y + 4, y + H - 4))]);
      } else for (let k = 1; k <= nP; k++) pts.push([Math.round(x + W * (0.35 + 0.55 * (k - 1) / (nP - 1 || 1)) + W * 0.05), ccy]);
      pts.forEach(([px, py]) => {
        markers.push({ type: "plot", x: px, y: py });
        actions.push({ fromx: ccx, fromy: ccy, tox: px, toy: py, type: "course", style: "dashed" });
      });
    } else if (/\bmur\b/.test(nom)) {
      // BALLE / RAQUETTE CONTRE UN MUR : joueur + mur (trait vertical) + envoi puis rebond.
      const wallX = x + W - 6;
      players.push({ id, role: "Joueur", team: "A", color: "#0072B2", x: x + 8, y: cy });
      markers.push({ type: "ballon", x: x + 11, y: cy - 3 });
      zones.push({ x: wallX, y: y + 4, w: 0, h: H - 8, style: "solid", ...(i === 0 ? { label: "Mur" } : {}) });
      actions.push({ from: id, tox: wallX - 2, toy: cy - 3, type: "tir", style: "solid" });
      actions.push({ fromx: wallX - 2, fromy: cy + 2, tox: x + 13, toy: cy + 5, type: "course", style: "dashed" });   // rebond
    } else if (/conduite|dribble|slalom|double.?pas|crochet/.test(nom)) {
      // CONDUITE / DRIBBLE EN SLALOM : un joueur zigzague entre des plots (avec ballon
      // si conduite/dribble, sinon slalom de motricité).
      const ball = /balle|ballon|conduite|dribble/.test(nom);
      const x0 = x + 12, x1 = x + W - 8, nC = 4;
      players.push({ id, role: ball ? "Conducteur" : "Élève", team: "A", color: "#0072B2", x: x + 6, y: cy });
      if (ball) markers.push({ type: "ballon", x: x + 9, y: cy - 3 });
      const segs = [[x + 6, cy]];
      for (let k = 0; k < nC; k++) {
        const px = Math.round(x0 + (x1 - x0) * (k + 0.5) / nC);
        markers.push({ type: "plot", x: px, y: cy });
        segs.push([px, Math.round(clampN(cy + (k % 2 ? 8 : -8), y + 5, y + H - 5))]);
      }
      segs.push([Math.round(x1), cy]);
      for (let k = 1; k < segs.length; k++)
        actions.push({ fromx: segs[k - 1][0], fromy: segs[k - 1][1], tox: segs[k][0], toy: segs[k][1], type: ball ? "dribble" : "course", style: ball ? "solid" : "dashed" });
    } else if (/jonglage|jongl|maniement/.test(nom)) {
      // JONGLAGE / MANIEMENT DE BALLE : joueur + ballon (geste technique sur place).
      const cxx = Math.round(x + W / 2);
      players.push({ id, role: "Joueur", team: "A", color: "#0072B2", x: cxx, y: cy + 4 });
      markers.push({ type: "ballon", x: cxx, y: Math.round(clampN(cy - 7, y + 4, cy)) });
      actions.push({ fromx: cxx, fromy: cy, tox: cxx, toy: Math.round(clampN(cy - 9, y + 4, cy)), type: "course", style: "dashed" });
    } else if (/reprise|corner|coup de pied de coin/.test(nom)) {
      // CENTRE ET REPRISE : un ailier centre depuis l'aile, un finisseur reprend au but.
      const bx = x + W - 5;
      players.push({ id: id + "a", role: "Ailier", team: "A", color: "#0072B2", x: x + 9, y: y + H - 6 });
      players.push({ id: id + "f", role: "Finisseur", team: "A", color: "#0072B2", x: x + W - 16, y: cy });
      markers.push({ type: "ballon", x: x + 12, y: y + H - 6 });
      markers.push({ type: "cible", x: bx, y: cy });
      actions.push({ from: id + "a", to: id + "f", type: "centre", ...(i === 0 ? { label: "Centre" } : {}) });
      actions.push({ from: id + "f", tox: bx, toy: cy, type: "tir", ...(i === 0 ? { label: "Reprise" } : {}) });
    } else if ((/cercle|[ée]toile|au centre|joueur central|pivot central|passe centrale/.test(nom)) && /passe|ballon|balle/.test(nom)) {
      // PASSES EN ÉTOILE / JOUEUR CENTRAL : 4 joueurs autour + 1 au centre (coéquipier),
      // toutes les passes passent par le PIVOT central puis ressortent (coopératif, pas
      // un taureau : le central est un partenaire, aucune interception).
      const cxx = Math.round(x + W / 2);
      const rx = Math.round(W * 0.3), ry = Math.max(7, Math.round(H / 2 - 6));
      const ring = [{ dx: 0, dy: -ry }, { dx: rx, dy: 0 }, { dx: 0, dy: ry }, { dx: -rx, dy: 0 }]
        .map(({ dx, dy }) => ({ x: Math.round(clampN(cxx + dx, x + 3, x + W - 3)), y: Math.round(clampN(cy + dy, y + 4, y + H - 4)) }));
      const ids = ring.map((_, k) => id + "o" + (k + 1));
      ring.forEach((p, k) => players.push({ id: ids[k], role: "Joueur", team: "A", color: "#0072B2", x: p.x, y: p.y }));
      players.push({ id: id + "c", role: "Pivot", team: "A", color: "#0072B2", x: cxx, y: cy });
      markers.push({ type: "ballon", x: Math.round(clampN(ring[0].x + 3, x + 3, x + W - 3)), y: ring[0].y });
      ring.forEach((_, k) => actions.push({ from: ids[k], to: id + "c", type: "passe", style: k ? "dashed" : "solid", ...(i === 0 && k === 0 ? { label: "Passe au pivot" } : {}) }));
    } else if (/passe|une.?deux|jeu [àa] deux|donne et|remise/.test(nom)) {
      // PASSES (à deux / précision / passe-et-suit) : 2 joueurs + circulation ; cible si « précision ».
      players.push({ id: id + "1", role: "Passeur", team: "A", color: "#0072B2", x: x + 11, y: cy - 5 });
      players.push({ id: id + "2", role: "Relais", team: "A", color: "#0072B2", x: x + W - 13, y: cy + 5 });
      markers.push({ type: "ballon", x: x + 14, y: cy - 5 });
      actions.push({ from: id + "1", to: id + "2", type: "passe", ...(i === 0 ? { label: "Passe" } : {}) });
      actions.push({ from: id + "2", to: id + "1", type: "passe", style: "dashed" });
      if (/pr[ée]cision|cible/.test(nom)) markers.push({ type: "cible", x: x + W - 5, y: cy });
    } else if (/mont[ée]e de balle|monter la balle|une touche|relais de passe|progress/.test(nom)) {
      // MONTÉE DE BALLE À 2 (en une touche) : deux joueurs PROGRESSENT vers l'avant (droite)
      // en se passant le ballon (la position évolue : chaque passe est un trait distinct).
      const y1 = Math.round(clampN(cy - 6, y + 5, y + H - 5)), y2 = Math.round(clampN(cy + 6, y + 5, y + H - 5));
      const xa = x + 9, xb = x + W - 12;
      players.push({ id: id + "1", role: "Joueur 1", team: "A", color: "#0072B2", x: xa, y: y1 });
      players.push({ id: id + "2", role: "Joueur 2", team: "A", color: "#0072B2", x: xa + 8, y: y2 });
      markers.push({ type: "ballon", x: xa + 3, y: y1 });
      actions.push({ from: id + "1", to: id + "2", type: "passe", ...(i === 0 ? { label: "Une touche" } : {}) });
      actions.push({ from: id + "1", tox: xb - 6, toy: y1, type: "course", style: "dashed" });
      actions.push({ from: id + "2", tox: xb, toy: y2, type: "course", style: "dashed" });
      actions.push({ from: id + "2", to: id + "1", type: "passe", style: "dashed" });
    } else if (/service|engagement|servir/.test(nom)) {
      // SERVICE SUR CIBLE : serveur + cible visée à distance.
      const tx = x + W - 8;
      players.push({ id, role: "Serveur", team: "A", color: "#0072B2", x: x + 8, y: cy });
      markers.push({ type: "ballon", x: x + 11, y: cy - 3 });
      markers.push({ type: "cible", x: tx, y: cy });
      actions.push({ from: id, tox: tx, toy: cy, type: "tir", style: "solid", ...(i === 0 ? { label: "Service" } : {}) });
    } else if (/motricit|[ée]chelle|cerceau|corde [àa] sauter|quadrup[ée]die|parcours/.test(nom)) {
      // MOTRICITÉ / PARCOURS : échelle de rythme (barreaux) ou parcours de plots à franchir.
      players.push({ id, role: "Élève", team: "A", color: "#0072B2", x: x + 6, y: cy });
      if (/[ée]chelle/.test(nom)) {
        const x0 = x + 12, x1 = x + W * 0.72, rungs = 5;
        for (let k = 0; k < rungs; k++) { const rx = Math.round(x0 + (x1 - x0) * k / (rungs - 1)); zones.push({ x: rx, y: cy - 5, w: 0, h: 10, style: "solid" }); }
        actions.push({ from: id, tox: Math.round(x + W - 6), toy: cy, type: "course", style: "dashed" });
      } else {
        const x0 = x + 14, x1 = x + W - 8, nO = 3; let prev = [x + 6, cy];
        for (let k = 0; k < nO; k++) {
          const px = Math.round(x0 + (x1 - x0) * k / (nO - 1)), py = Math.round(clampN(cy + (k % 2 ? 7 : -7), y + 5, y + H - 5));
          markers.push({ type: "plot", x: px, y: cy });
          actions.push({ fromx: prev[0], fromy: prev[1], tox: px, toy: py, type: "course", style: "dashed" });
          prev = [px, py];
        }
        actions.push({ fromx: prev[0], fromy: prev[1], tox: Math.round(x1), toy: cy, type: "course", style: "dashed" });
      }
    } else if (/taureau|rondo|\btoro\b|conservation|possession|\blosange\b|carr[ée] magique|conserver le ballon|garder le ballon/.test(nom)) {
      // ── ATELIER « TAUREAU » / RONDO : 4 joueurs en cercle font CIRCULER le ballon,
      // 1 défenseur (le « taureau ») au CENTRE tente d'intercepter. Mini-scène complète
      // par atelier (au lieu d'un placeholder « Groupe N + cône » qui ne montrait rien).
      const cx = Math.round(x + W / 2);
      const rx = Math.round(W * 0.3), ry = Math.max(7, Math.round(H / 2 - 6));
      const ring = [{ dx: 0, dy: -ry }, { dx: rx, dy: 0 }, { dx: 0, dy: ry }, { dx: -rx, dy: 0 }]
        .map(({ dx, dy }) => ({ x: Math.round(clampN(cx + dx, x + 3, x + W - 3)), y: Math.round(clampN(cy + dy, y + 4, y + H - 4)) }));
      const ids = ring.map((_, k) => id + "a" + (k + 1));
      ring.forEach((p, k) => players.push({ id: ids[k], role: "Joueur", team: "A", color: "#0072B2", x: p.x, y: p.y }));
      players.push({ id: id + "t", role: "Taureau", team: "B", color: "#D55E00", x: cx, y: cy });
      markers.push({ type: "ballon", x: Math.round(clampN(ring[0].x + 3, x + 3, x + W - 3)), y: ring[0].y });
      for (let k = 0; k < ring.length; k++)
        actions.push({ from: ids[k], to: ids[(k + 1) % ring.length], type: "passe", style: "solid", ...(i === 0 && k === 0 ? { label: "Fait circuler" } : {}) });
    } else {
      // Atelier générique : groupe + plot + sens de travail.
      players.push({ id, role: "Groupe " + num, team: "A", color: "#0072B2", x: x + 8, y: cy });
      markers.push({ type: "plot", x: x + 4, y: y + 5 });
      actions.push({ fromx: x + 15, fromy: cy, tox: x + W - 6, toy: cy, type: "course", style: "dashed" });
    }
  });
  // ROTATION : arcs de cercle COURTS sur le côté droit, d'un atelier au suivant
  // (1->2->3->1), au lieu de longues diagonales qui traversent tout le schéma.
  // Une rotation à UN SEUL atelier n'a rien à relier (la boucle a->a dessinait
  // une flèche NULLE sur elle-même).
  if (rotation && n < 2) rotation = false;
  if (rotation && cols === 1) {
    const at = zones.filter((z) => /^Atelier/.test(z.label || ""));
    const edge = at[0].x + W;                         // bord droit commun des ateliers
    for (let i = 0; i < n; i++) {
      const a = at[i], b = at[(i + 1) % n];
      const wrap = (i + 1) % n === 0;                 // dernier -> premier : arc plus ample
      actions.push({
        type: "rotation",
        fromx: edge, fromy: Math.round(a.y + a.h / 2),
        tox: edge, toy: Math.round(b.y + b.h / 2),
        bulge: wrap ? 12 : 7,
        ...(i === 0 ? { label: "Rotation des ateliers" } : {}),
      });
    }
  } else if (rotation) for (let i = 0; i < n; i++) {
    const at = zones.filter((z) => /^Atelier/.test(z.label || ""));
    const a = at[i], b = at[(i + 1) % n];
    actions.push({ fromx: a.x + W - 4, fromy: a.y + 2, tox: b.x + 4, toy: b.y + 2, type: "rotation", bulge: 6, ...(i === 0 ? { label: "Rotation des ateliers" } : {}) });
  }
  return {
    players, markers, zones, actions,
    steps: items.map((it, i) => `Atelier ${it.n || i + 1} : ${it.nom}`),
    analyse: { version: 1, famille: "atelier", rotation: !!rotation, ateliers: items.map((it) => ({ nom: it.nom.slice(0, 60) })) },
  };
}

/* Reconnaît la séquence d'éléments gymniques dans l'ordre du texte. */
const GYM_SKILLS = [
  [/course d'?[ée]lan/, "Course d'élan"],
  [/roulade avant/, "Roulade avant"],
  [/roulade arri[èe]re/, "Roulade arrière"],
  [/saut de mains/, "Saut de mains"],
  [/rondade/, "Rondade"],
  [/salto/, "Salto"],
  [/saut group[ée]/, "Saut groupé"],
  [/saut extension|saut tendu/, "Saut extension"],
  [/\broue\b/, "Roue"],
  [/souplesse avant/, "Souplesse avant"],
  [/souplesse arri[èe]re|pont/, "Souplesse arrière"],
  [/[ée]quilibre|arabesque/, "Équilibre (arabesque)"],
  [/\batr\b|appui tendu renvers/, "ATR"],
  [/demi.?tour|volte/, "Demi-tour sauté"],
  [/pas de liaison|liaison|quelques pas|chass[ée]/, "Pas de liaison"],
  [/r[ée]ception/, "Réception"],
];
/* Éléments dans l'ordre du texte, avec leur orientation explicite éventuelle
   (« vers la droite/gauche ») et le repère de demi-tour (qui inverse le sens de
   progression du parcours). */
function extractGymElements(text) {
  const t = text.toLowerCase();
  const found = [];
  for (const [re, label] of GYM_SKILLS) {
    const r = new RegExp(re.source, "g"); let m;
    while ((m = r.exec(t))) found.push({ pos: m.index, label });
  }
  found.sort((a, b) => a.pos - b.pos);
  const list = [];
  for (const f of found) if (!list.length || list[list.length - 1].label !== f.label) list.push(f);
  for (let i = 0; i < list.length; i++) {
    const seg = t.slice(list[i].pos, i + 1 < list.length ? list[i + 1].pos : t.length);
    list[i].lateral = /(?:vers|sur|c[ôo]t[ée]|[àa]) (?:la )?droite|\bdroite\b/.test(seg) ? "d"
      : (/(?:vers|sur|c[ôo]t[ée]|[àa]) (?:la )?gauche|\bgauche\b/.test(seg) ? "g" : null);
    list[i].demiTour = /demi.?tour|volte/.test(list[i].label.toLowerCase()) || /demi.?tour|volte|fait demi/.test(seg);
  }
  return list.length ? list : [{ label: "Élément", lateral: null, demiTour: false }];
}
/* Praticable : trace le PARCOURS du gymnaste. Chaque élément = une station
   numérotée portant son NOM, reliées par des flèches dans l'ordre. Les
   directions explicites sont honorées : « vers la droite » place la station
   plus bas (convention droite = bas), « vers la gauche » plus haut, et un
   « demi-tour » inverse le sens de progression horizontale. */
function gymFromElements(els, opts = {}) {
  const n = els.length;
  // AGRÈS LINÉAIRE (poutre, barre) : l'enchaînement se déroule SUR une ligne
  // droite (la poutre), de gauche à droite — pas sur les diagonales du sol.
  if (opts.linear) {
    const y0 = 50;
    const stationsL = els.map((e, i) => ({ x: Math.round(14 + 72 * (n > 1 ? i / (n - 1) : 0.5)), y: y0 }));
    const markersL = stationsL.map((s, i) => ({ type: "station", x: s.x, y: s.y, n: i + 1, label: els[i].label }));
    const actionsL = [];
    for (let k = 0; k < n - 1; k++) actionsL.push({
      fromx: stationsL[k].x, fromy: stationsL[k].y, tox: stationsL[k + 1].x, toy: stationsL[k + 1].y,
      type: "course", style: "dashed", label: els[k + 1].label,
    });
    // Le GYMNASTE au départ de l'enchaînement (une scène sans aucun acteur
    // laissait les stations « flotter » — convention produit : 1 Gymnaste).
    const gymnastL = { id: "GYM1", num: 1, role: "Gymnaste", team: "A", color: "#0072B2", x: Math.max(4, stationsL[0].x - 8), y: y0 };
    return { players: [gymnastL], markers: markersL, actions: actionsL, steps: els.map((e) => e.label) };
  }
  // Praticable vu de dessus : une chorégraphie au sol se construit sur les
  // DIAGONALES du praticable (passages d'angle à angle), pas en zigzag
  // horizontal. On répartit les stations le long d'UNE diagonale (séquence
  // courte) ou de DEUX (séquence longue : 1re diagonale, déplacement par le
  // haut vers l'angle opposé, 2e diagonale). Convention : x gauche→droite,
  // y haut(16)→bas(84). Départ et arrivée dans des angles.
  const A = { x: 16, y: 84 };   // angle de départ        (bas-gauche)
  const B = { x: 84, y: 16 };   // angle opposé           (haut-droite) — fin 1re diagonale
  const C = { x: 16, y: 16 };   // angle haut-gauche      (début 2e diagonale)
  const D = { x: 84, y: 84 };   // angle d'arrivée        (bas-droite) — fin 2e diagonale
  const lerp = (p, q, t) => ({ x: p.x + (q.x - p.x) * t, y: p.y + (q.y - p.y) * t });
  // (ox, oy) : léger décalage PERPENDICULAIRE à la diagonale, opposé d'une
  // diagonale à l'autre, pour que les deux passages ne se chevauchent pas au
  // centre du praticable (où les diagonales se croisent).
  const along = (p, q, k, out, ox = 0, oy = 0) => {
    for (let i = 0; i < k; i++) {
      const s = lerp(p, q, k > 1 ? i / (k - 1) : 0);
      out.push({ x: clampN(Math.round(s.x + ox), 8, 92), y: clampN(Math.round(s.y + oy), 12, 88) });
    }
  };

  const stations = [];
  if (n <= 5) {
    along(A, B, n, stations);                        // une seule diagonale d'angle à angle
  } else {
    const k = Math.ceil(n / 2), d = 5;
    along(A, B, k, stations, -d, -d);                // 1re diagonale (bas-gauche -> haut-droite)
    along(C, D, n - k, stations, d, -d);             // 2e diagonale  (haut-gauche -> bas-droite)
  }
  const markers = stations.map((s, i) => ({ type: "station", x: s.x, y: s.y, n: i + 1, label: els[i].label }));
  const actions = [];
  for (let k = 0; k < n - 1; k++) {
    actions.push({
      fromx: stations[k].x, fromy: stations[k].y, tox: stations[k + 1].x, toy: stations[k + 1].y,
      type: "course", style: "dashed", label: els[k + 1].label,
    });
  }
  // Le GYMNASTE à l'angle de départ (convention produit : 1 Gymnaste — une
  // scène sans aucun acteur laissait le parcours « flotter »).
  const st0 = stations[0] || { x: 16, y: 84 };
  const gymnast = { id: "GYM1", num: 1, role: "Gymnaste", team: "A", color: "#0072B2",
    x: Math.max(4, st0.x - 7), y: Math.min(94, st0.y + 5) };
  return { players: [gymnast], markers, actions, steps: els.map((e) => e.label) };
}
function buildGymnastics(text) {
  const els = extractGymElements(text);
  const linear = LINEAR_AGRES.test(String(text || "")) || VAULT_AGRES_RE.test(String(text || ""));
  const scene = dressGymAgres({ ...gymFromElements(els, { linear }) }, text, linear);
  return {
    ...scene,
    ...(linear ? { title: linearAgresTitle(text) } : {}),
    // Synthèse pour le panneau « Ce que j'ai compris » (même contrat que l'IA).
    analyse: {
      version: 1, famille: "parcours",
      elements: els.map((e) => ({ nom: e.label, direction: e.lateral === "g" ? "gauche" : e.lateral === "d" ? "droite" : null })),
    },
  };
}

/* Adaptateur V2 — famille PARCOURS : éléments gymniques STRUCTURÉS (analyse
   IA validée) -> même tracé de parcours que le texte libre. */
/* Agrès LINÉAIRES (l'enchaînement se déroule sur une ligne droite, gauche→droite),
   par opposition au praticable (sol) tracé sur les diagonales. */
const LINEAR_AGRES = /poutre|barre\s*fixe|barres?\s*asym|barres?\s*parall[èe]l|barre\s*d['’]?[ée]quilibre/i;
/* Agrès de FRANCHISSEMENT : le parcours est LINÉAIRE lui aussi (élan en ligne
   droite -> impulsion -> appui -> réception), jamais tracé sur les diagonales
   du praticable comme un enchaînement au sol. */
const VAULT_AGRES_RE = /cheval|table\s*de\s*saut|mini.?tramp|trampoline|tremplin/i;
function linearAgresTitle(text) {
  const t = String(text || "");
  if (/poutre/i.test(t)) return "Enchaînement à la poutre";
  if (/barres?\s*asym/i.test(t)) return "Enchaînement aux barres asymétriques";
  if (/barres?\s*parall[èe]l/i.test(t)) return "Enchaînement aux barres parallèles";
  if (/barre\s*fixe/i.test(t)) return "Enchaînement à la barre fixe";
  if (/cheval|table\s*de\s*saut/i.test(t)) return "Saut de cheval";
  if (/mini.?tramp|trampoline|tremplin/i.test(t)) return "Saut au mini-trampoline";
  return "Enchaînement sur agrès";
}
/* HABILLAGE AGRÈS d'un parcours gym (partagé local/analyse) : support horizontal
   sous les stations (poutre/barres), ou agrès de FRANCHISSEMENT posé à la
   station d'APPUI — tremplin devant, TAPIS de réception après, PAREUR(S) à côté
   quand le texte les décrit. Avant : le cheval était posé sur la station de
   RÉCEPTION, sans tremplin ni tapis ni pareur. */
function dressGymAgres(scene, text, linear) {
  const t = String(text || "");
  const ag = GYM_AGRES.find(([re, k]) => k !== "tapis" && re.test(c0(t)));
  if (!ag) return scene;
  const markers = scene.markers || (scene.markers = []);
  const st = markers.filter((m) => m.type === "station");
  if (ag[1] === "cheval" || ag[1] === "trampoline") {
    // Station du FRANCHISSEMENT : l'APPUI sur l'agrès en priorité (l'« impulsion
    // sur le tremplin » est la station PRÉCÉDENTE), sinon l'avant-dernière.
    const hit = st.find((m) => /appui|franchi/i.test(m.label || ""))
      || st.find((m) => /impulsion|\bsaut\b/i.test(m.label || ""))
      || st[Math.max(0, st.length - 2)] || { x: 62, y: 50 };
    markers.unshift({ type: "agres", kind: ag[1], x: hit.x, y: hit.y, w: 14, h: 12, label: ag[2] });
    if (ag[1] === "cheval" && /tremplin/i.test(t))
      markers.unshift({ type: "agres", kind: "tremplin", x: Math.max(6, hit.x - 11), y: hit.y, w: 6, h: 8, label: "Tremplin" });
    // Tapis de réception là où l'on RETOMBE : sous la dernière station (la
    // réception) quand elle est après l'agrès, sinon juste derrière l'agrès.
    if (/r[ée]ception|tapis|matelas/i.test(t)) {
      const land = st.length && st[st.length - 1].x > hit.x + 4 ? st[st.length - 1] : null;
      const zones = scene.zones || (scene.zones = []);
      zones.push({ x: land ? Math.min(80, land.x - 9) : Math.min(80, hit.x + 9), y: (land ? land.y : hit.y) - 9, w: 18, h: 18, fill: "tapis", style: "solid", label: "Tapis" });
    }
    if (/pareurs?/i.test(t)) {
      const two = /chaque c[ôo]t[ée]|(?:2|deux) pareurs/i.test(t);
      const players = scene.players || (scene.players = []);
      players.push({ id: "PAR1", role: "Pareur", team: "OBS", color: "#F2C200", x: Math.min(92, hit.x + 7), y: Math.min(90, hit.y + 13) });
      if (two) players.push({ id: "PAR2", role: "Pareur", team: "OBS", color: "#F2C200", x: Math.min(92, hit.x + 7), y: Math.max(10, hit.y - 13) });
    }
    // Élan synthétisé si le parcours n'a AUCUNE flèche (élément unique, ex.
    // « saut de lune ») : le gymnaste est reculé et court jusqu'à l'agrès.
    if (!(scene.actions || []).length && (scene.players || []).length) {
      const g = scene.players[0];
      g.x = Math.max(6, hit.x - 32);
      (scene.actions = scene.actions || []).push({ from: g.id, tox: Math.max(8, hit.x - 9), toy: hit.y, type: "course", label: "Course d'élan" });
    }
    return scene;
  }
  /* ⚠️ `linear` est déduit du TEXTE, pas de l'agrès DESSINÉ. Quand le texte
     nomme plusieurs agrès (« tapis, plan incliné … espace équilibre avec
     poutre »), `ag` retient le PREMIER — ici le plan incliné — et il héritait
     de la longueur d'une poutre : une rampe de 80 unités en travers de toute
     la scène (vu au rendu sur PROD13, et c'est l'observation de l'enseignant :
     « le matériel n'est pas forcément à l'échelle »).
     Un agrès ne s'étire que s'il est LUI-MÊME linéaire. */
  const LINEAIRES_KIND = new Set(["poutre", "barre_fixe", "barres_asym", "barres_par", "banc", "echelle"]);
  if (linear && LINEAIRES_KIND.has(ag[1])) {
    // poutre / barre(s) : support HORIZONTAL sous la ligne des stations (y=50).
    markers.unshift({ type: "agres", kind: ag[1], x: 50, y: 50, w: 80, h: ag[1] === "poutre" ? 5 : 18, label: ag[2] });
    // Tapis de réception à la SORTIE de l'agrès (si décrite).
    if (/sortie|r[ée]ception/i.test(t) && st.length) {
      const last = st[st.length - 1];
      const zones = scene.zones || (scene.zones = []);
      zones.push({ x: Math.min(82, last.x + 4), y: 56, w: 14, h: 16, fill: "tapis", style: "solid", label: "Tapis" });
    }
  } else {
    // Autre agrès nommé hors franchissement : au point de réalisation (dernière station).
    const last = st[st.length - 1] || { x: 70, y: 40 };
    markers.unshift({ type: "agres", kind: ag[1], x: last.x, y: last.y, w: 16, h: 14, label: ag[2] });
  }
  return scene;
}
export function gymFromAnalysis(elements, text = "") {
  const els = elements.map((e) => ({
    label: e.nom,
    lateral: e.direction === "gauche" ? "g" : e.direction === "droite" ? "d" : null,
    demiTour: /demi.?tour|volte/i.test(e.nom),
  }));
  const linear = LINEAR_AGRES.test(String(text || "")) || VAULT_AGRES_RE.test(String(text || ""));
  const scene = { ...gymFromElements(els, { linear }), title: linear ? linearAgresTitle(text) : "Enchaînement au sol" };
  return dressGymAgres(scene, text, linear);
}

/* FIGURE D'ACROSPORT (statique) : porteurs en base, voltigeur(s) au-dessus,
   pareur(s) à côté — sur un tapis, vue de dessus. Effectifs lus de la SITUATION
   NOMINALE (« deux porteurs », « pyramide à trois niveaux », « groupes de 6 » ;
   une VARIABLE « ajouter un voltigeur » ne gonfle rien). Une figure se TIENT
   (montage / tenue / démontage) : elle ne parcourt pas les diagonales du
   praticable comme un enchaînement — c'était le contresens à corriger. */
export function buildAcrosportFigure(text) {
  const t = c0(coreSituationText(text || ""));
  const W = { un: 1, une: 1, deux: 2, trois: 3, quatre: 4, cinq: 5, six: 6 };
  const rd = (m, d) => m ? (W[m[1]] || parseInt(m[1], 10) || d) : d;
  const duo = /\bduos?\b|\bbin[ôo]mes?\b|par (?:deux|2)\b/.test(t);
  const nP = clampN(rd(t.match(/(\d+|un|une|deux|trois|quatre)\s*porteurs?/), duo ? 1 : 2), 1, 4);
  const nV = clampN(rd(t.match(/(\d+|un|une|deux|trois)\s*voltigeurs?/), 1), 1, 3);
  // La parade est OBLIGATOIRE en acrosport scolaire : toujours au moins un pareur.
  const nPar = clampN(/chaque c[ôo]t[ée]/.test(t) ? 2 : rd(t.match(/(\d+|un|une|deux)\s*pareurs?/), 1), 1, 2);
  // « Pyramide à trois niveaux » / groupe de 6+ : base 3, milieu 2, sommet 1.
  const lv = t.match(/(\d+|deux|trois)\s*(?:niveaux|[ée]tages)/);
  const grp = t.match(/groupes? de (\d+|quatre|cinq|six|sept|huit)/);
  let levels = [nP, nV];
  if ((lv && rd(lv, 2) >= 3) || (!lv && grp && rd(grp, 4) >= 6)) levels = [3, 2, 1];
  const players = [], actions = [], zones = [];
  zones.push({ x: 28, y: 30, w: 44, h: 40, fill: "tapis", style: "solid", label: "Tapis" });
  const rowY = [60, 48, 38];   // base / 2e niveau / sommet (le « haut » de la figure vers le haut)
  let id = 0, firstV = null;
  levels.forEach((k, li) => {
    for (let i = 0; i < k; i++) {
      const p = { id: "F" + (++id), num: id, role: li === 0 ? "Porteur" : "Voltigeur",
        team: "A", color: "#0072B2", x: Math.round(50 + (i - (k - 1) / 2) * 10), y: rowY[Math.min(li, 2)] };
      players.push(p);
      if (li > 0 && !firstV) firstV = p;
    }
  });
  for (let i = 0; i < nPar; i++)
    players.push({ id: "PAR" + (i + 1), role: "Pareur", team: "OBS", color: "#F2C200", x: 78, y: nPar === 1 ? 50 : 42 + i * 16 });
  // Flèche de MONTAGE : le (premier) voltigeur monte depuis le bord du tapis.
  if (firstV) actions.push({ fromx: 36, fromy: 74, tox: firstV.x - 2, toy: firstV.y + 3, type: "course", style: "dashed", label: "Montage" });
  const hold = t.match(/ten(?:ue|ir|iennent)[^.\d]{0,24}(\d+)\s*s/);
  return {
    players, actions, zones,
    steps: ["Montage de la figure" + (levels.length > 2 ? " (base, puis étages)" : ""),
      `Tenue de la figure${hold ? ` ${hold[1]} secondes` : ""} sans déséquilibre`,
      "Démontage maîtrisé, le pareur sécurise"],
  };
}

/* POSTE HANDBALL d'après la position, pour une ATTAQUE PLACÉE à petit effectif
   (2-3 joueurs, là où le gabarit à 6 postes ne s'applique pas) : on nomme par
   poste EPS (Demi-centre / Arrière / Ailier / Pivot) plutôt que par fonction
   (Porteur / Finisseur…). Convention du moteur : but à droite, côté gauche = y
   faible. Le pivot (écran / servi « au pivot ») prime ; sinon largeur = ailier,
   axe = demi-centre (s'il a le ballon / organise) ou arrière. */
function handballPlacedRole(y, { carrier = false, pivot = false } = {}) {
  if (pivot) return "Pivot";
  if (y <= 40) return "Ailier gauche";
  if (y >= 60) return "Ailier droit";
  return carrier ? "Demi-centre" : "Arrière";
}

/* Lignes/tiers d'un SYSTÈME défensif lus dans le texte : suite de 2 à 4 nombres
   dont la SOMME == nb de défenseurs annoncés. Générique tous sports : football
   « 5-4-1 / 4-4-2 / 3-5-2 », handball « 6-0 / 5-1 / 0-6 », basket « 2-3 / 1-3-1 ».
   Un tier à 0 est CONSERVÉ (il porte la profondeur : « 6-0 » ≠ « 0-6 »). */
export function detectDefenseLines(text, nD) {
  const m = String(text || "").match(/(\d)\s*[-,\s]\s*(\d)(?:\s*[-,\s]\s*(\d))?(?:\s*[-,\s]\s*(\d))?/);
  if (!m) return null;
  const lines = m.slice(1).filter((x) => x != null).map(Number);
  if (lines.length >= 2 && lines.every((n) => n >= 0 && n <= 8) && lines.reduce((a, b) => a + b, 0) === nD) return lines;
  return null;
}

/* GÉOMÉTRIE PARTAGÉE d'un système défensif (famille « defense » ET attaque
   d'invasion CONTRE un système). tiers = [n0, n1, …] du plus proche du but (à
   DROITE) vers l'avant. Géométrie PROPRE À CHAQUE SPORT :
     • handball : arc BOMBÉ étagé (différencie 6-0 / 5-1 / 0-6) ;
     • basket   : lignes de ZONE étalées vers le panier (2-3, 1-3-1) ;
     • foot/futsal : LIGNES du bloc (hauteur bas/médian/haut lue dans `hint`).
   Renvoie { players, tierX }. Rôles : « Défenseur N » par défaut ; par LIGNE
   (Défenseur/Milieu/Attaquant) si footRoles (football). */
function defensiveSystemPlayers(tiers, sport, { hint = "", footRoles = false } = {}) {
  const isBasket = sport === "basketball" || sport === "basket3x3";
  const isFoot = sport === "football" || sport === "futsal";
  const T = Array.isArray(tiers) ? tiers.slice() : [];
  const tot = T.reduce((a, b) => a + b, 0);
  const hauteur = /haut/.test(hint) ? "haut" : /m[ée]dian|moyen|medium/.test(hint) ? "médian" : "bas";
  const win = hauteur === "haut" ? [30, 64] : hauteur === "médian" ? [45, 78] : [60, 88];
  const nTiers = T.length;
  // BASKET : une ZONE protège l'AXE — rangée avant à la tête de raquette (x≈72),
  // resserrée (2-3 : les deux « hauts » aux coudes, pas aux ailes), largeur
  // croissante vers la ligne de fond (raquette + corners).
  const xBase = isFoot ? win[1] : isBasket ? 72 : 80;   // tier 0 (près du but pour hand/foot)
  const xFar = isFoot ? win[0] : isBasket ? 88 : 54;
  const xAt = (i) => nTiers > 1 ? Math.round(xBase + (xFar - xBase) * i / (nTiers - 1)) : (isBasket ? 70 : isFoot ? Math.round((win[0] + win[1]) / 2) : 78);
  const bow = (isBasket || isFoot) ? 0 : 6;             // handball : arc ; basket/foot : lignes plates
  const FOOT_ROLES = ({ 1: ["Défenseur"], 2: ["Défenseur", "Milieu"], 3: ["Défenseur", "Milieu", "Attaquant"], 4: ["Défenseur", "Milieu déf.", "Milieu off.", "Attaquant"] })[nTiers] || T.map(() => "Défenseur");
  const players = [], tierX = []; let di = 0;
  // Le FUTSAL (4 joueurs, terrain étroit) ferme l'AXE comme la zone basket :
  // un losange 1-2-1 met ses milieux aux intervalles, pas aux ailes. Le
  // football 11 garde ses lignes pleine largeur.
  const tightAxis = isBasket || sport === "futsal";
  T.forEach((count, ti) => {
    tierX.push(xAt(ti));
    const last = ti === nTiers - 1;
    const half = !tightAxis ? 30 : count <= 1 ? 0 : count === 2 ? (last ? 15 : 12) : count === 3 ? (last ? 30 : 22) : 30;
    for (let j = 0; j < count; j++) {
      const frac = count > 1 ? j / (count - 1) : 0.5;
      const y = Math.round(clampN(tightAxis ? 50 + (frac - 0.5) * 2 * half : 20 + frac * 60, 14, 86));
      const x = Math.round(clampN(xAt(ti) - Math.sin(frac * Math.PI) * bow, 10, 92));
      di++;
      const role = (footRoles && isFoot) ? FOOT_ROLES[ti] + (count > 1 ? " " + (j + 1) : "") : "Défenseur" + (tot > 1 ? " " + di : "");
      players.push({ id: "D" + di, num: di, role, team: "B", color: "#D55E00", x, y });
    }
  });
  return { players, tierX };
}

/* ATTAQUE CONTRE UN BLOC DÉFENSIF (football/futsal) : un effectif défensif COMPLET
   (≥ 6) organisé en SYSTÈME (5-4-1, 5-3-2, bloc à 2 lignes…) qu'un petit groupe
   attaque. Le moteur générique « surnombre » empile les défenseurs en colonne et
   réduit l'attaque à un joueur seul (l'IA renvoie souvent 1 attaquant + une
   auto-passe). On rend ici la scène CANONIQUE et lisible : le bloc placé en LIGNES,
   et une réception ENTRE LES LIGNES (serveur -> receveur dans l'intervalle ->
   contrôle vers le but) — le serveur est synthétisé si l'IA n'a fourni qu'un seul
   attaquant. But attaqué à DROITE. */
function buildAttackVsBlock(spec, sport, lines) {
  const nD = spec.defenseurs;
  // Lignes par défaut si non détectées : bloc à 2 lignes (déf + milieu), sinon 1.
  const L = (lines && lines.reduce((a, b) => a + b, 0) === nD) ? lines
    : nD >= 4 ? [Math.ceil(nD / 2), Math.floor(nD / 2)]
    : [nD];
  const players = [], pos = {};
  // DÉFENSE : géométrie partagée (lignes du bloc football, hauteur lue dans le texte),
  // rôles par ligne (Défenseur / Milieu / Attaquant).
  const dfn = defensiveSystemPlayers(L, sport, { hint: spec.text, footRoles: true });
  dfn.players.forEach((d) => { players.push(d); pos[d.id] = { x: d.x, y: d.y }; });
  players.push({ id: "G", role: "Gardien", team: "k", color: "#F2C200", x: 94, y: 50 });
  // INTERVALLE entre la ligne de défense (tierX[0], près du but) et la suivante.
  const backX = dfn.tierX[0], midX = dfn.tierX[1] != null ? dfn.tierX[1] : backX - 14;
  const gapX = Math.round((backX + midX) / 2), recvY = 50;
  // ATTAQUE : un RÉCEPTEUR (A1) reçoit dans l'intervalle, un SERVEUR (A2) lui donne
  // depuis l'arrière (synthétisé si l'IA n'a donné qu'un attaquant).
  const recv = { id: "A1", num: 1, role: "Attaquant", team: "A", color: "#0072B2", x: Math.round(clampN(midX - 6, 24, 70)), y: recvY + 12 };
  const server = { id: "A2", num: 2, role: "Passeur", team: "A", color: "#0072B2", x: Math.round(clampN(midX - 22, 18, 52)), y: recvY - 12 };
  players.push(recv, server); pos.A1 = { x: recv.x, y: recv.y }; pos.A2 = { x: server.x, y: server.y };
  const actions = [
    { from: "A1", tox: gapX, toy: recvY, type: "course", order: 1, label: "Appel entre les lignes" },
    { from: "A2", to: "A1", type: "passe", order: 2, label: "Passe entre les lignes" },
    { from: "A1", tox: Math.round(clampN(gapX + 9, 6, 90)), toy: Math.round(clampN(recvY - 7, 8, 92)), type: "dribble", order: 3, label: "Contrôle orienté vers le but" },
  ];
  // CONTINUATION DÉCRITE — « le receveur se retourne et lance la profondeur »
  // puis tir : un 3e attaquant est lancé DERRIÈRE la défense et conclut
  // (avant : le gabarit s'arrêtait au contrôle et perdait la finition).
  if ((spec.attaquants || 0) >= 3 && Array.isArray(spec.phase)
    && spec.phase.some((s) => s.action === "tir")
    && /profondeur|derri[èe]re la d[ée]fense|dans le dos/i.test(coreSituationText(spec.text || ""))) {
    const runY = recvY + 26;
    players.push({ id: "A3", num: 3, role: "Attaquant lancé", team: "A", color: "#0072B2", x: Math.round(clampN(midX - 12, 20, 60)), y: runY });
    pos.A3 = { x: midX - 12, y: runY };
    actions.push({ from: "A3", tox: Math.round(clampN(backX + 8, 60, 90)), toy: runY, type: "course", order: 4, label: "Lancé en profondeur" });
    actions.push({ from: "A1", tox: Math.round(clampN(backX + 8, 60, 90)), toy: runY, type: "passe", order: 5, label: "Passe en profondeur" });
    actions.push({ from: "A3", fromx: Math.round(clampN(backX + 8, 60, 90)), fromy: runY, tox: 97, toy: 55, type: "tir", order: 6, label: "Conclusion" });
  }
  return {
    players,
    markers: [{ type: "ballon", x: Math.round(clampN(server.x + 4, 4, 96)), y: server.y }],
    actions,
    steps: actions.map((a) => a.label),
  };
}

/* DIMENSIONS D'UNE AIRE lues dans le texte — partagé entre l'overlay de
   délimitation (analysis.js) et les gabarits qui matérialisent leur propre
   aire (toro/rondo). Formes : « 10 m sur 10 » / « 10 mètres sur 10 m » /
   « 14 x 6 m » (l'unité peut n'apparaître qu'UNE fois, sur l'un des deux
   nombres), « N m de côté » (carré), longueur/largeur nommées. */
export function parseCourtDims(t) {
  const norm = (n) => `${n} m`;
  // « A m sur B [m] » (unité sur le 1er nombre) ou « A sur B m » (sur le 2e).
  let m = t.match(/(\d{1,3})\s*m(?:[èe]tres?)?\s*(?:sur|x|×|par)\s*(\d{1,3})(?:\s*m(?:[èe]tres?)?)?\b/i)
    || t.match(/(\d{1,3})\s*m?\s*(?:sur|x|×|par)\s*(\d{1,3})\s*m(?:[èe]tres?)?\b/i);
  if (m) return { len: norm(m[1]), wid: norm(m[2]) };
  // Aire CARRÉE « de N m de côté » (carré, losange…).
  m = t.match(/(\d{1,3})\s*m(?:[èe]tres?)?\s+de\s+c[ôo]t[ée]/i);
  if (m) return { len: norm(m[1]), wid: norm(m[1]) };
  const len = t.match(/longueur\D{0,8}(\d{1,3})\s*m|(\d{1,3})\s*m[èe]?[a-z]*\s*(?:de\s+)?long/i);
  const wid = t.match(/largeur\D{0,8}(\d{1,3})\s*m|(\d{1,3})\s*m[èe]?[a-z]*\s*(?:de\s+)?large/i);
  const pick = (mm) => mm ? norm(mm[1] || mm[2]) : null;
  return { len: pick(len), wid: pick(wid) };
}

/* Couleur des PLOTS annoncée dans le texte (« 4 plots rouges ») -> couleur du
   marqueur (le rendu accepte m.color ; orange par défaut sinon). */
const PLOT_COLORS = { rouge: "#DC2626", jaune: "#F2C200", bleu: "#0072B2", vert: "#2F9E44", blanc: "#F8FAFC", orange: "#EA7317", noir: "#0F172A" };
export function plotColorFromText(text) {
  const m = String(text || "").match(/(?:plots?|c[ôo]nes?|coupelles?)\s+(rouge|jaune|bleu|vert|blanc|orange|noir)e?s?\b/i);
  return m ? PLOT_COLORS[m[1].toLowerCase()] : null;
}

/* CÔTÉ EXPLICITE DES ACTEURS (invasion) — « le joueur B est sur l'aile
   droite », « C à l'entrée de la surface », note « appel côté gauche » : le
   texte fixe la LARGEUR d'un acteur, que les moteurs de placement doivent
   respecter (l'alternance haut/bas par défaut posait l'« ailier droit » à
   gauche). Sources, par priorité : la SITUATION NOMINALE (lettres A/B/C ou
   « joueur N » -> A1/A2/A3…), puis les notes de la phase (côté du RECEVEUR
   pour une passe, de l'ACTEUR sinon). Convention rendu : but à DROITE, côté
   gauche = haut (y faible), côté droit = bas (y grand).
   Renvoie { A1: "droite"|"gauche"|"centre", … } (vide si rien d'explicite). */
function segmentSide(seg) {
  // Une DIRECTION de mouvement (« vers la droite », « drible à droite ») n'est
  // pas une POSITION : « à droite » exige une frontière de mot devant le « à »
  // (sinon « la droite » matchait), et « vers la/le … » est explicitement exclu.
  if (/vers\s+(?:la\s+|le\s+)?(?:droite|gauche)/i.test(seg)) return null;
  if (/(?:aile|c[ôo]t[ée]|couloir|flanc)\s+droit(?:e)?\b|(?:^|[\s,;:('«])[àa]\s+droite\b|sur\s+la\s+droite\b/i.test(seg)) return "droite";
  if (/(?:aile|c[ôo]t[ée]|couloir|flanc)\s+gauche\b|(?:^|[\s,;:('«])[àa]\s+gauche\b|sur\s+la\s+gauche\b/i.test(seg)) return "gauche";
  if (/milieu\s+du\s+terrain|dans\s+l['’]axe|axe\s+central|plein\s+axe|entr[ée]e\s+de\s+la\s+surface|t[êe]te\s+de\s+raquette/i.test(seg)) return "centre";
  return null;
}
/* Côté d'une NOTE de phase : plus STRICT que le texte — seuls aile/couloir/
   flanc portent une position (« appel couloir droit ») ; « côté droit » y est
   presque toujours le côté d'un GESTE (écran posé côté droit, sortie côté
   droit) et « vers la droite » une direction. Les notes d'écran sont ignorées. */
function noteSide(note) {
  const n = String(note || "");
  if (/[ée]cran|\bbloc\b|\bscreen\b|\bpick\b/i.test(n)) return null;
  if (/(?:aile|couloir|flanc)\s+droit(?:e)?\b/i.test(n)) return "droite";
  if (/(?:aile|couloir|flanc)\s+gauche\b/i.test(n)) return "gauche";
  return null;
}
export function invasionSideHints(text, phase, nA) {
  const hints = {};
  const put = (id, side) => { if (id && side && !hints[id]) hints[id] = side; };
  // 1) Texte nominal, PROPOSITION par proposition (la virgule sépare aussi :
  //    « A au milieu, B sur l'aile droite, C à l'entrée de la surface » décrit
  //    trois placements distincts) : un côté + un acteur nommé par sa lettre
  //    (A=A1, B=A2…) ou « joueur N ». À plusieurs lettres dans la même
  //    proposition (« A passe à B sur l'aile droite »), le côté qualifie la
  //    lettre la plus PROCHE avant lui (ici B), pas la première venue.
  for (const seg of String(coreSituationText(text) || "").split(/[.;\n!?,]/)) {
    // Un ÉCRAN « posé côté droit » décrit le côté du bloc, pas la position du
    // poseur : les propositions d'écran ne donnent aucun hint.
    if (/[ée]cran|\bbloc\b|\bscreen\b/i.test(seg)) continue;
    const side = segmentSide(seg);
    if (!side) continue;
    const sideAt = seg.search(/aile|c[ôo]t[ée]|couloir|flanc|droite\b|gauche\b|milieu|axe|surface|raquette/i);
    const found = [];
    const scan = (re, toId) => { let m; while ((m = re.exec(seg))) found.push({ pos: m.index, id: toId(m) }); };
    scan(/\bjoueur(?:se)?\s+(\d{1,2})\b/gi, (m) => "A" + parseInt(m[1], 10));
    scan(/\b(?:joueur(?:se)?|[ée]l[èe]ve|attaquant|partenaire)\s+([A-K])\b/g, (m) => "A" + (m[1].charCodeAt(0) - 64));
    if (!found.length) scan(/(?:^|[\s,;:«"'(])([A-K])(?=$|[\s,;:.)»"'!?])/g, (m) => "A" + (m[1].charCodeAt(0) - 64));
    const before = found.filter((f) => f.pos <= sideAt), after = found.filter((f) => f.pos > sideAt);
    const best = before.length ? before[before.length - 1] : after[0];
    if (best) put(best.id, side);
  }
  // 2) Notes de la phase : « appel aile gauche », « passe couloir droit » —
  //    aile/couloir/flanc uniquement (cf. noteSide : « côté droit » et « vers
  //    la droite » y sont des gestes/directions, pas des positions).
  for (const st of (Array.isArray(phase) ? phase : [])) {
    const side = st && st.action !== "ecran" ? noteSide(st.note) : null;
    if (side) put(st.action === "passe" || st.action === "centre" ? st.cible : st.acteur, side);
  }
  for (const k of Object.keys(hints)) { const m = /^A(\d+)$/.exec(k); if (!m || +m[1] < 1 || +m[1] > nA) delete hints[k]; }
  return hints;
}

/* MARQUAGES EXPLICITES du texte — « le joueur B est sur l'aile droite, défendu
   de près par le joueur rouge 1 » : Dk doit COLLER cet attaquant (goalside),
   pas rejoindre l'étalement générique. Phrase entière (les virgules ne coupent
   pas : la relative « défendu par… » suit l'attaquant qu'elle qualifie).
   Renvoie { D1: { id: "A2", tight: true }, … }. */
export function invasionMarkHints(text, nA, nD) {
  const marks = {};
  for (const sent of String(coreSituationText(text) || "").split(/[.;\n!?]/)) {
    const dm = sent.match(/d[ée]fendue?s?\s+(?:de\s+pr[èe]s\s+)?par\s+.{0,24}?(?:rouge|d[ée]fenseur)\s*n?[°o]?\s*(\d)/i);
    if (!dm) continue;
    const di = parseInt(dm[1], 10);
    const before = sent.slice(0, sent.search(/d[ée]fendu/i));
    let ai = null;
    let m = before.match(/\bjoueur(?:se)?\s+(?:bleue?\s+)?(\d{1,2})\b/i);
    if (m) ai = parseInt(m[1], 10);
    if (ai == null) {
      m = before.match(/(?:^|[\s,;:«"'(])([A-FH-K])(?=$|[\s,;:.)»"'!?])/);
      if (m) { const c = m[1].toUpperCase().charCodeAt(0) - 64; ai = c > 7 ? c - 1 : c; }
    }
    if (ai != null && ai >= 1 && ai <= nA && di >= 1 && di <= nD) marks["D" + di] = { id: "A" + ai, tight: true };
  }
  return marks;
}

/* Adaptateur V2 — famille INVASION : phase à effectif réduit STRUCTURÉE
   (analyse IA validée). Placement aligné sur buildTeamAttack : attaquants
   étalés en largeur depuis la zone de départ, défenseurs interposés vers la
   cible (à droite), gardien optionnel devant son but. La chaîne `phase` est
   déroulée acteur par acteur, les positions évoluant au fil des courses. */
export function invasionFromAnalysis(spec, sport) {
  // RUGBY À XV COMPLET : 15 contre 15 ne se dessine pas (scène rejetée) — on
  // ne montre que la LIGNE concernée (8 max par camp) et les acteurs NOMMÉS
  // par leur numéro (« le 9 », « le 10 », « le 15 » -> A9, A10, A15) sont
  // REMAPPÉS en tête (A1..An) dans l'ordre croissant, phase comprise.
  if (sport === "rugby" && (spec.attaquants > 8 || spec.defenseurs > 8)) {
    const refd = [...new Set([...(spec.phase || []).map((s) => s.acteur), ...(spec.phase || []).map((s) => s.cible),
      ...((spec.acteurs || []).map((a) => a.id))].filter((id) => /^A\d+$/.test(String(id || ""))))]
      .sort((a, b) => +a.slice(1) - +b.slice(1));
    const map = {};
    refd.forEach((id, i) => { map[id] = "A" + (i + 1); });
    const re = (id) => (map[String(id)] || id);
    spec = { ...spec,
      attaquants: Math.min(8, Math.max(spec.attaquants > 8 ? refd.length + 2 : spec.attaquants, refd.length)),
      defenseurs: Math.min(8, spec.defenseurs),
      phase: (spec.phase || []).map((s) => ({ ...s, acteur: re(s.acteur), cible: re(s.cible) })),
      acteurs: (spec.acteurs || []).map((a) => ({ ...a, id: re(a.id) })),
    };
  }
  const nA = spec.attaquants;
  let nD = spec.defenseurs;
  // ZONE BASKET CHIFFRÉE (« zone 2-3 », « défense 1-3-1 ») : l'effectif défensif
  // est la SOMME des chiffres du système — l'IA le rabaisse parfois (1-3-1
  // déclaré à 4 défenseurs) et la zone perdait un joueur ET sa géométrie.
  if (["basketball", "basket3x3"].includes(sport)) {
    const zm = String(spec.text || "").match(/(?:zone|d[ée]fense)[^.;:]{0,16}?(\d(?:\s*-\s*\d){1,3})/i);
    if (zm) {
      const sum = zm[1].split(/\s*-\s*/).map(Number).reduce((a, b) => a + b, 0);
      if (sum >= 3 && sum <= 6 && nD > 0 && nD < sum) nD = sum;
    }
  }
  // Système défensif chiffré nommé par le texte (« zone 2-3 », « 1-3-1 ») —
  // partagé par le marquage, le rideau de chaîne et le staging système.
  const namedSystem = detectDefenseLines(spec.text, nD);
  // ATTAQUE CONTRE UN BLOC : football/futsal, défense COMPLÈTE (≥6) face à un petit
  // groupe (≤3). Le moteur générique empilerait les défenseurs en colonne ; on rend
  // le système en LIGNES + une réception entre les lignes (cf. buildAttackVsBlock).
  if (["football", "futsal"].includes(sport) && nD >= 6 && nA <= 3)
    return buildAttackVsBlock(spec, sport, spec.defenseLines || detectDefenseLines(spec.text, nD));
  // Sports à but AVEC gardien (≠ basket) : si la phase va jusqu'au TIR, on place
  // un gardien même si l'IA ne l'a pas déclaré — un tir au but sans gardien
  // n'a pas de sens (et la zone des 6 m en handball lui est réservée).
  // « SANS GARDIEN » / petits buts / but vide : le texte prime — pas de gardien
  // fantôme même si la phase conclut par un tir.
  const noGk = /sans gardien|pas de gardien|but vide|petits? buts?|mini.?buts?/i.test(String(spec.text || ""));
  const withGk = !noGk && (!!spec.gardien || (GK_SPORTS.has(sport) && spec.phase.some((s) => s.action === "tir")));
  // ATTAQUE PLACÉE (sport à but, défense EN PLACE, phase qui conclut par un TIR) :
  // un écran se pose sur un défenseur posté DEVANT sa zone, puis on tire — toute
  // la phase converge donc PRÈS du but attaqué. Elle ne peut pas se jouer dans le
  // camp PROPRE (cela placerait les attaquants dans la zone de leur PROPRE gardien,
  // incohérent et illégal). Le « depart » de l'IA est peu fiable ici (elle confond
  // le « point de départ pédagogique » avec « démarre dans son camp ») : pour une
  // attaque placée annoncée « camp_propre », on relève le staging dans le camp
  // adverse afin que l'écran et le tir restent cohérents.
  // TYPE DE PHASE (déclaré par l'IA) : "attaque_placee" | "contre_attaque" | "transition".
  // Il prime sur l'heuristique ; à défaut (analyse ancienne), on retombe sur « défense en
  // place + tir = attaque placée ». Une CONTRE-ATTAQUE / TRANSITION explicite n'est JAMAIS
  // stagée comme une attaque placée (elle traverse le terrain).
  const phaseType = typeof spec.type === "string" ? spec.type : null;
  const setAttack = phaseType === "attaque_placee"
    || (phaseType == null && GK_SPORTS.has(sport) && spec.defenseurs >= 1 && spec.phase.some((s) => s.action === "tir"));
  // ATTAQUE PLACÉE À POSTES — handball, basket (5-out), football/futsal (dernier tiers) :
  // on poste les attaquants à leurs PLACES classiques (court-circuite la backline déployée,
  // réservée aux contre-attaques / transitions). Rugby & ultimate gardent leur déploiement.
  const placedSet = ["handball", "basketball", "basket3x3", "football", "futsal"].includes(sport)
    && setAttack && nA >= 4 && nA <= 6;
  // ATTAQUE PLACÉE = phase jouée FACE À UNE DÉFENSE EN PLACE dans le camp adverse :
  // le « depart » de l'IA est peu fiable (elle renvoie parfois "camp_propre" ou
  // "ligne_mediane" sur un prompt vague). On force "camp_adverse" pour TOUTE attaque
  // placée — elle ne se stage jamais à la médiane ni dans son propre camp.
  const depart = setAttack && spec.depart !== "camp_adverse" ? "camp_adverse" : spec.depart;
  let startX = depart === "camp_adverse" ? 54 : depart === "ligne_mediane" ? 46 : 18;
  // PLACEMENT STRUCTURÉ (analyse.acteurs, validé en amont) — champs
  // CONSULTATIFS émis par l'IA : quand ils existent ils PRIMENT sur les
  // heuristiques texte ci-dessous ; absents, rien ne change (zéro régression).
  const structured = {};
  for (const a of (Array.isArray(spec.acteurs) ? spec.acteurs : [])) structured[a.id] = a;
  // Ancrage en PROFONDEUR d'un repère/d'une distance structurés (x, convention
  // but à droite). Échelle mètres->unités par sport (terrains de 40 m au
  // hand/futsal, 28 m au basket, ~105 m au football).
  const M_SCALE = { handball: 2.8, futsal: 2.35, basketball: 3.3, basket3x3: 3.3 };
  const REPERE_X = { "6m": sport === "handball" ? 73 : 84, "9m": 72, surface: 78,
    rond_central: 48, milieu_terrain: 48, ligne_3pts: 68, tete_raquette: 68 };
  const anchorX = (a) => a == null ? null
    : a.distance_but_m != null ? Math.round(clampN(97 - a.distance_but_m * (M_SCALE[sport] || 0.95), 20, 82))
    : a.repere != null ? REPERE_X[a.repere] ?? null
    : null;
  // « À N MÈTRES DU BUT » : la situation est un INSTANTANÉ à cette distance —
  // le staging se place à la profondeur correspondante. Distance STRUCTURÉE du
  // premier porteur (acteurs) prioritaire ; regex texte en filet.
  const firstBallActor = (spec.phase.find((s) => ["passe", "centre", "dribble", "tir"].includes(s.action)) || {}).acteur;
  const structStart = anchorX(structured[firstBallActor]);
  const dGoal = coreSituationText(spec.text).match(/[àa]\s+(\d{1,2})(?:[.,]\d+)?\s*m(?:[èe]tres?)?\s+du\s+(?:but|panier|cercle|en.?but)/i);
  if (structStart != null) startX = Math.round(clampN(structStart, 20, 80));
  else if (dGoal) startX = Math.round(clampN(97 - parseInt(dGoal[1], 10) * (M_SCALE[sport] || 0.95), 20, 80));
  // CÔTÉS EXPLICITES : côté STRUCTURÉ (acteurs.cote) prioritaire, heuristiques
  // texte/notes (« B sur l'aile droite ») en filet — tous les placements
  // ci-dessous les respectent au lieu de l'alternance par défaut.
  const sideHints = invasionSideHints(spec.text, spec.phase, nA);
  for (const id of Object.keys(structured)) {
    if (structured[id].cote) sideHints[id] = structured[id].cote;
    else if (structured[id].poste === "ailier_gauche") sideHints[id] = sideHints[id] || "gauche";
    else if (structured[id].poste === "ailier_droit") sideHints[id] = sideHints[id] || "droite";
  }

  // ULTIMATE : pas de portage (le modèle invasion « porteur central » ne convient
  // pas). Un GIVE-AND-GO (le disque revient à un ancien porteur : A1->A2->A1…) est
  // rendu littéralement (zig-zag qui contourne le défenseur jusqu'à l'en-but) ;
  // sinon, chaîne de passes vers l'avant (handler -> cutters).
  if (sport === "ultimate") {
    const eff = ultimatePasses(spec.phase);
    if (nA === 2 && eff.length >= 2) return buildUltimateGiveGo(spec, eff, nD);
    // Côtés explicites (structurés/texte) transmis par INDEX (A1..An).
    return buildUltimateChain(nA, nD, { sides: Array.from({ length: nA }, (_, i) => sideHints["A" + (i + 1)] || null) });
  }

  // CONTRE-ATTAQUE BASKET (transition / fast break) : un rebond puis une chaîne de
  // passes qui REMONTE le terrain pour finir au tir est rendue LITTÉRALEMENT (la
  // phase de l'IA telle quelle, sans give-and-go fabriqué), stagée sur tout le
  // terrain : rebondeur dans SA raquette, relais échelonnés (un au rond central),
  // finisseur derrière l'arc à 3 points, défenseurs en repli depuis le panier
  // qu'ils attaquaient. Déclenché par un texte de transition ou un départ camp propre.
  if (sport === "basketball" && (spec.transition || spec.depart === "camp_propre")
    && isDeployChain(spec.phase) && spec.phase.some((s) => s.action === "tir"))
    return buildBasketFastBreak(spec);

  // JEU DÉPLOYÉ : si la phase est une CHAÎNE de passes successives
  // (A1->A2->...->An), on la dessine en backline DIAGONALE lisible (porteur en
  // tête) plutôt qu'avec la géométrie « porteur central » des surnombres.
  // EXCEPTION : une attaque PLACÉE garde ses postes classiques (ci-dessous).
  // GARDE : la réduction en chaîne ne conserve QUE la circulation — interdite
  // dès qu'un GARDIEN ou un DÉFENSEUR agit dans la phase (relance, pressing
  // décrits : ils seraient perdus), ou quand une phase de sport à but ne
  // conclut pas par un tir (le moteur de chaîne en inventerait un).
  const chainSafe = !spec.phase.some((s) => s.acteur === "G" || /^D\d+$/.test(String(s.acteur || "")))
    && (!["football", "futsal", "handball", "basketball", "basket3x3"].includes(sport) || spec.phase.some((s) => s.action === "tir"));
  // POURSUIVANT ORDINAL : « un TROISIÈME, en retard, poursuit » implique 3
  // défenseurs même si l'IA n'en a compté que 2 (les deux replacés) — on
  // étend l'effectif pour que le poursuivant existe.
  const chaseOrdM = coreSituationText(spec.text).match(/(deuxi[èe]me|troisi[èe]me|quatri[èe]me)[^.;\n]{0,40}(?:en retard|poursuit|[àa] la poursuite)/i);
  const chaseOrd = chaseOrdM ? { "deuxième": 2, "deuxieme": 2, "troisième": 3, "troisieme": 3, "quatrième": 4, "quatrieme": 4 }[chaseOrdM[1].toLowerCase()] || 0 : 0;
  if (isDeployChain(spec.phase) && !placedSet && chainSafe)
    return buildDeployedLine(sport, nA, Math.max(nD, chaseOrd), {
      median: depart === "ligne_mediane" || depart === "camp_adverse",
      // ATTAQUE PLACÉE à petit effectif (nA<4, pas de postes 5-out) : on stage la
      // chaîne dans le CAMP ADVERSE (face à la défense), pas seulement à la médiane.
      placed: setAttack && depart === "camp_adverse",
      // Nommage par POSTE handball (Demi-centre/Arrière/Ailier) sur une attaque
      // placée EXPLICITE à ≥3 attaquants — pas sur une contre-attaque (rôles
      // fonctionnels gardés) ni un duel à 2 (pas de vraie structure de postes).
      posts: sport === "handball" && phaseType === "attaque_placee" && nA >= 3,
      corner: spec.phase.some((s) => /angle|coin|drapeau/i.test(s.note || "")),
      gardien: withGk,
      // « interdiction de dribbler / sans dribble » lu aussi dans le TEXTE
      // (le chemin analyse ne renseigne pas spec.noDribble) : le porteur fixe
      // en COURSE balle en main, pas en conduite.
      noDribble: !!spec.noDribble || /interdiction de dribbler|sans dribble|dribble interdit|interdit de dribbler/i.test(coreSituationText(spec.text)),
      // Côtés explicites projetés sur la POSITION dans la chaîne (le moteur
      // renumérote les acteurs par rang de porteur). Quand la chaîne réelle a
      // MOINS de maillons que d'attaquants (relais synthétisés), le côté du
      // FINISSEUR reste au bout — sinon il glissait sur un relais intermédiaire
      // (« l'ailier droit qui conclut » dessiné à gauche).
      sides: (() => {
        const cs = ballChain(spec.phase).map((id) => sideHints[id] || null);
        while (cs.length < nA) cs.splice(cs.length - 1, 0, null);
        return cs;
      })(),
      notes: chainNotes(spec.phase),
      // TRANSITION : défense décrite À LA POURSUITE (« en retard, poursuit »)
      // ou EN REPLI SPRINTÉ — la chaîne les montre en course, plus postés en
      // couverture profonde comme si la défense était déjà en place.
      chase: /en retard|[àa] la poursuite|poursuit/i.test(coreSituationText(spec.text)) ? 1 : 0,
      // « déjà replacés dans leur moitié/camp » : le rideau ET le défenseur
      // fixé restent dans leur moitié (x ≥ 52).
      postedHalf: /(?:replac|repli)[ée]s?[^.;\n]{0,30}dans\s+(?:leur|sa|son)\s+(?:moiti[ée]|camp|propre camp)|d[ée]fenseurs?[^.;\n]{0,20}(?:sont|d[ée]j[àa])\s+replac[ée]s/i.test(coreSituationText(spec.text)),
      // Adversaire « RESTÉ HAUT » : le premier défenseur presse le départ de
      // la montée (comme invasionScene), plus posté dans le rideau profond.
      stayedHigh: /rest[ée]s?\s+haut/i.test(coreSituationText(spec.text)),
      // GARDIEN-LIBÉRO encodé comme PORTEUR par l'IA (« le gardien sort de sa
      // surface pour jouer libéro ») : le porteur de la chaîne porte son rôle.
      carrierRole: /gardien[^.;\n]{0,40}(?:lib[ée]ro|sort de sa (?:surface|zone))/i.test(coreSituationText(spec.text))
        && !spec.phase.some((s) => s.acteur === "gardien" || s.acteur === "G") ? "Gardien-libéro" : null,
      retreat: (() => {
        const c = coreSituationText(spec.text);
        if (!/recul\w*|se replient|repli\b|revien\w*\s+en\s+sprint/i.test(c) || !/contre[- ]?attaque|transition|mont[ée]e de balle|d[èe]s la r[ée]cup[ée]ration/i.test(c)) return 0;
        const m = c.match(/(\d+|un|une|deux|trois|quatre|cinq|six)\s+(?:d[ée]fenseurs?|rouges?|joueurs?\s+rouges?)[^.;\n]{0,40}(?:recul|replient|revien)/i);
        return m ? frenchNum(m[1].toLowerCase()) : nD;
      })(),
      // Système défensif chiffré nommé (« zone 1-3-1 ») : le rideau de la
      // chaîne déployée le respecte au lieu d'une ligne plate.
      tiers: namedSystem,
    });

  // HANDBALL : zone de but (6 m) interdite aux JOUEURS (le gardien y est admis,
  // le ballon peut la traverser). On garde joueurs et déplacements à l'extérieur.
  const ZONE = sport === "handball" ? { rx: 23, ry: 36 } : null;
  const boundaryX = (y) => {
    if (!ZONE) return 96;
    const dy = Math.min(0.999, Math.abs(y - 50) / ZONE.ry);
    return 100 - ZONE.rx * Math.sqrt(1 - dy * dy);
  };
  const keepOut = (x, y, m = 2) => Math.round(clampN(Math.min(x, boundaryX(y) - m), 6, 95));
  // Un point est-il DANS une zone de but (6 m, gauche ou droite) ? Les décalages
  // anti-superposition doivent la respecter — un joueur de champ n'y entre pas.
  const inGoalZone = (x, y) => {
    if (!ZONE) return false;
    const bx = ZONE.rx * Math.sqrt(1 - Math.min(0.999, Math.abs(y - 50) / ZONE.ry) ** 2);
    return x < bx + 2 || x > 100 - bx - 2;
  };

  const players = [], pos = {};
  // Placement PILOTÉ PAR LA PHASE (et non par l'index) : l'IA ne garantit pas
  // que A1 est le porteur. On déduit les rôles RÉELS :
  //  - PORTEUR = 1er acteur d'une conduite/passe (placé au CENTRE, a le ballon) ;
  //  - FINISSEUR = acteur du tir (placé sur une aile) ;
  //  - les autres attaquants répartis. Ainsi l'action part toujours du porteur.
  const attIds = Array.from({ length: nA }, (_, i) => "A" + (i + 1));
  const phaseActor = (cond) => (spec.phase.find(cond) || {}).acteur;
  const shooterId = attIds.includes(phaseActor((s) => s.action === "tir")) ? phaseActor((s) => s.action === "tir") : null;
  // PORTEUR initial : le 1er à conduire/passer ; à défaut, s'il n'y a AUCUNE passe
  // (le ballon ne change jamais de mains), c'est le TIREUR qui le porte d'emblée
  // (il conduit puis tire). Sinon seulement, repli sur le 1er acteur — qui peut
  // n'être qu'un ÉCRANTEUR ou un appel SANS ballon (et ne doit pas hériter du ballon).
  const noPass = !spec.phase.some((s) => s.action === "passe");
  const carrierId = [
    // PORTEUR INITIAL = acteur du PREMIER geste de BALLE chronologique : une
    // PASSE D'OUVERTURE (rebondeur -> meneur) précède parfois le premier
    // dribble — prioriser le dribbleur réancrait la passe sur le receveur puis
    // la sautait comme auto-passe (même famille que la relance du gardien).
    phaseActor((s) => ["dribble", "passe", "centre"].includes(s.action)),
    phaseActor((s) => s.action === "dribble"),
    phaseActor((s) => s.action === "passe"),
    noPass ? shooterId : null,
    spec.phase[0]?.acteur,
  ].find((id) => attIds.includes(id)) || attIds[0];
  // PIVOT : l'attaquant qui POSE un écran (ou que l'on sert « au pivot ») — il
  // sera posté CENTRAL dans la bande 6-9 m (dos au but), et non sur une aile.
  // Le mot « pivot » vit parfois seulement dans le TEXTE (« passe à rebond au
  // pivot ») sans apparaître dans les notes : le receveur de la 1re passe est
  // alors le pivot servi.
  const structPivot = attIds.find((id) => ["pivot", "interieur", "avant_centre"].includes(structured[id]?.poste));
  const pivotId = (structPivot && structPivot !== carrierId ? structPivot : null)   // poste STRUCTURÉ prioritaire
    || (attIds.includes(phaseActor((s) => s.action === "ecran")) ? phaseActor((s) => s.action === "ecran")
    : (spec.phase.find((s) => s.action === "passe" && /pivot/i.test(s.note || "") && attIds.includes(s.cible)) || {}).cible
    || (/\bpivot\b/i.test(coreSituationText(spec.text))
      ? (spec.phase.find((s) => s.action === "passe" && attIds.includes(s.cible) && s.cible !== carrierId) || {}).cible
      : null)
    || null);
  const order = [];
  const add = (id) => { if (attIds.includes(id) && !order.includes(id)) order.push(id); };
  add(carrierId); add(shooterId); attIds.forEach(add);
  // Rang -> hauteur : 0 = centre · 1 = aile basse (droite) · 2 = aile haute · …
  const yByRank = (k) => k === 0 ? 50 : k === 1 ? 72 : k === 2 ? 28 : clampN(50 + (k % 2 ? 1 : -1) * (22 + 12 * Math.floor((k - 1) / 2)), 12, 88);
  // ATTAQUE PLACÉE À POSTES CLASSIQUES (handball / basket / football) face à une défense en
  // place : 1 organisateur central (porteur), une ligne intermédiaire, 2 joueurs LARGES, et
  // 1 poste avancé (pivot/intérieur/avant-centre). La circulation part de ces postes.
  // Convention : but à DROITE ; côté gauche = haut (y faible). Clés : C=central, b1/b2=
  // ligne intermédiaire, w1/w2=larges, post=avancé.
  if (placedSet) {
    // CHANTIER REPLI (👎 PROD94, « les places des joueurs au départ sont
    // fausses ») : les arrières étaient posés à x=50 — SUR la ligne médiane —
    // pour une attaque PLACÉE face à une défense en place. La base arrière
    // d'une placée vit vers 10-12 m (x≈56-58, mêmes profondeurs que le
    // placement à postes du mode match, ligne ~5576) ; la médiane, c'est la
    // transition.
    const POSTS = sport === "handball"
      ? [{ key: "C", role: "Demi-centre", x: 58, y: 50 }, { key: "b1", role: "Arrière G", x: 56, y: 30 }, { key: "b2", role: "Arrière D", x: 56, y: 70 },
         { key: "w1", role: "Ailier G", x: 66, y: 13 }, { key: "w2", role: "Ailier D", x: 66, y: 87 }, { key: "post", role: "Pivot", x: Math.round(clampN(boundaryX(50) - 5, 60, 74)), y: 50 }]
      : (sport === "basketball" || sport === "basket3x3")
        ? [{ key: "C", role: "Meneur", x: 55, y: 50 }, { key: "b1", role: "Ailier G", x: 64, y: 28 }, { key: "b2", role: "Ailier D", x: 64, y: 72 },
           { key: "w1", role: "Corner G", x: 75, y: 13 }, { key: "w2", role: "Corner D", x: 75, y: 87 }, { key: "post", role: "Intérieur", x: 80, y: 50 }]
        : [{ key: "C", role: "Meneur", x: 52, y: 50 }, { key: "b1", role: "Relayeur G", x: 58, y: 34 }, { key: "b2", role: "Relayeur D", x: 58, y: 66 },
           { key: "w1", role: "Ailier G", x: 70, y: 14 }, { key: "w2", role: "Ailier D", x: 70, y: 86 }, { key: "post", role: "Avant-centre", x: 78, y: 50 }];
    const byKey = Object.fromEntries(POSTS.map((p) => [p.key, p]));
    const used = new Set(), assign = {};
    // POSTES NOMMÉS dans les notes de la phase (« l'arrière gauche fixe puis
    // donne », « le demi-centre croise ») : chaque acteur prend LE poste que le
    // texte lui donne — le porteur n'est pas toujours le demi-centre/meneur.
    const POST_NAMES = [
      [/arri[èe]re\s+gauche|relayeur\s+g\b/i, "b1"], [/arri[èe]re\s+droit|relayeur\s+d\b/i, "b2"],
      [/ailier\s+gauche|corner\s+g\b/i, "w1"], [/ailier\s+droit|corner\s+d\b/i, "w2"],
      [/demi[- ]centre|meneur/i, "C"], [/pivot|int[ée]rieur|avant[- ]centre/i, "post"],
    ];
    // Postes STRUCTURÉS (acteurs.poste) : prioritaires sur les notes/texte.
    const POSTE_KEY = { arriere_gauche: "b1", arriere_droit: "b2", demi_centre: "C", meneur: "C",
      ailier_gauche: "w1", ailier_droit: "w2", pivot: "post", interieur: "post", avant_centre: "post" };
    for (const id of attIds) {
      const k = POSTE_KEY[structured[id]?.poste];
      if (k && !used.has(k) && !assign[id]) { assign[id] = k; used.add(k); }
    }
    for (const st of spec.phase) {
      if (!st || !st.note || !attIds.includes(st.acteur) || assign[st.acteur]) continue;
      const hit = POST_NAMES.find(([re]) => re.test(st.note));
      if (hit && !used.has(hit[1])) { assign[st.acteur] = hit[1]; used.add(hit[1]); }
    }
    // …ou nommés dans le TEXTE : « L'arrière gauche bleu a le ballon », « le
    // demi-centre tire » — le porteur/tireur prennent le poste que la phrase
    // leur donne même quand les notes de l'IA ne les nomment pas.
    for (const sent of coreSituationText(spec.text).split(/[.;\n!?]/)) {
      const hit = POST_NAMES.find(([re]) => re.test(sent));
      if (!hit || used.has(hit[1])) continue;
      if (/a (?:le|la) ball(?:on|e)|porteur|balle en main/i.test(sent) && attIds.includes(carrierId) && !assign[carrierId]) { assign[carrierId] = hit[1]; used.add(hit[1]); }
      else if (/\btire\b|\btir\b|conclut|\bmarque\b/i.test(sent) && shooterId && !assign[shooterId]) { assign[shooterId] = hit[1]; used.add(hit[1]); }
    }
    if (attIds.includes(carrierId) && !assign[carrierId] && !used.has("C")) { assign[carrierId] = "C"; used.add("C"); }
    if (pivotId && attIds.includes(pivotId) && !assign[pivotId] && !used.has("post")) { assign[pivotId] = "post"; used.add("post"); }
    // Un CÔTÉ EXPLICITE du texte oriente le choix des postes : droite = postes
    // « 2 » (y grand, convention but à droite), gauche = postes « 1 ».
    const postPref = (id, base) => sideHints[id] === "droite" ? ["b2", "w2", ...base]
      : sideHints[id] === "gauche" ? ["b1", "w1", ...base] : base;
    if (shooterId && !assign[shooterId]) {                          // le tireur conclut depuis le PÉRIMÈTRE (ligne intermédiaire / large)
      const pick = postPref(shooterId, ["b1", "b2", "w1", "w2"]).find((k) => !used.has(k));
      if (pick) { assign[shooterId] = pick; used.add(pick); }
    }
    const fillOrder = ["C", "b1", "b2", "w1", "w2", "post"];
    for (const id of attIds) { if (assign[id]) continue; const k = postPref(id, fillOrder).find((kk) => !used.has(kk)); if (k) { assign[id] = k; used.add(k); } }
    for (const id of attIds) {
      const post = byKey[assign[id]] || POSTS[0];
      const x = post.key === "post" ? post.x : keepOut(post.x, post.y);
      const p = { id, num: +id.slice(1), role: post.role, team: "A", color: "#0072B2", x, y: post.y };
      players.push(p); pos[id] = { x, y: post.y };
    }
  } else {
  // CÔTÉS EXPLICITES : un joueur à l'AILE fixée par le texte prend SA hauteur
  // (droite = bas, gauche = haut) ; les autres suivent l'alternance yByRank en
  // ÉVITANT les hauteurs réservées (sinon « B sur l'aile droite » restait posé
  // à gauche par la simple alternance). « centre » est ignoré ici : les
  // non-porteurs partagent le même x, deux axiaux se superposeraient.
  const Y_SIDE = { droite: 72, gauche: 28 };
  const wingOf = (id) => Y_SIDE[sideHints[id]] != null ? sideHints[id] : null;
  const reservedY = order.filter(wingOf).map((id) => Y_SIDE[sideHints[id]]);
  let rank = 0;
  const nextFreeY = () => {
    for (let guard = 0; guard < 24; guard++) {
      const y = yByRank(rank++);
      if (!reservedY.some((t) => Math.abs(t - y) < 12)) return y;
    }
    return yByRank(rank++);
  };
  const POSTE_LABEL = { arriere_gauche: "Arrière gauche", arriere_droit: "Arrière droit", demi_centre: "Demi-centre",
    ailier_gauche: "Ailier gauche", ailier_droit: "Ailier droit", pivot: "Pivot", meneur: "Meneur", interieur: "Intérieur", avant_centre: "Avant-centre" };
  order.forEach((id) => {
    const y = Math.round(wingOf(id) ? Y_SIDE[sideHints[id]] : nextFreeY());
    const x = keepOut(clampN(startX + (id === carrierId ? 0 : 6), 6, 70), y);
    const role = (sport === "handball" && phaseType === "attaque_placee" && nA >= 3)
      ? handballPlacedRole(y, { carrier: id === carrierId, pivot: id === pivotId })
      : POSTE_LABEL[structured[id]?.poste]   // poste STRUCTURÉ nommé par le texte : il fait foi
      || (id === carrierId ? "Porteur"
      : id === pivotId ? "Pivot"   // le pivot GARDE son poste même s'il conclut (servi, se retourne, tire)
      : id === shooterId ? (y > 52 ? "Ailier droit" : y < 48 ? "Ailier gauche" : "Attaquant")
      : sideHints[id] === "droite" ? "Ailier droit" : sideHints[id] === "gauche" ? "Ailier gauche"
      : "Attaquant");
    const p = { id, num: +id.slice(1), role, team: "A", color: "#0072B2", x, y };
    players.push(p); pos[id] = { x, y };
  });
  }
  // PIVOT : posté CENTRAL dos au but — bande 6-9 m au handball (ancré à la
  // ZONE), poste avancé dans l'axe ailleurs (futsal/foot/basket : « le pivot
  // au milieu du terrain, dos au but »). Il garde sa place MÊME s'il conclut
  // (pivot servi qui se retourne et tire) ; seul le PORTEUR initial ne peut
  // pas être posté pivot. L'arrière/porteur central se décale s'ils se
  // retrouvaient superposés (attaque relevée à hauteur du pivot).
  // (Sauté en attaque placée à postes : les postes sont déjà définitifs.)
  // (Sauté aussi pour un pivot/avant-centre LANCÉ en CONTRE-ATTAQUE : sa course
  //  « appel dans l'axe » vers le but partirait de la zone de tir — course
  //  quasi nulle supprimée, tir depuis 9 m. Il part de derrière et reçoit
  //  lancé : le poste « dos au but » n'a pas de sens en transition.)
  const pivotLance = pivotId && spec.phase.some((s) => s.acteur === pivotId && s.action === "course" && s.cible === "but")
    && (phaseType === "contre_attaque" || /contre[- ]?attaque|transition|mont[ée]e de balle/i.test(coreSituationText(spec.text || "")));
  if (!placedSet && !pivotLance && pivotId && pivotId !== carrierId
    && (ZONE || ["football", "futsal", "basketball", "basket3x3"].includes(sport))) {
    const setPos = (id, x, y) => { pos[id] = { x, y }; const p = players.find((pp) => pp.id === id); if (p) { p.x = x; p.y = y; } };
    const px = ZONE
      ? Math.round(clampN(boundaryX(50) - 5, Math.min(startX + 6, 72), 73))
      : Math.round(clampN(startX + 26, 30, 78));
    // Le porteur central se décale de l'axe UNIQUEMENT en cas de vraie
    // superposition avec le pivot ; un « axe » EXPLICITE (structuré) ne cède
    // qu'en dessous de 8 unités d'écart.
    const shiftGap = structured[carrierId]?.cote === "centre" ? 8 : 14;
    if (pos[carrierId] && Math.abs(pos[carrierId].y - 50) < 9 && Math.abs(px - pos[carrierId].x) < shiftGap)
      setPos(carrierId, pos[carrierId].x, 38);
    // SURCHARGE d'un côté (« on surcharge le côté droit, l'ailier, l'arrière et
    // le pivot du même côté ») : le pivot SUIT la surcharge (poste bas du côté
    // nommé) au lieu de rester dans l'axe.
    const overM = coreSituationText(spec.text).match(/surcharg\w*[^.;]{0,30}c[ôo]t[ée]\s+(droit|gauche)/i);
    setPos(pivotId, px, overM ? (/droit/i.test(overM[1]) ? 68 : 32) : 50);
  }
  // POWER PLAY : le porteur initial décrit comme GARDIEN-JOUEUR (futsal, hand
  // 7c6) porte son rôle — sinon le surnombre n'est pas lisible.
  if (/gardien[- ]joueur/i.test(coreSituationText(spec.text || ""))) {
    const cgj = players.find((p) => p.id === carrierId);
    if (cgj) cgj.role = "Gardien-joueur";
  }
  // ANCRAGE STRUCTURÉ EN PROFONDEUR (acteurs.repere / distance_but_m) : un
  // joueur SITUÉ par le texte (« le pivot sur la ligne des 6 m », « B en tête
  // de raquette ») prend SA profondeur — prioritaire sur le placement générique
  // et sur le pivot par défaut ; le premier porteur est déjà servi via startX.
  if (!placedSet) for (const id of attIds) {
    const ax = anchorX(structured[id]);
    if (ax == null || !pos[id]) continue;
    if (id === firstBallActor && structStart != null) continue;   // déjà appliqué (startX)
    const x = keepOut(ax, pos[id].y, 3);
    pos[id] = { x, y: pos[id].y };
    const p = players.find((pp) => pp.id === id);
    if (p) p.x = x;
  }
  // MARQUAGE INDIVIDUEL (nD == nA, PETITS effectifs) : chaque Dk se place sur SON
  // attaquant Ak (goalside) — indispensable pour qu'un écran « sur Dk » tombe sur le
  // bon défenseur. Au-delà (≥5 c 5 : attaque PLACÉE face à une défense de zone), on
  // place les défenseurs en RIDEAU étagé, pas en individuel (sinon ils s'agglutinent).
  // Un SYSTÈME chiffré cohérent nommé par le texte (« zone carrée 2-2 » à 4c4,
  // « zone triangle 1-2 » en 3x3) prime : c'est une ZONE, pas un marquage.
  const manToMan = nD === nA && nD <= 4 && !namedSystem;
  // DÉFENSE QUI RECULE (« les défenseurs reculent vers leur surface », repli) :
  // les défenseurs partent ENTRE le ballon et le but (près du ballon) et leur
  // course synthétique REVIENT vers le but (au lieu d'une « sortie » à contresens).
  const retreating = /recul\w*|se replient|repli\b|revien\w*\s+en\s+sprint/i.test(coreSituationText(spec.text));
  // Défenseurs décrits DÉJÀ REPLIÉS devant leur cible (« repliés devant le
  // panier », « replié dans la raquette ») : postés en protection profonde —
  // la course de repli synthétique devient inutile (déjà à poste).
  const postedDeep = /repli[ée]s?[^.;]{0,30}(?:devant|dans|sous|pr[èe]s)\s+(?:le\s+|la\s+|leur\s+)?(?:panier|raquette|cercle|but\b|surface)/i.test(coreSituationText(spec.text));
  // TRANSITION / CONTRE-ATTAQUE : une défense décrite EN REPLI (« deux
  // défenseurs reculent en sprint », « avant que la défense adverse soit
  // replacée ») n'est PAS un bloc installé — même à effectif complet, on ne
  // pose pas le système : le sous-groupe COMPTÉ revient en sprint entre le
  // ballon et le but, les autres sont pris de vitesse derrière le ballon
  // (ils attaquaient au moment de la perte). Un système NOMMÉ (« 6-0 »)
  // reste prioritaire, et les défenseurs décrits déjà repliés aussi.
  const transitionCtx = /contre[- ]?attaque|transition|mont[ée]e de balle|d[èe]s la r[ée]cup[ée]ration/i.test(coreSituationText(spec.text));
  // ADVERSAIRE « RESTÉ HAUT » (pressing sur la relance) : le premier défenseur
  // presse près du départ du ballon, pas dans le rideau profond.
  const stayedHigh = /rest[ée]s?\s+haut/i.test(coreSituationText(spec.text));
  const retreatCountM = coreSituationText(spec.text).match(/(\d+|un|une|deux|trois|quatre|cinq|six)\s+(?:d[ée]fenseurs?|rouges?|joueurs?\s+rouges?)[^.;\n]{0,40}(?:recul|replient|revien)/i);
  const retreatCount = retreating && transitionCtx && !postedDeep
    ? Math.min(nD, retreatCountM ? frenchNum(retreatCountM[1].toLowerCase()) : nD) : 0;
  // ÉCRANS requalifiés (note « écran » -> action ecran) calculés AVANT le placement
  // des défenseurs, pour pouvoir poster le défenseur écranté EN FACE de sa cible.
  const phase = withScreensFromNotes(spec.phase, manToMan, carrierId, shooterId, (id) => /^D(\d+)$/.test(id) && +id.slice(1) <= nD);
  // PICK ON-BALL : un écran sur un défenseur Dk SUIVI d'une conduite du PORTEUR vers
  // le but = le porteur utilise le bloc pour aller au panier. On en déduit que Dk
  // marquait le porteur : on le poste DEVANT lui, l'écran se met À CÔTÉ (côté sortie)
  // et le porteur CONTOURNE le bloc jusqu'au cerceau (lay-up). « side » = côté de la
  // sortie (déduit de la position de départ de l'écranteur).
  // MARQUAGES EXPLICITES — deux sources, chacune poste le défenseur FACE à son
  // attaquant plutôt que dans l'étalement générique :
  //  - texte « B, défendu de près par le joueur rouge 1 » -> D1 COLLE B (+9) ;
  //  - défenseur CIBLE d'une fixation (« A1 court vers D1 ») -> D1 posté EN FACE
  //    de A1, assez loin (+16) pour que la course de fixation reste visible.
  const markOf = invasionMarkHints(spec.text, nA, nD);            // { D1: { id, tight } }
  for (const st of phase) {
    if ((st.action === "course" || st.action === "dribble") && /^D\d+$/.test(String(st.cible || ""))
      && attIds.includes(st.acteur) && !markOf[st.cible]) markOf[st.cible] = { id: st.acteur, tight: false };
  }
  // Marquage STRUCTURÉ (acteurs.marque) : prioritaire sur les heuristiques.
  // Si la phase décrit la COURSE du défenseur vers son joueur (« vient
  // presser »), on le poste À DISTANCE (tight:false) pour que l'arrivée du
  // pressing reste visible — collé sinon.
  for (const id of Object.keys(structured)) {
    if (!/^D\d+$/.test(id) || !structured[id].marque || !attIds.includes(structured[id].marque)) continue;
    const runsAt = phase.some((s) => s.acteur === id && (s.action === "course" || s.action === "dribble") && s.cible === structured[id].marque);
    markOf[id] = { id: structured[id].marque, tight: !runsAt };
  }
  const screenIdx = phase.findIndex((s) => s.action === "ecran" && /^D\d+$/.test(String(s.cible || "")));
  // On ne traite comme PICK ON-BALL que le cas SIMPLE : le porteur ne conduit PAS
  // avant l'écran et FILE au but APRÈS (sinon set-play plus complexe — on laisse la
  // logique générique, p.ex. un 3c3 avec une conduite initiale).
  const drivesAfter = screenIdx >= 0 && phase.some((s, i) => i > screenIdx && s.acteur === carrierId && (s.action === "dribble" || s.action === "course") && s.cible === "but");
  const movesBefore = screenIdx >= 0 && phase.some((s, i) => i < screenIdx && s.acteur === carrierId && (s.action === "dribble" || s.action === "course"));
  const pick = (drivesAfter && !movesBefore)
    ? { screener: phase[screenIdx].acteur, defender: phase[screenIdx].cible, driver: carrierId,
        side: (pos[phase[screenIdx].acteur]?.y ?? 72) >= (pos[carrierId]?.y ?? 50) ? 1 : -1 }
    : null;
  // SYSTÈME DÉFENSIF (invasion CONTRE un dispositif) : tiers explicites lus dans le
  // texte (« 6-0 », « 4-4-2 », « 2-3 ») OU effectif COMPLET (≥6). On place le bloc
  // avec la GÉOMÉTRIE PROPRE AU SPORT (arc handball, zones basket, lignes foot) au
  // lieu du placement grossier de surnombre. Exclut le marquage individuel (petit
  // surnombre) et le pick on-ball (basket), qui gardent leur logique.
  // Anti-superposition ATTAQUANTS : deux joueurs envoyés au MÊME point (deux
  // côtés « droite » — ailier + latéral, deux finisseurs au même rang) sont
  // décalés à la première place libre ; les positions déjà distinctes ne
  // bougent pas.
  for (const idA of attIds) {
    const p0 = players.find((pp) => pp.id === idA);
    if (!p0) continue;
    if (players.some((pp) => pp !== p0 && Math.hypot(pp.x - p0.x, pp.y - p0.y) < 5)) {
      const free = [[p0.x - 7, p0.y], [p0.x, p0.y - 8], [p0.x, p0.y + 8], [p0.x - 7, p0.y - 8]]
        .map(([cx, cy]) => [Math.round(clampN(cx, 6, 95)), Math.round(clampN(cy, 8, 92))])
        .find(([cx, cy]) => !inGoalZone(cx, cy) && !players.some((pp) => pp !== p0 && Math.hypot(pp.x - cx, pp.y - cy) < 5));
      if (free) { p0.x = free[0]; p0.y = free[1]; pos[idA] = { x: free[0], y: free[1] }; }
    }
  }
  const sysTiers = namedSystem;
  const useSystem = !pick && !manToMan && (sysTiers || (nD >= 6 && !retreatCount));
  if (useSystem) {
    const isFoot = ["football", "futsal"].includes(sport);
    const tiers = sysTiers || (isFoot ? [Math.ceil(nD / 2), Math.floor(nD / 2)] : [nD]);
    const dfn = defensiveSystemPlayers(tiers, sport, { hint: spec.text, footRoles: isFoot });
    dfn.players.forEach((d) => { players.push(d); pos[d.id] = { x: d.x, y: d.y }; });
  } else
  for (let i = 0; i < nD; i++) {
    const did = "D" + (i + 1);
    const aid = "A" + (i + 1), mark = manToMan && pos[aid];
    let y, x;
    if (pick && did === pick.defender && pos[pick.driver]) {
      // Le défenseur écranté MARQUE le porteur : devant lui, côté panier.
      y = pos[pick.driver].y;
      x = keepOut(pos[pick.driver].x + 12, y, 4);
    } else if (markOf[did] && pos[markOf[did].id]) {
      // Marquage explicite / cible d'une fixation : FACE à son attaquant.
      const tgt = pos[markOf[did].id];
      y = tgt.y;
      x = keepOut(tgt.x + (markOf[did].tight ? 9 : 16), y, 4);
    } else if (retreatCount) {
      // Transition : les « retreatCount » premiers défenseurs REVIENNENT en
      // sprint entre le ballon et le but ; les autres, pris de vitesse par la
      // contre-attaque, sont encore derrière le ballon (ils attaquaient).
      if (i < retreatCount) {
        y = Math.round(clampN(50 + (i - (retreatCount - 1) / 2) * 16, 20, 80));
        x = keepOut(Math.round(clampN(startX + 22 + i * 6, 30, 62)), y, 5);
      } else {
        const j = i - retreatCount, nBeat = nD - retreatCount;
        y = Math.round(clampN(50 + (j - (nBeat - 1) / 2) * 18, 12, 88));
        // HORS de la zone de but (6 m) côté GAUCHE aussi : un joueur de champ
        // pris de vitesse reste à l'extérieur (il vient de tirer depuis 9 m) —
        // keepOut ne protège que la zone du but attaqué (droite).
        const lbx = ZONE ? Math.round(ZONE.rx * Math.sqrt(1 - Math.min(0.999, ((y - 50) / ZONE.ry) ** 2)) + 4) : 6;
        x = keepOut(Math.max(lbx, Math.round(startX - 6 - (j % 2) * 5)), y, 5);
      }
    } else {
      // Étalement vertical MIS À L'ÉCHELLE : pour un grand effectif (rideau de zone),
      // on resserre le pas pour répartir tous les défenseurs sans clamper deux d'entre
      // eux sur la même hauteur (16/84) — ce qui les superposait à 5 c 5 / 6 c 6.
      const stepY = nD > 1 ? Math.min(24, 68 / (nD - 1)) : 0;
      y = mark ? pos[aid].y : Math.round(clampN(50 + (i - (nD - 1) / 2) * stepY, 16, 84));
      // Défense de ZONE : juste DEVANT la ligne des 6 m (handball) ; sinon, en
      // marquage goalside de l'attaquant (individuel) ou en couverture (interposée).
      // FOOTBALL/FUTSAL en zone : LIGNE défensive PLATE (x constant, étalée en
      // largeur) plutôt qu'une diagonale qui empilait les défenseurs en colonne.
      const flatLine = ["football", "futsal"].includes(sport) && !mark;
      // Défenseur « EN RETARD » / à la poursuite : il revient DERRIÈRE le porteur
      // (il le poursuit), pas interposé entre le porteur et le but.
      const chasing = /en retard|[àa] la poursuite|poursuit|revien(?:t|nent) d[ée]fendre/i.test(coreSituationText(spec.text));
      if (stayedHigh && i === 0) { y = 44; x = keepOut(Math.round(startX + 16), y, 5); }
      else
      x = chasing ? keepOut(Math.max(6, (pos[carrierId]?.x ?? startX) - 8 - i * 6), y, 5)
        : ZONE ? Math.round(clampN(boundaryX(y) - 4, 10, 90))
        : mark ? keepOut(pos[aid].x + 9, y, 5)
        : postedDeep ? Math.round(clampN(boundaryX(y) - 6, 10, 90))
        : retreating ? keepOut(startX + 14, y, 5)
        : flatLine ? keepOut(startX + 26, y, 5)
        : keepOut(startX + 24 + i * 4, y, 5);
    }
    // Anti-superposition : un défenseur qui tombe SUR un joueur déjà posé
    // (pivot central, presseur face au porteur…) est décalé vers la première
    // place libre (hauteur, puis léger retrait).
    if (players.some((p) => Math.hypot(p.x - x, p.y - y) < 5)) {
      const free = [[x, y + 8], [x, y - 8], [x - 7, y], [x, y + 14], [x, y - 14]]
        .map(([cx, cy]) => [Math.round(clampN(cx, 6, 95)), Math.round(clampN(cy, 8, 92))])
        .find(([cx, cy]) => !inGoalZone(cx, cy) && !players.some((p) => Math.hypot(p.x - cx, p.y - cy) < 5));
      if (free) { x = free[0]; y = free[1]; }
    }
    const d = { id: did, num: i + 1, role: "Défenseur" + (nD > 1 ? " " + (i + 1) : ""), team: "B", color: "#D55E00", x, y };
    players.push(d); pos[d.id] = { x: d.x, y: d.y };
  }
  // « ENTRE DEUX DÉFENSEURS » : le pivot est décrit DANS l'intervalle — les
  // deux premiers défenseurs l'ENCADRENT (de part et d'autre, sur la ligne de
  // zone au handball) au lieu du marquage/étalement générique.
  if (!placedSet && !useSystem && pivotId && nD >= 2 && pos[pivotId]
    && /entre\s+(?:les\s+)?deux\s+d[ée]fenseurs/i.test(coreSituationText(spec.text))) {
    [-8, 8].forEach((dy, i) => {
      const did = "D" + (i + 1);
      if (!pos[did]) return;
      const y = Math.round(clampN(pos[pivotId].y + dy, 10, 90));
      const x = Math.round(ZONE ? clampN(boundaryX(y) - 4, 10, 90) : clampN(pos[pivotId].x + 8, 10, 92));
      pos[did] = { x, y };
      const d = players.find((p) => p.id === did);
      if (d) { d.x = x; d.y = y; }
      markOf[did] = markOf[did] || { id: pivotId, tight: true };   // engagé : pas de « sortie » synthétique
    });
  }

  // GARDIEN QUI RELANCE : si le PREMIER geste de balle est du gardien (« le
  // gardien relance vers A1 »), c'est le gardien de l'équipe QUI ATTAQUE — il
  // se place devant SON but (à gauche) avec le ballon, et la relance est
  // dessinée. Le but ATTAQUÉ garde son propre gardien (G2) si la phase va au
  // tir. (Avant : G posé au but adverse, et la relance G->A1 était réancrée
  // sur le porteur puis sautée comme auto-passe.)
  const firstBall = spec.phase.find((s) => ["passe", "centre", "dribble", "tir"].includes(s.action));
  const gkStarts = !!firstBall && firstBall.acteur === "G";
  if (gkStarts) {
    const g = { id: "G", role: "Gardien", team: "k", color: "#F2C200", x: 8, y: 50 };
    players.push(g); pos.G = { x: 8, y: 50 };
    if (withGk && spec.phase.some((s) => s.action === "tir"))
      { const g2 = { id: "G2", role: "Gardien", team: "k", color: "#F2C200", x: 94, y: 50 }; players.push(g2); pos.G2 = { x: 94, y: 50 }; }
  } else if (withGk) { const g = { id: "G", role: "Gardien", team: "k", color: "#F2C200", x: 94, y: 50 }; players.push(g); pos.G = { x: 94, y: 50 }; }

  // PIVOT DANS L'INTERVALLE : face à un RIDEAU défensif (système 6-0/5-1…), le
  // pivot se poste ENTRE deux défenseurs (au contact du bloc), jamais superposé
  // à l'un d'eux (à 6c6 contre une 5-1, il était sur le nez du défenseur
  // central). En MARQUAGE INDIVIDUEL, son défenseur le colle légitimement.
  if (useSystem) {
    const piv = players.find((p) => p.team === "A" && /pivot/i.test(p.role || ""));
    const dfs = players.filter((p) => p.team === "B");
    const near = piv && dfs.find((d) => Math.hypot(d.x - piv.x, d.y - piv.y) < 6);
    if (piv && near && dfs.length >= 2) {
      const ys = dfs.map((d) => d.y).sort((a, b) => a - b);
      let bestY = piv.y, bestGap = 0;
      for (let i = 0; i + 1 < ys.length; i++) {
        const gap = ys[i + 1] - ys[i], mid = (ys[i] + ys[i + 1]) / 2;
        if (gap > bestGap + 0.001 || (Math.abs(gap - bestGap) <= 0.001 && Math.abs(mid - 50) < Math.abs(bestY - 50))) { bestGap = gap; bestY = mid; }
      }
      if (bestGap >= 8) {
        piv.y = Math.round(bestY);
        piv.x = Math.round(Math.min(piv.x, near.x - 5));
        pos[piv.id] = { x: piv.x, y: piv.y };
      }
    }
  }

  // RUGBY : passe vers l'arrière/latérale — le receveur ne peut pas être EN
  // AVANT du porteur. On recule les receveurs derrière leur passeur.
  applyBackwardPasses(players, spec.phase.filter((s) => (s.action === "passe" || s.action === "centre") && s.cible).map((s) => ({ from: s.acteur, to: s.cible })), sport, pos);

  // Positions de DÉPART figées (pour le CROISÉ : le coureur prend la place que
  // son partenaire vient de QUITTER, pas sa position courante).
  const pos0 = {};
  for (const k of Object.keys(pos)) pos0[k] = { x: pos[k].x, y: pos[k].y };

  // Ballon figé AVANT la phase, sur le PORTEUR INITIAL à sa position de départ
  // (le gardien s'il relance — sinon la conduite déplace pos[] et le ballon se
  // retrouve en bout de course).
  const ballOwner = gkStarts ? "G" : carrierId;
  const ballPos = { x: clampN(Math.round((pos[ballOwner]?.x || 20) + 5), 4, 96), y: pos[ballOwner]?.y || 50 };

  // Sports où l'on TIRE/SHOOTE vers un but/panier. Pour les autres (rugby,
  // ultimate), il n'y a pas de tir : on MARQUE en aplatissant / réceptionnant
  // le ballon dans la zone (un portage du porteur, pas une frappe).
  const SHOOTS_AT_GOAL = new Set(["football", "futsal", "handball", "basketball", "basket3x3"]);
  const ESSAI_LABEL = sport === "ultimate" ? "Réception dans la zone" : "Essai";
  // RÈGLE DU BALLON (convention produit) : le tracé ondulé « conduite » encode
  // « BALLON PORTÉ » dans tous les sports — y compris le portage rugby/ultimate.
  const carryType = spec.noDribble ? "course" : "dribble";

  const LABEL = { passe: "Passe", course: "Course", dribble: "Conduite", tir: "Tir", centre: "Centre", ecran: "Écran" };
  // RÈGLE DU BALLON : un déplacement du PORTEUR se rend en CONDUITE (tracé ondulé,
  // ballon porté) ; un déplacement SANS ballon (appel, soutien) en COURSE (trait
  // plein). Le suivi de `holder` ci-dessous détermine donc qui conduit et qui court
  // — c'est ce qui rend lisible « qui a le ballon » à chaque étape de l'action.
  const actions = []; let ord = 1;
  const involved = new Set();
  const spAppelY = {};   // appels de coup de pied arrêté déjà dessinés (y visé par joueur)
  const extraMarkers = [];
  // Suivi du PORTEUR du ballon le long de la phase (départ = le porteur déduit,
  // ou le GARDIEN s'il relance — sinon sa relance serait réancrée sur le
  // porteur de champ puis sautée comme auto-passe).
  // Le type de flèche d'un déplacement dépend de qui porte le ballon, PAS du mot
  // choisi par l'IA : une course du PORTEUR est une conduite (balle en main,
  // ondulé) ; la flèche pleine « course » reste réservée au jeu SANS ballon
  // (appel, soutien, course avant la réception).
  let holder = ballOwner;
  // CENTRE + REPRISE : un centre suivi d'une reprise doit TOMBER dans la surface
  // LÀ OÙ le finisseur arrive (sinon centre et reprise se déconnectent). On fixe
  // un point de chute commun (2e poteau, côté opposé au centreur) : le centre y
  // va, le finisseur y fait son appel, et la reprise en part.
  const centreSt = spec.phase.find((s) => s.action === "centre");
  const crossTarget = centreSt ? (() => {
    const cy = pos[centreSt.acteur] ? pos[centreSt.acteur].y : 50;
    const ty = cy <= 50 ? 62 : 38;
    return { x: keepOut(82, ty, 2), y: ty };
  })() : null;
  // ÉCRAN MAL ÉTIQUETÉ : l'IA décrit parfois un écran dans la NOTE tout en le
  // classant « course/conduite vers le but ». En marquage individuel, on le
  // (la phase requalifiée — écrans déduits des notes — est calculée plus haut, avant
  // le placement des défenseurs, pour poster le défenseur écranté face à sa cible.)
  for (const st of phase) {
    const from = st.acteur; involved.add(from);
    const label = st.note || LABEL[st.action];
    // RUGBY / ULTIMATE : pas de tir au but. La CONCLUSION (porter le ballon dans
    // l'en-but) est un ESSAI — qu'elle soit notée "tir" OU une dernière course/conduite
    // du PORTEUR vers "but". Mais un JEU AU PIED (dégagement, chandelle, drop) n'est
    // PAS un essai : longue trajectoire vers l'avant, le ballon retombe plus loin.
    const rugbyEnd = !SHOOTS_AT_GOAL.has(sport) && st.cible === "but"
      && (st.action === "tir" || ((st.action === "course" || st.action === "dribble") && from === holder && st === phase[phase.length - 1]));
    if (rugbyEnd) {
      const p = pos[from] || { x: 70, y: 50 };
      if (/\bpied\b|d[ée]gage|chandelle|\bdrop\b|coup de pied|p[ée]nalit/i.test(st.note || "")) {
        const tx = keepOut(92, p.y, 1);
        actions.push({ from, tox: tx, toy: p.y, type: "passe", style: "dashed", order: ord++, label: st.note || "Jeu au pied" });
        extraMarkers.push({ type: "ballon", x: tx, y: p.y });
        holder = null;
      } else {
        let ty = p.y;
        if (/angle|coin|drapeau/i.test(st.note || "")) ty = p.y >= 50 ? 86 : 14;   // « dans l'angle »
        const tx = keepOut(94, ty, 1);
        actions.push({ from, tox: tx, toy: ty, type: carryType, order: ord++, label: st.note || ESSAI_LABEL });
        pos[from] = { x: tx, y: ty };
        extraMarkers.push({ type: "essai", x: tx, y: ty });
      }
      continue;
    }
    if (st.action === "tir" && /feinte/i.test(st.note || "")) {
      // FEINTE DE TIR étiquetée « tir » par l'IA : pas une vraie frappe — un
      // armé qui MENACE (petit appui vers le but, ballon conservé), jamais une
      // flèche de tir au but (elle doublait la vraie conclusion).
      const p = pos[from] || { x: 70, y: 50 };
      const fx = keepOut(p.x + 6, p.y, 3);
      if (fx - p.x >= 3) {
        actions.push({ from, tox: fx, toy: p.y, type: from === holder ? carryType : "course", order: ord++, label });
        pos[from] = { x: fx, y: p.y };
      }
      continue;
    }
    if (st.action === "tir") {
      // REPRISE sur centre : si un centre a fixé un point de chute (2e poteau) et
      // que le finisseur n'y est pas encore (l'IA a OMIS l'appel), on l'y amène —
      // sinon la reprise partirait loin du ballon centré (balle « téléportée »).
      // …vaut pour le tireur principal ET pour tout tireur dont l'action
      // précédente est un CENTRE (gammes de centres : chaque tête a son appel).
      const afterCross = (spec.phase[spec.phase.indexOf(st) - 1] || {}).action === "centre";
      if (crossTarget && (from === shooterId || afterCross) && pos[from] && spAppelY[from] == null
        && Math.hypot(pos[from].x - crossTarget.x, pos[from].y - crossTarget.y) > 5) {
        actions.push({ from, tox: crossTarget.x, toy: crossTarget.y, type: "course", order: ord++, label: "Appel au 2e poteau" });
        pos[from] = { x: crossTarget.x, y: crossTarget.y };
      }
      // POTEAU NOMMÉ (sports à but) : « frappe au second poteau » vise le poteau
      // OPPOSÉ au tireur, « premier poteau » son côté — au lieu du centre du but.
      let shotY = 50;
      if (GK_SPORTS.has(sport) && pos[from]) {
        // La NOTE du tir prime (chaque tireur a son poteau : « tête premier
        // poteau » / « tête second poteau ») ; le texte global ne sert de
        // repli qu'au tireur principal, sans note de poteau.
        const coreT = coreSituationText(spec.text || "");
        const gapM = (st.note || "").match(/entre (?:les )?(?:d[ée]fenseurs? )?(\d)\s+et\s+(\d)/i);
        if (spAppelY[from] != null)
          // L'APPEL du tireur visait déjà un point nommé (poteau…) : la frappe
          // part et conclut de CE côté — relire la note du tir depuis sa
          // NOUVELLE position inversait le poteau.
          shotY = spAppelY[from] <= 50 ? 42 : 58;
        else if (gapM && pos["D" + gapM[1]] && pos["D" + gapM[2]])
          // « tir entre 2 et 3 » : la frappe passe DANS l'intervalle des deux
          // défenseurs nommés.
          shotY = Math.round(clampN((pos["D" + gapM[1]].y + pos["D" + gapM[2]].y) / 2, 40, 60));
        else if (/(?:second|deuxi[èe]me|2e)\s+pot(?:eau|o|au)/i.test(st.note || ""))
          shotY = pos[from].y <= 50 ? 58 : 42;
        else if (/(?:premier|1er)\s+pot(?:eau|o|au)/i.test(st.note || ""))
          shotY = pos[from].y <= 50 ? 42 : 58;
        else if (/(?:second|deuxi[èe]me|2e)\s+pot(?:eau|o|au)/i.test(coreT) && from === shooterId)
          shotY = pos[from].y <= 50 ? 58 : 42;
        else if (/(?:premier|1er)\s+pot(?:eau|o|au)/i.test(coreT) && from === shooterId)
          shotY = pos[from].y <= 50 ? 42 : 58;
      }
      actions.push({ from, tox: 97, toy: shotY, type: "tir", order: ord++, label });   // le ballon va au but
    } else if (st.action === "course" || st.action === "dribble") {
      // GARDIEN qui REPREND SA PLACE (« retourne dans le but », « reprend sa
      // place ») : sa course revient vers SON but (à gauche) — la cible
      // « but » du moteur est le but ADVERSE, la course partait à contresens.
      if (from === "G" && /retourn\w*|reprend\s+sa\s+place|revien\w*\s+dans\s+(?:son|le)\s+but/i.test(st.note || "")) {
        actions.push({ from, tox: 14, toy: 50, type: "course", order: ord++, label });
        pos[from] = { x: 14, y: 50 };
        continue;
      }
      // PLAQUAGE / CONTACT (rugby, ou tout défenseur qui « arrête » le porteur) :
      // le défenseur VA AU CONTACT de sa cible (course dessinée MÊME courte —
      // sinon le garde anti-course-nulle < 6 unités la supprimait, et le
      // plaquage décrit disparaissait). Un repère de contact matérialise le duel.
      if (/^D\d+$/.test(String(from)) && /plaqu|tackle|plaque|ceinture|au contact|stoppe|arr[êe]te/i.test(st.note || "")
        && st.cible && pos[st.cible]) {
        const tgt = pos[st.cible];
        const cx = keepOut(tgt.x + (pos[from].x < tgt.x ? -3 : 3), tgt.y, 1);
        actions.push({ from, tox: cx, toy: Math.round(tgt.y), type: "course", style: "dashed", order: ord++, label });
        pos[from] = { x: cx, y: tgt.y };
        extraMarkers.push({ type: "contact", x: Math.round((cx + tgt.x) / 2), y: Math.round(tgt.y) });
        continue;
      }
      // PICK ON-BALL : le porteur CONTOURNE l'écran (passe à côté du bloc, du côté de
      // la sortie) puis FILE au cerceau — deux segments, et un lay-up tout près du
      // panier (et non une frappe à mi-distance à travers le défenseur).
      if (pick && from === pick.driver && st.cible === "but" && pos[pick.defender]) {
        const type0 = from === holder ? carryType : "course";
        const sc = pos[pick.defender];
        const wpy = clampN(sc.y + pick.side * 14, 12, 88);
        const wpx = keepOut(sc.x + 5, wpy, 3);
        actions.push({ from, tox: wpx, toy: wpy, type: type0, order: ord++, label });   // contourne le bloc
        const layy = clampN(50 + pick.side * 4, 38, 62), layx = keepOut(91, layy, 1);
        actions.push({ from, tox: layx, toy: layy, type: type0 });                       // file au panier
        pos[from] = { x: layx, y: layy };
        continue;
      }
      // Progression vers la cible : un joueur visé, le but, sinon vers l'avant —
      // toujours bornée HORS de la zone de but. Type effectif = conduite si le
      // joueur porte le ballon, course (sans ballon) sinon.
      let tx, ty;
      // Course de PLACEMENT avant son propre écran : on va SE POSTER juste devant
      // le défenseur à écranter (pas « vers le but »), pour que l'écran soit net.
      // SEULEMENT si l'écran est ENCORE À VENIR : la course d'APRÈS l'écran
      // (« roule vers le panier ») ne doit pas revenir sur le défenseur écranté.
      const stIdx = phase.indexOf(st);
      const screenOn = phase.find((s, si) => si > stIdx && s.acteur === from && s.action === "ecran" && pos[s.cible]);
      // DÉBORDEMENT sur l'aile : on FILE vers la ligne de fond en restant LARGE
      // (côté touche le plus proche), pour pouvoir centrer ensuite — sinon la
      // course « vers le but » dérive vers l'axe (un ailier ne déborde pas au centre).
      const wingRun = /d[ée]bord|sur l['’ ]?aile|\baile\b|\bflanc\b|couloir|ligne de fond|byline/i.test(st.note || "");
      if (screenOn) { tx = keepOut(pos[screenOn.cible].x - 4, pos[screenOn.cible].y, 3); ty = pos[screenOn.cible].y; }
      // « DÉCROCHE » : le joueur SANS ballon vient en RETRAIT vers le porteur
      // (créer le surnombre), il ne s'enfonce pas vers le but.
      else if (/d[ée]croch/i.test(st.note || "") && from !== holder) {
        tx = Math.round(clampN(pos[from].x - 12, 18, 90));
        ty = Math.round(clampN((pos[from].y + (pos[holder]?.y ?? 50)) / 2, 10, 90));
      }
      // APPELS DE COUP DE PIED ARRÊTÉ — la note nomme un POINT PRÉCIS (« appel
      // premier/second poteau », « point de penalty », « entrée de la
      // surface ») : la course vise ce point réel, pas la cible générique.
      // Côté des poteaux : côté du CORNER si c'en est un (locution de côté,
      // « pied gauche » exclu), sinon côté du centreur.
      else if (/(?:premier|1er|second|deuxi[èe]me|2e)\s+pot(?:eau|o|au)|point de penalty|entr[ée]e (?:de la )?surface/i.test(st.note || "")) {
        const n = st.note || "";
        const refY = (() => {
          const c = coreSituationText(spec.text || "");
          if (/\bcorner\b|coup de pied de coin/i.test(c)) {
            const sm = c.match(/(?:c[ôo]t[ée]|depuis (?:la|le c[ôo]t[ée])|corner)\s+(droite?|gauche)/i);
            return sm ? (/gauche/i.test(sm[1]) ? 6 : 94)
              : /\bgauche\b/i.test(c.replace(/pied\s+gauche/gi, "")) ? 6 : 94;
          }
          return pos[centreSt?.acteur]?.y ?? pos[from].y;
        })();
        if (/point de penalty/i.test(n)) { tx = 87; ty = 50; }
        else if (/entr[ée]e (?:de la )?surface/i.test(n)) { tx = 78; ty = 50; }
        else if (/(?:premier|1er)\s+pot/i.test(n)) { tx = 91; ty = refY <= 50 ? 42 : 58; }
        else { tx = 91; ty = refY <= 50 ? 58 : 42; }
        spAppelY[from] = ty;
      }
      else if (crossTarget && from === shooterId) { tx = crossTarget.x; ty = crossTarget.y; }   // appel du finisseur SUR le centre (2e poteau)
      else if ((sport === "basketball" || sport === "basket3x3") && /3\s*p(?:oin)?ts|derri[èe]re l['’]arc|ligne\s+(?:des|[àa])\s+3/i.test(st.note || "")) {
        // RETOUR DERRIÈRE L'ARC (V-cut, écartement) : la course RESSORT à la
        // ligne à 3 points — elle ne file pas au panier même si le tir suit.
        tx = 68; ty = pos[from].y;
      }
      else if (wingRun) {
        // On PROLONGE la conduite jusqu'à la LIGNE DE FOND en collant à la
        // touche : le centre partira ainsi de PRÈS du but (geste classique),
        // et non depuis le centre du terrain. Côté = celui du débordeur.
        const followsCentre = phase.some((s2) => s2.acteur === from && s2.action === "centre");
        ty = pos[from].y <= 50 ? clampN(pos[from].y - 14, 10, 40) : clampN(pos[from].y + 14, 60, 90);
        tx = followsCentre ? keepOut(90, ty, 2) : pos[from].x + 26;
      }
      else if (/crois/i.test(st.note || "") && from !== carrierId
        && pos0[/^A\d+$/.test(String(st.cible || "")) ? st.cible : carrierId]) {
        // CROISÉ : le coureur prend la place que son partenaire vient de
        // QUITTER (les deux trajectoires se croisent en X) — le partenaire
        // visé, sinon le PORTEUR (« croise derrière lui », cible « but »).
        const mate = /^A\d+$/.test(String(st.cible || "")) ? st.cible : carrierId;
        tx = pos0[mate].x; ty = pos0[mate].y;
      }
      else if (st.cible && pos[st.cible]) {
        const tgt = pos[st.cible];
        // On s'arrête DEVANT la cible, du CÔTÉ D'OÙ L'ON VIENT (un presseur
        // ne traverse pas son vis-à-vis pour finir dans son dos)…
        tx = tgt.x + (pos[from].x > tgt.x + 4 ? 8 : -8);
        ty = tgt.y;
        // …et un PORTEUR qui va vers un partenaire (fixation avant la passe,
        // « court vers le centre avec le ballon ») ne recule jamais.
        if (from === holder && /^A\d+$/.test(st.cible)) tx = Math.max(tx, pos[from].x + 6);
        tx = clampN(tx, 6, 92);
      }
      else if (st.cible === "but") {
        ty = clampN((pos[from].y + 50) / 2, 12, 88);
        // Le joueur qui VA TIRER ensuite court/conduit jusqu'à la zone de frappe
        // (près du but), pas seulement +22 — sinon le tir partirait de son propre
        // camp. STRICT : le tir doit être sa PROCHAINE action de balle. S'il
        // REDONNE le ballon entre-temps (jeu en une-deux : appel, remise, puis
        // tir), son appel reste PROGRESSIF (+22) — sinon il sprintait en zone de
        // frappe dès la 1re passe et chaque remise retraversait tout le terrain.
        const nextBall = phase.slice(phase.indexOf(st) + 1).find((s2) => s2.acteur === from && ["passe", "centre", "tir"].includes(s2.action));
        const willShoot = !!nextBall && nextBall.action === "tir";
        tx = willShoot ? 84 : pos[from].x + 22;
        // Course notée « dans le CORNER gauche/droit » (basket) : la cible est
        // le VRAI corner (ligne de fond, côté nommé), pas la zone de frappe —
        // le tir à 3 points partira bien du corner.
        if (["basketball", "basket3x3"].includes(sport) && /corner|\bcoin\b/i.test(st.note || "")) {
          const side = /gauche/i.test(st.note || "") ? 13 : /droit/i.test(st.note || "") ? 87 : (pos[from].y <= 50 ? 13 : 87);
          tx = 85; ty = side;
        }
      }
      else {
        tx = pos[from].x + 16;
        // « crochet intérieur / repique dans l'axe » : la conduite s'infléchit
        // vers l'axe au lieu de filer droit dans son couloir.
        ty = /\baxe\b|int[ée]rieur|repique|recentre/i.test(st.note || "")
          ? Math.round(clampN((pos[from].y + 50) / 2, 34, 66)) : pos[from].y;
      }
      tx = keepOut(tx, ty, 3);
      // ZONE handball : un attaquant filant au BUT déjà AU BORD de la zone,
      // que le clamp elliptique renverrait EN ARRIÈRE (l'arc se resserre vers
      // l'axe) : rien à dessiner — il garde sa position d'aile, le tir/geste
      // suivant partira de là (tir en extension à l'aile).
      if (ZONE && (st.cible === "but" || st.cible == null) && /^A\d+$/.test(String(from)) && tx < pos[from].x - 1) continue;
      // Course quasi NULLE (le joueur est déjà à destination — ex. sa course de
      // réception l'a déjà placé en zone de tir) : rien à dessiner.
      if (Math.hypot(tx - pos[from].x, ty - pos[from].y) < 6) { pos[from] = { x: tx, y: ty }; continue; }
      const type = from === holder ? carryType : "course";
      actions.push({ from, tox: tx, toy: ty, type, order: ord++, label });
      pos[from] = { x: tx, y: ty };
    } else if (st.action === "centre" && crossTarget) {
      // Centre sans cible explicite : il tombe au 2e poteau (point de chute commun).
      actions.push({ from, tox: crossTarget.x, toy: crossTarget.y, type: "centre", order: ord++, label });
      holder = null;   // ballon CENTRÉ (en l'air) : plus personne ne le porte -> les appels suivants sont des courses, pas des conduites
    } else if (st.action === "ecran" && pos[st.cible]) {
      // ÉCRAN : le poseur (pivot) vient AU CONTACT du défenseur ciblé (côté ballon)
      // PUIS pose le bloc -> écran net et LOCAL (et non une diagonale à travers le
      // terrain). S'il porte le ballon, sa venue se rend en conduite.
      const d = pos[st.cible];
      // Pick : l'écran se pose À CÔTÉ du défenseur (côté sortie du porteur) pour le
      // sceller ; sinon, au contact côté ballon (gauche).
      const isPick = pick && from === pick.screener && st.cible === pick.defender;
      const sy = isPick ? clampN(d.y + pick.side * 8, 12, 88) : d.y;
      const sx = isPick ? keepOut(d.x, sy, 3) : keepOut(d.x - 4, d.y, 3);
      if (Math.hypot(pos[from].x - sx, pos[from].y - sy) > 4) {
        actions.push({ from, tox: sx, toy: sy, type: from === holder ? carryType : "course", order: ord++, label: "Vient poser l'écran" });
        pos[from] = { x: sx, y: sy };
      }
      actions.push({ from, to: st.cible, type: "ecran", order: ord++, label });
      involved.add(st.cible);
    } else if (st.cible && pos[st.cible]) {
      // Une passe part TOUJOURS du porteur du ballon. Si l'IA fait « passer » un
      // joueur qui ne l'a pas/plus (double passe sans réception entre les deux),
      // on RÉANCRE la passe sur le porteur réel — sinon une flèche partirait d'un
      // joueur les mains vides. (Sans effet sur une phase cohérente : from===holder.)
      const passLike = st.action === "passe" || st.action === "centre";
      const src = (passLike && holder && holder !== from && pos[holder]) ? holder : from;
      // Passe REDONDANTE vers le porteur lui-même (l'IA fait « passer » à un joueur qui
      // a DÉJÀ le ballon) : on la saute plutôt que de dessiner une auto-passe A->A.
      if (passLike && src === st.cible) continue;
      // RUGBY — PAS D'EN-AVANT DYNAMIQUE : le placement statique ne peut pas
      // satisfaire une chaîne CYCLIQUE (A1->A2->A3->A1) ; si le receveur est
      // DEVANT le passeur au moment de la passe, il se REPLACE en soutien
      // derrière le porteur avant de recevoir.
      if (st.action === "passe" && PASS_BACKWARD_SPORTS.has(sport) && pos[st.cible].x > pos[src].x - 2) {
        const by = pos[st.cible].y;
        const bx = keepOut(Math.max(6, pos[src].x - 6), by, 3);
        if (Math.hypot(pos[st.cible].x - bx, pos[st.cible].y - by) > 3)
          actions.push({ from: st.cible, tox: bx, toy: by, type: "course", order: ord++, label: "Se replace en soutien" });
        pos[st.cible] = { x: bx, y: by };
        involved.add(st.cible);
      }
      // AILIER LANCÉ : le receveur d'une passe de PROGRESSION (« trouve l'ailier
      // dans le couloir », sa prochaine action est une conduite vers l'avant)
      // ne reçoit pas DERRIÈRE le passeur — sa course l'amène devant, dans la
      // course. Hors rugby (passes arrière légitimes).
      if (st.action === "passe" && !PASS_BACKWARD_SPORTS.has(sport) && pos[st.cible] && pos[src] && pos[st.cible].x < pos[src].x - 3) {
        const rNext2 = phase.slice(phase.indexOf(st) + 1).find((s2) => s2.acteur === st.cible && ["passe", "centre", "tir", "dribble", "course"].includes(s2.action));
        if (rNext2 && (rNext2.action === "dribble" || rNext2.action === "course")
          && /lanc[ée]|couloir|profondeur|franchi/i.test((st.note || "") + " " + (rNext2.note || ""))) {
          const ry2 = pos[st.cible].y;
          const rx2 = keepOut(Math.min(88, pos[src].x + 8), ry2, 2);
          actions.push({ from: st.cible, tox: rx2, toy: ry2, type: "course", order: ord++, label: "Se lance dans le couloir" });
          pos[st.cible] = { x: rx2, y: ry2 };
          involved.add(st.cible);
        }
      }
      // PASSE DÉCISIVE : si le RECEVEUR enchaîne par un TIR (sa prochaine action
      // de balle), la passe doit le trouver LANCÉ en zone de frappe — pas à
      // mi-terrain (sans dribble, il ne pourrait pas porter la balle jusqu'à la
      // zone). S'il n'y est pas déjà, sa course de réception PRÉCÈDE la passe.
      if (st.action === "passe" && SHOOTS_AT_GOAL.has(sport) && pos[st.cible] && pos[st.cible].x < 68) {
        const rNext = phase.slice(phase.indexOf(st) + 1).find((s2) => s2.acteur === st.cible && ["passe", "centre", "tir", "dribble"].includes(s2.action));
        if (rNext && rNext.action === "tir") {
          // TIR À 3 POINTS (basket) : le tireur ressort DERRIÈRE l'arc — au
          // sommet (x≈70), sur l'aile (x≈74) ou dans le CORNER (x≈82, près de
          // la ligne de fond, côté du tireur). L'appel l'y amène, le tir part de
          // là : un « tir à 3 points » ne doit jamais partir de sous le panier.
          const noteAll = (st.note || "") + " " + (rNext.note || "");
          const is3 = ["basketball", "basket3x3"].includes(sport)
            && /3\s*p(?:oin)?ts|trois\s+points|derri[èe]re l['’]arc|ligne\s+(?:des|[àa])\s+3|\bcorner\b|angle mort/i.test(noteAll);
          let rx, ry;
          if (is3) {
            const y0 = pos[st.cible].y;
            if (/\bcorner\b|\bcoin\b|angle mort|ligne de fond/i.test(noteAll)) { rx = 82; ry = y0 <= 50 ? 13 : 87; }
            else if (Math.abs(y0 - 50) < 14) { rx = 70; ry = y0; }        // sommet de l'arc
            else { rx = 74; ry = y0 <= 50 ? 28 : 72; }                    // aile
          } else { ry = pos[st.cible].y; rx = keepOut(80, ry, 2); }
          actions.push({ from: st.cible, tox: rx, toy: ry, type: "course", order: ord++, label: is3 ? "Ressort derrière l'arc" : "Appel en zone de tir" });
          pos[st.cible] = { x: rx, y: ry };
        }
      }
      actions.push({ from: src, to: st.cible, type: st.action, order: ord++, label });
      involved.add(st.cible);
      if (passLike) holder = st.cible;   // le ballon change de mains
    } else {
      actions.push({ from, tox: keepOut(pos[from].x + 20, pos[from].y, 3), toy: pos[from].y, type: st.action, order: ord++, label });
    }
  }
  // Défenseurs hors de la chaîne : depuis la ligne de zone, ils SORTENT contester
  // le porteur/tireur (vers le ballon, en restant à l'extérieur de la zone). On ne
  // le fait PAS pour un SYSTÈME complet (le bloc garde sa forme, sinon tout le
  // 4-4-2 « sortirait » de façon illisible).
  const tir = spec.phase.find((st) => st.action === "tir");
  const sy = tir && pos[tir.acteur] ? pos[tir.acteur].y : 50;
  // PRESSING DÉCRIT DANS LE TEXTE (« le joueur rouge 1 vient presser le joueur
  // bleu 1 ») mais ABSENT de la phase IA : la course de pressing est
  // synthétisée à la place de la « sortie » générique.
  const pressOf = {};
  for (const sent of coreSituationText(spec.text).split(/[.;\n!?]/)) {
    if (!/\bpress\w*/i.test(sent)) continue;
    const dmm = sent.match(/(?:rouge|d[ée]fenseur)\s*n?[°o]?\s*(\d)/i);
    const amm = sent.match(/(?:bleue?|porteur|attaquant)\s*n?[°o]?\s*(\d)/i);
    const did = "D" + (dmm ? dmm[1] : 1), aid = amm ? "A" + amm[1] : carrierId;
    if (pos[did] && pos[aid] && !pressOf[did]) pressOf[did] = aid;
  }
  if (!useSystem) for (let i = 0; i < nD; i++) {
    const id = "D" + (i + 1);
    // Défenseur déjà ENGAGÉ (dans la phase, ou posté SUR un attaquant par un
    // marquage explicite/une fixation) : pas de course de sortie synthétique.
    if (involved.has(id) || markOf[id]) continue;
    // Transition : seuls les défenseurs COMPTÉS en repli sprintent vers leur
    // but ; les autres, pris de vitesse, restent figés derrière le ballon.
    if (retreatCount && i >= retreatCount) continue;
    // Presseur « resté haut » : il presse la relance, pas de sortie synthétique.
    if (stayedHigh && i === 0) continue;
    if (pressOf[id]) {
      const tgt = pos[pressOf[id]];
      const tx = Math.round(clampN(tgt.x + (pos[id].x > tgt.x + 4 ? 8 : -8), 6, 92));
      if (Math.hypot(tx - pos[id].x, tgt.y - pos[id].y) > 4)
        actions.push({ from: id, tox: tx, toy: tgt.y, type: "course", style: "dashed", label: "Vient presser" });
      continue;
    }
    // CONSERVATION / possession SANS tir décrit (jeu des 10 passes, garder le
    // ballon) : une « sortie » ou un « repli » vers un but jamais attaqué n'a
    // pas de sens — pas de course défensive synthétique.
    if (!tir && !retreating) continue;
    // COUP DE PIED ARRÊTÉ (corner, coup franc, jet franc) : la défense est
    // POSTÉE dans la surface / dans le mur — aucune course synthétique.
    if (/\bcorner\b|coup de pied de coin|coup franc|jet franc/i.test(coreSituationText(spec.text || "")) && !retreating) continue;
    const ty = Math.round(clampN((pos[id].y + sy) / 2, 16, 84));
    // Défense qui RECULE (« reculent vers leur surface ») : la course revient
    // PROFOND vers le but (repli), au lieu d'une « sortie » à contresens.
    const tx = Math.round(retreating
      ? clampN(boundaryX(ty) - 6, pos[id].x, boundaryX(ty) - 4)
      : clampN(boundaryX(ty) - 12, 12, boundaryX(ty) - 4));
    if (Math.hypot(tx - pos[id].x, ty - pos[id].y) > 4)
      // Un défenseur de MI-TERRAIN renvoyé PROFOND ne « sort » pas : il se
      // replie — le libellé suit le sens réel de la course.
      actions.push({ from: id, tox: tx, toy: ty, type: "course", style: "dashed", label: retreating || tx > pos[id].x + 10 ? "Repli défensif" : "Sortie défensive" });
  }
  return {
    players,
    markers: [{ type: "ballon", x: ballPos.x, y: ballPos.y }, ...extraMarkers],
    actions,
    steps: actions.filter((a) => a.order != null).map((a) => a.label),
  };
}

/* REQUALIFICATION DES ÉCRANS : l'IA classe parfois un écran comme une simple
   « course/conduite vers le but » en ne le décrivant que dans la NOTE
   (« pose un écran sur le défenseur de l'ailier »). On le transforme en deux
   gestes nets — un PLACEMENT (le poseur vient au contact) puis le BLOC posé sur
   le bon défenseur. Cible (marquage individuel, Dk marque Ak) :
   - note « porteur/meneur » -> défenseur du porteur ;
   - note « ailier/aile/tireur » -> défenseur du finisseur ;
   - sinon : défenseur du PARTENAIRE servi juste après l'écran (prochaine passe). */
function withScreensFromNotes(phase, manToMan, carrierId, shooterId, hasPos) {
  if (!Array.isArray(phase)) return phase;
  // POSE d'un écran (≠ « utilise/sort de l'écran », qui est le bénéficiaire).
  const setsScreen = /(?:pose|poser|posent|met|mettre|mettent|fait|font|prend|prendre|prennent)\s+(?:un\s+|une\s+|le\s+|son\s+)?(?:[ée]cran|bloc|screen)|(?:[ée]cran|bloc|screen)\s+(?:sur|pour|du|de\s+l|de\s+la|au\b)/i;
  const usesScreen = /utilise|se\s+sert|sort\s+de|sortie\s+de|derri[èe]re|profite|b[ée]n[ée]ficie/i;
  const out = [];
  phase.forEach((st, i) => {
    const note = String(st.note || "");
    const screeny = st.action !== "ecran" && st.action !== "passe" && st.action !== "tir"
      && setsScreen.test(note) && !usesScreen.test(note)
      // Si l'acteur a DÉJÀ un écran explicite (IA structurée), on ne requalifie pas.
      && !phase.some((s) => s.action === "ecran" && s.acteur === st.acteur);
    if (!screeny) { out.push(st); return; }
    // 1) CIBLE EXPLICITE : l'IA a déjà désigné le défenseur écranté (cible "Dk") —
    //    on l'utilise tel quel, quel que soit l'effectif (pas besoin du marquage
    //    individuel). C'est le cas « course vers D1, note écran ».
    let did = /^D\d+$/.test(String(st.cible || "")) ? st.cible : null;
    // 1bis) Défenseur NOMMÉ DANS LA NOTE (« écran sur le côté droit de D1 »).
    if (!did) { const nm = note.match(/\bD(\d{1,2})\b/); if (nm) did = "D" + parseInt(nm[1], 10); }
    // 1ter) Défense à UN SEUL défenseur : l'écran ne peut être que sur lui.
    if (!did && hasPos("D1") && !hasPos("D2")) did = "D1";
    // 2) Sinon, en MARQUAGE INDIVIDUEL (Dk marque Ak), on DÉDUIT le défenseur écranté
    //    depuis le bénéficiaire : note « porteur/meneur » -> défenseur du porteur ;
    //    « ailier/tireur » -> défenseur du finisseur ; sinon défenseur du partenaire
    //    servi juste après l'écran (prochaine passe).
    if (!did && manToMan) {
      let benef = /\b(porteur|meneur)\b/i.test(note) ? carrierId
        : (/\b(ailier|aile|tireur)\b/i.test(note) && shooterId) ? shooterId : null;
      if (!benef)
        for (let j = i + 1; j < phase.length; j++)
          if (phase[j].action === "passe" && /^A\d+$/.test(phase[j].cible || "")) { benef = phase[j].cible; break; }
      benef = benef || shooterId || carrierId;
      did = benef ? "D" + String(benef).slice(1) : null;
    }
    if (!did || !hasPos(did)) { out.push(st); return; }
    // Requalifié en ÉCRAN sur le bon défenseur ; le poseur viendra au contact
    // (placement géré par la branche « ecran » du moteur, comme un écran explicite).
    out.push({ acteur: st.acteur, action: "ecran", cible: did, note: st.note });
  });
  return out;
}

/* La phase est-elle un JEU DÉPLOYÉ ? = au moins deux passes ENCHAÎNÉES entre
   attaquants (A1->A2, A2->A3, …) : le ballon circule de proche en proche. Un
   surnombre classique (2c1/3c2 avec appels + une seule passe) n'en est pas un. */
function isDeployChain(phase) {
  if (!Array.isArray(phase)) return false;
  // Un ÉCRAN (set-play avec jeu SANS ballon) n'est pas une simple circulation de
  // balle : on ne le réduit pas à une backline déployée, sinon le bloc est perdu.
  if (phase.some((s) => s && (s.action === "ecran" || /[ée]cran|\bbloc\b|\bscreen\b/i.test(String(s.note || ""))))) return false;
  const passes = phase.filter((s) => s && s.action === "passe" && /^A\d+$/.test(s.acteur || "") && /^A\d+$/.test(s.cible || ""));
  if (passes.length < 2) return false;
  // Le ballon circule vers de NOUVEAUX receveurs (A1->A2->A3…). S'il REVIENT à un
  // ancien porteur (A1->A2->A1), c'est un PASSE-ET-VA / jeu à deux, PAS un jeu
  // déployé : on garde la phase littérale (sinon on la réécrit en backline + dribble).
  const seen = new Set([passes[0].acteur]);
  for (let i = 0; i < passes.length; i++) {
    if (i > 0 && passes[i].acteur !== passes[i - 1].cible) return false;   // relais successifs
    if (seen.has(passes[i].cible)) return false;                            // retour à un ancien porteur
    seen.add(passes[i].cible);
  }
  return true;
}

/* JEU DÉPLOYÉ (rugby, ultimate…) : la ligne d'attaquants forme une DIAGONALE
   (backline) — le PORTEUR est en tête (le plus avancé), le ballon circule vers
   l'EXTÉRIEUR de proche en proche, et l'ailier au bout file conclure. Placement
   DÉDIÉ : porteur à une extrémité (passes non croisées), léger décalage en
   profondeur, défenseurs en ligne statique (on ne surcharge pas le schéma). */
function buildDeployedLine(sport, nA, nD, opts = {}) {
  if (sport === "ultimate") return buildUltimateChain(nA, nD, opts);
  if (sport !== "rugby") return buildForwardChain(sport, nA, nD, opts);   // sports à but : avance vers le but
  // RUGBY : backline en DIAGONALE (porteur en tête, passes ARRIÈRE) ; la ligne
  // MONTE collectivement (chaque relais avance en restant DERRIÈRE le porteur
  // précédent) ; l'ailier au bout déborde et APLATIT (essai).
  const median = !!opts.median, corner = !!opts.corner;
  const startX = median ? 42 : 30;
  const att = [];
  for (let i = 0; i < nA; i++) {
    const y = Math.round(nA > 1 ? clampN(20 + i * (60 / (nA - 1)), 12, 88) : 50);
    const x = Math.round(clampN(startX - i * 5, 8, 72));   // léger décalage : chaque relais un cran en retrait
    const role = i === 0 ? "Porteur" : i === nA - 1 ? "Ailier droit" : "Attaquant";
    att.push({ id: "A" + (i + 1), num: i + 1, role, team: "A", color: "#0072B2", x, y });
  }
  // Défense EN FACE des attaquants (et non une ligne lointaine) : chaque
  // défenseur marque l'attaquant qui lui fait face -> D1 se retrouve PRÈS DU
  // LANCEMENT (la mêlée) ; l'ailier au large reste libre (le surnombre 5c4).
  const defs = [];
  for (let i = 0; i < nD; i++) {
    const a = att[Math.min(i, nA - 1)];
    const x = Math.round(clampN(a.x + 11, 12, 88));
    const y = Math.round(clampN(a.y + (i >= nA ? 8 : 0), 10, 90));
    defs.push({ id: "D" + (i + 1), num: i + 1, role: "Défenseur" + (nD > 1 ? " " + (i + 1) : ""), team: "B", color: "#D55E00", x, y });
  }
  const markers = [{ type: "ballon", x: clampN(att[0].x + 5, 6, 96), y: att[0].y }];
  const actions = []; let order = 1;
  // 1) Le porteur (demi de mêlée) lance le jeu et perce balle en main.
  const a1x = clampN(att[0].x + 7, 8, 86);
  actions.push({ from: "A1", tox: a1x, toy: att[0].y, type: "dribble", order: order++, label: "Le porteur lance le jeu" });
  const advX = { A1: a1x };
  // 2) Le ballon CIRCULE vers l'aile ; après chaque passe, le relais MONTE vers
  //    l'avant en restant DERRIÈRE le porteur précédent (la passe reste arrière).
  for (let i = 1; i < nA; i++) {
    const id = "A" + (i + 1);
    actions.push({ from: "A" + i, to: id, type: "passe", order: order++, label: i === 1 ? "Passe au soutien" : null });
    if (i < nA - 1) {
      const tx = clampN(Math.min(att[i].x + 10, advX["A" + i] - 4), 8, 86);
      advX[id] = tx;
      actions.push({ from: id, tox: tx, toy: att[i].y, type: "course", order: order++, label: i === 1 ? "Montée de la ligne" : null });
    }
  }
  // 3) L'ailier déborde au large et aplatit (dans l'angle si précisé).
  const wing = att[nA - 1];
  const wy = corner ? (wing.y >= 50 ? 88 : 12) : wing.y, tryX = 92;
  actions.push({ from: "A" + nA, tox: tryX, toy: wy, type: "dribble", order: order++, label: "L'ailier déborde et aplatit" });
  markers.push({ type: "essai", x: tryX, y: wy });
  return { players: [...att, ...defs], markers, actions, steps: actions.filter((a) => a.label).map((a) => a.label) };
}

/* Sports à BUT (football/futsal/handball/basket) : chaîne de passes qui AVANCE
   vers le but. Porteur en retrait qui FIXE, relais/finisseur de plus en plus
   avancés (passes non croisées), le dernier FRAPPE au but. Défenseurs en
   dernière ligne. (Pour le handball, on garde les joueurs hors de la zone.) */
function buildForwardChain(sport, nA, nD, opts = {}) {
  const median = !!opts.median;
  // « partant de la ligne médiane » -> on DÉMARRE au centre du terrain (≈ médiane),
  // pas dans son propre camp (aligné sur buildTeamAttack). Une ATTAQUE PLACÉE
  // (opts.placed) se joue FACE À LA DÉFENSE, plus profond dans le camp adverse.
  const startX = opts.placed ? 54 : median ? 46 : 18;
  const ZONE = sport === "handball" ? { rx: 23, ry: 36 } : null;
  const boundaryX = (y) => ZONE ? 100 - ZONE.rx * Math.sqrt(1 - Math.min(0.999, Math.abs(y - 50) / ZONE.ry) ** 2) : 96;
  const keepOut = (x, y) => Math.round(clampN(Math.min(x, boundaryX(y) - 3), 6, 92));
  // HAUTEURS : alternance haut/bas par défaut, mais un CÔTÉ EXPLICITE du texte
  // (opts.sides, par position dans la chaîne) prime — « B sur l'aile droite »
  // va en bas (but à droite : droite = y grand), « à l'entrée de la surface »
  // reste dans l'axe. Un joueur NON contraint qui retombe sur la hauteur d'un
  // joueur contraint bascule sur l'aile opposée (sinon l'axe).
  const sides = Array.isArray(opts.sides) ? opts.sides : [];
  const Y_SIDE = { droite: 68, gauche: 32, centre: 50, axe: 50 };
  const ys = Array.from({ length: nA }, (_, i) => (i === 0 ? 50 : (i % 2 ? 32 : 68)));
  for (let i = 0; i < nA; i++) if (Y_SIDE[sides[i]] != null) ys[i] = Y_SIDE[sides[i]];
  for (let i = 0; i < nA; i++) {
    if (Y_SIDE[sides[i]] != null) continue;
    const clash = (y) => ys.some((yy, j) => j !== i && Y_SIDE[sides[j]] != null && Math.abs(yy - y) < 10);
    if (!clash(ys[i])) continue;
    const free = [100 - ys[i], 50, 20, 80].find((y) => !clash(y) && !ys.some((yy, j) => j !== i && Math.abs(yy - y) < 8));
    ys[i] = free != null ? free : !clash(100 - ys[i]) ? 100 - ys[i] : 50;
  }
  // LIBELLÉS FIDÈLES : les notes de la phase IA (opts.notes) priment sur les
  // libellés synthétiques (« passe en profondeur » ne devient plus « Remise »).
  const notes = opts.notes || {};
  const cap = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : null);
  const passNote = (k) => cap(Array.isArray(notes.pass) ? notes.pass[k] : null);
  const att = [];
  for (let i = 0; i < nA; i++) {
    const y = ys[i];
    const x = keepOut(nA > 1 ? clampN(startX + i * ((74 - startX) / (nA - 1)), 8, 82) : startX, y);
    const role = opts.posts
      ? handballPlacedRole(y, { carrier: i === 0 })
      : i === 0 ? (opts.carrierRole || "Porteur") : i === nA - 1 ? "Finisseur"
      : sides[i] === "droite" ? "Ailier droit" : sides[i] === "gauche" ? "Ailier gauche"
      : (nA === 3 ? "Pivot" : "Relais " + i);
    att.push({ id: "A" + (i + 1), num: i + 1, role, team: "A", color: "#0072B2", x, y });
  }
  const fin = att[nA - 1];
  // Défenseurs. Petit surnombre (≤3) : le 1er DEVANT le porteur (qu'on FIXE), les
  // autres en couverture près du but, côté OPPOSÉ au finisseur. ATTAQUE PLACÉE
  // (nD ≥ 4, face à une défense de zone) : RIDEAU étalé sur toute la largeur du but
  // (arc des 6 m en handball), pour que les défenseurs ne s'agglutinent pas.
  const wall = nD >= 4;
  const defs = [];
  // TRANSITION : les DERNIERS défenseurs sont décrits à la poursuite (« en
  // retard, poursuit le porteur ») ou en repli sprinté (« deux rouges reculent
  // vers leur zone ») — placés EN MOUVEMENT (derrière le porteur / entre le
  // ballon et la zone), plus en couverture profonde. Un système chiffré nommé
  // (rideau posé) garde la priorité.
  const namedTiers = wall && Array.isArray(opts.tiers) && opts.tiers.length;
  const chase = namedTiers ? 0 : Math.min(opts.chase || 0, nD);
  const retreat = namedTiers ? 0 : Math.min(opts.retreat || 0, nD - chase);
  const leftBX = (y) => ZONE ? ZONE.rx * Math.sqrt(1 - Math.min(0.999, Math.abs(y - 50) / ZONE.ry) ** 2) : 0;
  // Système chiffré nommé (« zone 1-3-1 », « 2-3 ») : le rideau respecte la
  // géométrie du système (rangées) au lieu d'une ligne plate.
  if (namedTiers) {
    defensiveSystemPlayers(opts.tiers, sport).players.forEach((d) => defs.push(d));
  } else
  for (let i = 0; i < nD; i++) {
    let x, y;
    const kChase = i - (nD - chase), kRet = i - (nD - chase - retreat);
    if (opts.stayedHigh && i === 0 && kChase < 0 && kRet < 0) {
      // Adversaire « resté haut » : il presse le départ de la montée.
      y = 44; x = keepOut(att[0].x + 14, y);
    } else if (kChase >= 0) {
      y = clampN(att[0].y + (kChase % 2 ? -9 : 9), 12, 88);
      x = Math.max(Math.round(leftBX(y)) + 3, Math.round(att[0].x - 10 - kChase * 6), 6);
    } else if (kRet >= 0) {
      y = Math.round(clampN(50 + (kRet - (retreat - 1) / 2) * 14, 18, 82));
      x = keepOut(clampN(att[0].x + 16 + kRet * 6, 30, 62), y);
    }
    else if (wall) { y = clampN(16 + i * (68 / (nD - 1)), 12, 88); x = ZONE ? clampN(boundaryX(y) - 4, 10, 90) : keepOut(76, y); }
    // « déjà replacés dans leur moitié » : le défenseur que le porteur fixe est
    // AUSSI dans sa moitié (x ≥ 52) — pas planté devant le porteur en camp adverse.
    else if (i === 0) { y = 49; x = Math.max(opts.postedHalf ? 54 : 0, keepOut(att[0].x + 18, y)); }
    else { y = fin.y > 50 ? clampN(32 - (i - 1) * 6, 14, 46) : clampN(68 + (i - 1) * 6, 54, 86); x = ZONE ? clampN(boundaryX(y) - 4, 10, 90) : 80; }
    defs.push({ id: "D" + (i + 1), num: i + 1, role: "Défenseur" + (nD > 1 ? " " + (i + 1) : ""), team: "B", color: "#D55E00", x: Math.round(x), y: Math.round(y) });
  }
  const markers = [{ type: "ballon", x: clampN(att[0].x + 5, 6, 96), y: att[0].y }];
  const actions = []; let order = 1;
  // 1) le PORTEUR fixe : conduite VERS le défenseur le plus proche de sa hauteur
  //    (le central du rideau pour une attaque placée), sinon vers l'avant.
  const fixDef = defs.length ? defs.reduce((a, b) => (Math.abs(b.y - att[0].y) < Math.abs(a.y - att[0].y) ? b : a), defs[0]) : null;
  const fixX = fixDef ? keepOut(fixDef.x - 9, att[0].y) : keepOut(att[0].x + 12, att[0].y);
  // Sans dribble : le porteur FIXE en se déplaçant balle en main (course), pas en
  // conduite balle au sol — la conduite (ondulé vert) signifierait un dribble.
  actions.push({ from: "A1", tox: fixX, toy: att[0].y, type: opts.noDribble ? "course" : "dribble", order: order++, label: "Le porteur fixe" });
  // 2) passes intermédiaires (porteur -> relais -> …) jusqu'à l'avant-dernier.
  //    Libellé par défaut fidèle au rôle réel du relais (aile vs pivot).
  for (let i = 1; i < nA - 1; i++) {
    const fallback = i === 1 ? (sides[i] === "droite" || sides[i] === "gauche" ? "Passe à l'aile" : "Passe au pivot") : null;
    actions.push({ from: "A" + i, to: "A" + (i + 1), type: "passe", order: order++, label: passNote(i - 1) || fallback });
  }
  // 3) le FINISSEUR part LANCÉ (course sans ballon), la passe décisive arrive
  //    sur sa position d'arrivée (fantôme), puis il FRAPPE au but.
  const recvX = keepOut(86, fin.y);
  actions.push({ from: "A" + nA, tox: recvX, toy: fin.y, type: "course", order: order++, label: cap(notes.run) || "Appel lancé" });
  actions.push({ from: "A" + (nA - 1), to: "A" + nA, type: "passe", order: order++, label: passNote(nA - 2) || "Remise" });
  // FRAPPE DANS le but : la bouche est centrée (y ~ 40..60). On vise le PREMIER
  // POTEAU (côté du tireur) pour que la frappe rentre, pas à côté. Au BASKET il
  // n'y a pas de poteaux : le tir vise le PANIER (centre), libellé neutre.
  if (sport === "basketball" || sport === "basket3x3") {
    actions.push({ from: "A" + nA, tox: 96, toy: 50, type: "tir", order: order++, label: cap(notes.shot) || "Tir" });
  } else {
    // Un POTEAU NOMMÉ dans la note du tir prime (typos « poto/potau » admises) :
    // « conclut au second poteau » vise le poteau OPPOSÉ au tireur, pas le sien.
    const shotNote = String(notes.shot || "");
    const nearPost = /(?:second|deuxi[èe]me|2e)\s+pot(?:eau|o|au)/i.test(shotNote) ? (fin.y >= 50 ? 42 : 58)
      : /(?:premier|1er)\s+pot(?:eau|o|au)/i.test(shotNote) ? (fin.y >= 50 ? 58 : 42)
      : fin.y >= 50 ? 58 : 42;
    actions.push({ from: "A" + nA, tox: 100, toy: nearPost, type: "tir", order: order++, label: cap(notes.shot) || "Frappe (1er poteau)" });
  }
  // Courses des défenseurs de TRANSITION : le poursuivant revient sur le
  // porteur, les replis sprintent vers leur zone (flèches non ordonnées).
  for (let i = 0; i < nD; i++) {
    const kChase = i - (nD - chase), kRet = i - (nD - chase - retreat), d = defs[i];
    if (!d) continue;
    if (kChase >= 0)
      actions.push({ from: d.id, tox: keepOut(att[0].x + 2, att[0].y), toy: Math.round(att[0].y), type: "course", style: "dashed", label: "Revient défendre" });
    else if (kRet >= 0)
      actions.push({ from: d.id, tox: keepOut(94, d.y), toy: d.y, type: "course", style: "dashed", label: "Repli défensif" });
  }
  // GARDIEN devant le but attaqué (droite) : un tir au but sans gardien n'a pas
  // de sens (et en handball la zone des 6 m lui est réservée).
  const gk = opts.gardien ? [{ id: "G", role: "Gardien", team: "k", color: "#F2C200", x: 94, y: 50 }] : [];
  return { players: [...att, ...defs, ...gk], markers, actions, steps: actions.filter((a) => a.label).map((a) => a.label) };
}

/* CHAÎNE DE BALLON : ordre des porteurs successifs déduit des passes de la phase
   (A1 -> A2 -> A3 …). Sans passe, on retombe sur le tireur (ou A1). */
function ballChain(phase) {
  const passes = (phase || []).filter((s) => s && s.action === "passe" && /^A\d+$/.test(s.acteur || "") && /^A\d+$/.test(s.cible || ""));
  if (!passes.length) {
    const tir = (phase || []).find((s) => s && s.action === "tir");
    return [tir?.acteur || "A1"];
  }
  return [passes[0].acteur, ...passes.map((p) => p.cible)];
}

/* NOTES DE LA PHASE projetées sur la chaîne synthétisée par buildForwardChain :
   libellés des passes (dans l'ordre du ballon), de l'appel du finisseur et du
   tir — le déroulement reste FIDÈLE au texte de l'IA (« passe en profondeur »
   ne devient plus « Remise », « appel croisé » plus « Appel lancé »). */
function chainNotes(phase) {
  const arr = Array.isArray(phase) ? phase : [];
  const chain = ballChain(arr);
  const fin = chain[chain.length - 1];
  return {
    pass: arr.filter((s) => s && s.action === "passe" && /^A\d+$/.test(String(s.acteur || "")) && /^A\d+$/.test(String(s.cible || ""))).map((s) => s.note || null),
    run: (arr.find((s) => s && s.action === "course" && s.acteur === fin && s.note) || {}).note || null,
    shot: (arr.find((s) => s && s.action === "tir") || {}).note || null,
  };
}

/* CONTRE-ATTAQUE BASKET (fast break après rebond) — rend la phase IA LITTÉRALEMENT
   (mêmes passes, même tireur, mêmes libellés) et la stage sur tout le terrain :
   le rebondeur part DANS SA RAQUETTE (panier gauche), les relais s'échelonnent vers
   l'avant (celui de la passe « rond central » est posté au centre), le finisseur
   reçoit DERRIÈRE l'arc à 3 points et tire ; les défenseurs, surpris en attaque,
   se REPLIENT depuis le panier gauche vers leur but (flèches de course).
   Repères terrain (coords 0..100, but attaqué à DROITE) : raquette gauche x≈0-18 ;
   rond central (50,50) ; arc à 3 points ~x≤74 au sommet ; cerceau droit x≈98. */
function buildBasketFastBreak(spec) {
  const nA = spec.attaquants, nD = spec.defenseurs;
  const chain = ballChain(spec.phase);                 // ex. [A1,A2,A3,A4]
  const nC = chain.length;
  const shooterId = (spec.phase.find((s) => s.action === "tir") || {}).acteur || chain[nC - 1];
  // RELAIS AU ROND CENTRAL : la cible de la passe dont la note évoque le centre.
  const centerPass = spec.phase.find((s) => s.action === "passe" && /central|centre|rond/i.test(s.note || ""));
  const centerId = centerPass ? centerPass.cible : null;

  const PAINT_X = 11, ARC_X = 68, HOOP = { x: 98, y: 50 };
  const pos = {}, players = [];
  // Placement de la CHAÎNE : du rebond (raquette) jusqu'à l'arc, en zigzag lisible.
  chain.forEach((id, i) => {
    let x, y;
    if (i === 0) { x = PAINT_X; y = 50; }                              // rebondeur
    else if (i === nC - 1) { x = ARC_X; y = 62; }                      // finisseur (derrière l'arc, aile)
    else { x = Math.round(PAINT_X + (i / (nC - 1)) * (ARC_X - PAINT_X)); y = i % 2 ? 32 : 68; }
    if (id === centerId && i !== 0 && i !== nC - 1) { x = 50; y = 50; } // relais au rond central
    pos[id] = { x, y };
  });
  // Attaquants HORS chaîne : traînards qui remontent en soutien, derrière le ballon.
  const attIds = Array.from({ length: nA }, (_, i) => "A" + (i + 1));
  const trailers = attIds.filter((id) => !chain.includes(id));
  trailers.forEach((id, k) => { pos[id] = { x: 18 + k * 12, y: k % 2 ? 40 : 60 }; });
  attIds.forEach((id) => {
    const p = pos[id] || { x: PAINT_X, y: 50 };
    const role = id === chain[0] ? "Porteur" : id === shooterId ? "Finisseur"
      : id === centerId ? "Relais central" : chain.includes(id) ? "Relais " + chain.indexOf(id) : "Soutien";
    players.push({ id, num: +id.slice(1), role, team: "A", color: "#0072B2", x: p.x, y: p.y });
  });

  // DÉFENSEURS : surpris en attaque sur le panier GAUCHE, groupés là-bas, ils se
  // REPLIENT vers leur but (droite) — une flèche de course par défenseur.
  // Décrits DÉJÀ REPLIÉS (« repliés devant le panier », « repliés dans la
  // raquette ») : postés en protection du cercle, sans course de repli.
  const coreFB = coreSituationText(spec.text || "");
  const posted = /repli[ée]s?[^.;]{0,30}(?:devant|dans|sous|pr[èe]s)\s+(?:le\s+|la\s+|leur\s+)?(?:panier|raquette|cercle)/i.test(coreFB)
    // « déjà replacés (dans leur moitié) » : même verdict — défense en place,
    // pas surprise côté rebond avec courses de repli.
    || /(?:d[ée]j[àa]\s+)?replac[ée]s?\s+dans\s+(?:leur|sa|son)\s+(?:moiti[ée]|camp)|d[ée]fenseurs?\s+(?:sont|d[ée]j[àa])\s+replac[ée]s/i.test(coreFB);
  // POURSUIVANT (« un troisième, en retard, poursuit ») : le dernier défenseur
  // part DERRIÈRE le porteur, course de poursuite — pas posté ni en repli.
  const fbChase = /en retard|[àa] la poursuite|poursuit/i.test(coreFB) ? 1 : 0;
  // Adversaire « RESTÉ HAUT » : le premier défenseur presse le rebondeur.
  const fbHigh = /rest[ée]s?\s+haut/i.test(coreFB);
  const defStart = posted ? [[84, 42], [82, 60], [88, 30], [88, 70], [78, 50]]
    : [[33, 38], [29, 64], [20, 24], [20, 76], [39, 50]];
  const actions = []; let order = 1;
  for (let i = 0; i < nD; i++) {
    let [dx, dy] = defStart[i % defStart.length];
    if (fbHigh && i === 0) { dx = 24; dy = 44; }
    else if (fbChase && i === nD - 1 && nD >= 2) { dx = 16; dy = 64; }
    const d = { id: "D" + (i + 1), num: i + 1, role: "Défenseur" + (nD > 1 ? " " + (i + 1) : ""), team: "B", color: "#D55E00", x: dx, y: dy };
    players.push(d); pos[d.id] = { x: dx, y: dy };
  }

  // ACTIONS = la phase IA, LITTÉRALE (passes + tir), dans l'ordre, avec ses notes.
  const LABEL = { passe: "Passe", tir: "Tir", course: "Course", dribble: "Conduite" };
  for (const st of spec.phase) {
    const label = st.note || LABEL[st.action] || st.action;
    if (st.action === "tir") {
      // Tireur DÉPLACÉ par une course précédente (corner…) : le tir part de sa
      // position COURANTE, pas de sa position de départ. Un « tir dans le
      // CORNER » (3 points) part du VRAI corner, côté du tireur.
      const p0 = players.find((pp) => pp.id === st.acteur);
      let cur = pos[st.acteur];
      if (/corner|\bcoin\b/i.test(st.note || "") && p0) cur = { x: 85, y: (cur?.y ?? p0.y) >= 50 ? 86 : 14 };
      const moved = p0 && cur && Math.hypot(cur.x - p0.x, cur.y - p0.y) > 2;
      if (moved) pos[st.acteur] = cur;
      actions.push({ from: st.acteur, ...(moved ? { fromx: Math.round(cur.x), fromy: Math.round(cur.y) } : {}), tox: HOOP.x, toy: HOOP.y, type: "tir", order: order++, label });
    }
    else if (st.action === "passe" && pos[st.cible]) actions.push({ from: st.acteur, to: st.cible, type: "passe", order: order++, label });
    else if ((st.action === "course" || st.action === "dribble") && pos[st.acteur]) {
      // Course notée « dans le CORNER » (tir à 3 points du corner) : la cible
      // est le VRAI corner (ligne de fond, côté du joueur), pas l'axe.
      const toCorner = /corner|\bcoin\b/i.test(st.note || "");
      const tx = toCorner ? 85 : clampN(pos[st.acteur].x + 16, 6, 94);
      const ty = toCorner ? (pos[st.acteur].y >= 50 ? 86 : 14) : pos[st.acteur].y;
      actions.push({ from: st.acteur, tox: tx, toy: ty, type: st.action, order: order++, label });
      // Suivi de position : le geste SUIVANT (tir du corner, passe) part de là.
      pos[st.acteur] = { x: tx, y: ty };
    }
  }
  // Repli des défenseurs (après le déroulé de la phase) : course vers leur but.
  // Déjà postés devant le panier : pas de course (ils y sont). Le POURSUIVANT
  // revient sur le porteur, le presseur « resté haut » ne se replie pas — et
  // un défenseur dont la PHASE décrit déjà la course ne reçoit pas de doublon.
  const dInPhase = new Set(spec.phase.filter((s) => /^D\d+$/.test(String(s.acteur || "")) && (s.action === "course" || s.action === "dribble")).map((s) => s.acteur));
  for (let i = 0; i < nD; i++) {
    const id = "D" + (i + 1), d = pos[id];
    if (dInPhase.has(id)) continue;
    if (fbChase && i === nD - 1 && nD >= 2) {
      actions.push({ from: id, tox: 38, toy: 52, type: "course", style: "dashed", label: "Revient défendre" });
      continue;
    }
    if (posted || (fbHigh && i === 0)) continue;
    actions.push({ from: id, tox: clampN(d.x + 36, 12, 80), toy: Math.round((d.y + 50) / 2), type: "course", style: "dashed", label: i === 0 ? "Repli défensif" : null });
  }

  const ball = pos[chain[0]] || { x: PAINT_X, y: 50 };
  return {
    players,
    markers: [{ type: "ballon", x: clampN(ball.x + 4, 4, 96), y: ball.y }],
    actions,
    steps: actions.filter((a) => a.order != null).map((a) => a.label),
  };
}

/* Passes EFFECTIVES d'une phase ultimate, sous forme {acteur, cible} d'attaquants.
   Une « passe vers but » (lob dans l'en-but) est résolue vers le CUTTER qui
   réceptionne (prochain « course -> but »), pour que la passe décisive et le
   bon marqueur soient rendus. */
function ultimatePasses(phase) {
  const arr = Array.isArray(phase) ? phase : [];
  const out = [];
  for (let i = 0; i < arr.length; i++) {
    const s = arr[i];
    if (!s || s.action !== "passe" || !/^A\d+$/.test(s.acteur || "")) continue;
    if (/^A\d+$/.test(s.cible || "")) { out.push({ acteur: s.acteur, cible: s.cible }); continue; }
    if (s.cible === "but") {
      const other = s.acteur === "A1" ? "A2" : "A1";
      let recv = (arr.slice(i + 1).find((x) => x.action === "course" && x.cible === "but" && /^A\d+$/.test(x.acteur || "")) || {}).acteur || other;
      if (recv === s.acteur) recv = other;
      out.push({ acteur: s.acteur, cible: recv });
    }
  }
  return out;
}

/* ULTIMATE GIVE-AND-GO (le disque circule entre 2 attaquants A1<->A2 en
   PROGRESSANT vers l'en-but, en zig-zag pour contourner le défenseur central).
   No-traveling : le PORTEUR ne court jamais ; c'est le RECEVEUR qui se démarque
   (course) avant chaque passe. Dernière réception DANS l'en-but (essai). */
function buildUltimateGiveGo(spec, passes, nD) {
  const startX = (spec.depart === "ligne_mediane" || spec.depart === "camp_adverse") ? 28 : 16;
  const endX = 92, n = passes.length;
  const step = (endX - startX) / n;
  const lane = (k) => (k % 2 === 0 ? 70 : 30);                 // zig-zag bas/haut LARGE (laisse le centre libre)
  const P = (k) => ({ x: Math.round(clampN(startX + k * step, 8, endX)), y: lane(k) });
  const firstHolder = passes[0].acteur, other = firstHolder === "A1" ? "A2" : "A1";
  const pos = {};
  pos[firstHolder] = P(0);
  pos[other] = { x: startX, y: lane(1) };                       // près du départ, couloir opposé
  const players = [
    { id: "A1", num: 1, role: "Handler", x: pos.A1.x, y: pos.A1.y, color: "#0072B2", team: "A" },
    { id: "A2", num: 2, role: "Receveur", x: pos.A2.x, y: pos.A2.y, color: "#0072B2", team: "A" },
  ];
  // Défenseur au CENTRE, posé À LA HAUTEUR d'un relais (sur l'axe, à ~y50) : les
  // passes contournent par les couloirs larges (haut/bas) sans le traverser.
  const dm = clampN(Math.round(n / 2), 1, Math.max(1, n - 1));
  // Plusieurs défenseurs : ÉTAGÉS le long de l'axe (jamais superposés au même point).
  for (let i = 0; i < nD; i++)
    players.push({ id: "D" + (i + 1), num: i + 1, role: "Défenseur" + (nD > 1 ? " " + (i + 1) : ""),
      x: clampN(P(dm).x + i * 10, 8, 88), y: Math.round(clampN(50 + (i - (nD - 1) / 2) * 16, 18, 82)), color: "#D55E00", team: "B" });
  const markers = [{ type: "disque", x: clampN(pos[firstHolder].x + 4, 4, 96), y: pos[firstHolder].y }];
  const actions = []; let ord = 1;
  for (let i = 0; i < n; i++) {
    const a = passes[i].acteur, b = passes[i].cible, target = P(i + 1);
    if (Math.hypot(pos[b].x - target.x, pos[b].y - target.y) > 3) {   // le RECEVEUR se démarque (course sans disque)
      actions.push({ from: b, tox: target.x, toy: target.y, type: "course", order: ord++, label: i === n - 1 ? "Démarquage dans l'en-but" : (i === 0 ? "Démarquage" : null) });
      pos[b] = { ...target };
    }
    actions.push({ from: a, to: b, type: "passe", order: ord++, label: i === 0 ? "Passe vers l'avant" : (i === n - 1 ? "Passe décisive" : null) });
  }
  const last = passes[n - 1].cible;
  markers.push({ type: "essai", x: pos[last].x, y: pos[last].y });
  return {
    players, markers, actions,
    steps: ["Passe vers l'avant puis démarquage (le porteur ne court pas)", "Le disque progresse en contournant le défenseur", "Réception dans l'en-but"],
  };
}

/* ULTIMATE : chaîne de passes vers l'AVANT. Le HANDLER est à gauche, les
   receveurs progressent vers la DROITE, et le dernier RÉCEPTIONNE dans l'en-but.
   Pas de portage (no traveling) : le disque circule par passes ; seul le dernier
   receveur (sans disque) fait une course pour attaquer la profondeur. */
function buildUltimateChain(nA, nD, opts = {}) {
  const n = Math.max(2, nA);
  // Terrain d'ultimate LONG : on évite la passe qui TRAVERSE tout le terrain. À 2
  // joueurs (handler + cutter), le handler TEMPORISE vers le centre et le cutter
  // part de près de lui pour attaquer la profondeur ; en CHAÎNE (n>2), les relais
  // avancent le disque depuis l'arrière (le handler peut rester en retrait).
  const endX = 90;
  const startX = n === 2 ? 46 : 14, cutFrom = n === 2 ? startX + 12 : endX - 16;
  // CÔTÉS EXPLICITES (opts.sides, par index — mêmes conventions que
  // buildForwardChain : droite = bas / y grand) : un cutter « sur l'aile
  // droite » prend SA hauteur au lieu de l'alternance zig-zag par défaut.
  const sides = Array.isArray(opts.sides) ? opts.sides : [];
  const Y_SIDE = { droite: 66, gauche: 34, centre: 50 };
  const att = [];
  for (let i = 0; i < n; i++) {
    const x = Math.round(n > 1 ? clampN(startX + i * ((cutFrom - startX) / (n - 1)), 8, endX) : startX);
    const y = Y_SIDE[sides[i]] != null ? Y_SIDE[sides[i]] : (i === 0 ? 50 : (i % 2 ? 34 : 66));
    const role = i === 0 ? "Handler" : i === n - 1 ? "Cutter" : "Relais " + i;
    att.push({ id: "A" + (i + 1), num: i + 1, role, team: "A", color: "#0072B2", x, y });
  }
  const last = att[n - 1], lastY = last.y;
  const defs = [];
  for (let i = 0; i < nD; i++) {
    // À 2 joueurs, « cutFrom - 10 » posait le marqueur SUR le handler (disques
    // superposés à 2 unités) — et le décoller dans l'axe le mettait dans le
    // couloir de passe : le marqueur se poste côté FERMÉ, hors de l'axe
    // handler->cutter (à l'opposé du cutter en hauteur).
    const y = Math.round(nD > 1 ? clampN(30 + i * (40 / (nD - 1)), 16, 84)
      : n === 2 ? (last.y <= 50 ? 58 : 42) : 50);
    defs.push({ id: "D" + (i + 1), num: i + 1, role: "Défenseur" + (nD > 1 ? " " + (i + 1) : ""), team: "B", color: "#D55E00", x: Math.round(clampN(cutFrom - 10, att[0].x + 5, 82)), y });
  }
  const markers = [{ type: "disque", x: clampN(att[0].x + 4, 4, 96), y: att[0].y }];
  const actions = []; let order = 1;
  for (let i = 1; i < n - 1; i++)
    actions.push({ from: "A" + i, to: "A" + (i + 1), type: "passe", order: order++, label: i === 1 ? "Passe au relais" : null });
  // Le dernier receveur (SANS disque) attaque la profondeur, puis réceptionne en zone.
  actions.push({ from: "A" + n, tox: endX, toy: lastY, type: "course", order: order++, label: "Attaque la profondeur" });
  actions.push({ from: "A" + (n - 1), to: "A" + n, type: "passe", order: order++, label: "Passe dans la zone" });
  markers.push({ type: "essai", x: endX, y: lastY });
  return { players: [...att, ...defs], markers, actions, steps: actions.filter((a) => a.label).map((a) => a.label) };
}

/* Sports d'invasion où la passe se joue vers l'ARRIÈRE/latérale (le receveur
   ne peut pas être plus avancé que le porteur). Attaque vers la droite, donc
   « plus avancé » = x plus grand. */
/* OVERLAY « COLONNES / FILES D'ATTENTE » — « 2 colonnes de 3 joueurs derrière
   la ligne médiane, les deux premiers attaquent » : les SUIVEURS attendent en
   file derrière chaque attaquant de départ. Générique toutes routes ; inactif
   quand la scène contient déjà ses files (ateliers : effectif complet dessiné). */
export function addWaitingColumns(scene, text) {
  const t = coreSituationText(String(text || "").toLowerCase());
  // Effectif par colonne optionnel (« deux files de joueurs ») : défaut 3.
  const m = t.match(/(\d|deux|trois)\s*(?:colonnes?|files?)\s*de\s*(?:(\d|deux|trois|quatre|cinq|six)\s*)?(?:joueurs?|[ée]l[èe]ves?|attaquants?)/);
  // « 2 colonnes DERRIÈRE la ligne de fond » (sans objet, orthographe tolérée) :
  // files positionnelles. « Vagues de N » : la vague SUIVANTE attend derrière
  // chaque attaquant (une rangée d'attente).
  const mPos = !m && t.match(/(\d|deux|trois)\s*colonn?es?\s+derr?i[èe]re?\b/);
  const mWave = !m && !mPos && t.match(/vagues? de (\d|deux|trois|quatre)/);
  if (!m && !mPos && !mWave) return addRoleWaitingColumns(scene, t);
  const W = { deux: 2, trois: 3, quatre: 4, cinq: 5, six: 6 };
  const src = m || mPos || mWave;
  const nCols = mWave ? 4 : clampN(W[src[1]] || parseInt(src[1], 10), 1, 4);
  const per = m && m[2] ? clampN(W[m[2]] || parseInt(m[2], 10), 2, 6) : mWave ? 2 : 3;
  const players = Array.isArray(scene.players) ? scene.players : [];
  // Compte les joueurs de champ Y COMPRIS les « En attente » : si un gabarit a
  // DÉJÀ dessiné ses files complètes (heads + suiveurs, ex. tir-rebond en deux
  // colonnes), l'effectif atteint nCols*per -> on ne redouble pas la colonne.
  const field = players.filter((p) => (p.team === "A" || p.team === "B") && !/gardien|observ/i.test(p.role || ""));
  if (field.length >= nCols * per) return scene;               // files déjà dessinées (atelier)
  const heads = players.filter((p) => p.team === "A" && !/gardien|observ|attente/i.test(p.role || ""));
  if (!heads.length) return scene;
  let num = 0;
  for (const p of players) num = Math.max(num, +p.num || 0);
  for (const h of heads.slice(0, nCols)) {
    for (let k = 1; k < per; k++) {
      // Tête déjà près du bord (départ ligne de fond) : la file s'empile en
      // hauteur plutôt que d'écraser tous les suiveurs sur x=3.
      const wx = Math.round(h.x - 7 * k);
      players.push({ id: `${h.id}w${k}`, num: ++num, role: "En attente", team: "A", color: h.color || "#0072B2",
        x: Math.max(3, wx), y: wx < 3 ? Math.round(clampN(h.y + 6 * k * (h.y <= 50 ? -1 : 1), 4, 96)) : h.y });
    }
  }
  return scene;
}

/* OVERLAY « COLONNES DE RÔLE » — « colonne de porteurs et colonne de soutiens »,
   « colonne d'attaquants … colonne de défenseurs » (SANS effectif chiffré) : les
   suiveurs « En attente » sont dessinés derrière chaque tête de colonne. Sens de
   la file : équipe A (attaque vers la droite) -> en retrait vers la gauche ;
   équipe B (défend le but de droite) -> en retrait vers la droite. Inactif si la
   scène a déjà ses files (« En attente ») ou est trop peuplée (phase de jeu). */
const ROLE_COLUMN_RE = /(?:\b(\d|deux|trois|quatre)\b\s+)?colonn?es? d(?:e |'|’)\s*(attaquants?|d[ée]fenseurs?|porteurs?|soutiens?|plaqueurs?|tireurs?|pas{1,2}eurs?|arri[èe]res?|pivots?|ailiers?|r[ée]cept?ionn?eurs?|serveurs?|relanceurs?|lanceurs?|receveurs?|frappeurs?|rebondeurs?)/g;
function addRoleWaitingColumns(scene, t) {
  const found = [...t.matchAll(ROLE_COLUMN_RE)];
  if (!found.length) return scene;
  const players = Array.isArray(scene.players) ? scene.players : [];
  if (players.some((p) => /attente/i.test(p.role || ""))) return scene;   // files déjà dessinées
  const field = players.filter((p) => (p.team === "A" || p.team === "B") && !/gardien|observ/i.test(p.role || ""));
  if (!field.length || field.length > 4) return scene;                    // phase de jeu peuplée : on ne touche pas
  // Têtes de colonne : les joueurs dont le RÔLE correspond à la mention
  // (« 2 colonnes de réceptionneurs » -> derrière les réceptionneurs, pas le
  // serveur ; le compte en préfixe = nombre de têtes du même rôle). Repli :
  // premiers joueurs de champ (comportement historique sans rôle correspondant).
  const WCOL = { deux: 2, trois: 3, quatre: 4 };
  const norm = (s) => String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z]/g, "");
  const heads = [];
  let wanted = 0;
  for (const m of found) {
    const count = clampN(WCOL[m[1]] || parseInt(m[1], 10) || 1, 1, 4);
    wanted += count;
    const stem = norm(m[2]).slice(0, 5);
    const match = field.filter((p) => norm(p.role).startsWith(stem) && !heads.includes(p));
    for (const p of match.slice(0, count)) heads.push(p);
  }
  // Complément : une mention sans joueur au rôle correspondant retombe sur les
  // premiers joueurs de champ libres (comportement historique).
  wanted = Math.min(wanted, field.length);
  for (const p of field) { if (heads.length >= wanted) break; if (!heads.includes(p)) heads.push(p); }
  let num = 0;
  for (const p of players) num = Math.max(num, +p.num || 0);
  for (const h of heads) {
    const dx = h.team === "B" ? 7 : -7;
    for (let k = 1; k <= 2; k++)
      players.push({ id: `${h.id}rw${k}`, num: ++num, role: "En attente", team: h.team, color: h.color,
        x: Math.round(clampN(h.x + dx * k, 3, 97)), y: h.y });
  }
  return scene;
}

/* OVERLAY « CHANGEMENT DE COLONNE AU FILET » — drill de filet 1c1 où chacun
   rejoint l'autre colonne après son coup (« le serveur sert, le relanceur
   contre, puis chacun rejoint l'autre colonne ») : les coups du rally routé sont
   CONSERVÉS, on ajoute les suiveurs en attente derrière chaque joueur et les
   arcs de rotation croisés. Inactif si la scène a déjà une rotation (tournante,
   montante) ou n'est pas un simple 1c1 (doubles, multi-terrains). */
export function addNetColumnSwap(scene, text) {
  const t = c0(text || "");
  if (!/rejoi\w+ l['’]autre colonne|colonne oppos[ée]e|chacun (?:rejoint|change de (?:colonne|c[ôo]t[ée]))/.test(t)) return scene;
  const players = Array.isArray(scene.players) ? scene.players : [];
  const field = players.filter((p) => p.team === "A" || p.team === "B");
  if (field.length !== 2) return scene;
  if ((scene.actions || []).some((a) => a.type === "rotation")) return scene;
  if (players.some((p) => /attente/i.test(p.role || ""))) return scene;
  let num = 0;
  for (const p of players) num = Math.max(num, +p.num || 0);
  const [a, b] = field;
  for (const h of field) {
    const dx = h.x < 50 ? -7 : 7;   // file en retrait, DOS au filet
    for (let k = 1; k <= 2; k++)
      players.push({ id: `${h.id}nw${k}`, num: ++num, role: "En attente", team: h.team, color: h.color,
        x: Math.round(clampN(h.x + dx * k, 3, 97)), y: h.y });
  }
  const acts = Array.isArray(scene.actions) ? scene.actions : (scene.actions = []);
  const maxOrd = Math.max(0, ...acts.map((x) => +x.order || 0));
  acts.push({ fromx: a.x, fromy: a.y, tox: b.x + (b.x < 50 ? -17 : 17), toy: b.y, type: "rotation", order: maxOrd + 1, bulge: 6, label: "Rejoint l'autre colonne" });
  acts.push({ fromx: b.x, fromy: b.y, tox: a.x + (a.x < 50 ? -17 : 17), toy: a.y, type: "rotation", order: maxOrd + 2, bulge: 6 });
  return scene;
}

/* OVERLAY « TÂCHE APRÈS LE SERVICE » (filet) — « après son service, le serveur
   court toucher la ligne des 3 mètres / recule au fond avant le renvoi » : une
   course ORDONNÉE est intercalée juste après le service, vers le repère nommé.
   Les numéros suivants sont décalés (la chronologie reste entière). */
export function addPostServiceRun(scene, text, sport) {
  const t = c0(text || "");
  // La PHRASE de la tâche : après « après son service » ou « sert …, puis ».
  const sm = t.match(/apr[èe]s (?:son|le|chaque) service([^.;]{0,90})/) || t.match(/\bsert\b[^.;]{0,30}?,?\s*puis([^.;]{0,90})/);
  if (!sm) return scene;
  const sent = sm[1] || "";
  // VERBE de déplacement exigé — « du fond du COURT » (nom) ne compte pas.
  if (!/\bcourt\b\s+(?:toucher|vers|jusqu)|\bva\b\s|recule|touche[r]?\s+(?:la|une) ligne|monte[r]? au filet|se replace/.test(sent)) return scene;
  const acts = Array.isArray(scene.actions) ? scene.actions : [];
  const serve = acts.filter((a) => a.type === "renvoi" && a.from).sort((a, b) => (a.order || 99) - (b.order || 99))[0];
  const server = serve && (scene.players || []).find((p) => p.id === serve.from);
  if (!server) return scene;
  if (acts.some((a) => a.from === server.id && a.type === "course" && /apr[èe]s le service|3 m|fond|filet/i.test(a.label || ""))) return scene;
  // Cible de la course : repère nommé (3 m AVANT fond — « fond du court » est
  // ambiant), du CÔTÉ du serveur.
  const left = server.x < 50;
  let tox, label;
  if (/3\s*m|trois m[èe]tres|ligne d'attaque/.test(sent)) { tox = left ? 33 : 67; label = "Après le service : touche la ligne des 3 m"; }
  else if (/filet/.test(sent)) { tox = left ? 42 : 58; label = "Après le service : monte au filet"; }
  else if (/fond/.test(sent)) { tox = left ? 6 : 94; label = "Après le service : recule au fond"; }
  else { tox = left ? Math.min(server.x + 14, 44) : Math.max(server.x - 14, 56); label = "Se replace après le service"; }
  if (Math.abs(tox - server.x) < 6) return scene;          // déjà sur place (TT au fond)
  const so = serve.order || 1;
  for (const a of acts) if ((a.order || 0) > so) a.order = a.order + 1;
  acts.push({ from: server.id, tox, toy: server.y, type: "course", order: so + 1, label });
  return scene;
}

/* TÂCHE APRÈS LA RÉCEPTION (filet) — « après chaque réception en manchette,
   toucher la ligne des 3 mètres puis se replacer » : même famille de consigne
   que l'après-service, mais pour le RÉCEPTIONNEUR (le joueur qui joue la
   première balle APRÈS le service). */
export function addPostReceptionRun(scene, text, sport) {
  const t = c0(text || "");
  const rm = t.match(/apr[èe]s (?:sa|la|chaque) r[ée]ception([^.;]{0,90})/);
  if (!rm) return scene;
  const sent = rm[1] || "";
  if (!/toucher?\s+(?:la|une) ligne|3\s*m|trois m[èe]tres|se replace|recule|monte[r]? au filet/.test(sent)) return scene;
  const acts = Array.isArray(scene.actions) ? scene.actions : [];
  const renvois = acts.filter((a) => a.type === "renvoi" && a.from).sort((a, b) => (a.order || 99) - (b.order || 99));
  if (!renvois.length) return scene;
  // Le réceptionneur = premier frappeur DIFFÉRENT du serveur (1er renvoi).
  const reception = renvois.find((a) => a.from !== renvois[0].from);
  const recv = reception && (scene.players || []).find((p) => p.id === reception.from);
  if (!recv) return scene;
  if (acts.some((a) => a.from === recv.id && a.type === "course" && /r[ée]ception/i.test(a.label || ""))) return scene;
  // Position RÉELLE du réceptionneur au moment de la réception : le rendu suit
  // les courses (son « déplacement sur la balle » l'a amené au point de chute) —
  // la course de replacement part de là, pas de sa pastille de départ.
  let px = recv.x, py = recv.y;
  for (const a of acts) {
    if (a.from === recv.id && a.type === "course" && (a.order || 0) <= (reception.order || 99) && a.tox != null) { px = a.tox; py = a.toy; }
  }
  const left = px < 50;
  let tox, label;
  if (/3\s*m|trois m[èe]tres|ligne d'attaque/.test(sent)) { tox = left ? 33 : 67; label = "Après la réception : touche la ligne des 3 m puis se replace"; }
  else if (/filet/.test(sent)) { tox = left ? 42 : 58; label = "Après la réception : monte au filet"; }
  else { tox = left ? Math.min(px + 14, 44) : Math.max(px - 14, 56); label = "Se replace après la réception"; }
  if (Math.abs(tox - px) < 6) return scene;
  const ro = reception.order || 2;
  for (const a of acts) if ((a.order || 0) > ro) a.order = a.order + 1;
  acts.push({ from: recv.id, tox, toy: py, type: "course", order: ro + 1, label });
  return scene;
}

/* TEXTE DE LA SITUATION NOMINALE d'une fiche de séance : coupe les sections
   VARIABLES / RÉGULATIONS / CRITÈRES / SÉCURITÉ, qui décrivent des évolutions
   conditionnelles (« si trop facile, défenseur en retard ») et ne doivent pas
   influencer le PLACEMENT de la situation de départ. */
export function coreSituationText(text) {
  // Une SITUATION ÉVOLUTIVE (« Situation 2 — … », « Évolutions : … »,
  // « Variante : … », « Étape 2 : … ») décrit la SUITE de la leçon : la
  // situation NOMINALE dessinée est celle qui précède ces marqueurs (décision
  // produit — les évolutions restent dans la pédagogie).
  return String(text || "").split(/\b(?:variables?(?:\s+didactiques?)?|r[ée]gulations?|crit[èe]res?\s+de\s+r[ée]ussite|s[ée]curit[ée]|simplifications?|complexifications?|si\s+trop\s+(?:facile|difficile)|[ée]volutions?\s*(?:possibles?)?\s*:|variantes?\s*:|situation\s*2\s*[:—–-]|[ée]tape\s*2\s*[:—–-])\s*/i)[0];
}

const PASS_BACKWARD_SPORTS = new Set(["rugby"]);
/* Recule chaque receveur derrière son passeur (x strictement <). Mute les
   joueurs et, si fourni, la table de positions `pos`. Sans effet hors rugby. */
function applyBackwardPasses(players, passes, sport, pos) {
  if (!PASS_BACKWARD_SPORTS.has(sport)) return;
  const byId = {}; players.forEach((p) => { byId[p.id] = p; });
  for (const { from, to } of passes) {
    const a = byId[from], b = byId[to];
    if (!a || !b || b.x <= a.x - 2) continue;     // déjà derrière : rien à faire
    b.x = Math.round(clampN(a.x - 6, 6, 90));
    if (pos && pos[b.id]) pos[b.id].x = b.x;
  }
}

/* ---- Famille CIBLE : tir à l'arc & pétanque ------------------------------- */
function buildArchery(text) {
  const t = c0(text || "");
  // Nombre de flèches (« volée de 3 flèches », « 5 flèches ») ; défaut 3.
  const NUMW = { une: 1, deux: 2, trois: 3, quatre: 4, cinq: 5, six: 6 };
  const fm = t.match(/(\d+|une|deux|trois|quatre|cinq|six)\s*fl[èe]ches?/) || t.match(/vol[ée]e de\s*(\d+|deux|trois|quatre|cinq|six)/);
  const n = clampN(fm ? (NUMW[fm[1]] != null ? NUMW[fm[1]] : parseInt(fm[1], 10) || 3) : 3, 1, 6);
  const players = [{ id: "T1", role: "Tireur", x: 12, y: 50, color: "#0072B2", team: "A" }];
  // CIBLE (le jaune) à droite, là où les flèches se groupent + LIGNE de tir devant
  // l'archer — sinon les flèches partaient vers le vide.
  const markers = [{ type: "cible", x: 88, y: 50 }];
  // Flèches GROUPÉES au centre (jaune), légèrement étalées pour la lisibilité.
  const actions = [];
  for (let i = 0; i < n; i++) {
    const toy = Math.round(clampN(50 + (i - (n - 1) / 2) * 3, 44, 56));
    actions.push({ from: "T1", tox: 88, toy, type: "tir", order: i + 1, label: "Flèche " + (i + 1) });
  }
  // BLASON / ZONES DE SCORE : quand le texte parle de points, de couronnes ou
  // du « jaune » qui vaut le plus, on ajoute l'encart-légende (cible détaillée en
  // anneaux colorés + zone visée), pour rendre visible le barème de la cible.
  if (/blason|couronnes?|le jaune|\bpoints?\b|score|compter|marque\b/.test(t))
    markers.push({ type: "cible_visee", x: 90, y: 15, text: "Zone visée : le jaune" });
  // Distance demandée (« à 18 mètres ») : cotée sous la trajectoire.
  const zones = [{ x: 20, y: 30, w: 0, h: 40, style: "solid", label: "Pas de tir" }];   // ligne de tir
  const dm = t.match(/(\d+)\s*m(?:[èe]tres?)?\b/);
  if (dm) zones.push({ x: 20, y: 72, w: 68, h: 0, style: "dashed", dim: dm[1] + " m" });
  return { players, markers, zones, actions, steps: ["Se placer sur la ligne de tir", "Viser le centre", "Grouper les flèches"] };
}
/* Tir à l'arc en ATELIERS : plusieurs POSTES de tir alignés sur la ligne, une
   CIBLE par poste, distance cotée, ramassage et rotation au signal (sécurité).
   Le terrain ne dessine PAS sa cible centrale unique (terrainOpts.stations). */
/* LANCER SUR CIBLE (frisbee, vortex, balle… sur des cônes/quilles-cibles à
   abattre) : N FILES de lanceurs à GAUCHE (un plot de lancer + l'engin + une file
   d'attente), des cônes-CIBLES à DROITE ; on lance pour les faire tomber, puis un
   joueur en attente va ramasser l'engin et l'on tourne. Aire NEUTRE. */
export function buildTargetThrow(text, sport, opts = {}) {
  const t = c0(text || "");
  const NW = { un: 1, une: 1, deux: 2, trois: 3, quatre: 4, cinq: 5, six: 6 };
  const gm = t.match(/(\d+|deux|trois|quatre|cinq|six)\s*(?:groupes?|[ée]quipes?|files?|ateliers?|postes?|cibles?|c[ôo]nes?)/);
  const nLanes = clampN(opts.lanes != null ? opts.lanes : (gm ? (NW[gm[1]] != null ? NW[gm[1]] : parseInt(gm[1], 10) || 3) : 3), 2, 5);
  const pm = t.match(/par\s*(\d+)|groupes?\s*de\s*(\d+)/);
  const per = clampN(opts.per != null ? opts.per : (pm ? +(pm[1] || pm[2]) : 3), 2, 4);
  // Engin lancé : frisbee -> disque ; sinon engin générique « ballon » (vortex,
  // javelot, anneau, balle…). On évite « temoin » (bâton de relais) qui suggérait
  // à tort une course de relais pour un lancer sur cible.
  const throwObj = (/frisbee|disque|ultimate/.test(t) || sport === "ultimate") ? "disque" : "ballon";
  // ATTENTE (file + plot) en arrière, puis PAS DE LANCER INDIVIDUEL (le lanceur
  // entre DEUX plots -> répartition espacée et sécurisée), enfin la CIBLE à
  // droite. L'engin est posé sur le lanceur (devant lui). 2 plots de lancer/file.
  const attX = 6, plotAttX = 12, fireX = 20, discX = 24, tgtX = 86, hitX = 80;
  const players = [], markers = [], actions = [], zones = [];
  const stepY = Math.min(26, 80 / (nLanes - 1 || 1));
  for (let i = 0; i < nLanes; i++) {
    const y = Math.round(clampN(50 + (i - (nLanes - 1) / 2) * stepY, 14, 86));
    const lid = "L" + (i + 1);
    players.push({ id: lid, num: i + 1, role: "Lanceur", x: fireX, y, color: "#0072B2", team: "A" });
    for (let j = 1; j < per; j++) {                                // file d'attente (zone gauche)
      // Espacés de 5 (les pions se superposaient à 4) et à ≥4 du plot de la
      // zone d'attente (le 2e de file était posé DESSUS) ; au-delà de 2, la
      // file se décale en hauteur.
      const wx = clampN(8 - ((j - 1) % 2) * 5, 1, plotAttX - 4);
      const wy = Math.round(clampN(y + Math.floor((j - 1) / 2) * 6, 3, 97));
      players.push({ id: "W" + (i + 1) + "_" + j, role: "En attente", x: wx, y: wy, color: "#0072B2", team: "A" });
    }
    markers.push({ type: "plot", x: plotAttX, y });               // plot de la zone d'attente
    const a = clampN(y - 6, 3, 97), b = clampN(y + 6, 3, 97);     // PAS DE LANCER individuel (2 plots)
    zones.push({ x: fireX, y: a, w: 0, h: b - a, style: "solid", ...(i === 0 ? { label: "Pas de lancer" } : {}) });
    markers.push({ type: "plot", x: fireX, y: a }, { type: "plot", x: fireX, y: b });
    markers.push({ type: throwObj, x: discX, y });                // engin SUR le lanceur (devant lui)
    markers.push({ type: "plot", x: tgtX, y });                   // cône-cible à faire tomber
    actions.push({ from: lid, tox: hitX, toy: y, type: "tir", order: i + 1, label: i === 0 ? "Lancer (revers) sur le cône" : null });
  }
  // Encart « cible + zone visée » (rappel de la zone à viser), réutilisé tel quel.
  markers.push({ type: "cible_visee", x: 90, y: 15, text: "Zone visée" });
  // Ramassage + rotation au signal : un joueur en attente récupère l'engin.
  const w1 = players.find((p) => /^W1_/.test(p.id));
  if (w1) actions.push({ from: w1.id, tox: tgtX - 6, toy: w1.y, type: "course", style: "dashed", label: "Va chercher l'engin, replace le cône" });
  return {
    players, markers, actions, zones,
    steps: ["Le lanceur vise le cône (lancer en revers) pour le faire tomber", "Au signal, un joueur en attente récupère l'engin et replace le cône", "Rotation : le lanceur passe en fin de file"],
    terrainOpts: { plain: true },
  };
}

function buildArcheryStations(text) {
  const t = c0(text || "");
  const NUMW = { un: 1, une: 1, deux: 2, trois: 3, quatre: 4, cinq: 5, six: 6 };
  const pm = t.match(/(\d+|deux|trois|quatre|cinq|six)\s*(?:postes?|cibles?|ateliers?|archers?|tireurs?)/);
  const n = clampN(pm ? (NUMW[pm[1]] != null ? NUMW[pm[1]] : parseInt(pm[1], 10) || 3) : 3, 2, 5);
  const dm = t.match(/(\d+)\s*m(?:[èe]tres?)?\b/);
  const rot = /rotation|tourne|change(?:nt)? de poste|au signal/.test(t);
  const ramass = /ramass/.test(t);
  const lineX = 14, fireX = 21, tgtX = 88, hitX = 82;   // archers DERRIÈRE le pas de tir
  const players = [], markers = [], actions = [], zones = [];
  const stepY = Math.min(28, 72 / (n - 1 || 1));
  const ys = [];
  for (let i = 0; i < n; i++) {
    const y = Math.round(clampN(50 + (i - (n - 1) / 2) * stepY, 18, 82));
    ys.push(y);
    players.push({ id: "T" + (i + 1), num: i + 1, role: "Archer " + (i + 1), x: lineX, y, color: "#0072B2", team: "A" });
    markers.push({ type: "cible", x: tgtX, y });                                  // une cible par poste
    // SÉQUENCE : tous tirent d'abord (① volée simultanée), PUIS on se déplace
    // pour ramasser (② plus bas). Même numéro de temps sur les tirs parallèles.
    actions.push({ from: "T" + (i + 1), tox: hitX, toy: y, type: "tir", order: 1, label: i === 0 ? "Volée de flèches" : null });
  }
  // PAS DE TIR INDIVIDUEL (sécurité, essentiel) : CHAQUE archer a son propre
  // poste, matérialisé entre DEUX plots — ce qui impose une répartition espacée
  // des élèves. Court trait vertical (w=0) encadré de 2 plots par poste ; les
  // archers tirent depuis leur pas de tir, sans le dépasser. -> 2×n plots.
  for (let i = 0; i < n; i++) {
    const a = clampN(ys[i] - 6, 4, 96), b = clampN(ys[i] + 6, 4, 96);
    zones.push({ x: fireX, y: a, w: 0, h: b - a, style: "solid", ...(i === 0 ? { label: "Pas de tir" } : {}) });
    markers.push({ type: "plot", x: fireX, y: a }, { type: "plot", x: fireX, y: b });
  }
  if (dm) zones.push({ x: fireX, y: 95, w: hitX - fireX, h: 0, style: "dashed", dim: dm[1] + " m" });
  // RAMASSAGE au signal : on avance ensemble récupérer les flèches.
  if (ramass) {
    const mid = Math.ceil(n / 2);
    actions.push({ from: "T" + mid, tox: tgtX - 6, toy: players[mid - 1].y, type: "course", order: 2, style: "dashed", label: "Ramassage au signal (②)" });
  }
  // ROTATION des archers le long de la ligne de tir (au signal), dans la marge.
  if (rot) for (let i = 0; i < n; i++) {
    const a = players[i], b = players[(i + 1) % n], wrap = (i + 1) % n === 0;
    actions.push({ type: "rotation", fromx: 10, fromy: a.y, tox: 10, toy: b.y, bulge: wrap ? 6 : 3, ...(i === 0 ? { label: "Rotation au signal" } : {}) });
  }
  // ENCART « cible + zone visée » (angle haut-droit) : rappelle la cible visée
  // (le jaune au centre) — le « meilleur des deux mondes » avec les cibles par
  // poste. Marqueur générique dessiné par render.js.
  markers.push({ type: "cible_visee", x: 90, y: 15, text: "Zone visée : le jaune" });
  const steps = ["Chaque archer tire sa volée depuis son poste"];
  if (ramass) steps.push("Au signal, tous avancent ramasser les flèches");
  if (rot) steps.push("Rotation d'un poste");
  return { players, markers, zones, actions, steps, terrainOpts: { stations: true } };
}
/* FAMILLE DISPOSITIF (aménagement matériel, générique toutes activités) :
   construit la scène à partir de l'analyse NORMALISÉE (validée en amont par
   analysis.js) — repères cotés sur un couloir/aire, rôles (coureur/lanceur/
   observateur/chrono/starter), geste de performance (sprint/lancer/…), signal
   et mesure. Le code DESSINE, l'IA a fourni l'intention. */
export function dispositifFromAnalysis(spec, sport) {
  const { forme, reperes, roles, geste, signal, mesure, texte } = spec;
  const y = 50, x0 = 9, x1 = 92;
  const dists = reperes.map((r) => r.dist).filter((d) => d != null && d >= 0);
  const maxD = dists.length ? Math.max(...dists) : 0;
  const xOf = (d) => maxD > 0 ? Math.round(clampN(x0 + (d / maxD) * (x1 - x0), x0, x1)) : Math.round((x0 + x1) / 2);
  const players = [], markers = [], actions = [], zones = [];
  // AIRE 2D (« déplacement en étoile », plots aux coins) : un repère « centre » au
  // milieu, les autres en ANNEAU autour — un plot par sommet. En linéaire, plusieurs
  // repères à la même distance se superposeraient (cas réel : étoile badminton).
  // Réservé au DÉPLACEMENT (pas un lancer sur cible) avec assez de sommets.
  const isStar = forme === "aire" && geste !== "lancer"
    && reperes.filter((r) => r.type === "plot" || r.type === "cible").length >= 3;
  if (isStar) {
    zones.push({ x: x0, y: 12, w: x1 - x0, h: 76, style: "dashed", label: "Aire" });
    const cxc = 50, cyc = 50;
    const isCenter = (r) => r.dist === 0 || /centre|milieu/i.test(r.label || "");
    const center = reperes.filter(isCenter), ring = reperes.filter((r) => !isCenter(r));
    const mk = (r, mx, my) => markers.push({ type: r.type === "cible" ? "cible" : r.type === "haie" ? "haie" : "plot", x: Math.round(mx), y: Math.round(my), ...(r.label ? { label: String(r.label).slice(0, 24) } : {}) });
    center.forEach((r) => mk(r, cxc, cyc));
    ring.forEach((r, i) => {
      const ang = -Math.PI / 2 + (i / Math.max(1, ring.length)) * 2 * Math.PI;
      mk(r, clampN(cxc + 33 * Math.cos(ang), x0 + 2, x1 - 2), clampN(cyc + 30 * Math.sin(ang), 16, 84));
    });
    let pid = 0;
    for (const R of roles) for (let k = 0; k < R.n; k++) {
      pid++;
      const obs = R.role === "observateur" || R.role === "chronometreur" || R.role === "starter";
      if (obs) players.push({ id: "O" + pid, role: R.role === "chronometreur" ? "Chronométreur" : R.role === "starter" ? "Starter" : "Observateur", team: "OBS", color: "#F2C200", x: x0 + 2, y: 80 });
      else players.push({ id: "C" + pid, num: pid, role: "Joueur", team: "A", color: "#0072B2", x: Math.round(clampN(cxc + (k - (R.n - 1) / 2) * 7, x0, x1)), y: cyc });
    }
    const mover = players.find((p) => p.team === "A");
    if (mover) {
      const ringMarks = markers.filter((m) => !(m.x === cxc && m.y === cyc));
      ringMarks.forEach((m, i) => actions.push({ from: mover.id, tox: m.x, toy: m.y, type: "course", style: "dashed", order: i + 1, label: i === 0 ? "Touche chaque plot et revient au centre" : null }));
    }
    const steps = [];
    if (signal) steps.push(signal);
    if (mesure) steps.push(mesure);
    return { players, markers, actions, zones, steps, terrainOpts: { plain: true } };
  }
  if (forme === "couloir") zones.push({ x: x0, y: 40, w: x1 - x0, h: 20, style: "dashed", label: "Couloir" });
  else if (forme === "aire") zones.push({ x: x0, y: 12, w: x1 - x0, h: 76, style: "dashed", label: "Aire" });
  // Repères matériels (cotés si distance fournie).
  reperes.forEach((r, i) => {
    const rx = r.dist != null ? xOf(r.dist) : Math.round(x0 + ((i + 1) / (reperes.length + 1)) * (x1 - x0));
    // Un label de COULEUR (« blanc », « rouge »…) colore le plot et la cote
    // redevient la distance ; sinon le label reste tel quel.
    const colHex = PLOT_COLOR_HEX[normPlotColor(r.label)] || null;
    const label = colHex ? (r.dist != null ? r.dist + " m" : null) : (r.label || (r.dist != null ? r.dist + " m" : null));
    if (r.type === "ligne") zones.push({ x: rx, y: 36, w: 0, h: 28, style: "solid", ...(label ? { label } : {}) });
    else if (r.type === "zone") {
      // Zone À DEUX BORNES (« de 10 à 30 m ») : bande RÉELLE entre les deux
      // distances — c'est ce que l'enseignant trace au sol. Sans bornes, on
      // garde la vignette centrée sur le repère (comportement historique).
      const rx2 = r.dist2 != null ? xOf(r.dist2) : null;
      if (rx2 != null && rx2 > rx) zones.push({ x: rx, y: 42, w: Math.max(6, rx2 - rx), h: 16, style: "dashed", ...(label ? { label } : {}) });
      else zones.push({ x: clampN(rx - 6, x0, x1 - 12), y: 42, w: 12, h: 16, style: "dashed", ...(label ? { label } : {}) });
    }
    // Une HAIE peut arriver soit via le type "haie", soit comme un "plot" étiqueté
    // « haie » (l'IA emploie souvent type:plot + label:haie) -> on dessine la haie.
    else if (r.type === "haie" || /\bhaies?\b/i.test(r.label || "")) markers.push({ type: "haie", x: rx, y });
    else markers.push({ type: r.type === "cible" ? "cible" : "plot", x: rx, y, ...(label ? { label } : {}), ...(colHex ? { color: colHex } : {}) });
  });
  // Stations physiques (haie/plot/cible cotées), pour répartir les observateurs.
  const stationX = reperes.filter((r) => ["haie", "plot", "cible"].includes(r.type) && r.dist != null).map((r) => xOf(r.dist));
  const repX = (idx) => (idx != null && Number.isInteger(idx) && reperes[idx - 1])
    ? (reperes[idx - 1].dist != null ? xOf(reperes[idx - 1].dist) : x0 + 10) : (stationX.length ? stationX[0] : Math.round((x0 + x1) / 2));
  let perfId = null, pi = 0, gi = -1;
  for (const R of roles) {
  gi++;
  for (let k = 0; k < R.n; k++) {
    pi++;
    // COULOIR : les acteurs restent DANS la bande (y 40-60) — un « couloir de
    // course » dont les coureurs débordent au-dessus et au-dessous n'est plus
    // un couloir (👎 PROD138 « cela doit se passer dans un couloir de
    // course »). Hors couloir, l'étalement large d'origine est conservé.
    // Au-delà de DEUX acteurs, on les ÉCHELONNE le long du couloir plutôt que
    // côte à côte : la bande ne fait que 20 unités de haut, quatre pastilles
    // n'y tiennent pas (le désempilement de `sanitizeScene`, à 9 unités, les
    // en ferait ressortir) — et des relayeurs répartis le long du parcours,
    // c'est le dispositif réel d'un relais.
    const enFile = forme === "couloir" && R.n >= 3;
    const pas = forme === "couloir" ? Math.min(12, 16 / Math.max(1, R.n - 1)) : 12;
    const yy = enFile ? 50 : (forme === "couloir"
      ? Math.round(clampN(50 + (k - (R.n - 1) / 2) * pas, 42, 58))
      : Math.round(clampN(50 + (k - (R.n - 1) / 2) * 12, 16, 84)));
    if (R.role === "coureur" || R.role === "lanceur") {
      const id = (R.role === "coureur" ? "C" : "L") + pi;
      // Libellé d'origine conservé (« attaquant » / « défenseur ») : les
      // DÉFENSEURS passent équipe B (orange) et se placent FACE aux attaquants
      // (mi-aire) — avant, deux groupes « coureur » s'empilaient au même point.
      const isDef = /d[ée]fen/i.test(R.label || "");
      const disp = R.label ? R.label.charAt(0).toUpperCase() + R.label.slice(1) : (R.role === "coureur" ? "Coureur" : "Lanceur");
      const gx = isDef ? Math.round(x0 + (x1 - x0) * 0.45)
        : (enFile ? Math.round(clampN(x0 + 2 + (k * (x1 - x0 - 6)) / Math.max(1, R.n - 1) * 0.8, x0, x1 - 4))
          : x0 + gi * 5);
      players.push({ id, num: pi, role: disp, team: isDef ? "B" : "A", x: gx, y: yy, color: isDef ? "#D55E00" : "#0072B2" });
      if (!perfId && !isDef) perfId = id;
    } else if (R.role === "observateur" || R.role === "chronometreur") {
      // 1 observateur par STATION quand il y en a plusieurs (« un par haie »).
      const sx = stationX.length ? stationX[k % stationX.length] : repX(R.au);
      // L'observateur se place SOUS le dispositif (y=72) : en couloir, la bande
      // s'arrête à y=60 — il ne doit pas se superposer au dernier acteur, qui
      // vient d'être resserré dans le couloir (cas remonté : le juge collé au
      // 4e coureur).
      players.push({ id: "O" + pi, role: R.role === "chronometreur" ? "Chronométreur" : "Observateur", team: "OBS", x: sx, y: forme === "couloir" ? 74 : 72, color: "#F2C200" });
    } else if (R.role === "starter") {
      players.push({ id: "S" + pi, role: "Starter", team: "OBS", x: clampN(x0 - 1, 2, x0), y: 50, color: "#F2C200" });
    }
  }
  }
  // RELAIS — CHAÎNE DE TRANSMISSIONS (test réel de l'enseignant : « relais
  // 4×40 m avec zone de transmission… recevoir le témoin en course », rendu
  // avec UNE seule flèche de course et AUCUN passage de témoin — l'objet même
  // de la situation manquait). Quand le texte décrit un relais/témoin et que
  // plusieurs coureurs sont ÉCHELONNÉS le long du couloir, on dessine la
  // chaîne réelle : chaque relayeur court jusqu'au suivant, lui transmet, et
  // le témoin est posé dans la main du premier.
  const relais = /relais|t[ée]moin|transmission/i.test(String(texte || ""));
  const coureurs = players.filter((p) => p.team === "A" && /coureur/i.test(p.role || ""))
    .slice().sort((a, b) => a.x - b.x);
  if (relais && coureurs.length >= 2 && forme === "couloir") {
    let ord = 1;
    for (let i = 0; i < coureurs.length; i++) {
      const c = coureurs[i], nxt = coureurs[i + 1];
      const arrivee = nxt ? nxt.x : (maxD > 0 ? xOf(maxD) : x1);
      actions.push({ from: c.id, tox: Math.round(arrivee), toy: y, type: "course", order: ord++,
        label: i === 0 ? "Course, témoin en main" : null });
      if (nxt) actions.push({ from: c.id, to: nxt.id, type: "passe", order: ord++,
        label: i === 0 ? "Transmission du témoin" : null });
    }
    markers.push({ type: "temoin", x: Math.round(clampN(coureurs[0].x + 2, x0, x1)), y, label: "Témoin" });
    const steps0 = ["Le relayeur court, témoin en main", "Transmission DANS la zone, receveur déjà lancé",
      "Le receveur enchaîne jusqu'au relais suivant"];
    // FOND : l'aire neutre est le défaut de tous les dispositifs, mais un
    // relais se court SUR UNE PISTE — on garde les couloirs tracés du sport
    // (demande de l'enseignant : « ce n'est pas au niveau attendu, ni dans la
    // piste »). Réservé à l'athlétisme : les autres dispositifs (pétanque,
    // cible, volley) gardent l'aire neutre qui leur convient.
    return { players, markers, actions, zones, steps: steps0,
      ...(sport === "athletisme" ? {} : { terrainOpts: { plain: true } }) };
  }
  // Geste de performance.
  if (perfId) {
    if (geste === "lancer") {
      const cible = reperes.find((r) => r.type === "cible");
      const tx = cible ? (cible.dist != null ? xOf(cible.dist) : x1 - 8) : (maxD > 0 ? xOf(maxD) : x1 - 8);
      actions.push({ from: perfId, tox: tx, toy: y, type: "tir", order: 1, label: "Lancer sur la cible" });
    } else {
      actions.push({ from: perfId, tox: maxD > 0 ? xOf(maxD) : x1, toy: y, type: "course", order: 1, label: geste === "sprint" ? "Sprint — départ arrêté" : "Course" });
    }
  }
  const firstDist = reperes.find((r) => r.dist != null && r.dist > 0);
  if (firstDist) zones.push({ x: x0, y: 33, w: xOf(firstDist.dist) - x0, h: 0, style: "dashed", dim: firstDist.dist + " m" });
  const steps = [];
  if (geste) steps.push(geste === "lancer" ? "Lancer vers la cible" : "Sprint / course depuis le départ");
  if (signal) steps.push(signal);
  if (mesure) steps.push(mesure);
  return { players, markers, actions, zones, steps, terrainOpts: { plain: true } };
}

/* PRIMITIVE GÉNÉRIQUE « ZONES + RÔLES » — terrain découpé en N bandes, avec
   1 rôle (+ son opposé éventuel) PAR bande et un geste répété dans chaque zone.
   Réutilisable pour tout sport : zones de service (volley), couloirs de tir
   (hand/basket), canaux de passe… On garde le VRAI terrain (filet, etc.).
   - orientation "couloirs" (défaut) : bandes empilées dans la largeur (lignes
     horizontales), jeu de GAUCHE (départ) vers la DROITE (opposé) ;
   - orientation "tranches" : bandes en profondeur (lignes verticales). */
export function zonesFromAnalysis(spec, sport) {
  const { n, orientation, labels, geste, roles } = spec;
  const x0 = 8, x1 = 92, y0 = 6, y1 = 94;
  const couloirs = orientation !== "tranches";
  const players = [], markers = [], actions = [], zones = [];
  const OPP_RE = /recep|récep|defens|défens|adversaire|cible|retour/i;     // côté opposé / défensif
  const cap = (s) => { s = String(s || "").replace(/_/g, " "); return s ? s.charAt(0).toUpperCase() + s.slice(1) : ""; };
  const sideOf = (cote, idx) => {
    const c = String(cote || "").toLowerCase();
    if (/depart|départ|fond|arri|serv|lanc/.test(c)) return "depart";
    if (/oppos|avant|recep|récep|face|but|cible|retour/.test(c)) return "oppose";
    if (/centre|milieu/.test(c)) return "centre";
    return idx === 0 ? "depart" : "oppose";
  };
  // Une MISE EN JEU / un SERVICE se fait DERRIÈRE la ligne de fond (zone libre,
  // x ~ 5) ; les autres gestes partent du terrain (x ~ 15).
  const gIsService = (() => { const g = String(geste || "").toLowerCase(); return /service|renvoi|mise en jeu|engage/.test(g); })();
  const departA = gIsService ? 5 : 15;
  const along = (s) => s === "depart" ? departA : s === "centre" ? 34 : 66;
  const teamOf = (role) => OPP_RE.test(role) ? "B" : "A";
  const colorOf = (role) => OPP_RE.test(role) ? "#D55E00" : "#0072B2";
  const gType = (() => {
    const g = String(geste || "").toLowerCase();
    if (/service|renvoi/.test(g)) return "renvoi";
    if (/passe/.test(g)) return "passe";
    if (/tir|frapp|shoot|smash|attaq/.test(g)) return "tir";
    if (/lanc/.test(g)) return "tir";
    if (/dribble|conduite/.test(g)) return "dribble";
    return NET_SPORTS.includes(sport) ? "renvoi" : "passe";
  })();
  const span = couloirs ? (y1 - y0) : (x1 - x0);
  for (let i = 0; i < n; i++) {
    const c = Math.round((couloirs ? y0 : x0) + (i + 0.5) * span / n);
    if (i > 0) {
      const b = Math.round((couloirs ? y0 : x0) + i * span / n);
      zones.push(couloirs ? { x: x0, y: b, w: x1 - x0, h: 0, style: "dashed" } : { x: b, y: y0, w: 0, h: y1 - y0, style: "dashed" });
    }
    const lab = (labels && labels[i]) || `Zone ${i + 1}`;
    zones.push(couloirs ? { x: x0 + 2, y: c, w: 0, h: 0, label: lab } : { x: c, y: y0 + 4, w: 0, h: 0, label: lab });
    let departId = null, opposePt = null;
    // Deux rôles du MÊME côté (« un passeur et un attaquant » côté départ) ne
    // se superposent plus : chaque doublon est décalé vers le centre. Et le
    // GESTE part de son EXÉCUTANT (« smash » -> l'attaquant, pas le passeur).
    const sideCount = {};
    const execRe = /tir|frapp|shoot|smash|attaq/.test(String(geste || "").toLowerCase()) ? /attaqu|smash|frapp|tireur/i
      : /service/.test(String(geste || "").toLowerCase()) ? /serveur/i
      : /passe/.test(String(geste || "").toLowerCase()) ? /passeur/i : null;
    roles.forEach((r, ri) => {
      const s = sideOf(r.cote, ri);
      const dup = sideCount[s] || 0; sideCount[s] = dup + 1;
      const shift = dup * (s === "depart" ? 9 : -9);
      const px = couloirs ? along(s) + shift : c;
      const py = couloirs ? c : along(s) + shift;
      if (/cible/i.test(r.role)) { markers.push({ type: "cible", x: px, y: py }); if (s !== "depart" && !opposePt) opposePt = { x: px, y: py }; return; }
      const id = (teamOf(r.role) === "B" ? "Z" : "J") + (ri + 1) + "_" + (i + 1);
      players.push({ id, num: i + 1, role: cap(r.role) || "Joueur", team: teamOf(r.role), color: colorOf(r.role), x: px, y: py });
      if (s === "depart" && (!departId || (execRe && execRe.test(r.role) && !execRe.test((players.find((p) => p.id === departId) || {}).role || "")))) departId = id;
      if (s !== "depart" && !opposePt) opposePt = { x: px, y: py };
    });
    if (departId) {
      const tx = opposePt ? opposePt.x : (couloirs ? 66 : c);
      const ty = opposePt ? opposePt.y : (couloirs ? c : 66);
      actions.push({ from: departId, tox: tx, toy: ty, type: gType, order: i + 1, label: i === 0 ? (cap(geste) || "Geste") : null });
    }
  }
  const steps = [];
  if (geste) steps.push(`${cap(geste)} dans chaque zone`);
  return { players, markers, actions, zones, steps };
}

/* ZONES CIBLES VISÉES PAR UN SEUL EXÉCUTANT — « un serveur/lanceur/tireur
   enchaîne N envois pour atteindre N zones cibles du terrain (adverse) découpé
   en carrés / bandes / couloirs ». UN acteur au départ, une GRILLE de N zones
   cibles sur la moitié EN FACE, N flèches numérotées en éventail vers le centre
   de chaque zone. À distinguer de zonesFromAnalysis (un exécutant PAR bande) :
   ici la découpe matérialise des CIBLES, pas des postes de travail parallèles.
   `layout` : "grille" (carrés) | "largeur" (couloirs, empilés en y) |
   "profondeur" (bandes/tranches, échelonnées en x, ex. court/long). */
/* Observateur / chronométreur : pastille OBS dans un coin + bulle de consigne
   (« top départ », chrono). Partagé par les gabarits de zones. `role` : libellé
   (Observateur / Juge / Arbitre / Chronométreur). Muté sur players/markers. */
export function pushObserver(players, markers, { role = "Observateur", note } = {}) {
  players.push({ id: "OBS", role, team: "OBS", color: "#F2C200", x: 7, y: 90 });
  if (note) markers.push({ type: "consigne", x: 21, y: 90, text: String(note).slice(0, 40) });
}

/* Observateur lu dans le TEXTE (jamais du seul rôle IA : elle en hallucine).
   Reconnaît juge / arbitre / chronométreur / starter / observateur / camarade
   qui note, et compose la consigne (top départ, chrono, annonce des points). */
export function observerFromText(text) {
  const t = c0(text || "");
  const m = t.match(/juge|arbitre|chronom[èe]tr|starter|observ|camarade|marqueur|secr[ée]taire/);
  if (!m) return null;
  const role = /juge/.test(m[0]) ? "Juge" : /arbitre/.test(m[0]) ? "Arbitre"
    : /chronom|starter/.test(m[0]) ? "Chronométreur" : "Observateur";
  const note = [
    /\btop\b|signal|d[ée]part/.test(t) ? "Top départ" : null,
    /chrono|minute|seconde|\btemps\b/.test(t) ? "chrono" : null,
    /annonce|compte|note/.test(t) ? "annonce" : null,
  ].filter(Boolean).join(" + ") || "Observe";
  return { role, note };
}

export function targetZonesFromAnalysis(spec, sport) {
  const { n, labels, geste, actorRole, layout = "grille", casier, cerceaux, cerceauxParZone = 0, nActors = 1, aide, observer } = spec;
  const cap = (s) => { s = String(s || "").replace(/_/g, " "); return s ? s.charAt(0).toUpperCase() + s.slice(1) : ""; };
  const net = NET_SPORTS.includes(sport);
  const g = String(geste || "").toLowerCase();
  const gType = /service|renvoi|mise en jeu|engage/.test(g) ? "renvoi"
    : /passe/.test(g) ? "passe"
    : /tir|frapp|shoot|smash|attaq|lanc|pied|bott|kick|d[ée]gag/.test(g) ? "tir"
    : net ? "renvoi" : "passe";
  const isService = /service|renvoi|mise en jeu|engage/.test(g);
  // Aire des zones cibles : la moitié EN FACE. Filet -> au-delà du filet (x>52) ;
  // sports de champ -> moitié droite (le geste va vers le camp adverse).
  // RÈGLES DE SERVICE : au badminton, jamais dans la « rivière » (la grille
  // démarre à la ligne de service courte) ; au tennis, « dans le carré de
  // service » (spec.box) borne la grille au carré (x <= 72).
  const gx0 = net ? (sport === "badminton" && isService ? 66 : 54) : 52;
  const gx1 = spec.box && sport === "tennis" ? 72 : 93;
  const gy0 = 12, gy1 = 88;
  // Grille lignes × colonnes selon la découpe demandée.
  let cols, rows;
  if (layout === "largeur") { cols = 1; rows = n; }            // couloirs : bandes empilées en largeur
  else if (layout === "profondeur") { cols = n; rows = 1; }    // bandes/tranches : échelonnées en profondeur
  else { cols = n <= 3 ? n : Math.ceil(Math.sqrt(n)); rows = Math.ceil(n / cols); }  // grille (carrés)
  const cw = (gx1 - gx0) / cols, ch = (gy1 - gy0) / rows;

  const players = [], markers = [], actions = [], zones = [];
  // Acteur(s) au départ (derrière la ligne de fond pour un service). Un DÉFI à
  // N joueurs (nActors > 1) les étage côte à côte, chacun son ballon — les
  // flèches partent du premier (le geste est le même ; le tour de rôle vit dans
  // la pédagogie).
  const ax = isService ? 6 : 12, ay = 50;
  const baseRole = cap(actorRole && actorRole.role) || (net ? "Serveur" : "Joueur");
  const k0 = Math.max(1, Math.min(4, nActors));
  for (let j = 0; j < k0; j++) {
    const py = Math.round(clampN(ay + (j - (k0 - 1) / 2) * 14, 12, 88));
    players.push({ id: "A" + (j + 1), num: k0 > 1 ? j + 1 : undefined, role: k0 > 1 ? `${baseRole} ${j + 1}` : baseRole, team: "A", color: "#0072B2", x: ax, y: py });
    markers.push({ type: "ballon", x: ax + 4, y: py });
  }
  if (casier) markers.push({ type: "casier", x: Math.max(3, ax - 1), y: Math.min(86, ay + (k0 > 1 ? 24 : 18)) });

  // Matérialisation : CERCEAU au centre de chaque zone si le texte le dit,
  // sinon cônes aux intersections de la grille (« plots / coupelles »).
  if (!cerceaux)
    for (let r = 0; r <= rows; r++)
      for (let c = 0; c <= cols; c++)
        markers.push({ type: "cone", x: Math.round(gx0 + c * cw), y: Math.round(gy0 + r * ch) });

  // Zones (rectangles pointillés + étiquette) + une flèche numérotée du départ
  // vers chaque zone (éventail). Sur une grille à UNE SEULE rangée (ou colonne),
  // les centres sont alignés avec le départ : les flèches se superposeraient —
  // le point d'arrivée est ÉTAGÉ à l'intérieur de chaque zone pour que
  // l'éventail reste lisible (le cerceau-cible suit ce point).
  let k = 0;
  for (let r = 0; r < rows && k < n; r++)
    for (let c = 0; c < cols && k < n; c++, k++) {
      const zx = gx0 + c * cw, zy = gy0 + r * ch;
      const cx = Math.round(zx + cw / 2), cy = Math.round(zy + ch / 2);
      const tx = cols === 1 ? Math.round(zx + cw * (0.32 + (k % 3) * 0.18)) : cx;
      const ty = rows === 1 ? Math.round(zy + ch * (0.32 + (k % 3) * 0.18)) : cy;
      zones.push({ x: Math.round(zx), y: Math.round(zy), w: Math.round(cw), h: Math.round(ch), style: "dashed", label: (labels && labels[k]) || `Zone ${k + 1}` });
      // « N cerceaux par zone » : anneaux répartis autour du point visé ;
      // sinon un seul cerceau-cible au point visé.
      if (cerceaux) {
        const kc = Math.max(1, cerceauxParZone || 1);
        for (let j = 0; j < kc; j++) {
          const a2 = (j / kc) * 2 * Math.PI - Math.PI / 2;
          markers.push({ type: "cerceau", x: kc > 1 ? Math.round(clampN(tx + 6 * Math.cos(a2), zx + 3, zx + cw - 3)) : tx, y: kc > 1 ? Math.round(clampN(ty + 6 * Math.sin(a2), zy + 3, zy + ch - 3)) : ty });
        }
      }
      actions.push({ from: "A1", tox: tx, toy: ty, type: gType, order: k + 1, label: k === 0 ? (cap(geste) || "Vise chaque zone") : null });
    }
  // Partenaire-AIDE (déplace/replace le matériel) : posté côté zones.
  if (aide) players.push({ id: "P_AIDE", role: "Partenaire", team: "A", color: "#0072B2", x: Math.min(95, gx1 + 2), y: 20 });
  if (observer) pushObserver(players, markers, observer);
  return { players, markers, actions, zones, steps: [`${cap(geste) || "Envoi"} : une zone cible différente à chaque essai`] };
}

/* DÉFI / DUEL / CONCOURS à N joueurs (« défi entre 2 joueurs », « duel »,
   « défi à trois ») : nombre d'exécutants à dessiner au départ. 1 = pas un défi
   multi-joueurs. Un « défi » sans effectif (« réaliser ce défi en 1 minute »)
   reste individuel. */
export function defiActorsCount(text) {
  const t = c0(text || "");
  let m = t.match(/(?:d[ée]fis?|duel|concours)[^.]{0,24}?(?:entre|[àa])\s+(\d+|deux|trois|quatre)/);
  if (!m) m = t.match(/entre\s+(\d+|deux|trois|quatre)\s+joueurs/);
  if (m) return clampN(frenchNumWord(m[1]), 2, 4);
  return /\bduel\b/.test(t) ? 2 : 1;
}

/* « N cerceaux par zone / dans chaque zone » : compte de cerceaux posés DANS
   chaque zone cible (0 = non précisé). */
export function cerceauxPerZoneCount(text) {
  const t = c0(text || "");
  const m = t.match(/(\d+|un|deux|trois|quatre)\s*cerceaux?[^.]{0,16}?(?:par|dans chaque)\s*zones?/)
    || t.match(/dans chaque zone[^.]{0,12}(\d+|un|deux|trois|quatre)\s*cerceaux?/);
  return m ? clampN(frenchNumWord(m[1]), 1, 4) : 0;
}

/* PARTENAIRE-AIDE : « un partenaire déplace/replace le cerceau » — un assistant
   matériel, pas une opposition. */
export function helperPartnerInText(text) {
  return /partenaires?\b[^.]{0,30}(?:d[ée]place|replace|repositionne|change|ramasse)/.test(c0(text || ""));
}

/* Nombre de zones cibles lisible dans le texte (« 4 zones cibles », « coupé en
   3 bandes », « découpé en quatre ») — null si aucun compte exploitable. */
function zonesCiblesCount(t) {
  let m = t.match(/(\d+|deux|trois|quatre|cinq|six)\s*(?:zones?|carr[ée]s?|couloirs?|bandes?|espaces?|cerceaux|cibles?)/);
  if (!m) m = t.match(/(?:coup[ée]|d[ée]coup[ée]|divis[ée]|partag[ée])[^.]{0,14}\ben\s+(\d+|deux|trois|quatre|cinq|six)\b/);
  if (!m) return null;
  const n = frenchNumWord(m[1]);
  return n >= 2 && n <= 6 ? n : null;
}

/* ZONES CIBLES depuis le TEXTE SEUL (gabarit local `zones_cibles`) : quand le
   texte est explicite (« le botteur vise 3 zones cibles… »), le placement ne
   dépend plus de la structuration IA — tout sport (rugby jeu au pied, ultimate,
   invasion, filet). Geste et acteur déduits du vocabulaire, matériel (casier,
   cerceaux) et observateur/juge lus du texte. */
export function buildZonesCiblesFromText(text, sport) {
  const t = c0(text || "");
  const n = zonesCiblesCount(t) ?? 4;
  const geste = /\bserv/.test(t) ? "service"
    : /bott|jeu au pied|coup de pied|d[ée]gag/.test(t) ? "jeu au pied"
    : /passe/.test(t) ? "passe"
    : /lanc/.test(t) ? "lancer"
    : /frapp|tir/.test(t) ? "tir" : "envoi";
  const actorRole = { role: /bott/.test(t) ? "Botteur" : /serveur/.test(t) ? "Serveur"
    : /lanceur/.test(t) ? "Lanceur" : /tireur/.test(t) ? "Tireur" : /passeur/.test(t) ? "Passeur" : "Joueur" };
  const layout = /couloirs?/.test(t) ? "largeur"
    : /bandes?|tranches?|profondeur|[ée]chelonn|avant[^.]{0,24}arri[èe]re|court[^.]{0,18}long/.test(t) ? "profondeur"
    : /carr[ée]s?|quadrill|damier|grille/.test(t) ? "grille"
    : n <= 2 ? "largeur" : "grille";
  const casier = /casier|caisse|chariot|r[ée]serve de ball|seau de ball/.test(t);
  const cerceaux = /cerceaux?/.test(t);
  const box = /carr[ée]s? de service/.test(t);
  const nActors = defiActorsCount(t);
  const scene = targetZonesFromAnalysis({ n, labels: null, geste, actorRole, layout, casier, cerceaux,
    cerceauxParZone: cerceauxPerZoneCount(t), nActors, box, aide: helperPartnerInText(t), observer: observerFromText(t) }, sport);
  const capG = geste.charAt(0).toUpperCase() + geste.slice(1);
  return { ...scene, title: nActors > 1 ? `Défi de ${geste} en zones cibles` : `${geste === "envoi" ? "Précision" : capG} en zones cibles` };
}

/* TIR AU BUT DEPUIS DES ZONES (« défi des N zones ») — les zones sont des
   ESPACES d'où l'on tire, PAS des cibles : le terrain est découpé en N zones, le
   joueur MARQUE (but/panier) DEPUIS chacune. On tuile TOUT le terrain en N zones
   + un tireur (dans la zone 1) + une flèche de tir de chaque zone VERS le but.
   À distinguer de targetZonesFromAnalysis (viser des cibles en face). */
export function buildShootingZones(spec, sport) {
  const { n, labels, geste, layout = "grille", nActors = 1, observer } = spec;
  const cap = (s) => { s = String(s || ""); return s ? s.charAt(0).toUpperCase() + s.slice(1) : ""; };
  const basket = sport === "basketball" || sport === "basket3x3";
  const goalX = 97, goalY = 50;
  // Découpe de TOUT le terrain de jeu (le but reste à droite, hors grille).
  const gx0 = 8, gx1 = 88, gy0 = 12, gy1 = 88;
  let cols, rows;
  if (layout === "largeur") { cols = 1; rows = n; }
  else if (layout === "profondeur") { cols = n; rows = 1; }
  else { cols = n <= 3 ? n : Math.ceil(Math.sqrt(n)); rows = Math.ceil(n / cols); }
  const cw = (gx1 - gx0) / cols, ch = (gy1 - gy0) / rows;

  const players = [], markers = [], actions = [], zones = [];
  const centers = [];
  let k = 0;
  for (let r = 0; r < rows && k < n; r++)
    for (let c = 0; c < cols && k < n; c++, k++) {
      const zx = gx0 + c * cw, zy = gy0 + r * ch;
      centers.push([Math.round(zx + cw / 2), Math.round(zy + ch / 2)]);
      zones.push({ x: Math.round(zx), y: Math.round(zy), w: Math.round(cw), h: Math.round(ch), style: "dashed", label: (labels && labels[k]) || `Zone ${k + 1}` });
    }
  for (let r = 0; r <= rows; r++)
    for (let c = 0; c <= cols; c++)
      markers.push({ type: "cone", x: Math.round(gx0 + c * cw), y: Math.round(gy0 + r * ch) });
  // Le(s) tireur(s) commence(nt) dans la zone 1, ballon en main — un DÉFI à N
  // joueurs (nActors > 1) les étage dans la zone de départ, chacun son ballon.
  const kA = Math.max(1, Math.min(4, nActors));
  for (let j = 0; j < kA; j++) {
    const px = Math.round(clampN(centers[0][0] + (j - (kA - 1) / 2) * 8, 10, 92));
    const py = Math.round(clampN(centers[0][1] + (j - (kA - 1) / 2) * 8, 14, 86));
    players.push({ id: "T" + (j + 1), num: j + 1, role: kA > 1 ? `Tireur ${j + 1}` : "Tireur", team: "A", color: "#0072B2", x: px, y: py });
    markers.push({ type: "ballon", x: px + 4, y: py - 3 });
  }
  // Un tir DEPUIS chaque zone VERS le but / panier (numéroté).
  centers.forEach(([cx, cy], i) => {
    actions.push({ fromx: cx, fromy: cy, tox: goalX, toy: clampN(46 + (i % 3) * 4, 42, 58), type: "tir", order: i + 1, label: i === 0 ? (cap(geste) || "Tir au but depuis la zone") : null });
  });
  // Sports à but AVEC gardien (hors basket) : gardien dans la cage.
  if (!basket) players.push({ id: "G", role: "Gardien", team: "k", color: "#F2C200", x: 94, y: 50 });
  if (observer) pushObserver(players, markers, observer);
  return {
    players, markers, actions, zones,
    steps: ["Marquer depuis chaque zone", "Enchaîner les zones sans perdre de temps", observer && observer.note ? "L'observateur donne le top et chronomètre" : null].filter(Boolean),
  };
}

/* TIR DEPUIS LES ZONES construit du TEXTE SEUL (gabarit `tir_depuis_zones`) :
   « marquer un panier depuis chaque zone », « défi à trois : chaque joueur doit
   marquer depuis les 4 zones » — robuste à l'étiquetage IA (famille atelier /
   rotation au lieu de dispositif zones). */
export function buildShootingZonesFromText(text, sport) {
  const t = c0(text || "");
  const n = zonesCiblesCount(t) ?? 4;
  const layout = /couloirs?/.test(t) ? "largeur"
    : /bandes?|tranches?|profondeur|[ée]chelonn/.test(t) ? "profondeur"
    : n <= 2 ? "largeur" : "grille";
  const nActors = defiActorsCount(t);
  const scene = buildShootingZones({ n, labels: null, geste: "tir", layout, nActors, observer: observerFromText(t) }, sport);
  return { ...scene, title: nActors > 1 ? `Défi des ${n} zones à ${nActors}` : `Défi des ${n} zones` };
}

/* ─────────── CALQUE « DECOUPAGE » (terrain découpé en zones) ───────────────
   Calque MATÉRIEL applicable à toute famille : lignes de séparation (+ plots)
   et étiquettes de zones, dessinées par-dessus la scène. Convention : but
   attaqué à DROITE (x grand). Vocabulaire de repères le plus large possible. */
function dkNorm(s) { return String(s == null ? "" : s).toLowerCase().trim().replace(/[éèê]/g, "e").replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, ""); }
const LM_GEN = {
  mediane: 50, ligne_mediane: 50, milieu: 50, mi_terrain: 50, centre: 50, moitie: 50, rond_central: 50, cercle_central: 50,
  ligne_fond: 97, ligne_de_fond: 97, ligne_but: 97, ligne_de_but: 97, fond: 96, but: 98,
  depart: 8, ligne_depart: 8, ligne_de_depart: 8, debut: 8, arrivee: 96, ligne_arrivee: 96,
  tiers: 33, premier_tiers: 33, deux_tiers: 67, deuxieme_tiers: 67, quart: 25, premier_quart: 25, trois_quart: 75, trois_quarts: 75, dernier_quart: 75,
};
const LM_SPORT = {
  // ⚠️ 7 m À x77, PAS x86 (salve handball nº1, H17) : la zone STYLISÉE s'étend
  // jusqu'à x80 — un tireur posé à x86 est DANS l'arc dessiné, et la garde le
  // refuse à raison. Dans l'échelle stylisée (zone 6 m = 20 unités), le point
  // de 7 m tombe à ~23 unités du but, ENTRE la zone (x80) et le 9 m (x72).
  handball: { "6m": 80, six_metres: 80, zone: 80, ligne_de_surface: 80, surface: 80, "9m": 72, neuf_metres: 72, jet_franc: 72, ligne_de_jet_franc: 72, "7m": 77, sept_metres: 77, penalty: 77 },
  football: { surface: 80, "16m": 80, "16m50": 80, surface_de_reparation: 80, point_de_penalty: 88, penalty: 88, "6m": 92, surface_de_but: 92, rond_central: 50 },
  // Même correction au futsal : penalty SUR la ligne de zone stylisée (x80),
  // pas à x88 (qui est dans l'ellipse 20×34, interdite aux joueurs de champ).
  futsal: { surface: 82, double_penalty: 74, "10m": 74, penalty: 80, "6m": 80 },
  basketball: { raquette: 82, lancer_franc: 80, lf: 80, "3_points": 70, ligne_3_points: 70, "6m75": 70, cercle: 96, panier: 97 },
  basket3x3: { raquette: 82, lancer_franc: 80, "3_points": 70, "6m75": 70, panier: 97 },
  volleyball: { filet: 50, "3m": 33, ligne_attaque: 33, ligne_d_attaque: 33, zone_avant: 33, ligne_de_fond: 8 },
  badminton: { filet: 50, ligne_service: 30, ligne_de_service: 30, fond: 8 },
  tennis: { filet: 50, ligne_de_service: 72, carre_de_service: 72, fond: 96 },
  tennis_de_table: { filet: 50 },
  rugby: { ligne_essai: 97, ligne_d_essai: 97, en_but: 99, "22m": 74, ligne_22: 74, "10m": 60 },
  ultimate: { en_but: 90, zone_en_but: 90, ligne_en_but: 84 },
};
const SPORT_DEPTH = { handball: 20, football: 35, futsal: 20, basketball: 14, basket3x3: 14, rugby: 50, ultimate: 40, athletisme: 40 };

export function landmarkX(repere, sport, distance_m) {
  const k = dkNorm(repere);
  const sp = LM_SPORT[sport] || {};
  if (sp[k] != null) return sp[k];
  if (LM_GEN[k] != null) return LM_GEN[k];
  const d = distance_m != null ? distance_m : (typeof repere === "number" ? repere : (/^(\d+(?:\.\d+)?)\s*m/.test(String(repere || "")) ? parseFloat(RegExp.$1) : null));
  if (d != null && Number.isFinite(d)) return Math.round(clampN(100 - (d / (SPORT_DEPTH[sport] || 30)) * 90, 8, 96));  // mètres DU BUT (droite)
  return null;
}
function landmarkY(repere) {
  const M = { moitie: 50, milieu: 50, mediane: 50, centre: 50, tiers: 33, premier_tiers: 33, deux_tiers: 67, deuxieme_tiers: 67, quart: 25, premier_quart: 25, trois_quart: 75, trois_quarts: 75, dernier_quart: 75 };
  return M[dkNorm(repere)] ?? null;
}

export function decoupageFromAnalysis(decoupage, sport, opts = {}) {
  if (!decoupage || typeof decoupage !== "object") return null;
  // Vocabulaire NON AMBIGU (aligné sur forme:zones) : "couloirs" = bandes DANS LE
  // SENS DE LA LONGUEUR (couloirs latéraux côte à côte, séparés par des lignes
  // parallèles aux lignes de touche → découpe selon y) ; "tranches" = bandes EN
  // TRAVERS, empilées vers le but (découpe selon x). Rétro-compatibilité : l'ancien
  // "largeur" = couloirs (découpe y), l'ancien "longueur" = tranches (découpe x).
  const norm = dkNorm(decoupage.sens);
  const sens = /couloir|largeur|lat[ée]ral/.test(norm) ? "largeur" : "longueur";
  // En invasion (demi-terrain offensif), on ne découpe QUE la moitié attaquée :
  // de la médiane (50) à la ligne de fond. Sinon on couvre tout le terrain.
  const baseLo = typeof opts.base === "number" ? opts.base : 8;
  const zones = [], markers = [], cuts = [];
  for (const l of (Array.isArray(decoupage.lignes) ? decoupage.lignes : [])) {
    const pos = sens === "longueur"
      ? landmarkX(l.repere != null ? l.repere : l.distance_m, sport, typeof l.distance_m === "number" ? l.distance_m : undefined)
      : landmarkY(l.repere);
    if (pos == null) continue;                                   // repère inconnu -> ligne ignorée (robuste)
    cuts.push(pos);
    if (sens === "longueur") {
      zones.push({ x: pos, y: 2, w: 0, h: 96, style: "dashed" });   // ligne sur toute la largeur
      if (l.plots !== false) [10, 50, 90].forEach((yy) => markers.push({ type: "plot", x: pos, y: yy }));
    } else {
      zones.push({ x: 2, y: pos, w: 96, h: 0, style: "dashed" });   // ligne sur toute la longueur
      if (l.plots !== false) [10, 50, 90].forEach((xx) => markers.push({ type: "plot", x: xx, y: pos }));
    }
  }
  if (!cuts.length) return null;
  cuts.sort((a, b) => a - b);
  const labels = (Array.isArray(decoupage.zones) ? decoupage.zones : []).map((z) => (typeof z === "string" ? z : (z && z.label)) || null);
  // Si une ligne tombe EN DEÇÀ de la base (ex. invasion base=50 mais lignes aux
  // « tiers » 33/67 : le découpage couvre TOUT le terrain), on repart du bord —
  // sinon les bandes se chevauchent et les étiquettes s'agglutinent au centre.
  const fullField = cuts[0] <= baseLo && baseLo > 8;
  const lo = fullField ? 8 : baseLo;
  const bounds = sens === "longueur" ? [lo, ...cuts, 96] : [lo, ...cuts, 92];
  const bands = [];
  const n = bounds.length - 1;
  for (let i = 0; i < n; i++) {
    const c = Math.round((bounds[i] + bounds[i + 1]) / 2);
    bands.push(sens === "longueur" ? { x: c, y: 50 } : { x: 50, y: c });
    // En longueur le but est à DROITE (x grand) : zone 1 = la plus proche du but
    // (convention demi-terrain offensif). MAIS un découpage PLEIN TERRAIN est un
    // parcours de FRANCHISSEMENT : les zones se numérotent dans le sens de la
    // progression (zone/tranche 1 = celle du départ, à gauche).
    const li = sens === "longueur" && !fullField ? n - 1 - i : i;
    const lab = labels[li] || `zone ${li + 1}`;
    zones.push(sens === "longueur" ? { x: c, y: 10, w: 0, h: 0, label: lab } : { x: 12, y: c, w: 0, h: 0, label: lab });
  }
  return { zones, markers, bands };
}

/* Adaptateur V2 — famille DÉFENSE : SYSTÈME défensif (handball « 6-0 / 5-1 /
   3-2-1 », basket zone « 2-3 / 1-2-2 / 1-3-1 »). On dessine la FORME du dispositif
   — les défenseurs étagés en tiers devant le but/panier DÉFENDU (à droite), un
   gardien (handball), et, en option, la ligne d'attaque qui lui fait face. Le but
   est de montrer le PLACEMENT du système, pas une séquence d'attaque.
   Convention : tiers lus du but vers l'extérieur. Handball : tier 0 = ligne de 6 m
   (près du but) ; basket : tier 0 = haut de la zone (loin du panier). */
export function defenseFromAnalysis(analyse, sport, text) {
  const isBasket = sport === "basketball" || sport === "basket3x3";
  const isFoot = sport === "football" || sport === "futsal";
  const isVolley = sport === "volleyball";
  // ULTIMATE — ZONE « CUP » : 3 défenseurs en coupe autour du porteur du
  // disque, les autres couvrent les couloirs de passe derrière. (Branche
  // isolée : aucun autre sport n'est affecté.)
  if (sport === "ultimate") {
    const eff0 = analyse.effectif || {};
    const nD = clampN(parseInt(eff0.defenseurs, 10) || 7, 3, 7);
    const cup = Math.min(3, nD);
    const players = [{ id: "A1", num: 1, role: "Porteur", team: "A", color: "#0072B2", x: 20, y: 50 }];
    const markers = [{ type: "disque", x: 24, y: 50 }];
    for (let i = 0; i < cup; i++) {
      const ang = (i - (cup - 1) / 2) * (Math.PI / 4);
      players.push({ id: "D" + (i + 1), num: i + 1, role: "Cup", team: "B", color: "#D55E00",
        x: Math.round(20 + 13 * Math.cos(ang)), y: Math.round(clampN(50 + 16 * Math.sin(ang), 10, 90)) });
    }
    const rest = nD - cup;
    for (let i = 0; i < rest; i++)
      players.push({ id: "D" + (cup + i + 1), num: cup + i + 1, role: "Couloir", team: "B", color: "#D55E00",
        x: 50 + (i % 2) * 16, y: Math.round(clampN(20 + i * (60 / Math.max(1, rest - 1)), 14, 86)) });
    return {
      players, markers, actions: [],
      steps: ["Trois défenseurs en coupe (cup) autour du porteur", "Les autres couvrent les couloirs de passe", "La cup se déplace avec le disque"],
    };
  }
  if (sport !== "handball" && !isBasket && !isFoot && !isVolley) return null;
  const eff = analyse.effectif || {};
  const sys = String(analyse.systeme || "").toLowerCase().trim();

  // VOLLEY : filet vertical au centre (x=50). L'équipe qui DÉFEND est à DROITE :
  // CONTRE au filet (1 à 3 contreurs) + DÉFENSEURS en couverture arrière, face à un
  // attaquant adverse qui smashe depuis la gauche. systeme « 2-3 » = 2 contreurs / 3
  // défenseurs ; sinon effectif (contre / defenseurs).
  if (isVolley) {
    const dg = sys.split(/[^0-9]+/).filter(Boolean).map(Number).filter((n) => n >= 0 && n <= 6);
    let nBlock = dg.length ? dg[0] : (parseInt(eff.contre, 10) || 2);
    const totalD = parseInt(eff.defenseurs, 10);
    let nBack = dg.length >= 2 ? dg[1] : (Number.isFinite(totalD) ? totalD - nBlock : 3);
    nBlock = clampN(nBlock, 1, 3); nBack = clampN(nBack, 1, 5);
    const nA = clampN(parseInt(eff.attaquants, 10) || 1, 0, 2);
    const players = [], actions = [], markers = [];
    for (let i = 0; i < nBlock; i++) {
      const y = Math.round(clampN(50 + (i - (nBlock - 1) / 2) * 15, 28, 72));
      players.push({ id: "B" + (i + 1), role: "Contreur" + (nBlock > 1 ? " " + (i + 1) : ""), team: "B", color: "#D55E00", x: 57, y });
    }
    for (let i = 0; i < nBack; i++) {
      const y = Math.round(clampN(20 + i * (60 / (nBack - 1 || 1)), 14, 86));
      players.push({ id: "D" + (i + 1), role: "Défenseur" + (nBack > 1 ? " " + (i + 1) : ""), team: "B", color: "#E69F00", x: 78 + (i % 2) * 9, y });
    }
    for (let i = 0; i < nA; i++) {
      players.push({ id: "A" + (i + 1), role: i === 0 ? "Attaquant" : "Passeur", team: "A", color: "#0072B2", x: i === 0 ? 44 : 33, y: i === 0 ? 42 : 60 });
    }
    if (nA > 0) { markers.push({ type: "ballon", x: 40, y: 42 }); actions.push({ from: "A1", tox: 72, toy: 56, type: "tir", order: 1, label: "Attaque" }); }
    return {
      players, markers, actions,
      title: "Contre à " + nBlock + " + défense",
      analyse: { version: 1, famille: "defense", systeme: nBlock + "-" + nBack, effectif: { defenseurs: nBlock + nBack, contre: nBlock, attaquants: nA } },
    };
  }

  let tiers = sys.split(/[^0-9]+/).filter(Boolean).map(Number).filter((n) => n >= 0 && n <= 8);
  const tierSum = tiers.reduce((a, b) => a + b, 0);
  let nD = parseInt(eff.defenseurs, 10);
  if (!tiers.length || tierSum === 0) {                        // pas de système chiffré
    if (isFoot) tiers = [4, 4, 2];                             // bloc football : équipe complète par défaut
    else if (nD >= 1 && nD <= 8) tiers = [nD];                 // hand/basket : un seul rideau
    else return null;
    nD = tiers.reduce((a, b) => a + b, 0);
  } else nD = tierSum;
  const nMax = isFoot ? 11 : 8;
  if (nD < 1 || nD > nMax) return null;
  const nA = Math.min(8, Math.max(0, parseInt(eff.attaquants, 10) || 0));
  const gardien = (sport === "handball" || isFoot) && eff.gardien !== false;
  // Football : BLOC compact dont la hauteur (bas/médian/haut) fixe la fenêtre en x
  // devant le but DÉFENDU (à droite). tier 0 (défenseurs) au plus près du but.
  const hauteur = /haut/.test(sys) ? "haut" : /m[ée]dian|moyen|medium/.test(sys) ? "médian" : "bas";
  const win = hauteur === "haut" ? [30, 64] : hauteur === "médian" ? [45, 78] : [60, 88];

  const players = [], actions = [], markers = [];
  // Placement du système via la GÉOMÉTRIE PARTAGÉE (même moteur que l'attaque
  // d'invasion contre un système) : arc handball, zones basket, lignes foot.
  defensiveSystemPlayers(tiers, sport, { hint: sys }).players.forEach((d) => players.push(d));
  if (gardien) players.push({ id: "G", role: "Gardien", team: "k", color: "#111827", x: 95, y: 50 });
  // LIGNE D'ATTAQUE éventuelle face au dispositif. En HANDBALL, une attaque CONTRE une
  // défense placée se joue dans le DEMI-TERRAIN ADVERSE (pas dans son camp) : on poste les
  // attaquants à leurs PLACES classiques (ailiers larges, ligne arrière ARG/demi-centre/ARD,
  // pivot devant la zone) DEVANT la défense, et on indique leur MISE EN PLACE depuis une
  // entrée à la médiane (course en pointillés). Convention : but à DROITE, aile G = haut.
  if (nA > 0 && sport === "handball") {
    const POSTS = [
      { role: "Demi-centre", x: 58, y: 50 }, { role: "Arrière G", x: 56, y: 32 }, { role: "Arrière D", x: 56, y: 68 },
      { role: "Ailier G", x: 69, y: 15 }, { role: "Ailier D", x: 69, y: 85 }, { role: "Pivot", x: 73, y: 50 },
    ].slice(0, Math.min(nA, 6));
    POSTS.forEach((p, i) => {
      players.push({ id: "A" + (i + 1), role: p.role, team: "A", color: "#0072B2", x: p.x, y: p.y });
      const sy = Math.round(clampN(26 + (POSTS.length > 1 ? i / (POSTS.length - 1) : 0.5) * 48, 14, 86));
      actions.push({ fromx: 46, fromy: sy, tox: p.x, toy: p.y, type: "course", style: "dashed", ...(i === 0 ? { label: "Mise en place" } : {}) });
    });
    markers.push({ type: "ballon", x: POSTS[0].x - 4, y: POSTS[0].y });
  } else if (nA > 0) {
    // Autres sports : ligne d'attaque simple face au dispositif (à gauche).
    for (let i = 0; i < nA; i++) {
      const y = Math.round(clampN(20 + (nA > 1 ? i / (nA - 1) : 0.5) * 60, 14, 86));
      players.push({ id: "A" + (i + 1), role: i === Math.floor((nA - 1) / 2) ? "Porteur" : "Attaquant", team: "A", color: "#0072B2", x: 34 - (i % 2) * 6, y });
    }
    markers.push({ type: "ballon", x: 30, y: 50 });
  }
  // PRESSING / ZONE PRESS « TOUT TERRAIN » (basket surtout) : le dispositif ne
  // protège pas son panier, il HARCÈLE la remontée — les lignes sont AVANCÉES
  // vers le camp adverse (près du porteur), pas postées devant leur but.
  const pressT = /tout terrain|press(?:ing)?\s+haut|zone press/i.test(coreSituationText(String(text || "")));
  if (pressT && ["basketball", "basket3x3"].includes(sport))
    for (const p of players) if (p.team === "B") p.x = Math.round(clampN(p.x - 34, 24, 60));
  // Lisibilité : le défenseur le plus AVANCÉ monte sur le porteur.
  const defs = players.filter((p) => p.team === "B");
  const head = defs.reduce((a, b) => (b.x < a.x ? b : a), defs[0]);
  if (head) actions.push({ from: head.id, tox: Math.round(clampN(head.x - 10, 8, 90)), toy: head.y, type: "course", style: "dashed", order: 1, label: "Monte sur le porteur" });
  // « PRISE À DEUX » : le 2e défenseur le plus proche du porteur vient doubler.
  if (head && /prise [àa] deux/i.test(coreSituationText(String(text || "")))) {
    const second = defs.filter((d) => d !== head).reduce((a, b) => (a == null || Math.abs(b.y - head.y) < Math.abs(a.y - head.y) ? b : a), null);
    if (second) actions.push({ from: second.id, tox: Math.round(clampN(head.x - 8, 8, 90)), toy: Math.round((second.y + head.y) / 2), type: "course", style: "dashed", order: 2, label: "Prise à deux" });
  }

  const label = (tierSum ? sys.replace(/[^0-9]+/g, "-").replace(/^-|-$/g, "") : `${nD}`);
  const title = isFoot
    ? `Bloc ${hauteur}` + (tierSum ? ` (${label})` : "")
    : "Défense " + (tierSum ? label : `${nD} joueurs`);
  return {
    players, markers, actions, title,
    analyse: { version: 1, famille: "defense", systeme: tierSum ? label : (isFoot ? `bloc_${hauteur}` : String(nD)), effectif: { defenseurs: nD, attaquants: nA, gardien } },
  };
}

/* DÉCOUPAGE EN DIAGONALE (hybride invasion) — le terrain est coupé par une
   DIAGONALE de plots et DEUX 3v2 jouent SIMULTANÉMENT en MIROIR, chacun vers
   SON but. On part de la scène d'invasion (groupe 1, qui attaque le but DROIT),
   on la DÉCALE sous l'anti-diagonale (triangle bas-droite), puis on pose sa
   ROTATION 180° autour du centre (groupe 2, triangle haut-gauche). Cette
   rotation envoie le but DROIT exactement sur le but GAUCHE : tirs (cible = but)
   et gardiens se replacent donc TOUT SEULS, sans cas particulier. Les flèches
   sont mirorées (une passe vers la droite devient une passe vers la gauche). */
export function applyDiagonalMirror(scene) {
  if (!scene || !Array.isArray(scene.players)) return scene;
  const isGk = (p) => p && (p.team === "k" || /gardien|keeper/i.test(p.role || ""));
  const R = (v) => 100 - v;              // rotation 180° (coordonnée)
  // Transformation AFFINE (donc cohérente joueurs/flèches) qui range le groupe 1
  // DANS le triangle bas-droite : on resserre l'étalement vertical (SY) et on
  // pousse vers le bas-droite (DX, DY) pour DÉGAGER la médiane — la séparation
  // doit se lire comme une DIAGONALE, pas comme la ligne médiane.
  const DX = 10, SY = 0.72, DY = 18;
  const tx = (x) => clampN(x + DX, 4, 96);
  const ty = (y) => clampN(50 + (y - 50) * SY + DY, 6, 94);

  // 1) DÉCALAGE du groupe 1. Le gardien reste devant SON but ; les tirs visent le
  //    but (cible fixe) -> on ne les décale pas.
  for (const p of scene.players) if (!isGk(p)) { p.x = tx(p.x); p.y = ty(p.y); }
  for (const m of (scene.markers || [])) { m.x = tx(m.x); m.y = ty(m.y); }
  for (const a of (scene.actions || [])) {
    if (a.type === "tir" || typeof a.tox !== "number") continue;
    a.tox = tx(a.tox); a.toy = ty(a.toy);
  }

  // 2) GROUPE 2 = rotation 180°. Nouveaux identifiants (pas de collision) ;
  //    numéros décalés ; rôles conservés (chaque groupe a son porteur/pivot…).
  const nA = scene.players.filter((p) => p.team === "A").length;
  const idMap = {};
  const players2 = scene.players.map((p) => {
    const np = { ...p, x: R(p.x), y: R(p.y) };
    if (isGk(p)) np.id = "G2";
    else { np.id = p.id + "b"; np.num = (p.num || 0) + nA; }
    idMap[p.id] = np.id;
    return np;
  });
  const actions2 = (scene.actions || []).map((a) => {
    const na = { ...a };
    // Le déroulement pédagogique liste les actions par « order » : la copie
    // miroir garde les MÊMES numéros mais SANS libellés, sinon chaque étape
    // apparaissait deux fois (« 1. passe 1 / 2. passe 1 / 3. appel… »).
    na.label = null;
    if (a.from) na.from = idMap[a.from] || a.from;
    if (a.to) na.to = idMap[a.to] || a.to;
    if (typeof a.tox === "number") { na.tox = R(a.tox); na.toy = R(a.toy); }
    return na;
  });
  const markers2 = (scene.markers || []).map((m) => ({ ...m, x: R(m.x), y: R(m.y) }));

  // 3) DIAGONALE de plots (anti-diagonale bas-gauche -> haut-droite) séparant les
  //    deux demi-terrains.
  const diag = [];
  for (let i = 1; i <= 7; i++) { const u = i / 8; diag.push({ type: "plot", x: Math.round(100 * u), y: Math.round(100 * (1 - u)) }); }

  scene.players.push(...players2);
  scene.actions = [...(scene.actions || []), ...actions2];
  scene.markers = [...(scene.markers || []), ...markers2, ...diag];
  return scene;
}

/* GROUPES EN PARALLÈLE — « terrain coupé en deux/trois dans la largeur, N groupes
   qui travaillent en même temps » : on DUPLIQUE la scène (un même exercice) dans
   N bandes empilées EN LARGEUR (axe y), séparées par une ligne de plots. Le fond
   de terrain (filet vertical) est partagé par les bandes. Renvoie le nombre de
   groupes lu dans le texte (1 = pas de duplication). Conservateur : exige une
   VRAIE consigne de découpage du terrain (pas un simple « 2 joueurs »). */
const NUMW_GROUPS = { deux: 2, trois: 3, quatre: 4, "2": 2, "3": 3, "4": 4 };
/* « N terrains / N tables » : PLUSIEURS courts physiques côte à côte (≠ un
   terrain découpé en zones). Partagé avec le hook multi-courts du routage. */
const MULTI_COURTS_RE = /\b(deux|trois|quatre|[2-9])\s+(?:demi.?)?(?:terrains?|tables?)\b/;
export function multiCourtsCount(text) {
  const m = String(text || "").toLowerCase().match(MULTI_COURTS_RE);
  return m ? clampN(NUMW_GROUPS[m[1]] || parseInt(m[1], 10) || 1, 1, 4) : 1;
}
/* TERRAINS DE JEU parallèles au filet — « deux matchs / parties », ou un
   terrain COUPÉ en N + une CONFRONTATION répétée dans chaque bande (« 3x3 sur
   chacun », « 1x1 dans chaque couloir »). Distinct d'un terrain découpé en
   zones-CIBLES (atelier de service) : là, chaque subdivision n'est PAS un
   terrain de jeu et on ne duplique pas la scène. */
export function parallelCourtsFilet(text) {
  const t = String(text || "").toLowerCase();
  const games = t.match(/\b(deux|trois|quatre|[2-4])\s+(?:matchs?|parties?|rencontres?|jeux)\b/);
  if (games) return clampN(NUMW_GROUPS[games[1]] || parseInt(games[1], 10) || 2, 2, 4);
  const split = /(terrain|court)[^.]{0,30}?(coup[ée]|divis[ée]|s[ée]par[ée]|partag[ée])/.test(t);
  const perGame = /\b\d\s*(?:x|contre|c\.?)\s*\d\b|\b1\s*(?:x|contre|c\.?)\s*1\b/.test(t)
    && /(dans|sur|par|pour)\s+(chaque|chacun)|\bdans chaque (?:couloir|bande|terrain)/.test(t);
  if (split && perGame) {
    const cm = t.match(/(?:coup[ée]|divis[ée]|s[ée]par[ée])[^.]{0,20}?en\s+(deux|trois|quatre|[2-4])\b/)
      || t.match(/\b(deux|trois|quatre|[2-4])\s+(?:couloirs?|bandes?)\b/);
    return cm ? clampN(NUMW_GROUPS[cm[1]] || parseInt(cm[1], 10) || 2, 2, 4) : 2;
  }
  return 1;
}
/* MINI-TERRAINS PARALLÈLES (sports d'invasion) — « terrain découpé en 3 bandes
   pour faire 3 mini terrains côte à côte, 4 contre 4 sur chacun » : chaque bande
   est un TERRAIN DE JEU complet, l'effectif annoncé y est dupliqué. Distinct
   d'un terrain simplement découpé en zones (couloirs de progression) : il faut
   un jeu À EFFECTIF annoncé DANS CHAQUE subdivision. Retour prod 👎 (le
   découpage sortait en couloirs de passe-et-suit, sans les 4 contre 4). */
export function parallelCourtsInvasion(text) {
  const t = c0(text || "");
  const mini = t.match(/\b(deux|trois|quatre|[2-4])\s+(?:mini.?terrains?|petits? terrains?|terrains?)\b/);
  const split = t.match(/(?:coup[ée]|d[ée]coup[ée]|divis[ée]|s[ée]par[ée]|partag[ée])[^.]{0,24}?en\s+(deux|trois|quatre|[2-4])\b/)
    || t.match(/\b(deux|trois|quatre|[2-4])\s+(?:bandes?|couloirs?)\b/);
  if (!mini && !split) return 1;
  // Un JEU À EFFECTIF (« 4 contre 4 », « 3x3 ») joué DANS CHAQUE subdivision.
  const perCourt = /\b\d\s*(?:contre|c\.?|vs|x)\s*\d\b/.test(t)
    && /(?:sur|dans|par|pour)\s+(?:chaque|chacun)|\bpar (?:mini.?terrain|terrain|bande|couloir)\b/.test(t);
  if (!perCourt) return 1;
  const w = (mini || split)[1];
  return clampN(NUMW_GROUPS[w] || parseInt(w, 10) || 2, 2, 4);
}

export function parallelGroupsCount(text) {
  const t = String(text || "").toLowerCase();
  // Signal FORT : le TERRAIN est coupé/divisé/séparé, « N terrains/tables »,
  // ou des GROUPES qui travaillent « en parallèle ». Un « en parallèle » NU ne
  // suffit pas : c'est aussi une DIRECTION de frappe (« dégage en parallèle »),
  // il doit se rapporter à des groupes/terrains pour compter.
  const splitCourt = /(terrain|demi.?terrain)[^.]{0,40}?(coup[ée]|divis[ée]|s[ée]par[ée]|partag[ée])/.test(t)
    || /(coup[ée]|divis[ée]|s[ée]par[ée])[^.]{0,24}?(en\s+)?(deux|trois|quatre|\d)[^.]{0,16}?(largeur|longueur|profondeur|moiti|terrains?)/.test(t)
    || /\bsur\s+(deux|trois|quatre|\d)\s+(demi.?terrains?|sous.?terrains?|terrains?|tables?)/.test(t)
    || MULTI_COURTS_RE.test(t)
    || /(groupes?|[ée]quipes?|ateliers?|terrains?|tables?|exercices?|matchs?|binômes?|paires?|travaillent|jouent)[^.]{0,24}?\ben\s+parall[èe]le\b/.test(t)
    || /\ben\s+parall[èe]le\b[^.]{0,24}?(groupes?|[ée]quipes?|terrains?|tables?|binômes?|paires?)/.test(t);
  if (!splitCourt) return 1;
  const m = t.match(/\b(deux|trois|quatre|\d)\s+(?:groupes?|binômes?|paires?)\b/)
    || t.match(/(coup[ée]|divis[ée]|s[ée]par[ée])[^.]{0,20}?en\s+(deux|trois|quatre|\d)\b/)
    || t.match(/\bsur\s+(deux|trois|quatre|\d)\s+(?:demi.?terrains?|sous.?terrains?|terrains?|tables?)/)
    || t.match(MULTI_COURTS_RE);
  const w = m ? (m[2] && NUMW_GROUPS[m[2]] ? m[2] : m[1]) : null;
  const n = w != null ? (NUMW_GROUPS[w] || parseInt(w, 10)) : 2;   // « en parallèle » sans nombre -> 2
  return clampN(n || 2, 1, 4);
}

export function applyParallelGroups(scene, n) {
  if (!scene || !Array.isArray(scene.players) || !(n >= 2)) return scene;
  n = Math.min(n, 4);
  const M = 5, band = 100 / n;
  const yb = (y, k) => Math.round(clampN(k * band + M + (clampN(y, 0, 100) / 100) * (band - 2 * M), 3, 97));
  const baseP = scene.players.map((p) => ({ ...p }));
  const baseA = (scene.actions || []).map((a) => ({ ...a }));
  const baseM = (scene.markers || []).map((m) => ({ ...m }));
  const players = [], actions = [], markers = [];
  for (let k = 0; k < n; k++) {
    const suf = k === 0 ? "" : "_g" + (k + 1);
    const idMap = {};
    for (const p of baseP) { const id = p.id + suf; idMap[p.id] = id; players.push({ ...p, id, y: yb(p.y, k) }); }
    for (const a of baseA) {
      const na = { ...a };
      if (a.from) na.from = idMap[a.from] || a.from;
      if (a.to) na.to = idMap[a.to] || a.to;
      if (typeof a.toy === "number") na.toy = yb(a.toy, k);
      if (typeof a.fromy === "number") na.fromy = yb(a.fromy, k);
      actions.push(na);
    }
    for (const m of baseM) markers.push({ ...m, y: yb(m.y, k) });
  }
  // Ligne(s) de séparation entre bandes (plots sur toute la longueur).
  for (let k = 1; k < n; k++) {
    const y = Math.round(k * band);
    for (const x of [10, 30, 50, 70, 90]) markers.push({ type: "plot", x, y });
  }
  scene.players = players; scene.actions = actions; scene.markers = markers;
  return scene;
}

/* HOOK MULTI-COURTS (sports de filet) — « 3 terrains dans la largeur », « 4
   tables », « montante-descendante » : l'exercice se joue sur N COURTS
   PHYSIQUES côte à côte, quelle que soit la route retenue (analyse, template,
   hybride). Appliqué par routeDecision APRÈS l'arbitrage :
   - scène déjà dupliquée (route analyse filet) -> ne rien faire ;
   - scène = ÉCHANGE mono-court -> la dupliquer dans N bandes ;
   - scène inadaptée (atelier générique, zones dégénérées) -> reconstruire la
     CELLULE (montante : match 1c1 synthétique ; sinon moteur local) puis dupliquer.
   Montante-descendante : bandes étiquetées « Terrain k » (1 = en haut) +
   flèches gagnant ↑ / perdant ↓ entre terrains adjacents.
   Renvoie la scène (mutée ou reconstruite), ou null si rien à faire. */
const MONTANTE_RE = /montante.?descendante|gagnant[^.]{0,40}?\bmonte\b|perdant[^.]{0,40}?\bdescend/i;
export function multiCourtsFilet(scene, text, sport, kind) {
  if (!scene) return null;
  const montante = MONTANTE_RE.test(String(text || ""));
  // « N terrains/tables » OU des JEUX parallèles (« deux matchs », « 1x1 dans
  // chaque couloir ») sur un terrain coupé — mais PAS un terrain découpé en
  // zones-CIBLES (le « terrain adverse coupé en 4 carrés » d'un atelier de
  // service ne fait pas 4 terrains de jeu).
  let n = Math.max(multiCourtsCount(text), parallelCourtsFilet(text));
  if (n < 2) { if (!montante) return null; n = 3; }   // montante sans effectif de terrains : 3
  // Scène DÉJÀ dupliquée (route analyse : applyParallelGroups) : on n'y touche
  // plus… sauf l'HABILLAGE montante (étiquettes Terrain k + flèches gagnant ↑ /
  // perdant ↓), qui était perdu sur ce chemin.
  if ((scene.players || []).some((p) => /_g\d$/.test(String(p.id)))) {
    if (!montante || (scene.zones || []).some((z) => /^Terrain \d/.test(z.label || ""))) return null;
    const bands = 1 + Math.max(0, ...(scene.players || []).map((p) => { const m = /_g(\d+)$/.exec(String(p.id)); return m ? +m[1] - 1 : 1; }));
    dressMontante(scene, Math.min(Math.max(bands, 2), 4));
    return scene;
  }
  const isExchange = (sc) => !!sc && (sc.players || []).some((p) => p.team === "A" && +p.x < 50)
    && (sc.players || []).some((p) => p.team === "B" && +p.x > 50)
    && (sc.actions || []).some((a) => a.type === "renvoi" || a.type === "tir");
  let cell = null;
  if (isExchange(scene)) cell = scene;
  else if (montante) cell = buildRacketSequence("", sport, { format: "simple", shots: [
    { type: "renvoi", name: "Échange", seg: "", who: "A" },
    { type: "renvoi", name: "Échange", seg: "", who: "B" },
  ] });
  else { const lp = localParse(text, { sport, kind }); if (isExchange(lp)) cell = lp; }
  if (!cell) return null;
  const out = applyParallelGroups(cell, n);
  if (montante) dressMontante(out, Math.min(n, 4));
  return out;
}
/* Habillage montante-descendante d'une scène dupliquée en N bandes :
   étiquettes « Terrain k » + flèches de circulation gagnant ↑ / perdant ↓. */
function dressMontante(out, n) {
  const band = 100 / n;
  const zones = out.zones || (out.zones = []);
  const actions = out.actions || (out.actions = []);
  for (let k = 0; k < n; k++)
    zones.push({ x: 1, y: Math.round(k * band + 1), w: 10, h: 5, label: `Terrain ${k + 1}`, style: "dashed" });
  for (let k = 1; k < n; k++) {
    const up = Math.round(k * band - band * 0.3), dn = Math.round(k * band + band * 0.3);
    actions.push({ fromx: 4, fromy: dn, tox: 4, toy: up, type: "rotation", label: k === 1 ? "Gagnant ↑" : null });
    actions.push({ fromx: 96, fromy: up, tox: 96, toy: dn, type: "rotation", label: k === 1 ? "Perdant ↓" : null });
  }
}

/* Sprint SOLO BALISÉ (≠ vagues) : un couloir, un coureur en départ arrêté à la
   ligne 0 m, des plots JALONNÉS aux distances données (« premier à 16 m puis
   tous les 3 m jusqu'à 31 m » ou une liste de « N m »), un observateur/chrono
   au 1er plot (signal sonore à 5 s) qui relève la distance atteinte. */
/* Noms de couleurs FR -> hex des plots (alignés sur la palette de cônes de
   l'éditeur), pour rendre les couleurs quand le texte/l'analyse les précise. */
const PLOT_COLOR_HEX = { blanc: "#FFFFFF", rouge: "#DC2626", jaune: "#F2C200", vert: "#15803D", bleu: "#1366D6", orange: "#EA7317", noir: "#1E293B" };
const PLOT_COLOR_RE = /\b(blanc(?:he)?s?|rouges?|jaunes?|verte?s?|bleue?s?|oranges?|noire?s?)\b/g;
function normPlotColor(w) {
  w = String(w || "").toLowerCase();
  if (w.startsWith("blanc")) return "blanc"; if (w.startsWith("roug")) return "rouge";
  if (w.startsWith("jaun")) return "jaune"; if (w.startsWith("vert")) return "vert";
  if (w.startsWith("bleu")) return "bleu"; if (w.startsWith("orang")) return "orange";
  if (w.startsWith("noir")) return "noir"; return null;
}
/* Associe une couleur à chaque distance de plot. Cas courant (description
   séquentielle « plot blanc à 16 m, rouge à 19 m… ») : on zippe couleurs et
   distances dans l'ORDRE quand les comptes coïncident ; sinon, repli sur la
   couleur la plus PROCHE en position dans le texte. */
function plotColorsFor(t, dists) {
  const cols = []; let m; const re = new RegExp(PLOT_COLOR_RE.source, "g");
  while ((m = re.exec(t))) { const c = normPlotColor(m[1]); if (c) cols.push({ pos: m.index, c }); }
  if (!cols.length) return dists.map(() => null);
  if (cols.length === dists.length) return cols.map((x) => PLOT_COLOR_HEX[x.c]);
  return dists.map((d) => {
    let best = null, bd = 1e9; const dre = new RegExp("\\b" + d + "\\s*m", "g"); let dm;
    while ((dm = dre.exec(t))) for (const c of cols) { const delta = Math.abs(c.pos - dm.index); if (delta < bd) { bd = delta; best = c.c; } }
    return best && bd <= 60 ? PLOT_COLOR_HEX[best] : null;
  });
}

function buildSprintBalise(text) {
  const t = c0(text);
  // Zone d'ÉLAN (départ lancé) : sa distance borne la ligne de départ/chrono et
  // n'est PAS un plot. « 4 secondes » etc. ne sont pas captés (pas de « m »).
  const elan = t.match(/zone\s+d['e ]?[ée]lan\s+de\s*(\d+)\s*m/) || t.match(/[ée]lan\s+de\s*(\d+)\s*m/);
  const elanD = elan ? +elan[1] : null;
  const lance = /d[ée]part\s*lanc/.test(t) || elanD != null;
  const sec = (t.match(/(\d+)\s*secondes?\b/) || t.match(/au bout de\s*(\d+)\s*s\b/) || [])[1];
  const secLabel = sec ? sec + " s" : "5 s";

  let dists = [];
  const arith = t.match(/(?:premier|1er|1 er)[^.]{0,24}?(\d+)\s*m[^.]{0,40}?tous les\s*(\d+)\s*m[^.]{0,30}?jusqu['’ ]?(?:à|a)\s*(\d+)\s*m/);
  if (arith) { const s = +arith[1], st = Math.max(1, +arith[2]), e = +arith[3]; for (let d = s; d <= e + 0.001 && dists.length < 12; d += st) dists.push(Math.round(d)); }
  if (dists.length < 2) {
    const all = [...t.matchAll(/(\d+)\s*m(?:[èe]tres?)?\b/g)].map((m) => +m[1]).filter((v) => v >= 5 && v <= 200);
    if (all.length >= 2) dists = [...new Set(all)].sort((a, b) => a - b);
  }
  if (dists.length < 2) dists = [10, 20, 30];
  // La distance d'élan est une LIGNE de départ, pas un plot : on la retire de la
  // liste des plots si elle s'y trouve.
  if (elanD != null) dists = dists.filter((d) => d !== elanD);
  if (dists.length < 1) dists = [10, 20, 30];

  const maxD = Math.max(...dists), y = 50, x0 = 9, x1 = 92;
  const xOf = (d) => Math.round(clampN(x0 + (d / maxD) * (x1 - x0), x0, x1));
  const players = [], markers = [], actions = [], zones = [];
  zones.push({ x: x0, y: 40, w: x1 - x0, h: 20, style: "dashed", label: "Couloir" });
  players.push({ id: "C1", num: 1, role: "Coureur", team: "A", x: x0, y, color: "#0072B2" });
  // Plots cotés, COLORÉS quand le texte le précise (rendu fidèle aux consignes).
  const cols = plotColorsFor(t, dists);
  dists.forEach((d, i) => markers.push({ type: "plot", x: xOf(d), y, label: d + " m", ...(cols[i] ? { color: cols[i] } : {}) }));

  // Départ LANCÉ : ligne de départ/chrono à la fin de la zone d'élan, et la
  // zone d'élan cotée (double-flèche). Sinon départ arrêté sur la ligne (0 m).
  let obsX;
  if (elanD != null) {
    const dx = xOf(elanD);
    zones.push({ x: dx, y: 34, w: 0, h: 32, style: "solid", label: "Départ / chrono" });
    zones.push({ x: x0, y: 33, w: dx - x0, h: 0, style: "dashed", dim: elanD + " m (élan)" });
    obsX = dx;
  } else {
    obsX = xOf(dists[0]);
    zones.push({ x: x0, y: 33, w: obsX - x0, h: 0, style: "dashed", dim: dists[0] + " m" });
  }
  players.push({ id: "O1", role: "Observateur / chrono — signal " + secLabel, team: "OBS", x: obsX, y: 72, color: "#F2C200" });
  actions.push({ from: "C1", tox: xOf(maxD), toy: y, type: "course", order: 1, label: `Sprint ${secLabel} — départ ${lance ? "lancé" : "arrêté"}` });
  const steps = lance
    ? [`Lancement dans la zone d'élan (${elanD ?? "—"} m)`, `Au passage de la ligne de départ, sprint ${secLabel} à vitesse maximale`, `Au signal (${secLabel}), l'observateur relève le plot atteint`]
    : ["Départ arrêté à la ligne (0 m)", "Sprint à vitesse maximale", `Au signal sonore (${secLabel}), l'observateur relève le plot le plus proche`];
  return { players, markers, actions, zones, steps, terrainOpts: { plain: true } };
}

function buildPetanque(text) {
  const t = c0(text);
  const A_COLOR = "#0072B2", B_COLOR = "#D55E00";
  // L'adversaire TIRE pour dégager la boule bien placée ?
  const wantsTir = /\btire?(?:nt|r)?\b|d[ée]gage|carreau|chass|frappe/.test(t);
  // CARREAU : le tireur chasse la boule ET prend EXACTEMENT sa place (le geste
  // emblématique de la pétanque) — on l'illustre spécifiquement.
  const carreau = /carreau/.test(t);
  // « au ras / contre / collé » : la boule doit TOUCHER presque le cochonnet.
  const auRas = /au ras|contre le cochonnet|coll|tout pr[èe]s|sur le cochonnet|au plus pr[èe]s|au plus proche/.test(t);
  const coch = { x: 74, y: 46 };
  // MÈNE : plusieurs boules des DEUX équipes autour du cochonnet, on pointe à
  // tour de rôle, la plus proche tient « le point ». Sans tir explicite, on
  // dessine l'amas (positions fixes, déterministes) plutôt qu'une seule boule.
  const mene = !wantsTir && /\bm[èe]ne\b|chaque [ée]quipe|(?:deux|trois|quatre|cinq|six|\d+)\s+boules|plusieurs boules|[àa] tour de r[ôo]le|tour [àa] tour/.test(t);
  if (mene) {
    const players = [{ id: "L1", role: "Pointeur", x: 11, y: 44, color: A_COLOR, team: "A" }];
    // Couronne de boules autour du cochonnet ; la 1re (A) est la plus proche.
    const ring = [
      { x: 71, y: 49, color: A_COLOR, point: true },   // le point : boule la plus proche (A)
      { x: 79, y: 41, color: B_COLOR },
      { x: 81, y: 52, color: A_COLOR },
      { x: 68, y: 39, color: B_COLOR },
      { x: 83, y: 47, color: A_COLOR },
    ];
    const markers = [{ type: "cochonnet", x: coch.x, y: coch.y }];
    for (const b of ring) markers.push({ type: "boule", x: b.x, y: b.y, color: b.color, ...(b.point ? { label: "le point" } : {}) });
    const actions = [{ from: "L1", tox: ring[0].x, toy: ring[0].y, type: "pointer", order: 1, label: "Pointer au plus près" }];
    return { players, markers, actions, steps: ["Chaque joueur pointe à tour de rôle", "Le point : la boule la plus proche du cochonnet"] };
  }
  // Boule pointée : « au ras » -> quasiment tangente au cochonnet ; sinon proche.
  const placed = auRas ? { x: 72, y: 48 } : { x: 66, y: 51 };
  const players = [{ id: "L1", role: "Pointeur", x: 11, y: 44, color: A_COLOR, team: "A" }];
  const markers = [
    { type: "cochonnet", x: coch.x, y: coch.y },                       // pas de label (encombrant si beaucoup de boules)
    { type: "boule", x: placed.x, y: placed.y, color: A_COLOR },       // boule de A, bien placée
  ];
  const actions = [
    { from: "L1", tox: placed.x, toy: placed.y, type: "pointer", order: 1, label: "Pointer au ras du cochonnet" },
  ];
  const steps = ["Pointer au plus près du cochonnet"];
  if (wantsTir) {
    players.push({ id: "L2", role: "Tireur", x: 13, y: 57, color: B_COLOR, team: "B" });
    actions.push({ from: "L2", tox: placed.x, toy: placed.y, type: "tir", order: 2, label: carreau ? "Tir au fer (carreau)" : "Tirer pour dégager la boule" });
    // La boule de A (bien placée) est CHASSÉE, loin du cochonnet.
    const out = { x: clampN(placed.x + 12, 6, 92), y: clampN(placed.y + 16, 8, 90) };
    markers.push({ type: "boule", x: out.x, y: out.y, color: A_COLOR });
    actions.push({ fromx: placed.x, fromy: placed.y, tox: out.x, toy: out.y, type: "course", order: 3, label: "Boule dégagée" });
    if (carreau) {
      // La boule du TIREUR prend la place laissée libre : on repeint la marque de
      // départ (« placed ») aux couleurs du tireur — B occupe désormais le point.
      markers[1] = { type: "boule", x: placed.x, y: placed.y, color: B_COLOR };
      steps.push("Carreau : le tireur chasse la boule et prend sa place");
    } else {
      steps.push("L'adversaire tire pour dégager la boule bien placée");
    }
  }
  return { players, markers, actions, steps };
}

/* ---- Famille ATHLÉTISME : relais-vitesse ---------------------------------- */
function buildRelay() {
  const players = [
    { id: "S1", role: "Donneur", x: 28, y: 52, color: "#0072B2", team: "A" },
    { id: "E1", role: "Receveur", x: 50, y: 48, color: "#0072B2", team: "A" },
  ];
  const zones = [{ x: 38, y: 30, w: 26, h: 42, label: "Zone de transmission", style: "dashed" }];
  const markers = [{ type: "temoin", x: 32, y: 52, label: "Témoin" }];
  const actions = [
    { from: "E1", tox: 64, toy: 48, type: "course", order: 1, label: "L'entrant lance sa course" },
    { from: "S1", tox: 50, toy: 52, type: "course", order: 2, label: "Le sortant rejoint la zone" },
    { from: "S1", to: "E1", type: "passe", order: 3, label: "Transmission du témoin" },
  ];
  return { zones, players, markers, actions, steps: ["L'entrant démarre dans la zone", "Le sortant arrive lancé", "Transmission du témoin sans ralentir"] };
}

/* Course de VITESSE en vagues (montante-descendante) : couloirs empilés =
   vagues ; coureurs sur la ligne de départ, sprint vers la ligne d'arrivée ;
   starter en bout de couloir ; rotation montante (le 1er) / descendante (le
   dernier) entre les vagues. Deux vagues dessinées = représentatives. */
function buildSprintWaves(text) {
  const t = c0(text);
  const mm = t.match(/vagues? de (\d+|deux|trois|quatre)/);
  const per = mm ? clampN(frenchNumWord(mm[1]), 2, 3) : 3;
  const dm = t.match(/(\d+)\s*m(?:[èe]tres)?\b/);
  const dist = (dm ? dm[1] : "10") + " m";
  const bands = [{ y: 8, h: 36, label: "Vague supérieure" }, { y: 56, h: 36, label: "Vague inférieure" }];
  const startX = 14, finishX = 74;
  const zones = [], markers = [], players = [], actions = [];
  let id = 1, ord = 1;
  bands.forEach((b, bi) => {
    zones.push({ x: 8, y: b.y, w: 72, h: b.h, label: b.label, dim: bi === 0 ? dist : "", style: "dashed" });
    [b.y, b.y + b.h].forEach((yy) => {
      markers.push({ type: "plot", x: startX, y: Math.round(yy) });
      markers.push({ type: "plot", x: finishX, y: Math.round(yy) });
    });
    for (let i = 0; i < per; i++) {
      const y = Math.round(b.y + ((i + 1) / (per + 1)) * b.h);
      const p = { id: "C" + id, num: id, role: i === 1 ? "Coureurs" : "", team: "A", x: startX, y, color: "#0072B2" };
      players.push(p);
      actions.push({ from: p.id, tox: finishX, toy: y, type: "course",
        ...(i === 0 ? { order: ord++, label: "Sprint " + dist } : {}) });
      id++;
    }
    players.push({ id: "O" + (bi + 1), role: "Starter", team: "OBS", x: 88, y: Math.round(b.y + b.h / 2), color: "#F2C200" });
  });
  actions.push({ fromx: 81, fromy: bands[1].y + 6, tox: 81, toy: bands[0].y + bands[0].h - 4, type: "course", order: ord++, label: "Le 1er monte" });
  actions.push({ fromx: 85, fromy: bands[0].y + bands[0].h - 4, tox: 85, toy: bands[1].y + 6, type: "course", order: ord++, label: "Le dernier descend" });
  return { zones, markers, players, actions,
    steps: ["Départ au signal du starter", `Sprint sur ${dist} par vagues de ${per}`, "Le 1er monte d'une vague, le dernier descend", "Le 2e reste dans sa vague"] };
}

/* ---- Situations d'attaque collective (contre-attaque, montée de balle…) ----
   Lit les indices spatiaux du texte plutôt que de s'en remettre au placement
   souvent erroné de l'IA. */
const INVASION_SPORTS = ["football", "futsal", "handball", "basketball", "basket3x3", "rugby", "ultimate"];
const clampN = (v, a, b) => Math.max(a, Math.min(b, v));
/* « N contre M » en CHIFFRES ou en TOUTES LETTRES (« 3 contre 2 », « deux
   contre deux »…) : motif partagé pour que les nombres écrits soient reconnus
   partout (routage, cohérence) comme une situation à effectif réduit. */
const MATCHUP_RE = /(\d+|un|une|deux|trois|quatre|cinq|six|sept)\s*contre\s*(\d+|un|une|deux|trois|quatre|cinq|six|sept)/;
function frenchNumWord(w) {
  const m = { un: 1, une: 1, deux: 2, trois: 3, quatre: 4, cinq: 5, six: 6 };
  return m[w] != null ? m[w] : parseInt(w, 10);
}
function countAttackers(t) {
  let m = t.match(/(\d+|un|une|deux|trois|quatre|cinq|six)\s*(?:attaquant|joueur)/);
  if (m) return clampN(frenchNumWord(m[1]), 1, 6);
  m = t.match(/(\d+|deux|trois|quatre|cinq)\s*contre\s*(\d+|un|une|deux|trois)/);
  if (m) return clampN(frenchNumWord(m[1]), 1, 6);
  return 2;
}
function countDefenders(t) {
  // « N contre M » fait foi en PRIORITÉ : « fixe UN défenseur » ne doit pas
  // ramener l'effectif à 1 dans une situation « 3 contre 2 ».
  let m = t.match(/contre\s*(\d+|un|une|deux|trois)/);
  if (m) return clampN(frenchNumWord(m[1]), 0, 5);
  m = t.match(/(\d+|deux|trois)\s*(?:défenseurs|defenseurs)/);
  if (m) return clampN(frenchNumWord(m[1]), 0, 5);
  return 1;
}
/* Le texte donne-t-il des consignes de placement explicites ? -> on privilégie
   alors le moteur local (déterministe) sur le placement de l'IA. */
export function hasStrongLayoutCues(text) {
  const t = c0(text);   // normalise aussi « 2c1 » / « 5v2 » -> « contre »
  return MATCHUP_RE.test(t)
    || /\bligne m[ée]diane|(\d+|deux|trois|quatre|cinq|six)\s*(attaquant|d[ée]fenseur|joueur)|contre.?attaque|mont[ée]e de balle|surnombre|sup[ée]riorit|en retard|[àa] contre.?pied/.test(t);
}

/* Famille/template DÉTERMINISTE reconnu pour ce texte (placement fiable), ou
   null si la situation doit s'en remettre à la géométrie de l'IA.
   C'est le cœur du « template-first » : si une famille solide est reconnue,
   le code place la scène et l'IA ne fournit que le texte pédagogique. */
export function recognizedTemplate(text, opts = {}) {
  const T = TEMPLATES.find((x) => x.test(makeCtx(text, opts)));
  return (T && T.deterministic) ? T.id : null;
}

/* Templates GÉNÉRIQUES, équivalents à l'analyse structurée de l'IA (même
   géométrie) : pour eux, l'analyse IA est préférée (compréhension plus fine).
   Tout AUTRE template reconnu est un coup/SET-PLAY SPÉCIFIQUE (pick and roll,
   fixation, 2-c-1, rondo…) que l'IA générique ne saurait dessiner aussi bien. */
const ANALYSIS_GENERIC = new Set(["filet_echange", "invasion_attaque", "phase_jeu_nommee", "gym_parcours", "ateliers_nommes", "defi_tir_dribble"]);

/* SET-PLAY SPÉCIFIQUE reconnu ? — renvoie l'id du gabarit SEULEMENT s'il décrit
   un coup précis (pick and roll, fixation, 2-c-1, rondo, tournante…), jamais un
   gabarit GÉNÉRIQUE.

   C'est la porte du MODE SCÈNE DIRECTE (src/main.js) : un set-play spécifique
   est dessiné par le moteur local mieux que par l'IA et INSTANTANÉMENT, donc il
   court-circuite la scène directe ; un gabarit générique, lui, n'est qu'un
   équivalent grossier de l'analyse structurée (c'est déjà ce que dit
   `preferAiAnalysis` juste en dessous) et ne doit PAS priver l'enseignant de la
   scène directe.

   Distinguer les deux est indispensable : `recognizedTemplate` reconnaît un
   gabarit sur 581 des 695 prompts du lot doré, dont 407 GÉNÉRIQUES. L'employer
   tel quel comme porte réduisait la scène directe à 16 % du trafic — la
   bascule mesurée (garde+repli 591/682 contre 554/682 au pipeline classique)
   était de fait éteinte pour la majorité des enseignants. */
export function specificTemplate(text, opts = {}) {
  const id = recognizedTemplate(text, opts);
  return id && !ANALYSIS_GENERIC.has(id) ? id : null;
}

/* L'analyse IA doit-elle primer pour ce texte ? Oui si aucun template n'est
   reconnu, ou si le template reconnu est l'un des génériques ci-dessus.
   EXCEPTION filet : le moteur LOCAL (extraction des coups depuis le texte) est
   fiable et poli ; les "coups" de l'IA sont instables (sur-décomposition, coup
   gagnant mal classé). Pour les sports de filet, le local PRIME (l'IA conserve
   titre + pédagogie). */
export function preferAiAnalysis(text, opts = {}) {
  const sport = opts.sport || detectSport(text);
  // EXCEPTION filet : pour les sports de filet, le moteur local (extraction des
  // coups) prime — mais SEULEMENT pour un ÉCHANGE (rally). Si l'IA a structuré
  // une AUTRE famille (atelier, dispositif, parcours…), il n'existe pas de rally
  // local concurrent pertinent : on la laisse primer, sinon ces familles seraient
  // INACCESSIBLES sur volley/badminton/tennis/ping (un atelier 4 zones tombait
  // alors sur un template d'échange à 2 joueurs).
  const fam = opts.famille;
  // SYSTÈME DÉFENSIF : aucun template local concurrent — l'analyse IA prime.
  if (fam === "defense") return true;
  // ATTAQUE CONTRE UN BLOC (football/futsal) : défense COMPLÈTE (≥6) face à un petit
  // groupe (≤3) — aucun template local pertinent (les set-plays empilent les défenseurs
  // en colonne). L'analyse IA (bloc en lignes, réception entre les lignes) prime.
  if (fam === "invasion" && ["football", "futsal"].includes(sport) && (opts.defenseurs | 0) >= 6 && (opts.attaquants | 0) <= 3) return true;
  // ULTIMATE : pas de but/tir. L'invasion structurée par l'IA (handler immobile,
  // réception en zone) prime sur tout template local de sport À BUT (give-and-go qui
  // ajoute un « tir » et un défenseur fantôme).
  if (sport === "ultimate" && fam === "invasion") return true;
  if (NET_SPORTS.includes(sport)) {
    // Gabarits à géométrie DÉDIÉE de filet (la tournante, le panier de balles,
    // les colonnes réception-passe) : ils priment sur une analyse « atelier/
    // dispositif » générique (l'IA classe souvent ces drills en atelier ->
    // boîte sans rapport).
    const tid = recognizedTemplate(text, opts);
    if (tid === "volley_tournante" || tid === "panier_de_balles" || tid === "volley_passe_colonnes") return false;
    return !!fam && fam !== "filet";
  }
  const id = recognizedTemplate(text, opts);
  // « N couloirs » : le template local atelier_couloir n'est qu'un repli faible (2
  // files passe-suit). Si l'IA a STRUCTURÉ une famille (dispositif zones à N couloirs,
  // atelier…), on la préfère — sinon « trois couloirs » tombait sur 2 files génériques.
  if (id === "atelier_couloir" && fam) return true;
  return id == null || ANALYSIS_GENERIC.has(id);
}
/* opts.sport / opts.kind : valeurs IMPOSÉES par l'utilisateur (menu Sport,
   segmenté Match/Exercice) — elles priment sur la détection par mots-clés. */
function makeCtx(text, opts = {}) {
  return { text, t: c0(text), sport: opts.sport || detectSport(text), kind: opts.kind || detectKind(text) };
}

/* Le DESSIN de l'IA contredit-il les consignes explicites du texte ? Si oui,
   on préfère le moteur local. Vérifie l'effectif, le départ « ligne médiane »
   et la position « en retard » du défenseur. (Convention : but attaqué à droite.) */
export function isIncoherentScene(data, text) {
  const t = String(text || "").toLowerCase();
  const players = Array.isArray(data?.players) ? data.players : [];
  if (players.length < 2) return true;
  const isDef = (p) => /d[ée]fenseur|defense|barragiste/i.test(p.role || "");
  const isAux = (p) => /gardien|keeper|observ/i.test(p.role || "");
  const def = players.filter(isDef);
  const att = players.filter((p) => !isDef(p) && !isAux(p));
  const meanX = (arr) => arr.reduce((s, p) => s + (Number(p.x) || 0), 0) / (arr.length || 1);

  // 1) Effectif d'attaquants explicitement donné
  if (att.length && (/(\d+|deux|trois|quatre|cinq|six)\s*(attaquant|joueur)/.test(t) || MATCHUP_RE.test(t))) {
    if (att.length !== countAttackers(t)) return true;
  }
  // 2) « ligne médiane » -> attaquants proches du centre
  if (att.length && /ligne m[ée]diane|mi-?terrain|milieu de terrain|m[ée]dian|centre du terrain/.test(t)) {
    const m = meanX(att);
    if (m < 30 || m > 72) return true;
  }
  // 3) Défenseur « en retard » -> doit être DERRIÈRE les attaquants (x plus petit)
  if (def.length && att.length && /retard|poursui|revient|retour/.test(t)) {
    if (meanX(def) > meanX(att) + 6) return true;
  }
  // 4) Surnombre / supériorité annoncé -> attaquants strictement plus nombreux
  if (def.length && att.length && /surnombre|sup[ée]riorit/.test(t)) {
    if (att.length <= def.length) return true;
  }
  // 5) Placement aberrant : tous les joueurs alignés ou agglutinés dans un coin
  if (players.length >= 3) {
    const xs = players.map((p) => Number(p.x) || 0), ys = players.map((p) => Number(p.y) || 0);
    const rx = Math.max(...xs) - Math.min(...xs), ry = Math.max(...ys) - Math.min(...ys);
    if (rx < 8 || ry < 8) return true;        // tous sur une même ligne (verticale/horizontale)
    if (rx < 12 && ry < 12) return true;      // agglutinés
  }
  return false;
}

function buildTeamAttack(text, sport) {
  const t = text.toLowerCase();
  const nA = countAttackers(t), nD = countDefenders(t);
  const median = /ligne m[ée]diane|mi-?terrain|milieu de terrain|m[ée]dian|centre du terrain/.test(t);
  const late = /retard|poursui|revient|retour|d[ée]cal/.test(t);
  const startX = median ? 46 : 20;
  const goalSport = !["rugby", "ultimate"].includes(sport);   // rugby/ultimate : pas de tir, on aplatit/réceptionne

  // JEU DÉPLOYÉ / passes successives : le ballon CIRCULE d'attaquant en attaquant
  // vers l'extérieur (l'ailier conclut). On délègue le rendu au moteur d'analyse
  // (chaîne COMPLÈTE A1->A2->...->An), au lieu du schéma porteur -> 1 seul ailier.
  const deployed = /d[ée]ploy|passes?\s+successives|fait (?:circuler|tourner) le ballon|vers l['’ ]ext[ée]rieur/.test(t);
  if (deployed && nA >= 2) {
    const corner = /angle|coin|drapeau/.test(t);
    const scene = buildDeployedLine(sport, nA, nD, { median, corner });
    // Analyse synthétisée pour le panneau « Ce que j'ai compris » (chaîne).
    const depart = median ? "ligne_mediane" : "camp_propre";
    const phase = [{ acteur: "A1", action: "dribble", cible: null, note: "Lance le mouvement" }];
    for (let i = 1; i < nA; i++) phase.push({ acteur: "A" + i, action: "passe", cible: "A" + (i + 1), note: null });
    phase.push({ acteur: "A" + nA, action: "tir", cible: "but", note: goalSport ? null : "Essai" });
    scene.analyse = { version: 1, famille: "invasion", effectif: { attaquants: nA, defenseurs: nD, gardien: false }, depart, phase };
    return scene;
  }

  // HANDBALL : la zone de but (6 m) est STRICTEMENT INTERDITE aux joueurs.
  // On la modélise par une ellipse autour du but droit et on garde tous les
  // joueurs (et leurs déplacements) à l'extérieur — le ballon, lui, peut y entrer.
  const ZONE = sport === "handball" ? { rx: 23, ry: 36 } : null;
  const boundaryX = (y) => {
    if (!ZONE) return 96;
    const dy = Math.min(0.999, Math.abs(y - 50) / ZONE.ry);
    return 100 - ZONE.rx * Math.sqrt(1 - dy * dy);
  };
  const keepOut = (x, y, margin = 2) => Math.round(clampN(Math.min(x, boundaryX(y) - margin), 6, 95));
  // …et la surface de but DE SON PROPRE CAMP (miroir en x) : un défenseur
  // replié ne se poste pas dans sa zone, elle lui est interdite aussi.
  const keepOutOwn = (x, y, margin = 2) => Math.round(clampN(Math.max(x, 100 - boundaryX(y) + margin), 4, 95));

  // Côté du PORTEUR et de l'ailier visé, lus dans le texte (défaut : porteur
  // central — cas typique d'une contre-attaque où l'axial sert un ailier).
  const has = (re) => re.test(t);
  const carrierIdx = has(/porteur\s+(?:gauche|de gauche|c[ôo]t[ée] gauche)/) ? 0
    : has(/porteur\s+(?:droit|de droite|c[ôo]t[ée] droit)/) ? nA - 1
    : Math.floor((nA - 1) / 2);
  let recvIdx = has(/ailier\s+droit|aile droite/) ? nA - 1
    : has(/ailier\s+gauche|aile gauche/) ? 0
    : (carrierIdx === 0 ? nA - 1 : 0);
  if (recvIdx === carrierIdx) recvIdx = carrierIdx === 0 ? nA - 1 : 0;

  // Attaquants étalés en largeur (convention : droite = bas / y grand).
  const att = [];
  const spanStep = nA > 1 ? Math.min(28, 56 / (nA - 1)) : 0;
  for (let i = 0; i < nA; i++) {
    const y = Math.round(clampN(50 + (i - (nA - 1) / 2) * spanStep, 12, 88));
    const x = keepOut(clampN(startX + i * 6, 6, 70), y);
    att.push({ id: "A" + (i + 1), team: "A", color: "#0072B2", x, y });
  }
  const carrier = att[carrierIdx];
  const recv = att[recvIdx];
  att.forEach((a) => {
    a.role = a === carrier ? "Porteur"
      : a === recv ? (a.y > 52 ? "Ailier droit" : a.y < 48 ? "Ailier gauche" : "Attaquant")
      : "Attaquant";
  });

  // Défenseurs : interposés devant le but, ÉTALÉS en hauteur (pas de fantômes
  // superposés) et maintenus HORS de la zone.
  const defs = [];
  for (let i = 0; i < nD; i++) {
    const dy = Math.round(clampN(50 + (i - (nD - 1) / 2) * (nD > 1 ? 24 : 0), 16, 84));
    // Défense de ZONE : juste devant la ligne des 6 m (handball) ; sinon couverture.
    const dx = late ? keepOutOwn(clampN(startX - 14, 4, 60), dy, 4)
      : (ZONE ? Math.round(clampN(boundaryX(dy) - 4, 10, 90)) : keepOut(startX + 24, dy, 5));
    defs.push({ id: "D" + (i + 1), role: "Défenseur" + (nD > 1 ? " " + (i + 1) : ""), team: "B", color: "#D55E00", x: dx, y: dy });
  }

  const players = [...att, ...defs];
  const markers = [{ type: "ballon", x: Math.round(clampN(carrier.x + 5, 6, 96)), y: carrier.y }];
  const actions = []; let order = 1;

  // 1) le porteur progresse (conduite) en fixant la défense — sans entrer en zone.
  const advX = keepOut(carrier.x + 20, carrier.y, 4);
  actions.push({ from: carrier.id, tox: advX, toy: carrier.y, type: "dribble", style: "solid", order: order++ });

  let shooterY = carrier.y;
  if (nA > 1) {
    // 2) passe vers l'ailier, 3) il PORTE le ballon vers la cible (conduite, pas
    //    une course : il a reçu), 4) conclusion. Rugby/ultimate : essai/aplatir.
    actions.push({ fromx: advX, fromy: carrier.y, to: recv.id, type: "passe", style: "solid", order: order++ });
    const runY = clampN(recv.y + (recv.y >= 50 ? -6 : 6), 12, 88);
    if (goalSport) {
      const shotX = keepOut(86, runY, 2);
      actions.push({ from: recv.id, tox: shotX, toy: runY, type: "dribble", order: order++ });
      actions.push({ fromx: shotX, fromy: runY, tox: 97, toy: 50, type: "tir", style: "solid", order: order++ });
    } else {
      const tryX = keepOut(94, runY, 1);
      actions.push({ from: recv.id, tox: tryX, toy: runY, type: "dribble", order: order++, label: "Essai" });
      markers.push({ type: "essai", x: tryX, y: runY });
    }
    shooterY = runY;
  } else if (goalSport) {
    actions.push({ fromx: advX, fromy: carrier.y, tox: 97, toy: 50, type: "tir", style: "solid", order: order++ });
  } else {
    const tryX = keepOut(94, carrier.y, 1);
    actions.push({ from: carrier.id, tox: tryX, toy: carrier.y, type: "dribble", order: order++, label: "Essai" });
    markers.push({ type: "essai", x: tryX, y: carrier.y });
  }

  // Défenseurs : depuis la ligne de zone, ils SORTENT contester le tireur
  // (vers le ballon, en restant hors de la zone).
  defs.forEach((d) => {
    const ty = Math.round(clampN((d.y + shooterY) / 2, 16, 84));
    const tx = Math.round(ZONE ? clampN(boundaryX(ty) - 12, 12, boundaryX(ty) - 4) : clampN(advX + 4, 30, 88));
    if (Math.hypot(tx - d.x, ty - d.y) > 4)
      actions.push({ from: d.id, tox: tx, toy: ty, type: "course", style: "dashed" });
  });

  // Synthèse pour le panneau « Ce que j'ai compris » (contrat invasion).
  const phase = [{ acteur: carrier.id, action: "dribble", cible: null, note: "Le porteur fixe la défense" }];
  if (nA > 1) {
    phase.push({ acteur: carrier.id, action: "passe", cible: recv.id, note: null });
    phase.push({ acteur: recv.id, action: "course", cible: "but", note: "Appel lancé" });
    phase.push({ acteur: recv.id, action: "tir", cible: "but", note: "Tir en course" });
  } else {
    phase.push({ acteur: carrier.id, action: "tir", cible: "but", note: null });
  }
  const analyse = {
    version: 1, famille: "invasion",
    effectif: { attaquants: nA, defenseurs: nD, gardien: false },
    depart: median ? "ligne_mediane" : "camp_propre",
    phase,
  };
  return { players, markers, actions, analyse };
}

function detectMatchActions(text, players) {
  const t = text.toLowerCase(), found = [];
  for (const [type, words] of Object.entries(ACTION_KEYWORDS))
    for (const w of words) { const p = keywordIndex(t, w); if (p !== -1) { found.push({ type, pos: p }); break; } }
  found.sort((a, b) => a.pos - b.pos);
  const actions = [];
  if (players.length < 2) return actions;
  found.forEach((act, i) => actions.push({
    from: players[i % players.length].id, to: players[(i + 1) % players.length].id,
    type: act.type, label: act.type, color: ACTION_COLORS[act.type] || "#e74c3c",
    style: act.type === "course" || act.type === "ecran" ? "dashed" : "solid", order: i + 1,
  }));
  return actions;
}

/* PHASE GÉNÉRIQUE SUR FORMATION — le texte nomme une formation ET décrit une
   phase collective (transition, relance, pressing) sans nommer de joueur : on
   dessine le geste ESSENTIEL de la phase avec les joueurs de la formation. */
function buildFormationPhase(text, players) {
  const t = c0(text);
  const byRole = (re) => players.filter((p) => re.test(p.role || ""));
  const mids = byRole(/milieu|piston/i), wides = byRole(/ailier|piston/i), fwds = byRole(/avant|attaquant|buteur/i);
  const gk = players.find((p) => /gardien/i.test(p.role || ""));
  const defs = byRole(/d[ée]f/i);
  const actions = [], markers = [];
  let ord = 1;
  const central = (arr) => arr.slice().sort((a, b) => Math.abs(a.y - 50) - Math.abs(b.y - 50))[0];
  if (/transition|r[ée]cup[ée]ration|contre.?attaque/.test(t)) {
    const mid = central(mids) || central(defs);
    if (!mid) return null;
    markers.push({ type: "ballon", x: clampN(mid.x + 3, 4, 96), y: mid.y });
    const fw = central(fwds) || wides[0];
    if (fw) actions.push({ from: mid.id, to: fw.id, type: "passe", order: ord++, label: "Passe de transition" });
    // Les joueurs de couloir MONTENT en prenant la largeur.
    const rail = wides.length >= 2 ? [wides[0], wides[wides.length - 1]] : fwds.slice(0, 2);
    for (const w of rail) {
      if (!w || w === fw) continue;
      actions.push({ from: w.id, tox: clampN(w.x + 24, 8, 90), toy: clampN(w.y <= 50 ? w.y - 4 : w.y + 4, 6, 94),
        type: "course", order: ord++, label: ord <= 3 ? "Monte en prenant la largeur" : null });
    }
  } else if (/relance/.test(t) && gk && defs.length) {
    markers.push({ type: "ballon", x: clampN(gk.x + 3, 4, 96), y: gk.y });
    // Les centraux s'écartent, le gardien relance court, le milieu décroche.
    const wideDefs = defs.slice().sort((a, b) => a.y - b.y);
    const d1 = wideDefs[0], d2 = wideDefs[wideDefs.length - 1];
    if (d1 !== d2) {
      actions.push({ from: d1.id, tox: d1.x, toy: clampN(d1.y - 10, 6, 94), type: "course", order: ord++, label: "S'écarte" });
      actions.push({ from: d2.id, tox: d2.x, toy: clampN(d2.y + 10, 6, 94), type: "course", order: ord++ });
    }
    actions.push({ from: gk.id, to: d1.id, type: "passe", order: ord++, label: "Relance courte" });
    const mid = central(mids);
    if (mid) actions.push({ from: d1.id, to: mid.id, type: "passe", order: ord++, label: "Trouve le milieu" });
  } else if (/pressing|\bpresse\b|harc[èe]le/.test(t)) {
    // La ligne haute déclenche le pressing vers le camp adverse (courses).
    for (const p of (fwds.length ? fwds : mids).slice(0, 3))
      actions.push({ from: p.id, tox: clampN(p.x + 14, 8, 94), toy: p.y, type: "course", order: ord++, label: ord === 1 ? "Déclenche le pressing" : null });
  }
  return actions.length ? { actions, markers } : null;
}

/* Pick and roll : porteur + poseur d'écran, 2 défenseurs. La passe part après
   le roll (suivi temporel des positions par le moteur de rendu). */
function buildPickAndRoll() {
  // Demi-terrain OFFENSIF (panier attaqué à DROITE, ≈ x97). Tout se passe près
  // du panier : meneur sur l'aile, intérieur qui monte poser l'écran, puis
  // roule au cercle ; le tir part de PRÈS du panier, pas du milieu de terrain.
  // GÉOMÉTRIE DE L'ÉCRAN (invariant testé — corpus basket_pick_and_roll) :
  // à hauteur de l'écran, du haut vers le bas on doit lire
  //     défenseur (y≈41)  <  bloc (y=43)  <  drive (y≈48,5)
  // Le T du bloc est COLLÉ sous la pastille du défenseur (même x, distance
  // 2,0 pour un rayon de pastille ≈2,7 : léger chevauchement visuel, comme
  // un vrai corps posé au contact) et le meneur SORT DERRIÈRE son partenaire
  // (épaule contre épaule, côté OPPOSÉ au défenseur) — jamais entre l'écran
  // et le défenseur, sinon le bloc ne bloque rien.
  const players = [
    { id: "P1", num: 1, role: "Meneur", team: "A", x: 56, y: 40, color: "#0072B2" },
    { id: "P2", num: 2, role: "Intérieur", team: "A", x: 62, y: 58, color: "#0072B2" },
    { id: "D1", num: 1, role: "Déf. meneur", team: "B", x: 65, y: 41, color: "#D55E00" },
    { id: "D2", num: 2, role: "Déf. intérieur", team: "B", x: 72, y: 60, color: "#D55E00" },
  ];
  const markers = [{ type: "ballon", x: 59, y: 40 }];
  const actions = [
    // 1) L'intérieur monte COLLER son bloc sous la pastille du défenseur.
    { from: "P2", tox: 65, toy: 43, type: "ecran", order: 1, label: "Écran sur le défenseur" },
    // 2) Le meneur utilise l'écran : il sort DERRIÈRE le bloc (côté bas) et
    //    attaque le cercle balle en main — la conduite frôle l'écran (y≈48,5
    //    à x=65) en laissant le défenseur bloqué de l'autre côté.
    { from: "P1", tox: 73, toy: 56, type: "dribble", order: 2, label: "Drive vers le cercle" },
    // 3) L'intérieur roule (plonge) vers le panier depuis son écran.
    { from: "P2", tox: 87, toy: 57, type: "course", order: 3, label: "Roule au cercle" },
    // 4) Le meneur ressort la balle au rouleur.
    { from: "P1", to: "P2", type: "passe", order: 4, label: "Passe au rouleur" },
    // 5) Finition au cercle.
    { from: "P2", tox: 96, toy: 50, type: "tir", order: 5, label: "Finition au cercle" },
    // Le défenseur de l'intérieur tente de fermer (aide), sans gêner la lecture.
    { from: "D2", tox: 83, toy: 53, type: "course", style: "dashed" },
  ];
  return { players, markers, actions, steps: ["Écran sur le défenseur du meneur", "Le meneur sort derrière l'écran et attaque le cercle", "L'intérieur roule au panier", "Le meneur ressort au rouleur", "Finition au cercle"] };
}

/* Croisement devant le pivot (basket / hand) : un joueur posté DOS AU PANIER
   reçoit, puis deux extérieurs CROISENT leur course devant lui ; il remet à
   l'un d'eux (C) au passage, qui attaque le panier. Demi-terrain offensif,
   panier à DROITE (≈ x96), pivot juste devant le cercle (« face à la 6,75 m »). */
function buildCrossOverPivot() {
  const players = [
    { id: "A", num: 1, role: "Pivot (dos au panier)", team: "A", x: 74, y: 50, color: "#0072B2" },
    { id: "B", num: 2, role: "Extérieur B", team: "A", x: 58, y: 34, color: "#0072B2" },
    { id: "C", num: 3, role: "Extérieur C", team: "A", x: 58, y: 66, color: "#0072B2" },
    { id: "D1", num: 1, role: "Déf. B", team: "B", x: 64, y: 34, color: "#D55E00" },
    { id: "D2", num: 2, role: "Déf. pivot", team: "B", x: 80, y: 50, color: "#D55E00" },
    { id: "D3", num: 3, role: "Déf. C", team: "B", x: 64, y: 66, color: "#D55E00" },
  ];
  const markers = [{ type: "ballon", x: 60, y: 32 }];
  const actions = [
    // 1) B donne au pivot, posté DOS AU PANIER sur la ligne des 6,75 m.
    { from: "B", to: "A", type: "passe", order: 1, label: "Passe au pivot" },
    // 2) B et C CROISENT leur course devant le pivot.
    { from: "B", tox: 70, toy: 62, type: "course", order: 2, label: "Croisement" },
    { from: "C", tox: 70, toy: 38, type: "course", order: 3, label: "Croisement" },
    // 3) Au passage, le pivot REMET à C.
    { from: "A", to: "C", type: "passe", order: 4, label: "Remise à C au croisement" },
    // 4) C attaque le panier et conclut.
    { from: "C", tox: 88, toy: 46, type: "dribble", order: 5, label: "Attaque le panier" },
    { from: "C", tox: 95, toy: 50, type: "tir", order: 6, label: "Tir" },
  ];
  return { players, markers, actions, steps: ["B donne au pivot (dos au panier, sur la 6,75 m)", "B et C croisent leur course devant le pivot", "Le pivot remet à C au passage", "C attaque le panier"] };
}

/* Fixation-passe : le porteur fixe un défenseur puis sert un partenaire libre.
   CHANTIER REPLI (👎 PROD98, « tir depuis l'aile ») : le partenaire recevait à
   (42,24) — DERRIÈRE le point de fixation (52,48), dans son propre camp, et sa
   colonne d'attente encore plus loin. Même correctif que build2v1 (verrou
   corpus « montée jusqu'à hauteur de la fixation ») : le receveur MONTE dans
   sa course avant la passe, et reçoit à hauteur de la fixation, dans le camp
   adverse — la réception « dans la course » que décrivent ces textes. */
function buildFixationPasse() {
  const players = [
    { id: "P1", role: "Porteur", team: "A", x: 30, y: 56, color: "#0072B2" },
    { id: "P2", role: "Partenaire", team: "A", x: 42, y: 24, color: "#0072B2" },
    { id: "D1", role: "Défenseur", team: "B", x: 58, y: 46, color: "#D55E00" },
  ];
  const markers = [{ type: "ballon", x: 36, y: 56 }];
  const actions = [
    { from: "P1", tox: 52, toy: 48, type: "dribble", order: 1, label: "Fixe le défenseur" },
    { from: "P2", tox: 56, toy: 24, type: "course", order: 2, label: "Monte dans la course" },
    { from: "P1", to: "P2", type: "passe", order: 3, label: "Passe au partenaire libre" },
    { from: "P2", tox: 80, toy: 22, type: "course", order: 4, label: "Prend l'espace libre" },
    { from: "P2", tox: 97, toy: 38, type: "tir", order: 5, label: "Conclusion" },
  ];
  return { players, markers, actions, steps: ["Fixe le défenseur", "Le partenaire monte dans la course", "Passe au partenaire libre", "Prend l'espace libre", "Conclusion"] };
}

/* Jeu à deux (une-deux / passe-et-va) : le porteur donne à un relais, part dans
   l'espace, reçoit la remise dans sa course et conclut. La passe retour part
   APRÈS la course (suivi temporel des positions par le moteur de rendu). */
function buildGiveAndGo() {
  const players = [
    { id: "P1", role: "Porteur", team: "A", x: 26, y: 62, color: "#0072B2" },
    { id: "P2", role: "Relais", team: "A", x: 48, y: 34, color: "#0072B2" },
    { id: "D1", role: "Défenseur", team: "B", x: 52, y: 58, color: "#D55E00" },
  ];
  const markers = [{ type: "ballon", x: 32, y: 62 }];
  const actions = [
    { from: "P1", to: "P2", type: "passe", order: 1, label: "Donne au relais" },
    { from: "P1", tox: 72, toy: 58, type: "course", order: 2, label: "Part dans l'espace" },
    { from: "P2", to: "P1", type: "passe", order: 3, label: "Remise dans la course" },
    { from: "P1", tox: 97, toy: 50, type: "tir", order: 4, label: "Conclusion" },
  ];
  return { players, markers, actions, steps: ["Donne au relais", "Part dans l'espace", "Remise dans la course", "Conclusion"] };
}

/* Deux contre un : le porteur fixe le défenseur (conduite vers lui) puis sert le
   partenaire libre, qui attaque l'espace et conclut. Géométrie 2v1 explicite. */
function build2v1(sport, text) {
  const t = coreSituationText(c0(text || ""));
  const goalSport = !["rugby", "ultimate"].includes(sport);
  // DÉPART « juste derrière la ligne médiane » (vagues) : les attaquants partent
  // COLLÉS à la médiane (sinon staging historique à mi-camp).
  // « à partir du / depuis le milieu de terrain » = même départ que la médiane
  // (retour prod : colonnes « à partir du milieu de terrain » restaient à mi-camp).
  const median = /derri[èe]re la (?:ligne )?m[ée]diane|depuis la (?:ligne )?m[ée]diane|au niveau de la (?:ligne )?m[ée]diane|partent? (?:de la|du) (?:ligne m[ée]diane|milieu)|[àa] partir (?:du|de la) (?:milieu|(?:ligne )?m[ée]diane)|depuis le milieu|au rond central|du rond central/.test(t);
  // DÉPART « derrière la ligne de fond » (colonnes de contre-attaque, faute
  // d'orthographe tolérée) : les attaquants partent du FOND — tout le terrain
  // à traverser.
  const baseline = /derr?i[èe]re? la ligne de fond|partent? (?:de|depuis) la ligne de fond/.test(t);
  // Duel joué « à l'entrée de la surface » : staging avancé (zone de
  // finition) — pas quand c'est le DÉFENSEUR qui s'y tient (repère défensif).
  const surface2 = /[àa] l'entr[ée]e de la surface/.test(t)
    && !/d[ée]fenseur[^.;]{0,30}[àa] l'entr[ée]e de la surface/.test(t);
  const ax = surface2 ? 54 : baseline ? 8 : median ? 45 : 30;
  // DÉFENSEUR à un REPÈRE COTÉ (« entre la ligne des 6 et 9 mètres », « aux 9 m »,
  // « à l'entrée de la surface ») : profondeur lue via les repères du sport.
  let dx = surface2 ? 72 : median ? 62 : baseline ? 50 : 56;
  const between = t.match(/d[ée]fenseur[^.]{0,40}?entre (?:la ligne des? )?(\d{1,2})\s*(?:m[èe]tres?\s*|m\s*)?et (?:la ligne des? |les? )?(\d{1,2})\s*m/);
  const at = t.match(/d[ée]fenseur[^.]{0,30}?(?:aux?|sur la ligne des?)\s*(\d{1,2})\s*m/);
  if (between) {
    const a = landmarkX(`${between[1]}m`, sport), b = landmarkX(`${between[2]}m`, sport);
    if (a != null && b != null) dx = Math.round((a + b) / 2);
  } else if (at) {
    const a = landmarkX(`${at[1]}m`, sport);
    if (a != null) dx = a;
  } else if (/d[ée]fenseur[^.]{0,30}?[àa] l'entr[ée]e de la surface/.test(t)) {
    const a = landmarkX("surface", sport);
    if (a != null) dx = a;
  }
  const players = [
    { id: "A1", role: "Porteur", team: "A", x: ax, y: 36, color: "#0072B2" },
    { id: "A2", role: "Partenaire", team: "A", x: ax + 4, y: 72, color: "#0072B2" },
    { id: "D1", role: "Défenseur", team: "B", x: dx, y: 50, color: "#D55E00" },
  ];
  const markers = [{ type: "ballon", x: ax + 6, y: 36 }];
  const actions = [
    { from: "A1", tox: clampN(dx - 4, 40, 88), toy: 44, type: "dribble", order: 1, label: "Fixe le défenseur" },
  ];
  let ord = 2;
  // En VAGUE depuis la médiane ou la ligne de fond, le partenaire MONTE en même
  // temps que le porteur (sinon la passe le desservait 25 unités en arrière).
  // Garde géométrique (indépendante de la formulation) : un partenaire qui
  // recevrait la passe LOIN derrière le point de fixation (défenseur à un repère
  // profond, départ resté à mi-camp) monte aussi — un 2c1 ne se joue pas vers
  // l'arrière. Seuil > 25 : le staging historique (écart 18) reste intact.
  const farBehind = (dx - 4) - (ax + 4) > 25;
  if (median || baseline || farBehind) actions.push({ from: "A2", tox: clampN(dx - 8, 40, 86), toy: 70, type: "course", order: ord++, label: "Accompagne la montée" });
  actions.push({ from: "A1", to: "A2", type: "passe", order: ord++, label: "Sert le partenaire libre" });
  // A2 a REÇU le ballon : il PORTE (conduite, pas une course sans ballon). Puis
  // conclusion : tir au but (sports à but) ou essai/aplatir (rugby, ultimate).
  // UNE-DEUX : « donne en appui, part dans le dos et reprend la remise pour
  // frapper » — c'est le PORTEUR qui conclut sur la remise du partenaire (le
  // gabarit servait le partenaire, la remise disparaissait).
  if (goalSport && /une[- ]deux|reprend la remise|remise[^.;]{0,30}(?:frappe|reprend|conclut)/.test(t)) {
    const runY = 44, runX = clampN(dx + 12, 60, 84);
    actions.push({ from: "A1", tox: runX, toy: runY, type: "course", order: ord++, label: "Part dans le dos" });
    actions.push({ from: "A2", tox: runX, toy: runY, type: "passe", order: ord++, label: "Remise en une touche" });
    actions.push({ from: "A1", fromx: runX, fromy: runY, tox: 97, toy: 46, type: "tir", order: ord++, label: "Frappe en première intention" });
  } else if (goalSport) {
    actions.push({ from: "A2", tox: clampN(Math.max(78, dx + 8), 60, 88), toy: 66, type: "dribble", order: ord++, label: "Attaque l'espace" });
    // Poteau nommé : « frappe du premier poteau » = côté du tireur (y=66 -> 58),
    // « second poteau » = côté opposé.
    const post2 = /premier poteau|1er poteau/.test(t) ? 58 : /(?:second|deuxi[èe]me|2e)\s+poteau/.test(t) ? 42 : 52;
    actions.push({ from: "A2", tox: 97, toy: post2, type: "tir", order: ord++, label: post2 === 58 ? "Frappe (1er poteau)" : post2 === 42 ? "Frappe (2e poteau)" : "Conclusion" });
  } else {
    actions.push({ from: "A2", tox: 94, toy: 66, type: "dribble", order: ord++, label: "Essai" });
    markers.push({ type: "essai", x: 94, y: 66 });
  }
  return { players, markers, actions, steps: actions.filter((a) => a.label).map((a) => a.label) };
}

/* Conservation en losange (rondo / toro) : N porteurs sur le pourtour font
   circuler le ballon autour de M défenseur(s) au centre. L'effectif est lu dans
   le texte (« 5 contre 2 », « à 4 avec deux au milieu »…) — défaut 4 c 1. */
/* JEU À MULTIBUTS — aire RÉDUITE (cotée si le texte donne ses dimensions) avec
   N mini-buts PAR CÔTÉ, posés près des coins (« à 2 m des points de corner »),
   et un match à effectif équilibré sans gardien. La leçon est le CHANGEMENT DE
   CIBLE : fixer d'un côté, marquer dans le but resté libre. */
function buildMultiGoals(sport, text) {
  const t = c0(text);
  const dims = parseCourtDims(t);
  const duel = t.match(/\b([2-8])\s*(?:contre|c\.?|vs|x)\s*([2-8])\b/);
  const nA = clampN(duel ? parseInt(duel[1], 10) : 4, 2, 6);
  const nB = clampN(duel ? parseInt(duel[2], 10) : nA, 2, 6);
  const gm = t.match(/(\d+|deux|trois)\s*(?:mini.?buts?|petits? buts?|buts?)[^.;]{0,24}?(?:de chaque c[ôo]t[ée]|par c[ôo]t[ée]|de part et d['’]autre)/);
  const perSide = clampN(gm ? frenchNumWord(gm[1]) : 2, 1, 3);
  const X0 = 8, X1 = 92, Y0 = 12, Y1 = 88;                 // emprise de l'aire réduite
  const players = [], markers = [], actions = [];
  // MINI-BUTS : répartis sur chaque ligne de fond, ramenés vers les COINS.
  const goalYs = perSide === 1 ? [50]
    : Array.from({ length: perSide }, (_, i) => Math.round(Y0 + 11 + i * ((Y1 - Y0 - 22) / (perSide - 1))));
  for (const gx of [X0, X1]) for (const gy of goalYs) markers.push({ type: "but", x: gx, y: gy, w: 2, h: 6 });
  // PLOTS aux quatre coins + COTES : l'aire réduite doit se LIRE sur le fond de
  // terrain réglementaire (c'est le « repère de distance » attendu).
  for (const [px, py] of [[X0, Y0], [X1, Y0], [X1, Y1], [X0, Y1]]) markers.push({ type: "plot", x: px, y: py });
  if (dims.len) markers.push({ type: "cote", x: 50, y: Math.max(2, Y0 - 5), w: X1 - X0, text: dims.len });
  const zones = [];
  if (dims.wid && dims.wid !== dims.len) zones.push({ x: X0, y: Y0, w: 0, h: Y1 - Y0, style: "dashed", label: dims.wid });
  // ÉQUIPES : deux rideaux par camp, décalés en hauteur (jamais en colonne).
  const line = (n, x0, x1, team, color, tag) => {
    for (let i = 0; i < n; i++) {
      const col = i % 2, row = Math.floor(i / 2);
      players.push({ id: tag + (i + 1), num: i + 1, role: `${team === "A" ? "Attaquant" : "Défenseur"} ${i + 1}`,
        team, color, x: col ? x1 : x0, y: Math.round(clampN(26 + row * 24 + (col ? 12 : 0), Y0 + 4, Y1 - 4)) });
    }
  };
  line(nA, 22, 38, "A", "#0072B2", "A");
  line(nB, 76, 60, "B", "#D55E00", "D");
  const carrier = players[0];
  markers.push({ type: "ballon", x: Math.round(clampN(carrier.x + 5, 4, 96)), y: carrier.y });
  // CHANGEMENT DE CIBLE : fixer d'un côté, servir le partenaire à l'opposé, et
  // conclure dans le mini-but resté libre (l'autre coin).
  const relais = players[Math.min(1, nA - 1)];
  const cible = goalYs[goalYs.length - 1];
  actions.push({ from: carrier.id, tox: Math.round(clampN(carrier.x + 14, 10, 46)), toy: carrier.y, type: "dribble", order: 1, label: "Fixer d'un côté" });
  actions.push({ from: carrier.id, to: relais.id, type: "passe", order: 2, label: "Renverser vers l'autre but" });
  // Le receveur PORTE jusqu'au camp adverse : un tir part de la moitié attaquée,
  // jamais de son propre camp (contrôle qualité).
  actions.push({ from: relais.id, tox: 70, toy: cible, type: "dribble", order: 3, label: "Attaquer le but libre" });
  actions.push({ from: relais.id, tox: X1 - 4, toy: cible, type: "tir", order: 4, label: "Marquer dans le but resté libre" });
  return { players, markers, actions, zones,
    steps: ["Fixer la défense sur un but", "Renverser le jeu vite", "Marquer dans le but laissé libre"] };
}

/* CONSERVATION ENTRE DEUX ÉQUIPES (« passe à 10 » en 4 contre 4) — les
   récupérateurs ne sont PAS au centre d'un pourtour (ce serait un rondo) : les
   deux équipes sont IMBRIQUÉES dans l'aire de jeu, et le ballon circule d'un
   porteur à l'autre en progressant (jeu « orienté »). Retour prod 👎 : un
   « 4 X 4 en passe à 10 » sortait en rondo à 1 défenseur. */
function buildKeepAwayTeams(nA, nD) {
  const players = [], actions = [];
  const px = (i, n) => Math.round(clampN(14 + i * (64 / Math.max(1, n - 1)), 8, 86));
  for (let i = 0; i < nA; i++)
    players.push({ id: "A" + (i + 1), num: i + 1, role: "Porteur " + (i + 1), team: "A", color: "#0072B2",
      x: px(i, nA), y: i % 2 ? 74 : 26 });
  // Récupérateurs : dans les INTERVALLES laissés libres par le zigzag des
  // passes (à mi-chemin en x, du côté que la passe vient de quitter). Ils sont
  // ainsi au contact du jeu sans qu'aucune trajectoire ne les traverse — un
  // écart en X, seule dimension qui survit à la compression en bandes.
  for (let i = 0; i < nD; i++) {
    const a = players[Math.min(i, nA - 1)], b = players[Math.min(i + 1, nA - 1)];
    players.push({ id: "D" + (i + 1), num: i + 1, role: "Récupérateur " + (i + 1), team: "B", color: "#D55E00",
      x: Math.round(clampN(a === b ? a.x + 8 : (a.x + b.x) / 2, 8, 92)), y: a.y });
  }
  const markers = [{ type: "ballon", x: Math.round(clampN(players[0].x + 5, 4, 96)), y: players[0].y }];
  for (let i = 0; i < nA - 1; i++)
    actions.push({ from: "A" + (i + 1), to: "A" + (i + 2), type: "passe", order: i + 1,
      label: i === 0 ? "Faire circuler (10 passes)" : null });
  return { players, markers, actions, title: `Conservation ${nA} contre ${nD}`,
    steps: ["Enchaîner les passes sans perdre le ballon", "Se démarquer entre les récupérateurs", "Progresser vers l'avant (jeu orienté)"] };
}

/* Étiquettes « Terrain k » des BANDES dupliquées (mini-terrains parallèles en
   invasion) — même vignette que l'habillage montante-descendante au filet. */
export function labelParallelCourts(scene, n) {
  if (!scene || !(n >= 2)) return scene;
  const zones = Array.isArray(scene.zones) ? scene.zones : (scene.zones = []);
  if (zones.some((z) => /^Terrain \d/.test(z.label || ""))) return scene;
  const band = 100 / n;
  for (let k = 0; k < n; k++)
    zones.push({ x: 1, y: Math.round(k * band + 1), w: 10, h: 5, label: `Terrain ${k + 1}`, style: "dashed" });
  return scene;
}

function buildPossessionDiamond(text) {
  const t = c0(text);
  const duel = t.match(/\b([3-8])\s*(?:contre|c\.?|vs|x)\s*([1-8])\b/)
    || t.match(/\b(trois|quatre|cinq|six)\s+contre\s+(un|deux|trois|quatre|cinq|six)\b/);
  const W = { un: 1, deux: 2, trois: 3, quatre: 4, cinq: 5, six: 6, sept: 7, huit: 8 };
  const num = (s) => W[s] || parseInt(s, 10);
  let nA = duel ? num(duel[1]) : 4;
  let nD = duel ? num(duel[2]) : (/(?:deux|2)\s*d[ée]fenseur|deux au centre|[àa] deux au milieu/.test(t) ? 2 : 1);
  // ÉQUIPES ÉGALES (ou à un joueur près) : conservation à deux équipes, pas un
  // rondo — les récupérateurs seraient empilés au centre.
  if (nD >= 3 && nD >= nA - 1) return buildKeepAwayTeams(clampN(nA, 3, 6), clampN(nD, 3, 6));
  // EFFECTIF PAR COULEURS / RÔLES (« 4 joueurs bleus sur les côtés, 2 joueurs
  // rouges à l'intérieur ») : la convention produit fait des BLEUS l'équipe A
  // (porteurs) et des ROUGES l'équipe B (récupérateurs au centre).
  if (!duel) {
    const ma = t.match(/(\d+|trois|quatre|cinq|six|sept|huit)\s+(?:joueurs?\s+)?(?:bleus?|attaquants?|porteurs?|passeurs?)\b/)
      || t.match(/(\d+|trois|quatre|cinq|six|sept|huit)\s+joueurs?[^.;]{0,30}?(?:sur les c[ôo]t[ée]s|autour|pourtour|en cercle|[àa] l['’]ext[ée]rieur)/);
    if (ma) nA = num(ma[1]);
    const md = t.match(/(\d+|un|deux|trois)\s+(?:joueurs?\s+)?(?:rouges?|d[ée]fenseurs?|r[ée]cup[ée]rateurs?|chasseurs?)\b/)
      || t.match(/(\d+|un|deux|trois)\s+joueurs?[^.;]{0,30}?(?:[àa] l['’]int[ée]rieur|au centre|au milieu|dedans)/);
    if (md) nD = num(md[1]);
  }
  nA = clampN(nA, 3, 8); nD = clampN(nD, 1, 3);
  // TAILLE DE L'AIRE : un carré/losange COTÉ (« carré de 10 m sur 10 ») est
  // dessiné COMPACT (rayon ∝ dimension, borné pour rester lisible), pas sur
  // tout le terrain. Sans cote : géométrie historique (34/36).
  const dims = parseCourtDims(t);
  const side = dims.len ? parseInt(dims.len, 10) : dims.wid ? parseInt(dims.wid, 10) : null;
  const rx = side ? Math.round(clampN(side * 2.2, 18, 34)) : 34;
  const ry = side ? rx : 36;
  // Porteurs sur le POURTOUR (polygone régulier, départ en haut), défenseurs au centre.
  const players = [];
  for (let i = 0; i < nA; i++) {
    const a = -Math.PI / 2 + (i / nA) * 2 * Math.PI;
    players.push({ id: "A" + (i + 1), num: i + 1, role: "Joueur " + (i + 1), team: "A", color: "#0072B2",
      x: Math.round(clampN(50 + rx * Math.cos(a), 8, 92)), y: Math.round(clampN(50 + ry * Math.sin(a), 8, 92)) });
  }
  // Récupérateurs au centre — offsets mis À L'ÉCHELLE de l'aire.
  const s = rx / 34;
  const DPOS = [[-6, -6], [8, 8], [0, -14]].map(([dx, dy]) => [Math.round(50 + dx * s), Math.round(50 + dy * s)]);
  for (let i = 0; i < nD; i++)
    players.push({ id: "D" + (i + 1), role: "Défenseur" + (nD > 1 ? " " + (i + 1) : ""), team: "B", color: "#D55E00", x: DPOS[i][0], y: DPOS[i][1] });
  const markers = [{ type: "ballon", x: players[0].x, y: players[0].y + 6 }];
  // MATÉRIEL DÉCRIT (« carré délimité par 4 plots rouges », « matérialisé par
  // des coupelles ») : les plots aux COINS du carré (les joueurs sont SUR les
  // côtés) + la COTE de la bibliothèque (📏) au-dessus du bord haut quand la
  // dimension est donnée. L'overlay générique (addCourtDelimitation) voit le
  // matériel déjà posé et n'en rajoute pas.
  if (/plots?|c[ôo]nes?|coupelles?|d[ée]limit|mat[ée]rialis|balis/.test(t)) {
    const color = plotColorFromText(t);
    const cl = (v) => Math.round(clampN(v, 3, 97));
    for (const [px, py] of [[50 - rx, 50 - ry], [50 + rx, 50 - ry], [50 + rx, 50 + ry], [50 - rx, 50 + ry]])
      markers.push({ type: "plot", x: cl(px), y: cl(py), ...(color ? { color } : {}) });
    if (dims.len) markers.push({ type: "cote", x: 50, y: cl(50 - ry - 5), w: 2 * rx, text: dims.len });
  }
  const actions = [];
  for (let i = 0; i < nA; i++)
    actions.push({ from: "A" + (i + 1), to: "A" + ((i + 1) % nA + 1), type: "passe", order: i + 1, label: i === 0 ? "Fait circuler" : null });
  // PRESSING DÉCLENCHÉ (« quand le ballon revient au joueur cible, les
  // défenseurs déclenchent le pressing ensemble ») : la cible est nommée, la
  // dernière passe la sert, les courses de pressing suivent DANS L'ORDRE.
  if (/pressing/.test(t) && /cible/.test(t)) {
    players[0].role = "Joueur cible";
    actions[nA - 1].label = "Retour au joueur cible";
    for (let i = 0; i < nD; i++)
      actions.push({ from: "D" + (i + 1), tox: players[0].x + (i ? 6 : -6), toy: players[0].y + 8, type: "course", order: nA + 1 + i, label: i === 0 ? "Ballon sur la cible : pressing ensemble" : null });
  } else if (/\bpress\w*/.test(t)) {
    // PRESSING DU RECEVEUR (« le joueur rouge 1 court vers le joueur bleu 2
    // pour le presser ») : la course du défenseur SUIT la première passe.
    // Numéros lus du texte quand ils y sont, sinon D1 presse le receveur.
    const seg = t.split(/[.;\n]/).find((sg) => /\bpress\w*/.test(sg)) || "";
    const di = clampN(parseInt((seg.match(/(?:rouge|d[ée]fenseur)\s*n?[°o]?\s*(\d)/) || [])[1], 10) || 1, 1, nD);
    const ai = clampN(parseInt((seg.match(/(?:bleu|attaquant|porteur|joueur)\s*n?[°o]?\s*(\d)/) || [])[1], 10) || 2, 1, nA);
    const target = players[ai - 1];
    for (const a of actions) if (a.order > 1) a.order += 1;
    actions.push({ from: "D" + di, tox: Math.round(target.x + (50 - target.x) * 0.2), toy: Math.round(target.y + (50 - target.y) * 0.2),
      type: "course", order: 2, label: "Presse le receveur" });
  }
  return { players, markers, actions, steps: ["Faire circuler vite le ballon", "Garder les intervalles du pourtour", "Se rendre disponible pour le porteur"] };
}

/* Repli défensif (transition) : après la perte (ballon à droite), les joueurs
   reviennent vite se replacer entre le ballon et leur but (à gauche). */
function buildDefensiveRecovery() {
  const players = [
    { id: "P1", role: "Joueur", team: "A", x: 64, y: 26, color: "#0072B2" },
    { id: "P2", role: "Joueur", team: "A", x: 58, y: 52, color: "#0072B2" },
    { id: "P3", role: "Joueur", team: "A", x: 66, y: 78, color: "#0072B2" },
  ];
  const markers = [{ type: "ballon", x: 88, y: 50 }];
  const actions = [
    { from: "P1", tox: 28, toy: 30, type: "course", order: 1, label: "Repli" },
    { from: "P2", tox: 22, toy: 50, type: "course", order: 2, label: "Cadre l'axe" },
    { from: "P3", tox: 30, toy: 72, type: "course", order: 3, label: "Repli" },
  ];
  return { players, markers, actions, steps: ["Se replacer entre le ballon et le but", "Protéger l'axe en priorité", "Refermer les intervalles"] };
}

/* Sortie de balle (relance courte) : le gardien relance court, les défenseurs
   s'écartent, le milieu décroche pour recevoir et oriente le jeu vers l'avant. */
function buildBuildUp() {
  const players = [
    { id: "P1", role: "Gardien", team: "k", x: 8, y: 50, color: "#F2C200" },
    { id: "P2", role: "Défenseur G", team: "A", x: 24, y: 26, color: "#0072B2" },
    { id: "P3", role: "Défenseur D", team: "A", x: 24, y: 74, color: "#0072B2" },
    { id: "P4", role: "Milieu", team: "A", x: 44, y: 50, color: "#0072B2" },
    { id: "P5", role: "Relais avancé", team: "A", x: 68, y: 40, color: "#0072B2" },
  ];
  const markers = [{ type: "ballon", x: 12, y: 50 }];
  const actions = [
    { from: "P2", tox: 18, toy: 16, type: "course", order: 1, label: "S'écarte" },
    { from: "P3", tox: 18, toy: 84, type: "course", order: 2, label: "S'écarte" },
    { from: "P1", to: "P2", type: "passe", order: 3, label: "Relance courte" },
    { from: "P4", tox: 36, toy: 42, type: "course", order: 4, label: "Décroche" },
    { from: "P2", to: "P4", type: "passe", order: 5, label: "Trouve le milieu" },
    { from: "P4", to: "P5", type: "passe", order: 6, label: "Oriente vers l'avant" },
  ];
  return { players, markers, actions, steps: ["Les défenseurs s'écartent, le gardien relance court", "Le milieu décroche pour recevoir", "Orienter le jeu vers l'avant"] };
}

/* Défi/relais de TIR EN COURSE (basket surtout) : une ou DEUX files d'attente au
   milieu de terrain (chacune un plot), le 1er de chaque file s'élance EN DRIBBLE
   vers le panier unique (à droite) et conclut par un tir en course. PAS de passe
   ni d'opposition : c'est un atelier-défi, pas une phase de jeu. `opts` (chemin
   analyse IA) : { teams, per, geste } — sinon tout est lu dans le texte. */
/* CONCOURS-NAVETTE DE BALLONS : deux colonnes en MIROIR aux coins opposés,
   chacune derrière son CASIER à ballons ; slalom en dribble qui SERPENTE entre
   les plots sur toute la longueur, conclusion sur la cible OPPOSÉE. Le nombre
   total de plots du texte (« 14 plots ») se répartit entre les deux couloirs. */
function buildRackShuttleContest(t, sport, { per = 3 } = {}) {
  const basket = sport === "basketball" || sport === "basket3x3";
  const raw = t.match(/(\d+)\s*(?:plots?|c[ôo]nes?|coupelles?)/);
  const nPer = clampN(raw ? Math.round(parseInt(raw[1], 10) / 2) : 6, 4, 8);
  const players = [], markers = [], actions = [], zones = [];
  // Chaque équipe occupe SON coin : casier collé au coin, colonne juste à côté
  // (jamais dans la boîte du casier ni sur la zone de conclusion adverse).
  const lanes = [
    { id: "A", color: "#0072B2", colX: 13, colY: 27, colDY: -7, laneY: 38, x0: 20, x1: 80, hoopX: 94, casier: { x: 2, y: 8, w: 8, h: 14 } },
    { id: "B", color: "#D55E00", colX: 87, colY: 73, colDY: 7, laneY: 62, x0: 80, x1: 20, hoopX: 6, casier: { x: 90, y: 78, w: 8, h: 14 } },
  ];
  let ord = 1;
  lanes.forEach((L, li) => {
    // Colonne empilée derrière le casier, dans le coin.
    for (let i = 0; i < per; i++)
      players.push({ id: L.id + (i + 1), num: i + 1, role: "Équipe " + L.id, team: L.id, color: L.color,
        x: L.colX, y: clampN(L.colY + i * L.colDY, 6, 94) });
    // Casier à ballons : réserve matérialisée + ballons en stock + ballon du 1er.
    zones.push({ x: L.casier.x, y: L.casier.y, w: L.casier.w, h: L.casier.h, style: "solid", label: li === 0 ? "Casier A" : "Casier B" });
    markers.push({ type: "ballon", x: L.casier.x + 3, y: L.casier.y + 4 });
    markers.push({ type: "ballon", x: L.casier.x + 7, y: L.casier.y + 8 });
    markers.push({ type: "ballon", x: L.colX + 3, y: L.colY });
    // Plots du slalom en quinconce le long du couloir, et conduite qui SERPENTE
    // entre eux (chaque segment vise l'intervalle opposé au plot).
    const step = (L.x1 - L.x0) / (nPer - 1 || 1);
    let px = L.colX, py = L.colY;
    for (let k = 0; k < nPer; k++) {
      const x = Math.round(L.x0 + k * step);
      const off = (k % 2 ? 5 : -5);
      markers.push({ type: "plot", x, y: clampN(L.laneY + off, 8, 92) });
      const ty = clampN(L.laneY - off, 8, 92);
      actions.push({ ...(k === 0 ? { from: L.id + "1" } : { fromx: px, fromy: py }),
        tox: x, toy: ty, type: "dribble", order: ord++, ...(k === 0 && li === 0 ? { label: "Slalom en dribble" } : {}) });
      px = x; py = ty;
    }
    // Conclusion sur la cible OPPOSÉE (panier/but d'en face ; essai au rugby).
    if (sport === "rugby") {
      actions.push({ fromx: px, fromy: py, tox: L.hoopX, toy: L.laneY, type: "dribble", order: ord++, ...(li === 0 ? { label: "Aplatit dans l'en-but" } : {}) });
      markers.push({ type: "essai", x: L.hoopX, y: L.laneY });
    } else {
      actions.push({ fromx: px, fromy: py, tox: L.hoopX, toy: 50, type: "tir", order: ord++,
        ...(li === 0 ? { label: basket ? "Tir en course au panier opposé" : "Tir sur la cible opposée" } : {}) });
    }
  });
  return {
    players, markers, actions, zones, sport,
    steps: ["Prendre un ballon dans son casier, slalom en dribble sur toute la longueur",
      basket ? "Tir au panier opposé" : "Conclusion sur la cible opposée",
      "Si marqué : déposer le ballon dans le casier ADVERSE et revenir prendre le suivant",
      "Si raté : retour en dribble au départ du slalom"],
  };
}

export function buildDribbleShootRelay(text, sport, opts = {}) {
  const t = c0(text || "");
  const twoTeams = opts.teams != null ? opts.teams >= 2
    : /\bduel\b|(?:2|deux)\s*[ée]quipes|s['’ ]affrontent|[ée]quipe adverse|contre l['’ ]autre/.test(t);
  const m = t.match(/(\d+)\s*(?:ou\s*(\d+))?\s*(?:joueurs?|[ée]l[èe]ves?)/);
  const per = clampN(opts.per != null ? opts.per : (m ? Math.max(+m[1], +(m[2] || 0)) : 3), 2, 4);
  // Le geste se conclut par un TIR, sauf s'il est explicitement SANS frappe
  // (conduite simple / slalom / navette / relais de course).
  // Le geste se conclut par un TIR sauf s'il est explicitement SANS frappe. Un geste
  // de FINITION (« dribble_tir », « conduite_tir », « slalom_tir »…) tire malgré le
  // mot « conduite/slalom » qu'il contient — sinon la frappe finale était perdue.
  const gst = String(opts.geste || "");
  const shoot = opts.geste != null
    ? /tir|frapp|shoot|\bbut\b|panier|lay.?up|smash|marque|finition/i.test(gst) || !/conduite|slalom|navette|\bcourse\b|relais simple|passe/i.test(gst)
    : true;
  // Slalom lu sur le GESTE ou sur le TEXTE (l'analyse IA dit souvent juste
  // « conduite_tir » alors que le texte décrit « slalom entre 6 plots »).
  const slalom = /slalom/i.test(gst) || /slalom|zigzag|entre les (?:plots|c[ôo]nes|cerceaux)/.test(t);
  // Matériel du slalom : nombre de plots/cerceaux et espacement cotés dans le texte.
  const slalomN = (() => { const m2 = t.match(/(\d+|deux|trois|quatre|cinq|six|sept|huit)\s*(?:plots?|c[ôo]nes?|cerceaux?|piquets?)/); return clampN(m2 ? frenchNumWord(m2[1]) : 4, 3, 8); })();
  const slalomGap = (() => { const m2 = t.match(/(?:espac[ée]s?\s*(?:de\s*)?|tous les\s*)(\d+(?:[.,]\d+)?)\s*m/); return m2 ? m2[1].replace(",", ".") : null; })();
  const slalomHoops = /cerceaux?/.test(t);
  // DUEL D'OPPOSITION (geste de contact : plaquage, franchissement) : ce n'est
  // pas un relais de tir mais un 1 contre 1 — attaquant porteur FACE à un
  // défenseur, le porteur cherche à franchir, le défenseur monte et plaque.
  // Le sport « rugby » ne FORCE PLUS l'opposition : un relais, un slalom, un
  // atelier de passes ou une touche rugby utilisent le moteur de file normal
  // (finition adaptée : aplatir, pas de tir). Seul un geste/texte de CONTACT
  // déclenche le duel (sinon 5 ateliers rugby sur 6 devenaient un plaquage).
  const CONTACT_RE = /plaquage|plaque[rz]?\b|franchiss|contact\b|opposition|1\s*contre\s*1/;
  const opposition = CONTACT_RE.test(gst) || (sport === "rugby" && CONTACT_RE.test(t));
  if (opposition) {
    const players = [], markers = [], actions = [];
    // « par binômes » (parEquipe:1) : un duel 1c1 net, pas deux files de 2.
    const nPer = opts.per != null ? clampN(opts.per, 1, 4) : per;
    // Rôles NOMMÉS du texte (Porteur/Plaqueur au rugby), suiveurs « En attente »
    // (avant : 4 « Attaquant » + 4 « Défenseur » indistincts sur une même ligne).
    // Rangées LÉGÈREMENT décalées (47/53) : les deux flèches du duel ne se
    // superposent plus sur l'axe.
    const tackle = /plaqu/.test(t);
    const AY = 47, BY = 53;
    for (let i = 0; i < nPer; i++) players.push({ id: "A" + (i + 1), num: i + 1, role: i === 0 ? (tackle ? "Porteur" : "Attaquant") : "En attente", team: "A", color: "#0072B2", x: clampN(20 - i * 7, 4, 40), y: AY });
    for (let i = 0; i < nPer; i++) players.push({ id: "B" + (i + 1), num: i + 1, role: i === 0 ? (tackle ? "Plaqueur" : "Défenseur") : "En attente", team: "B", color: "#D55E00", x: clampN(80 + i * 7, 60, 96), y: BY });
    markers.push({ type: "ballon", x: 24, y: AY - 4 });
    actions.push({ from: "A1", tox: 56, toy: 48, type: "dribble", order: 1, label: "Le porteur attaque le défenseur" });
    actions.push({ from: "B1", tox: 60, toy: 52, type: "course", order: 2, label: tackle ? "Le défenseur monte et plaque" : "Le défenseur monte au contact" });
    // CHANGEMENT DE RÔLE par colonne (« le porteur devient plaqueur ») : arcs de
    // rotation croisés vers la queue de la colonne opposée.
    if (/devient|inverse (?:les|des) r[ôo]les|rejoi\w+ la colonne|change[nt]* de (?:r[ôo]le|colonne)/.test(t)) {
      actions.push({ from: "A1", tox: clampN(80 + (nPer - 1) * 7 + 3, 60, 97), toy: BY, type: "rotation", order: 3, bulge: 5, label: tackle ? "Le porteur devient plaqueur" : "L'attaquant devient défenseur" });
      actions.push({ from: "B1", tox: clampN(20 - (nPer - 1) * 7 - 3, 3, 40), toy: AY, type: "rotation", order: 4, bulge: 5, label: tackle ? "Le plaqueur rejoint les porteurs" : "Le défenseur rejoint les attaquants" });
    }
    return { players, markers, actions, sport, steps: ["Le porteur cherche à franchir le défenseur", tackle ? "Le défenseur monte et plaque" : "Le défenseur monte au contact", "On inverse les rôles à chaque passage"] };
  }
  // CONCOURS-NAVETTE DE BALLONS (« casiers à ballons ») : deux équipes en
  // MIROIR sur toute la longueur — chaque colonne part de SON coin derrière
  // son casier, slalome à travers le terrain et conclut sur la cible OPPOSÉE
  // (paniers/buts opposés, comme sur les fiches fédérales). Le mot « casier »
  // signe ce genre ; le duel générique (files vers une seule cible) est
  // inchangé pour tout le reste.
  if (twoTeams && /casiers?\b/.test(t)) return buildRackShuttleContest(t, sport, { per });
  const basket = sport === "basketball" || sport === "basket3x3";
  const hoopX = 96;                                   // anneau (panier unique attaqué, à droite)
  // Files DÉCALÉES en hauteur (haut/bas) pour un DOUBLE-PAS à ~45° vers l'anneau ;
  // le plot est posé du côté EXTÉRIEUR (visible, hors de la ligne de drive) et le
  // tir CONVERGE sur l'anneau (y~50), pas à côté.
  // 1 à 4 FILES réparties en hauteur, chacune CONVERGEANT vers le cercle (y≈50).
  // Deux files -> haut/bas ; trois couloirs -> haut/milieu/bas, etc.
  const nLanes = opts.teams != null ? clampN(opts.teams, 1, 4) : (twoTeams ? 2 : 1);
  const COLORS = ["#0072B2", "#D55E00", "#117733", "#AA4499"];
  const teams = [];
  for (let i = 0; i < nLanes; i++) {
    const fy = nLanes === 1 ? 40 : Math.round(clampN(18 + i * (64 / (nLanes - 1)), 12, 88));
    teams.push({ id: String.fromCharCode(65 + i), color: COLORS[i % COLORS.length], fileY: fy,
      driveY: Math.round(fy + (50 - fy) * 0.5), tirY: Math.round(fy + (50 - fy) * 0.92), plotDY: fy <= 50 ? -8 : 8 });
  }
  const players = [], markers = [], actions = [], zones = [];
  const frontX = 44;
  let ord = 1;
  teams.forEach((tm, ti) => {
    for (let i = 0; i < per; i++)                      // file : 1er devant, les suivants en queue vers la gauche
      players.push({ id: tm.id + (i + 1), num: i + 1, role: "Équipe " + tm.id, team: tm.id, color: tm.color, x: clampN(frontX - i * 7, 6, 60), y: tm.fileY });
    markers.push({ type: "plot", x: frontX, y: clampN(tm.fileY + tm.plotDY, 6, 94) });   // plot du côté EXTÉRIEUR, bien visible
    markers.push({ type: "ballon", x: frontX + 4, y: tm.fileY });                          // ballon SUR le 1er joueur (celui qui part)
    if (slalom) {
      // Plots (ou cerceaux, posés À PLAT = même repère) du slalom, en quinconce
      // le long de la conduite ; l'espacement coté est rappelé sous le 1er intervalle.
      const xs = [];
      for (let k = 1; k <= slalomN; k++) {
        const x = Math.round(frontX + k * ((80 - frontX) / (slalomN + 1)));
        xs.push(x);
        // Cerceaux dessinés comme tels depuis que le marqueur existe (anneaux
        // posés à plat) — les plots restent le défaut.
        markers.push({ type: slalomHoops ? "cerceau" : "plot", x, y: clampN(tm.driveY + (k % 2 ? -5 : 5), 8, 92) });
      }
      if (slalomGap && ti === 0 && xs.length >= 2)
        zones.push({ x: xs[0], y: clampN(tm.driveY + 10, 8, 92), w: xs[1] - xs[0], h: 0, style: "dashed", dim: `${slalomGap} m` });
    }
    const lead = tm.id + "1";
    if (shoot) {
      if (sport === "rugby") {
        // Pas de tir au rugby : le porteur file APLATIR dans l'en-but (portage),
        // DANS SON COULOIR (l'en-but couvre toute la largeur — pas de convergence
        // vers le centre comme au panier).
        actions.push({ from: lead, tox: 92, toy: tm.driveY, type: "dribble", order: ord++, label: ti === 0 ? "Aplatit dans l'en-but" : null });
        markers.push({ type: "essai", x: 92, y: tm.driveY });
      } else {
        actions.push({ from: lead, tox: 84, toy: tm.driveY, type: "dribble", order: ord++, label: ti === 0 ? (basket ? "Drive vers le panier" : "Conduite vers la cible") : null });
        actions.push({ from: lead, tox: hoopX, toy: tm.tirY, type: "tir", order: ord++, label: ti === 0 ? (basket ? "Tir en course (double-pas)" : "Tir au but") : null });
      }
    } else if (/demi.?tour|aller.?retour|\bretour\b|revien/.test(t)) {
      // NAVETTE / RELAIS ALLER-RETOUR : course jusqu'au plot (coté si donné),
      // demi-tour et retour vers la file (transmission au suivant).
      const dm = t.match(/plot[^.]{0,20}?[àa]\s*(\d{1,3})\s*m|[àa]\s*(\d{1,3})\s*m[èe]?[a-z]*\b/);
      const turnX = 64;
      markers.push({ type: "plot", x: turnX + 3, y: tm.fileY });
      actions.push({ from: lead, tox: turnX, toy: tm.fileY, type: "dribble", order: ord++, label: ti === 0 ? "Course jusqu'au plot" : null });
      actions.push({ from: lead, tox: frontX + 6, toy: clampN(tm.fileY + 5, 6, 94), type: "course", order: ord++, label: ti === 0 ? "Retour et transmission" : null });
      if (dm && ti === 0) zones.push({ x: frontX, y: clampN(tm.fileY - 8, 4, 96), w: turnX - frontX, h: 0, style: "dashed", dim: `${dm[1] || dm[2]} m` });
    } else {
      actions.push({ from: lead, tox: hoopX - 6, toy: tm.driveY, type: "dribble", order: ord++, label: ti === 0 ? "Conduite jusqu'à la cible" : null });
    }
  });
  return {
    players, markers, actions, zones, sport,
    steps: shoot
      ? (basket
        ? ["Au top, le 1er de chaque file part en dribble vers le panier", "Tir en course (double-pas) dans la zone matérialisée", "Le joueur suivant enchaîne — 1re équipe à 10 paniers gagne"]
        : ["Au top, le 1er de chaque file conduit le ballon vers la cible", "Tir au but", "Le joueur suivant enchaîne"])
      : ["Au top, le 1er de chaque file conduit le ballon jusqu'à la cible", "Le joueur suivant enchaîne (relais)"],
  };
}

/* PASSES DANS DES PORTES — « passes à travers des portes matérialisées par 2
   cônes », N portes réparties sur une aire, chaque passe réussie dans une porte
   DIFFÉRENTE compte. Un binôme : la passe TRAVERSE la porte, le passeur se
   replace derrière une autre porte pour la passe suivante. (Sans ce gabarit,
   l'IA classait la situation en « lancer sur cible » — contresens.) */
export function buildGatesPassing(text) {
  const t = c0(text || "");
  const nm = t.match(/(\d+|deux|trois|quatre|cinq|six)\s*portes?/);
  const n = clampN(nm ? frenchNumWord(nm[1]) : 3, 2, 6);
  const gapM = t.match(/(\d+(?:[.,]\d+)?)\s*(?:m\b|m[èe]tres?)/);
  const SPOTS = [[32, 30], [68, 26], [50, 54], [30, 74], [70, 74], [50, 12]];
  const zones = [], markers = [], gates = [];
  for (let i = 0; i < n; i++) {
    const [gx, gy] = SPOTS[i % SPOTS.length];
    gates.push({ x: gx, y: gy });
    markers.push({ type: "plot", x: gx, y: gy - 5 }, { type: "plot", x: gx, y: gy + 5 });
  }
  // Écartement coté sur la 1re porte.
  if (gapM) zones.push({ x: gates[0].x, y: gates[0].y - 5, w: 0, h: 10, style: "dashed", label: `${gapM[1].replace(",", ".")} m` });
  const g1 = gates[0], g2 = gates[1] || gates[0];
  const a1 = { x: clampN(g1.x - 16, 4, 96), y: g1.y };
  const a2 = { x: clampN(g1.x + 16, 4, 96), y: g1.y };
  const players = [
    { id: "A1", num: 1, role: "Passeur", team: "A", color: "#0072B2", x: a1.x, y: a1.y },
    { id: "A2", num: 2, role: "Partenaire", team: "A", color: "#0072B2", x: a2.x, y: a2.y },
  ];
  // Passe 1 dans la porte 1 ; A1 se replace de l'AUTRE côté de la porte 2 (le
  // symétrique de A2 par rapport à la porte : la passe 2 traverse la porte 2).
  const a1b = { x: clampN(2 * g2.x - a2.x, 4, 96), y: clampN(2 * g2.y - a2.y, 6, 94) };
  const actions = [
    { from: "A1", to: "A2", type: "passe", order: 1, label: "Passe dans la porte 1" },
    { from: "A1", tox: a1b.x, toy: a1b.y, type: "course", order: 2, label: "Se replace derrière une autre porte" },
    { from: "A2", to: "A1", type: "passe", order: 3, label: "Passe dans la porte 2" },
  ];
  markers.push({ type: "ballon", x: clampN(a1.x + 4, 4, 96), y: a1.y });
  return {
    players, markers, zones, actions,
    steps: ["Passe à travers une porte vers le partenaire", "Se replacer pour viser une porte différente", "1 point par porte différente réussie"],
  };
}

/* CONDUITE RÉACTIVE AUX COULEURS — carrés/plots de couleur disposés aux coins ;
   au SIGNAL (l'enseignant annonce une couleur), le porteur conduit son ballon
   jusqu'au repère de la couleur annoncée. */
export function buildReactiveDribble(text) {
  const t = c0(text || "");
  const cols = [];
  { let m; const re = new RegExp(PLOT_COLOR_RE.source, "g");
    while ((m = re.exec(t))) { const c = normPlotColor(m[1]); if (c && !cols.includes(c)) cols.push(c); } }
  for (const c of ["rouge", "bleu", "jaune", "vert"]) if (cols.length < 4 && !cols.includes(c)) cols.push(c);
  const CORNERS = [[16, 16], [84, 16], [84, 84], [16, 84]];
  const zones = [], markers = [];
  cols.slice(0, 4).forEach((cName, i) => {
    const [cx, cy] = CORNERS[i];
    zones.push({ x: cx - 8, y: cy - 8, w: 16, h: 16, style: "dashed", label: cName.charAt(0).toUpperCase() + cName.slice(1) });
    markers.push({ type: "plot", x: cx - 5, y: cy - 5, color: PLOT_COLOR_HEX[cName] });
    markers.push({ type: "plot", x: cx + 5, y: cy + 5, color: PLOT_COLOR_HEX[cName] });
  });
  const players = [{ id: "A1", num: 1, role: "Porteur", team: "A", color: "#0072B2", x: 50, y: 50 }];
  markers.push({ type: "ballon", x: 54, y: 50 });
  const actions = [
    { from: "A1", tox: CORNERS[0][0] + 7, toy: CORNERS[0][1] + 7, type: "dribble", order: 1, label: `Conduite vers « ${cols[0]} »` },
    { from: "A1", tox: CORNERS[2][0] - 7, toy: CORNERS[2][1] - 7, type: "course", style: "dashed", order: 2, label: "Selon la couleur annoncée" },
  ];
  return {
    players, markers, zones, actions,
    steps: ["Conduite de balle libre au centre", "Au signal, rejoindre le carré de la couleur annoncée", "Garder le ballon au pied jusque dans le carré"],
  };
}

/* CIRCUIT EN BOUCLE (course de durée) — « courir N minutes sur un circuit de
   200 m balisé par des plots » : anneau de plots, coureur qui boucle, cote du
   circuit, observateur/compteur de tours au centre. */
/* GÉOMÉTRIE DE LA PISTE, paramétrée EN MÈTRES. Le renderer dessine un stade
   (rectangle à extrémités semi-circulaires) dont la bande de couloirs fait
   16 % de la hauteur, divisée en 4 couloirs ; le milieu du couloir j (1 =
   intérieur) est donc à 16·(9−2j)/8 % du bord. En unités de scène (0-100) et
   avec le rapport largeur/hauteur du cadre (~1,74) :
     · lignes droites de x = 28,8 à x = 71,2 (identiques pour tous les couloirs) ;
     · couloir j : y = 100−dy et y = dy en haut, virages de demi-largeur
       RY = 50−dy en y et RY/1,74 en x, avec dy = 14, 10, 6, 2.
   Le couloir 1 mesure 2×73,6 + 2π×36 = 373,4 unités « verticales » pour les
   400 m réglementaires, soit K = 0,9335 unité par mètre. Ce MÊME facteur sert
   à tous les couloirs : deux équipes côte à côte se retrouvent alignées sur
   les lignes droites, et le couloir extérieur couvre un peu plus de terrain
   dans les virages — ce qui est la réalité de la piste. */
const LANE_K = 0.9335;                 // unités de scène (verticales) par mètre
const LANE_DY = [14, 10, 6, 2];        // milieu des couloirs 1 à 4
const LANE_XA = 28.8, LANE_XB = 71.2, LANE_DROITE = 73.6;   // en unités
export const LANE_LAP = 400;
/* Position sur le couloir `voie` (1 = intérieur ; accepte une valeur
   fractionnaire pour tracer une bande à cheval sur plusieurs couloirs) à
   l'abscisse `u` MÈTRES, comptée dans le sens de la course depuis le début de
   la ligne droite avant. */
export function laneAt(u, voie = 1) {
  // `voie` accepte les DEMI-COULOIRS (0,5 = corde intérieure de la pelouse ;
  // 4,5 = corde extérieure) : c'est ainsi qu'on trace un trait EN TRAVERS d'un
  // couloir, en reliant ses deux bords.
  const dy = LANE_DY[0] - (Math.max(0.5, Math.min(4.5, voie)) - 1) * 4;
  const RY = 50 - dy, RX = RY / 1.737, VIR = Math.PI * RY;
  const per = 2 * LANE_DROITE + 2 * VIR;
  let s = ((u * LANE_K) % per + per) % per;
  if (s <= LANE_DROITE) return { x: LANE_XA + (LANE_XB - LANE_XA) * (s / LANE_DROITE), y: 100 - dy };
  s -= LANE_DROITE;
  if (s <= VIR) { const th = Math.PI / 2 - (s / VIR) * Math.PI; return { x: LANE_XB + RX * Math.cos(th), y: 50 + RY * Math.sin(th) }; }
  s -= VIR;
  if (s <= LANE_DROITE) return { x: LANE_XB - (LANE_XB - LANE_XA) * (s / LANE_DROITE), y: dy };
  const th = -Math.PI / 2 + ((s - LANE_DROITE) / VIR) * Math.PI;
  return { x: LANE_XA - RX * Math.cos(th), y: 50 + RY * Math.sin(th) };
}
const r1 = (v) => Math.round(v * 10) / 10;
const lanePt = (u, voie) => { const p = laneAt(u, voie); return { x: r1(p.x), y: r1(p.y) }; };
/* Échantillonnage d'une PORTION de couloir : la polyligne que suivent les
   flèches de course et les rubans de zone. Un point tous les ~4 m — assez fin
   pour que la corde ne quitte jamais le couloir dans un virage. */
export function laneSpan(u0, u1, voie, pas = 4) {
  const n = Math.max(2, Math.ceil(Math.abs(u1 - u0) / pas));
  const out = [];
  for (let i = 0; i <= n; i++) out.push(lanePt(u0 + ((u1 - u0) * i) / n, voie));
  return out;
}

/* ============================ RELAIS D'ATHLÉTISME ==========================
   Gabarit DÉDIÉ (le dispositif générique n'y arrivait pas : un seul segment de
   course, une seule zone, des coureurs hors couloir). Ce que doit montrer un
   schéma de relais, et que rien d'autre ne montrait :
     · les N relayeurs d'une équipe sont dans LE MÊME COULOIR, échelonnés ;
     · il y a N-1 ZONES DE TRANSMISSION, une par passage — pas une seule ;
     · dans chaque zone le RECEVEUR DÉMARRE en début de zone et la transmission
       se fait MAIN À MAIN en FIN de zone ;
     · chaque relayeur COURT SA TRANCHE, flèche à l'appui : sans elle on ne
       voit pas la course (et le diaporama n'a rien à animer) ;
     · l'ÉCHELLE est réelle — un 4×40 (160 m) occupe 40 % de l'anneau, un
       4×60 (240 m) en occupe 60 % : les deux schémas doivent différer.
   Références : 4×100 — trois zones de 30 m se terminant aux marques des 100 /
   200 / 300 m (règle actuelle) ; formats scolaires — zone de 20 m par défaut,
   ou la valeur donnée par le texte. */
export function buildRelaisAthle(text, nCoureurs) {
  const t = c0(text);
  const mx = t.match(/(\d)\s*[x×]\s*(\d{2,3})\s*m/);
  const n = clampN(nCoureurs || (mx ? +mx[1] : 4), 2, 4);
  const relais = clampN(mx ? +mx[2] : 100, 20, 100);
  // Zone : celle du texte si elle y est, sinon 30 m (format fédéral) ou 20 m
  // (formats scolaires courts).
  const mz = t.match(/zone[^.]{0,30}?de\s*(\d{1,2})\s*m/);
  const zoneLen = clampN(mz ? +mz[1] : (relais >= 80 ? 30 : 20), 5, Math.max(10, Math.round(relais * 0.75)));
  // Équipes en confrontation : une par COULOIR, dans la limite des couloirs
  // dessinés utilisables (3 — le 4e frôle le bord de l'image).
  const me = t.match(/(\d|deux|trois|quatre)\s*[ée]quipes/);
  const mots = { deux: 2, trois: 3, quatre: 4 };
  const equipes = clampN(me ? (parseInt(me[1], 10) || mots[me[1]] || 1) : 1, 1, 3);
  const TEAMS = ["A", "B", "C"];
  const ARRIVEE = LANE_DROITE / LANE_K;        // fin de la ligne droite avant
  const DEPART = ARRIVEE - n * relais;         // échelle RÉELLE : 4×40 ≠ 4×60
  const marque = (k) => DEPART + k * relais;   // fin de la zone k (k = 1..n-1)
  const attente = (k) => marque(k) - zoneLen;  // le receveur k+1 y patiente
  const trans = (k) => marque(k) - zoneLen * 0.15;   // main à main, en FIN de zone

  const players = [], markers = [], zones = [], actions = [];
  let ord = 1;
  /* Une zone en BANDE le long d'une polyligne. La boîte englobante est
     conservée : les contrôles (lisibilité, chevauchement) raisonnent en
     x/y/w/h ; seul le RENDU suit la bande. */
  const poserZone = (band, thick, style, label) => {
    const xs = band.map((p) => p.x), ys = band.map((p) => p.y);
    zones.push({ band, thick, style, label,
      x: Math.round(Math.min(...xs) - 3), y: Math.round(Math.min(...ys) - 3),
      w: Math.round(Math.max(...xs) - Math.min(...xs) + 6),
      h: Math.round(Math.max(...ys) - Math.min(...ys) + 6) });
  };
  for (let e = 0; e < equipes; e++) {
    const voie = e + 1, team = TEAMS[e];
    // Les N relayeurs de l'équipe, tous sur LE MÊME couloir.
    const posts = [DEPART - 4];
    for (let k = 1; k < n; k++) posts.push(attente(k));
    posts.forEach((u, i) => {
      const p = lanePt(u, voie);
      // Rôles étiquetés sur la SEULE équipe de référence : douze libellés
      // empilés sur trois couloirs voisins ne se lisent plus, et la couleur
      // (rappelée en légende) suffit à identifier les autres équipes.
      players.push({ id: `R${voie}${i + 1}`, num: i + 1, team,
        role: e > 0 ? "" : (i === 0 ? "1er relayeur (départ)" : `${i + 1}e relayeur`),
        x: p.x, y: p.y });
    });
    const tm = lanePt(DEPART - 9, voie);
    markers.push({ type: "temoin", x: tm.x, y: tm.y, label: e === 0 ? "Témoin" : null });
    // LA COURSE DE CHAQUE RELAYEUR, sur toute sa tranche et SUIVANT LE COULOIR,
    // puis la TRANSMISSION qui la conclut — dans cet ordre, en alternance :
    // la chronologie du schéma (et du diaporama) doit être celle de la course,
    // pas « les quatre courses puis les trois passages ».
    // Sans ces flèches on ne voyait pas les élèves courir, et le diaporama,
    // qui anime la pointe le long de `path`, n'avait rien à montrer. `path`
    // évite la corde droite, qui coupait la pelouse dans les virages.
    for (let i = 0; i < n; i++) {
      const u0 = i === 0 ? DEPART : attente(i);
      const u1 = i === n - 1 ? ARRIVEE : trans(i + 1);
      actions.push({ from: `R${voie}${i + 1}`, path: laneSpan(u0, u1, voie), type: "course",
        // Chronologie NUMÉROTÉE sur la seule équipe de référence : trois jeux
        // de badges superposés sur trois couloirs voisins ne se lisent plus.
        ...(e === 0 ? { order: ord++ } : {}),
        label: e === 0
          ? (i === 0 ? `Le 1er relayeur court sa tranche de ${relais} m`
            : (i === n - 1 ? `Le ${n}e relayeur finit sur la ligne d'arrivée`
              : `Le ${i + 1}e relayeur démarre en début de zone et court sa tranche`))
          : null });
      // TRANSMISSION MAIN À MAIN, en fin de zone : quelques mètres, à l'échelle
      // du geste réel — jamais un segment entre deux pastilles éloignées.
      if (i === n - 1) continue;
      const k = i + 1;
      const a = lanePt(trans(k) - 5, voie), b = lanePt(trans(k), voie);
      actions.push({ fromx: a.x, fromy: a.y, tox: b.x, toy: b.y, type: "passe",
        ...(e === 0 ? { order: ord++ } : {}),
        label: e === 0 && k === 1 ? "Témoin transmis main à main, en fin de zone" : null });
    }
    // LIGNE DE DÉPART, EN TRAVERS DU COULOIR (deux bords du couloir reliés).
    // Avec plusieurs équipes elles sont DÉCALÉES : c'est le stagger, rendu
    // visible — chaque couloir part d'un point différent pour que tous
    // parcourent la même distance jusqu'à l'arrivée, qui est commune.
    poserZone([lanePt(DEPART, voie - 0.5), lanePt(DEPART, voie + 0.5)], 1.8, "solid",
      e === 0 ? (equipes > 1 ? "Départs décalés (stagger)" : "Départ") : null);
    // LES ZONES DE TRANSMISSION, UNE PAR COULOIR. Un ruban unique en travers
    // des couloirs serait faux dans un virage : les zones sont staggerées
    // comme les départs (mesuré : ~6 m d'écart sur le couloir extérieur pour
    // une zone prise dans le virage, soit un quart de zone).
    for (let k = 1; k < n; k++)
      poserZone(laneSpan(attente(k), marque(k), voie), 4.4, "dashed",
        // Étiquette sur la SEULE équipe de référence : neuf libellés empilés
        // ne se lisent plus. Le mot « transmission » reste — c'est lui que
        // lisent les filtres de zones (analysis.js) et les bancs.
        e === 0 ? `Transmission ${k} · ${zoneLen} m` : null);
  }
  // OBSERVATEUR : « au centre du stade » = dans la pelouse, d'où il voit les
  // trois couloirs — surtout pas sur la piste, où il gênerait la course.
  const obs = /observateur|observatrice|juge|arbitre/.test(t);
  if (obs) players.push({ id: "OBS", team: "OBS", color: "#F2C200",
    role: "Observateur", num: "", x: 50, y: 50 });
  const steps = [
    `${equipes > 1 ? `${equipes} équipes, une par couloir — ` : ""}${n} relayeurs par équipe, un par tranche de ${relais} m`,
    `Le receveur démarre en début de zone (${zoneLen} m), sans regarder derrière`,
    "Le donneur arrive lancé : transmission main à main en FIN de zone",
    "Témoin transmis hors zone ou à l'arrêt : l'équipe est disqualifiée",
  ];
  if (obs) steps.push("L'observateur, au centre, relève les transmissions hors zone ou à l'arrêt");
  return {
    players, markers, zones, actions,
    // `stadePlace` : la scène est DÉJÀ posée sur les couloirs — ni
    // `applyStadeTerrain` ni l'écartement anti-superposition ne doivent y
    // toucher (deux couloirs voisins ne sont distants que de 4 unités).
    terrainOpts: { stade: true, stadePlace: true },
    steps,
  };
}

export function buildCircuitLoop(text) {
  const t = c0(text || "");
  const dm = t.match(/circuit\s+de\s*(\d{2,4})\s*m/);
  const cx = 50, cy = 52, rx = 36, ry = 30;
  const players = [], markers = [], actions = [];
  for (let i = 0; i < 10; i++) {
    const a = (i / 10) * 2 * Math.PI;
    markers.push({ type: "plot", x: Math.round(cx + rx * Math.cos(a)), y: Math.round(cy + ry * Math.sin(a)) });
  }
  players.push({ id: "C1", num: 1, role: "Coureur", team: "A", color: "#0072B2", x: cx - rx + 5, y: cy });
  const pt = (a) => ({ x: Math.round(cx + (rx - 6) * Math.cos(a)), y: Math.round(cy + (ry - 6) * Math.sin(a)) });
  let prev = pt(Math.PI);
  [Math.PI * 1.5, 0, Math.PI * 0.5, Math.PI].forEach((a, i) => {
    const q = pt(a);
    actions.push({ ...(i === 0 ? { from: "C1" } : { fromx: prev.x, fromy: prev.y }), tox: q.x, toy: q.y,
      type: "course", order: i + 1, label: i === 0 ? "Course en boucle" : null });
    prev = q;
  });
  players.push({ id: "O1", role: "Observateur", team: "OBS", color: "#F2C200", x: cx, y: cy });
  markers.push({ type: "consigne", x: cx, y: cy - 6, text: "Compte les tours" });
  const zones = dm ? [{ x: cx - rx, y: Math.max(4, cy - ry - 10), w: rx * 2, h: 0, style: "dashed", dim: `${dm[1]} m` }] : [];
  return {
    players, markers, actions, zones,
    steps: ["Courir sans s'arrêter sur le circuit balisé", "L'observateur compte les tours de son binôme", "S'arrêter au signal de fin"],
  };
}

/* TIRS MULTI-POSTES (sports à but) — « N plots à des distances variées dans le
   demi-terrain d'attaque, tirs successifs depuis chaque position » : postes en
   éventail (jamais alignés), le tireur enchaîne tir puis déplacement vers le
   poste suivant (le rendu suit sa pastille), gardien dans le but. */
export function buildMultiSpotShooting(text, sport) {
  const t = c0(text || "");
  const NUMW = { deux: 2, trois: 3, quatre: 4, cinq: 5, six: 6 };
  const nm = t.match(/(\d+|deux|trois|quatre|cinq|six)\s*(?:plots?|c[ôo]nes?|coupelles?|postes?|positions?|spots?|cerceaux?)/);
  const n = clampN(nm ? (NUMW[nm[1]] || parseInt(nm[1], 10)) : 4, 3, 6);
  // Postes matérialisés par des CERCEAUX (« déclenche un tir depuis chaque
  // cerceau ») : le marqueur suit le matériel dit.
  const hoopSpots = /cerceaux?/.test(t);
  const basket = sport === "basketball" || sport === "basket3x3";
  // NIVEAUX PROGRESSIFS (« Niveau 1 : tir à 2 m … Niveau 3 : lancer franc ») :
  // un poste PAR NIVEAU, à la distance dite (échelle du sport : la ligne des
  // lancers francs ≈ 4,6 m -> x=80 au basket), en éventail devant la cible.
  let spots = null, spotLabels = null;
  const lvls = [...t.matchAll(/niveaux?\s*\d+\s*[:\-—]?\s*([^.;]*)/g)].map((m) => m[1]);
  if (lvls.length >= 2) {
    const perM = { basketball: 3.7, basket3x3: 3.7, handball: 2.8 }[sport] || 1.0;
    spots = []; spotLabels = [];
    lvls.slice(0, 6).forEach((seg, i) => {
      let x = null;
      const dm = seg.match(/(\d+(?:[.,]\d+)?)\s*m/);
      if (dm) x = clampN(97 - parseFloat(dm[1].replace(",", ".")) * perM, 55, 92);
      else if (/lancer franc/.test(seg)) x = (LM_SPORT[sport] || {}).lancer_franc || 80;
      else if (/penalty|7 m/.test(seg)) x = (LM_SPORT[sport] || {}).penalty || (LM_SPORT[sport] || {})["7m"] || 86;
      if (x == null) x = 90 - i * 10;
      spots.push([Math.round(x), 50 + (i % 2 ? -1 : 1) * Math.min(8 + i * 4, 22)]);
      spotLabels.push(`Niveau ${i + 1}`);
    });
  }
  // Postes posés À UNE MÊME DISTANCE (« 3 cerceaux posés à 9 mètres ») : tous
  // sur l'arc de cette distance (repères du sport), étagés en largeur.
  if (!spots) {
    const dm = t.match(/(?:pos[ée]s?|plac[ée]s?|situ[ée]s?)[^.]{0,24}?[àa]\s*(\d+(?:[.,]\d+)?)\s*m/);
    if (dm) {
      const x = landmarkX(`${dm[1]}m`, sport) ?? 76;
      spots = [];
      for (let i = 0; i < n; i++) spots.push([x, Math.round(clampN(50 + (i - (n - 1) / 2) * (44 / Math.max(1, n - 1)), 18, 82))]);
    }
  }
  // Postes à DISTANCES VARIÉES du but (proches/lointains alternés, en éventail).
  const SPOTS = [[62, 32], [76, 64], [68, 46], [82, 24], [72, 76], [60, 58]];
  if (!spots) spots = SPOTS.slice(0, n);
  const players = [{ id: "T1", num: 1, role: "Tireur", team: "A", color: "#0072B2", x: spots[0][0], y: spots[0][1] }];
  const markers = [{ type: "ballon", x: spots[0][0] + 4, y: spots[0][1] - 3 }];
  const actions = [];
  let ord = 1;
  spots.forEach(([sx, sy], i) => {
    markers.push({ type: hoopSpots ? "cerceau" : "plot", x: sx - 3, y: sy + 4, ...(spotLabels ? { label: spotLabels[i] } : {}) });   // le poste (plot ou cerceau), au pied du tireur
    actions.push({ from: "T1", tox: 97, toy: clampN(44 + (i % 3) * 6, 40, 60), type: "tir", order: ord++, label: i === 0 ? (spotLabels ? "Tir du niveau 1" : "Tir du poste 1") : null });
    if (i < spots.length - 1) {
      const [nx, ny] = spots[i + 1];
      actions.push({ from: "T1", tox: nx, toy: ny, type: "course", style: "dashed", order: ord++, label: i === 0 ? "Rejoint le poste suivant" : null });
    }
  });
  if (!basket) players.push({ id: "G", role: "Gardien", team: "k", color: "#F2C200", x: 94, y: 50 });
  return {
    players, markers, actions,
    steps: ["Tir depuis le poste 1", "Rejoindre le poste suivant et enchaîner", basket ? "Un tir par poste, compter les paniers" : "Un tir par poste face au gardien"],
  };
}

/* Effectif d'une colonne : « deux colonnes DE 3 joueurs », « files de quatre ».
   Défaut 3, borné 2-5 (au-delà les suiveurs sortent du terrain). */
function columnSize(t) {
  const NUMW = { deux: 2, trois: 3, quatre: 4, cinq: 5, six: 6 };
  const m = t.match(/(?:colonnes?|files?|lignes?|rang[ée]es?)\s*de\s*(\d+|deux|trois|quatre|cinq|six)/);
  return clampN(m ? (NUMW[m[1]] || parseInt(m[1], 10)) : 3, 2, 5);
}

/* TIR-REBOND À DEUX COLONNES (échauffement classique basket / handball) — deux
   colonnes FACE AU PANIER/BUT : le tireur dribble et marque (double pas / tir en
   course), le rebondeur capte le rebond et RELANCE le tireur suivant ; après son
   passage chacun CHANGE de colonne (le tireur rejoint les rebondeurs, le
   rebondeur suit sa passe chez les tireurs). Sans ce gabarit, l'IA le classait en
   « atelier/rotation » -> DEUX boîtes déconnectées (slalom + passe), ou en deux
   lignes qui tirent toutes les deux, jamais la rotation croisée décrite. */
/* CÔTÉ DE LA COLONNE DES TIREURS (« colonne de droite (les tireurs)… ») — lu du
   texte. Convention terrain : le but/panier attaqué est À DROITE de l'écran,
   les colonnes lui font face -> LEUR droite = le BAS du schéma (y grand), leur
   gauche = le haut. On isole le segment qui suit chaque « colonne de droite/
   gauche » (borné à la mention de l'autre colonne / fin de phrase) et on y
   cherche le rôle. Retourne "droite" | "gauche" | null (côté non précisé). */
const SHOOTER_WORDS = /tireurs?|\btirs?\b|\btirent?\b|\btire\b|shoot|lay.?up|double.?(?:pas|appui)|marque|aplat|essai|frappe/;
const SUPPORT_WORDS = /rebond|r[ée]cup[èe]r|relance/;
function shooterColumnSide(t) {
  const seg = (side) => {
    const m = t.match(new RegExp(`(?:colonnes?|files?|lignes?|rang[ée]es?)\\s+de\\s+${side}`));
    if (!m) return "";
    const rest = t.slice(m.index + m[0].length);
    // Borné à la PROPOSITION de cette colonne : mention de l'autre colonne
    // (« colonne de gauche », « l'autre colonne ») ou ponctuation — sinon le
    // segment avale la description de la colonne d'en face.
    const cut = rest.search(/(?:colonnes?|files?|lignes?|rang[ée]es?)\s+de\s+(?:droite|gauche)|autre\s+(?:colonne|file|ligne)|[.;,]/);
    return rest.slice(0, cut < 0 ? 120 : cut);
  };
  const segD = seg("droite"), segG = seg("gauche");
  // Les mots d'APPUI priment : « relance le TIREUR suivant » cite le tireur
  // comme DESTINATAIRE dans le segment de la colonne d'appui — un mot de
  // rebond/récupération identifie sans ambiguïté la colonne d'appui.
  if (SUPPORT_WORDS.test(segD)) return "gauche";
  if (SUPPORT_WORDS.test(segG)) return "droite";
  if (SHOOTER_WORDS.test(segD)) return "droite";
  if (SHOOTER_WORDS.test(segG)) return "gauche";
  // Formulations inversées (« les tireurs sont à droite », « à droite, les tireurs »).
  if (/tireurs?[^.;]{0,30}[àa] droite|[àa] droite[^.;]{0,30}tireurs?/.test(t)) return "droite";
  if (/tireurs?[^.;]{0,30}[àa] gauche|[àa] gauche[^.;]{0,30}tireurs?/.test(t)) return "gauche";
  return null;
}

/* POSITION DE DÉPART des colonnes lue du TEXTE : « à partir de la ligne
   médiane », « départ au rond central », « à N mètres du panier/but ».
   Lit le texte de la situation NOMINALE (coreSituationText) — une variante
   « si trop facile, partir de la médiane » ne déplace pas le départ. */
function columnsHeadX(text, sport, def) {
  const t = c0(coreSituationText(text || ""));
  if (/ligne m[ée]diane|mi.?terrain|rond central|milieu d[ue] terrain|centre du terrain/.test(t)) return 50;
  const m = t.match(/[àa]\s*(\d+(?:[.,]\d+)?)\s*m(?:[èe]tres?)?\s*d[ue]s?\s*(?:panier|but\b|cercle|cible)/);
  if (m) {
    const x = landmarkX(null, sport, parseFloat(m[1].replace(",", ".")));
    if (x != null) return clampN(x, 20, 60);   // borné : il faut la place de dribbler et conclure
  }
  return def;
}

export function buildShootRebound(text, sport) {
  const t = c0(text || "");
  const per = columnSize(t);
  const basket = sport === "basketball" || sport === "basket3x3";
  const rugby = sport === "rugby";
  // Le RÔLE de la 2e colonne dépend du sport : « rebond » n'a de sens qu'au
  // panier (basket / handball, la balle rebondit) ; au football/futsal/rugby on
  // RÉCUPÈRE le ballon et on relance. Labels et marqueur adaptés.
  const rebounds = basket || sport === "handball";
  const shooterRole = rugby ? "Porteur" : "Tireur";
  const supportRole = rebounds ? "Rebondeur" : "Récupérateur";
  const supportRun = rebounds ? "Va au rebond" : "Récupère le ballon";
  const players = [], actions = [], markers = [];
  // CÔTÉ des tireurs lu du TEXTE (face au but à droite : leur droite = bas du
  // schéma). Non précisé -> tireurs en haut (comme avant).
  const side = shooterColumnSide(t);
  const SHY = side === "droite" ? 66 : 34, RBY = SHY === 66 ? 34 : 66;
  const HEADX = columnsHeadX(text, sport, 33), STEP = 8;
  const bx = (i) => Math.max(6, HEADX - i * STEP);
  const tailX = bx(per - 1);
  for (let i = 0; i < per; i++) {
    players.push({ id: "S" + (i + 1), num: i + 1, role: i === 0 ? shooterRole : "En attente", team: "A", color: "#0072B2", x: bx(i), y: SHY });
    players.push({ id: "R" + (i + 1), num: i + 1, role: i === 0 ? supportRole : "En attente", team: "B", color: "#D55E00", x: bx(i), y: RBY });
  }
  markers.push({ type: "ballon", x: HEADX + 4, y: SHY + (SHY < 50 ? -3 : 3) });
  // Points d'appui MIROITÉS avec la rangée des tireurs (conclusion côté tireurs,
  // rebond capté entre les deux rangées).
  const layX = 80, layY = 50 + (SHY < 50 ? -8 : 8), rebX = 84, rebY = 50 + (RBY < 50 ? -6 : 6);
  let ord = 1;
  // MATÉRIEL : slalom décrit (« slalom entre 4 plots ») -> plots en quinconce
  // le long de la conduite du tireur (l'aménagement matériel est montré).
  if (/slalom|zigzag/.test(t)) {
    const nm2 = t.match(/(\d+|deux|trois|quatre|cinq|six)\s*(?:plots?|c[ôo]nes?|coupelles?|piquets?)/);
    const NW = { deux: 2, trois: 3, quatre: 4, cinq: 5, six: 6 };
    const nP = clampN(nm2 ? (NW[nm2[1]] || parseInt(nm2[1], 10)) : 4, 3, 6);
    const x0 = HEADX + 8, x1 = layX - 6;
    for (let k = 0; k < nP; k++)
      markers.push({ type: "plot", x: Math.round(x0 + (x1 - x0) * (k / (nP - 1))), y: SHY + (k % 2 ? 5 : -5) });
  }
  // 1) Le tireur conduit vers le cercle/but et conclut (double pas / tir en
  //    course / aplatissement — converti par le chokepoint rugby de finalizeScene).
  actions.push({ from: "S1", tox: layX, toy: layY, type: "dribble", order: ord++, label: basket ? "Dribble puis double pas" : rugby ? "Porte le ballon" : "Conduit et tire" });
  actions.push({ from: "S1", tox: 96, toy: 50, type: "tir", order: ord++, label: basket ? "Tir en course" : "Tir au but" });
  // 2) L'appui (rebondeur/récupérateur) récupère puis relance le tireur suivant.
  actions.push({ from: "R1", tox: rebX, toy: rebY, type: "course", order: ord++, label: supportRun });
  if (rebounds) markers.push({ type: "rebond", x: rebX + 3, y: rebY + (RBY < 50 ? -4 : 4) });
  actions.push({ from: "R1", to: per >= 2 ? "S2" : "S1", type: "passe", order: ord++, label: `Relance au ${rugby ? "porteur" : "tireur"} suivant` });
  // 3) Rotation CROISÉE : le tireur file dans l'autre colonne, l'appui suit sa
  //    passe chez les tireurs (arcs de rotation en gris, secondaires au jeu).
  actions.push({ from: "S1", tox: tailX - 3, toy: RBY, type: "rotation", order: ord++, bulge: 4, label: `Le ${rugby ? "porteur" : "tireur"} rejoint les ${rebounds ? "rebondeurs" : "récupérateurs"}` });
  actions.push({ from: "R1", tox: tailX - 3, toy: SHY, type: "rotation", order: ord++, bulge: 4, label: `Suit sa passe et devient ${rugby ? "porteur" : "tireur"}` });
  return {
    players, markers, actions,
    steps: [
      basket ? "Le tireur dribble et marque (double pas / tir en course)" : rugby ? "Le porteur avance et aplatit l'essai" : "Le tireur conduit et tire au but",
      `${rebounds ? "Le rebondeur capte le rebond" : "Le récupérateur récupère le ballon"} et relance le ${rugby ? "porteur" : "tireur"} suivant`,
      `Chacun change de colonne : le ${rugby ? "porteur" : "tireur"} devient appui, l'appui devient ${rugby ? "porteur" : "tireur"}`,
    ],
  };
}

/* PASSE-ET-SUIT À DEUX COLONNES (échauffement de passes, tous sports) — deux
   colonnes FACE À FACE : on passe au premier de la colonne d'en face puis on SUIT
   sa passe pour se replacer au bout de cette colonne (rotation en miroir). Sans
   ce gabarit, l'IA le rendait en deux « couloirs » parallèles (passeur/receveur
   figés, observateur halluciné) ou en conduite vers une cible. */
export function buildPassAndFollow(text, sport) {
  const t = c0(text || "");
  const per = columnSize(t);
  const players = [], actions = [], markers = [];
  const LHX = 28, RHX = 72, STEP = 10, Y = 50;
  const lx = (i) => Math.max(4, LHX - i * STEP);
  const rx = (i) => Math.min(96, RHX + i * STEP);
  for (let i = 0; i < per; i++) {
    players.push({ id: "L" + (i + 1), num: i + 1, role: i === 0 ? "Passeur" : "Colonne A", team: "A", color: "#0072B2", x: lx(i), y: Y });
    players.push({ id: "R" + (i + 1), num: i + 1, role: i === 0 ? "Receveur" : "Colonne B", team: "B", color: "#D55E00", x: rx(i), y: Y });
  }
  markers.push({ type: sport === "ultimate" ? "disque" : "ballon", x: LHX + 4, y: Y - 4 });
  const ballWord = sport === "rugby" ? "Passe à la main" : "Passe";
  let ord = 1;
  // 1) Le premier passe à la colonne d'en face puis SUIT sa passe au bout de cette colonne.
  actions.push({ from: "L1", to: "R1", type: "passe", order: ord++, label: ballWord });
  actions.push({ from: "L1", tox: rx(per - 1) + 2, toy: Y + 12, type: "course", order: ord++, style: "dashed", label: "Suit sa passe (au bout de la colonne d'en face)" });
  // 2) Le receveur enchaîne symétriquement -> montre la rotation en miroir.
  if (per >= 2) {
    actions.push({ from: "R1", to: "L2", type: "passe", order: ord++, label: "Enchaîne" });
    actions.push({ from: "R1", tox: lx(per - 1) - 2, toy: Y - 12, type: "course", order: ord++, style: "dashed", label: "Suit sa passe" });
  }
  return {
    players, markers, actions,
    steps: [
      "Passer au premier joueur de la colonne d'en face",
      "Suivre sa passe et se placer au bout de cette colonne",
      "Le receveur enchaîne à son tour (rotation en miroir)",
    ],
  };
}

/* Suiveurs « En attente » derrière une tête de colonne (dx/dy par pas). */
function pushWaiters(players, head, dx, dy, n) {
  for (let i = 1; i < n; i++)
    players.push({ id: head.id + "w" + i, num: i + 1, role: "En attente", team: head.team, color: head.color,
      x: Math.round(clampN(head.x + dx * i, 3, 97)), y: Math.round(clampN(head.y + dy * i, 3, 97)) });
}

/* PASSE -> TIR À POSTES DE RÔLE (colonnes, sports à but) — un PASSEUR alimente
   un FINISSEUR posté puis les rôles s'ÉCHANGENT. Trois variantes :
   • CENTRE-REPRISE (foot/futsal) : ailier au poteau de corner, centre, reprise de volée ;
   • PASSE-REMISE-TIR (hand) : une-deux avec le pivot posté aux 6 m ;
   • POSTE MATÉRIALISÉ (« cerceau posé à 9 m ») : passeur à l'aile, tir en appui du cerceau.
   Sans ce gabarit, l'IA classait en « atelier » -> boîtes déconnectées, voire 1 seul joueur. */
export function buildPassShootColumns(text, sport) {
  const t = c0(text || "");
  const per = columnSize(t);
  const players = [], actions = [], markers = [];
  let ord = 1;
  const centre = /centr/.test(t) && /repren|reprise|vol[ée]e|de la t[êe]te/.test(t);
  const remise = /pivot/.test(t) && /remise|remet|redonne/.test(t);
  if (centre) {
    const P = { id: "P1", num: 1, role: "Ailier", team: "A", color: "#0072B2", x: 93, y: 88 };
    const S = { id: "S1", num: 1, role: "Attaquant", team: "B", color: "#D55E00", x: 79, y: 50 };
    players.push(P, S);
    markers.push({ type: "ballon", x: P.x - 3, y: P.y - 4 });
    const rx = 86, ry = 54;                       // point de reprise, dans la surface
    actions.push({ from: "P1", tox: rx, toy: ry, type: "centre", order: ord++, label: "Centre" });
    actions.push({ from: "S1", tox: rx, toy: ry, type: "course", order: ord++, label: "Attaque le centre" });
    actions.push({ fromx: rx, fromy: ry, tox: 96, toy: 50, type: "tir", order: ord++, label: "Reprise de volée" });
    pushWaiters(players, P, -6, 2, per);          // file d'ailiers le long de la ligne
    pushWaiters(players, S, -8, 0, per);
    actions.push({ from: "P1", tox: 79 - 8 * (per - 1) - 3, toy: 50, type: "rotation", order: ord++, bulge: 5, label: "L'ailier devient attaquant" });
    actions.push({ from: "S1", tox: 93 - 6 * (per - 1) - 3, toy: 90, type: "rotation", order: ord++, bulge: 5, label: "L'attaquant rejoint les ailiers" });
    return { players, markers, actions, steps: ["L'ailier centre depuis le poteau de corner", "L'attaquant reprend de volée", "Changement de rôle après chaque passage"] };
  }
  if (remise) {
    const px = landmarkX("6m", sport) ?? 80;      // pivot posté à la zone
    const A = { id: "A1", num: 1, role: "Arrière", team: "A", color: "#0072B2", x: 56, y: 50 };
    const B = { id: "B1", num: 1, role: "Pivot", team: "B", color: "#D55E00", x: px, y: 50 };
    players.push(A, B);
    markers.push({ type: "ballon", x: A.x + 3, y: A.y - 4 });
    actions.push({ from: "A1", to: "B1", type: "passe", order: ord++, label: "Passe au pivot" });
    actions.push({ from: "A1", tox: 70, toy: 42, type: "course", order: ord++, label: "S'engage" });
    actions.push({ fromx: px, fromy: 50, tox: 70, toy: 42, type: "passe", order: ord++, label: "Remise" });
    actions.push({ fromx: 70, fromy: 42, tox: 96, toy: 50, type: "tir", order: ord++, label: "Tir au but" });
    pushWaiters(players, A, -8, 0, per);
    pushWaiters(players, B, 0, 9, per);           // pivots en attente HORS de la zone (vers l'aile)
    actions.push({ from: "A1", tox: px, toy: 50 + 9 * (per - 1) + 4, type: "rotation", order: ord++, bulge: 5, label: "L'arrière rejoint les pivots" });
    actions.push({ from: "B1", tox: 56 - 8 * (per - 1) - 3, toy: 50, type: "rotation", order: ord++, bulge: 5, label: "Le pivot rejoint les arrières" });
    return { players, markers, actions, steps: ["L'arrière sert le pivot et s'engage", "Le pivot remise dans la course", "Tir au but, puis échange des rôles"] };
  }
  // POSTE MATÉRIALISÉ (cerceau à N mètres) / passeur à l'aile.
  const dm = t.match(/cerceau[^.;]{0,32}?[àa]\s*(\d+(?:[.,]\d+)?)\s*m/);
  const sx = dm ? (landmarkX(`${dm[1].replace(",", ".")}m`, sport) ?? 74) : 74;
  const wing = /aile/.test(t);
  const S = { id: "S1", num: 1, role: "Tireur", team: "B", color: "#D55E00", x: sx, y: 50 };
  const P = { id: "P1", num: 1, role: "Passeur", team: "A", color: "#0072B2", x: wing ? 82 : 44, y: wing ? 86 : 50 };
  players.push(P, S);
  if (/cerceau/.test(t)) markers.push({ type: "cerceau", x: sx, y: 54 });
  markers.push({ type: "ballon", x: P.x + 3, y: P.y - 4 });
  actions.push({ from: "P1", to: "S1", type: "passe", order: ord++, label: "Passe" });
  actions.push({ from: "S1", tox: 96, toy: 50, type: "tir", order: ord++, label: "Tir en appui" });
  pushWaiters(players, P, wing ? -6 : -8, wing ? 2 : 0, per);
  actions.push({ from: "P1", tox: sx - 2, toy: 50, type: "rotation", order: ord++, bulge: 5, label: "Prend la place au poste" });
  actions.push({ from: "S1", tox: P.x - (wing ? 6 : 8) * (per - 1) - 3, toy: P.y + (wing ? 2 : 0) * (per - 1), type: "rotation", order: ord++, bulge: 5, label: "Rejoint la colonne des passeurs" });
  return { players, markers, actions, steps: ["Le passeur sert le tireur posté", "Tir en appui", "Le passeur prend le poste, le tireur rejoint les passeurs"] };
}

/* DÉFENSEURS ENTRANTS PAR VAGUES (supériorité provisoire) — attaque à N contre
   des défenseurs qui ENTRENT EN JEU un à un depuis des COLONNES postées en
   touche (souvent derrière une PORTE de deux plots) : le premier entre au
   départ, le suivant quand le ballon franchit un repère (la médiane…).
   Convention produit : les plots sont posés À L'INTÉRIEUR du terrain, jamais
   SUR la ligne de touche. Sans gabarit : l'analyse IA perdait portes et
   colonnes (plots hallucinés sur la médiane, 1 seul défenseur par vague). */
export function buildDefendersEntryWaves(text, sport) {
  const t = c0(text || "");
  const NW = { un: 1, une: 1, deux: 2, trois: 3, quatre: 4 };
  const perM = t.match(/colonnes? de (\d|deux|trois|quatre)\s*d[ée]fenseurs?/);
  // SANS colonne décrite : un seul défenseur par vague (« un premier défenseur
  // entre depuis la touche… un second entre… »).
  const perD = clampN(perM ? (NW[perM[1]] || parseInt(perM[1], 10)) : (/colonne/.test(t) ? 3 : 1), 1, 4);
  const gates = /portes?\b/.test(t);
  // PORTEUR SOLO (rugby : « le porteur part de sa ligne d'en-but… ») : pas de
  // passes à l'opposé — portage continu, chaque franchissement de seuil
  // déclenche l'entrée d'un défenseur depuis la touche.
  const solo = /\ble porteur\b/.test(t) && !/attaquants?\b/.test(t);
  if (solo) {
    const players = [], actions = [], markers = [];
    players.push({ id: "A1", num: 1, role: "Porteur", team: "A", color: "#0072B2", x: 6, y: 50 });
    players.push({ id: "D1", num: 1, role: "Défenseur 1", team: "B", color: "#D55E00", x: 34, y: 2 });
    players.push({ id: "D2", num: 2, role: "Défenseur 2", team: "B", color: "#D55E00", x: 62, y: 98 });
    markers.push({ type: "ballon", x: 9, y: 46 });
    const l1 = /22\s*m/.test(t) ? "Franchit les 22 m" : "S'élance";
    let ord = 1;
    actions.push({ from: "A1", tox: 30, toy: 48, type: "dribble", order: ord++, label: l1 });
    actions.push({ from: "D1", tox: 38, toy: 40, type: "course", order: ord++, label: "Entre depuis la touche" });
    actions.push({ from: "A1", tox: 56, toy: 52, type: "dribble", order: ord++, label: "Franchit la médiane" });
    actions.push({ from: "D2", tox: 64, toy: 56, type: "course", order: ord++, label: "Entre du côté opposé" });
    actions.push({ from: "A1", tox: 96, toy: 48, type: "tir", order: ord++ });
    return { players, markers, actions,
      steps: ["Le porteur s'élance de son en-but", "Chaque ligne franchie déclenche l'entrée d'un défenseur depuis la touche", "Conclure dans l'en-but adverse"] };
  }
  const nA = clampN(countAttackers(t) || 3, 2, 4);
  const players = [], actions = [], markers = [];
  // ATTAQUANTS : départ de LEUR zone des 9 m (camp gauche), étagés en largeur.
  const ax = 28;
  const ys = nA === 2 ? [38, 62] : nA === 3 ? [30, 50, 70] : [24, 42, 58, 76];
  const mid = Math.floor((nA - 1) / 2);
  ys.forEach((y, i) => players.push({ id: "A" + (i + 1), num: i + 1, role: i === mid ? "Demi-centre" : "Ailier", team: "A", color: "#0072B2", x: ax, y }));
  // COLONNE 1 — moitié des attaquants (gauche), le long de la touche HAUTE ;
  // porte de 2 plots posée SUR la ligne de touche (y=1 : à cheval sur la ligne),
  // colonne en attente À CÔTÉ de la porte, le long de la ligne : l'entrée en jeu
  // TRAVERSE la porte (course en deux segments qui enfile les deux plots).
  if (gates) markers.push({ type: "plot", x: 44, y: 1 }, { type: "plot", x: 50, y: 1 });
  for (let i = 0; i < perD; i++)
    players.push({ id: i ? "D1w" + i : "D1", num: i + 1, role: i ? "En attente" : "Défenseur 1", team: "B", color: "#D55E00", x: 36 - i * 6, y: 5 });
  // COLONNE 2 — moitié OPPOSÉE (droite), touche BASSE (côté opposé).
  if (gates) markers.push({ type: "plot", x: 60, y: 98 }, { type: "plot", x: 66, y: 98 });
  for (let i = 0; i < perD; i++)
    players.push({ id: i ? "D2w" + i : "D2", num: i + 1, role: i ? "En attente" : "Défenseur 2", team: "B", color: "#D55E00", x: 74 + i * 6, y: 94 });
  let ord = 1;
  const lowY = ys[nA - 1], highY = ys[0];
  // CAUSALITÉ lue du TEXTE (« du texte au schéma ») — qui déclenche la situation ?
  //  • « au départ des attaquants, le défenseur entre » -> les attaquants OUVRENT,
  //    le défenseur réagit (cas le plus courant : défaut) ;
  //  • « le défenseur entre en jeu, PUIS les attaquants partent » / « une fois le
  //    défenseur entré, l'attaque démarre » -> le DÉFENSEUR ouvre.
  const defenderFirst = /d[ée]fenseur[^.;]{0,60}?(?:entre|sort)[^.;]{0,60}?(?:puis|ensuite|avant (?:que|le d[ée]part))[^.;]{0,40}?attaqu|(?:une fois|d[èe]s que|quand|apr[èe]s que) le (?:premier |1er )?d[ée]fenseur[^.;]{0,40}?(?:entr[ée]|sorti|en jeu)[^.;]{0,50}?attaqu/.test(t);
  // DÉPART DES ATTAQUANTS : leur première course. ENTRÉE DE LA 1re VAGUE : la
  // course PASSE PAR LA PORTE (entre les deux plots) puis monte au jeu.
  const attStart = () => actions.push({ from: "A" + nA, tox: 46, toy: lowY - 6, type: "course", order: ord++, label: defenderFirst ? "Les attaquants s'élancent ensuite" : "Départ des attaquants" });
  const d1Entry = () => {
    actions.push({ from: "D1", tox: 47, toy: 2, type: "course", order: ord++ });
    actions.push({ from: "D1", tox: 52, toy: 30, type: "course", order: ord++, label: defenderFirst ? "Entre en jeu en premier, par la porte" : "Entre en jeu au départ des attaquants" });
  };
  if (defenderFirst) { d1Entry(); attStart(); } else { attStart(); d1Entry(); }
  // LOGIQUE DU DRILL : on joue À L'OPPOSÉ du défenseur entrant. Le 1er entre
  // par le HAUT -> 1re passe vers l'ailier BAS, lancé.
  actions.push({ from: "A" + (mid + 1), to: "A" + nA, type: "passe", order: ord++, label: "1re passe : à l'opposé du 1er défenseur" });
  // 5) Le porteur FRANCHIT la médiane -> déclenche la 2e vague.
  actions.push({ from: "A" + nA, tox: 58, toy: lowY - 10, type: "dribble", order: ord++, label: "Franchit la médiane" });
  // 6-7) 2e vague : entre par la porte BASSE quand le ballon est dans la 2e moitié.
  actions.push({ from: "D2", tox: 63, toy: 97, type: "course", order: ord++ });
  actions.push({ from: "D2", tox: 70, toy: 58, type: "course", order: ord++, label: "Entre en jeu : ballon dans la 2e moitié" });
  // 8-10) Le 2e défenseur vient du BAS -> 2e passe vers l'ailier HAUT, puis tir.
  actions.push({ from: "A1", tox: 68, toy: highY + 2, type: "course", order: ord++, label: "Appel dans l'intervalle" });
  actions.push({ from: "A" + nA, to: "A1", type: "passe", order: ord++, label: "2e passe : à l'opposé du 2e défenseur" });
  actions.push({ from: "A1", tox: 96, toy: 44, type: "tir", order: ord++, label: "Tir" });
  return {
    players, markers, actions,
    steps: defenderFirst ? [
      "Le 1er défenseur entre par la porte — les attaquants s'élancent ensuite",
      "1re passe à l'opposé, le porteur franchit la médiane : le 2e défenseur entre",
      "2e passe à l'opposé du 2e défenseur, conclusion au tir",
    ] : [
      "Les attaquants partent — le 1er défenseur réagit et entre par la porte",
      "1re passe à l'opposé, le porteur franchit la médiane : le 2e défenseur entre",
      "2e passe à l'opposé du 2e défenseur, conclusion au tir",
    ],
  };
}

/* ─────────── FAMILLE « TEMPORALITÉ » : déclencheurs, retards, vagues ───────────
   La chronologie (numéros d'ordre + libellés) encode la CAUSALITÉ dite dans le
   texte : « quand X, alors Y », tâche à accomplir AVANT de défendre, vague qui
   part APRÈS la frappe, poursuite avec retard… */

/* DÉFENSE DIFFÉRÉE (tâche intercalée) — « le passeur donne le ballon PUIS doit
   contourner un plot avant de pouvoir défendre » : le 2cN se joue pendant que
   le défenseur fait son détour. Sans gabarit : 2c1 générique, plot perdu. */
export function buildDelayedDefense(text, sport) {
  const t = c0(text || "");
  const basket = /^basket/.test(sport);
  const players = [], actions = [], markers = [];
  const D = { id: "D1", num: 1, role: "Passeur-défenseur", team: "B", color: "#D55E00", x: 16, y: 50 };
  const A1 = { id: "A1", num: 1, role: "Attaquant", team: "A", color: "#0072B2", x: 24, y: 36 };
  const A2 = { id: "A2", num: 2, role: "Attaquant", team: "A", color: "#0072B2", x: 24, y: 64 };
  players.push(A1, A2, D);
  markers.push({ type: "ballon", x: D.x + 3, y: D.y - 4 });
  const plotPos = { x: 50, y: 78 };                       // le plot du détour, hors de l'axe
  markers.push({ type: "plot", x: plotPos.x, y: plotPos.y });
  let ord = 1;
  actions.push({ from: "D1", to: "A1", type: "passe", order: ord++, label: "Donne le ballon" });
  // Le DÉTOUR (2 segments par le plot) : il retarde l'entrée en défense.
  actions.push({ from: "D1", tox: plotPos.x, toy: plotPos.y - 4, type: "course", order: ord++, label: "Contourne le plot…" });
  actions.push({ from: "D1", tox: 62, toy: 52, type: "course", order: ord++, label: "…avant de pouvoir défendre" });
  // Pendant ce temps, les 2 attaquants montent et concluent.
  actions.push({ from: "A1", tox: 52, toy: 38, type: "dribble", order: ord++, label: "Monte la balle" });
  actions.push({ from: "A2", tox: 62, toy: 62, type: "course", order: ord++, label: "Accompagne" });
  actions.push({ from: "A1", to: "A2", type: "passe", order: ord++ });
  actions.push({ from: "A2", tox: 96, toy: 52, type: "tir", order: ord++, label: basket ? "Conclusion au panier" : "Tir" });
  return { players, markers, actions,
    steps: ["Le passeur donne puis contourne le plot (défense différée)", "Les 2 attaquants montent pendant le détour", "Conclure avant le retour du défenseur"] };
}

/* VAGUES D'ATTAQUANTS SUCCESSIVES — « la deuxième paire s'élance dès que la
   première a frappé au but » : vague 1 attaque et frappe, vague 2 en attente
   part À LA FRAPPE (l'ordre des flèches suit le déclencheur). */
export function buildAttackWaves(text, sport) {
  const t = c0(text || "");
  const NW = { deux: 2, trois: 3 };
  const wm = t.match(/(deux|trois|2|3)\s*(?:paires?|vagues?|binômes?)/);
  const nW = clampN(wm ? (NW[wm[1]] || parseInt(wm[1], 10)) : 2, 2, 3);
  const per = /paires?|binômes?|[àa] deux|(?:vagues?|groupes?) de (?:deux|2)\b/.test(t) ? 2 : 1;
  const players = [], actions = [], markers = [];
  let ord = 1;
  for (let w = 0; w < nW; w++) {
    for (let i = 0; i < per; i++) {
      const y = per === 2 ? (i ? 62 : 38) : 50;
      players.push({ id: `W${w + 1}A${i + 1}`, num: w * per + i + 1, role: w === 0 ? "1re vague" : `${w + 1}e vague`, team: "A", color: w === 0 ? "#0072B2" : "#117733", x: 26 - w * 9, y });
    }
  }
  markers.push({ type: "ballon", x: 29, y: 34 });
  actions.push({ from: "W1A1", tox: 66, toy: 40, type: "dribble", order: ord++, label: "1re vague : monte" });
  if (per === 2) actions.push({ from: "W1A2", tox: 68, toy: 60, type: "course", order: ord++ });
  actions.push({ from: "W1A1", tox: 96, toy: 48, type: "tir", order: ord++, label: "Frappe" });
  actions.push({ from: "W2A1", tox: 60, toy: 40, type: "course", order: ord++, label: "La 2e vague s'élance DÈS LA FRAPPE" });
  if (per === 2) actions.push({ from: "W2A2", tox: 62, toy: 60, type: "course", order: ord++ });
  return { players, markers, actions,
    steps: ["La 1re vague attaque et frappe", "La frappe DÉCLENCHE le départ de la vague suivante", "Enchaîner sans temps mort"] };
}

/* COURSE DE RÉACTION DOS À DOS — deux élèves assis dos à dos au centre : au
   coup de sifflet, chacun sprinte vers SON plot (directions opposées). */
export function buildBackToBackReaction(text) {
  const t = c0(text || "");
  const dm = t.match(/[àa]\s*(\d+)\s*m/);
  const d = dm ? clampN(parseInt(dm[1], 10), 5, 30) : 10;
  const span = clampN(Math.round(d * 3.2), 20, 42);       // ~10 m -> 32 unités
  const players = [
    { id: "E1", num: 1, role: "Élève 1", team: "A", color: "#0072B2", x: 47, y: 50 },
    { id: "E2", num: 2, role: "Élève 2", team: "B", color: "#D55E00", x: 53, y: 50 },
  ];
  const markers = [
    { type: "plot", x: 47 - span, y: 50 },
    { type: "plot", x: 53 + span, y: 50 },
  ];
  const actions = [
    { from: "E1", tox: 47 - span + 3, toy: 50, type: "course", order: 1, label: "Au sifflet : sprint vers son plot" },
    { from: "E2", tox: 53 + span - 3, toy: 50, type: "course", order: 2 },
  ];
  return { players, markers, actions,
    steps: ["Assis dos à dos au centre", "Au coup de sifflet : réaction et sprint opposés", "Premier au plot vainqueur"] };
}

/* BÉRET-BALLON — deux équipes alignées face à face, ballon au centre :
   l'enseignant annonce un numéro, les deux joueurs concernés sprintent vers le
   ballon, le premier arrivé attaque le petit but adverse. */
export function buildBeretBallon(text) {
  const t = c0(text || "");
  const NW = { trois: 3, quatre: 4, cinq: 5, six: 6 };
  const nm = t.match(/[ée]quipes? de (\d|trois|quatre|cinq|six)/);
  const per = clampN(nm ? (NW[nm[1]] || parseInt(nm[1], 10)) : 4, 3, 6);
  const players = [], actions = [], markers = [];
  for (let i = 0; i < per; i++) {
    const y = Math.round(20 + (i * 60) / (per - 1));
    players.push({ id: "A" + (i + 1), num: i + 1, role: i === 1 ? "Appelé" : "Équipe A", team: "A", color: "#0072B2", x: 22, y });
    players.push({ id: "B" + (i + 1), num: i + 1, role: i === 1 ? "Appelé" : "Équipe B", team: "B", color: "#D55E00", x: 78, y });
  }
  markers.push({ type: "ballon", x: 50, y: 50 });
  // Petits buts matérialisés derrière chaque équipe (2 plots).
  markers.push({ type: "plot", x: 8, y: 44 }, { type: "plot", x: 8, y: 56 });
  markers.push({ type: "plot", x: 92, y: 44 }, { type: "plot", x: 92, y: 56 });
  actions.push({ from: "A2", tox: 47, toy: 50, type: "course", order: 1, label: "Au numéro annoncé : sprint au ballon" });
  actions.push({ from: "B2", tox: 53, toy: 50, type: "course", order: 2 });
  actions.push({ fromx: 50, fromy: 50, tox: 88, toy: 50, type: "dribble", order: 3, label: "Le premier arrivé attaque le petit but adverse" });
  return { players, markers, actions,
    steps: ["Deux équipes numérotées face à face, ballon au centre", "Au numéro annoncé, les deux joueurs sprintent au ballon", "Le premier arrivé attaque le petit but adverse"] };
}

/* CONTRE-ATTAQUE SUR RELANCE DU GARDIEN — « dès que le gardien a le ballon en
   main, l'ailier s'élance et reçoit la relance longue » : le déclencheur est la
   POSSESSION du gardien (ballon sur lui), la course précède la relance. */
export function buildKeeperLaunchCounter(text, sport) {
  const t = c0(text || "");
  const players = [], actions = [], markers = [];
  players.push({ id: "G", role: "Gardien", team: "k", color: "#F2C200", x: 6, y: 50 });
  players.push({ id: "A1", num: 1, role: "Ailier", team: "A", color: "#0072B2", x: 22, y: 12 });
  players.push({ id: "G2", role: "Gardien adverse", team: "k", color: "#F2C200", x: 94, y: 50 });
  markers.push({ type: "ballon", x: 9, y: 46 });          // le ballon EST au gardien (déclencheur)
  actions.push({ from: "A1", tox: 66, toy: 10, type: "course", order: 1, label: "Dès que le gardien a le ballon : s'élance" });
  actions.push({ from: "G", tox: 66, toy: 10, type: "passe", order: 2, label: "Relance longue" });
  actions.push({ from: "A1", tox: 80, toy: 20, type: "dribble", order: 3 });
  actions.push({ from: "A1", tox: 96, toy: 46, type: "tir", order: 4, label: "Tir" });
  return { players, markers, actions,
    steps: ["Le gardien capte le ballon : c'est le signal", "L'ailier s'élance le long de la touche", "Relance longue dans la course, conclusion au tir"] };
}

/* DUEL DE POURSUITE À DÉPARTS NOMMÉS — « l'attaquant part de la ligne médiane ;
   le défenseur part du poteau de corner et revient en poursuite » : chaque
   départ est placé À SON repère, la poursuite vise l'interception. */
export function buildPursuitDuel(text, sport) {
  const t = c0(text || "");
  const players = [], actions = [], markers = [];
  // Départ de l'ATTAQUANT : ligne médiane (défaut), ou repère coté.
  const ax = /m[ée]diane|mi.?terrain|rond central/.test(t) ? 47 : 30;
  players.push({ id: "A1", num: 1, role: "Attaquant", team: "A", color: "#0072B2", x: ax, y: 50 });
  // PARTENAIRE DÉCRIT (« deux attaquants », « son partenaire se démarque ») :
  // la poursuite se joue à DEUX — le porteur conduit, le partenaire accompagne
  // en largeur et reçoit pour conclure avant le retour du défenseur. (Avant :
  // le partenaire était purement perdu — défaut relevé par le banc de fidélité.)
  const duo = /deux attaquants|2 attaquants|son partenaire|le partenaire|[àa] deux\b/.test(t);
  if (duo) players.push({ id: "A2", num: 2, role: "Partenaire", team: "A", color: "#0072B2", x: ax + 2, y: 72 });
  // Départ du DÉFENSEUR : poteau de corner (coin du terrain ATTAQUÉ), ou derrière.
  const corner = /poteau de corner|drapeau|corner|coin (?:du terrain|oppos[ée])/.test(t);
  const D = corner ? { x: 96, y: 92 } : { x: ax - 8, y: 50 };
  players.push({ id: "D1", num: 1, role: "Défenseur", team: "B", color: "#D55E00", x: D.x, y: D.y });
  markers.push({ type: "ballon", x: ax + 3, y: 46 });
  actions.push({ from: "A1", tox: duo ? 68 : 78, toy: 48, type: "dribble", order: 1, label: "Part au signal, conduite vers le but" });
  if (duo) actions.push({ from: "A2", tox: 76, toy: 70, type: "course", order: 2, label: "Se démarque en largeur" });
  actions.push({ from: "D1", tox: 82, toy: 56, type: "course", order: duo ? 3 : 2, label: corner ? "Part du corner, revient en poursuite" : "Part en poursuite" });
  if (duo) {
    actions.push({ from: "A1", to: "A2", type: "passe", order: 4, label: "Échange avant le retour" });
    actions.push({ from: "A2", tox: 96, toy: 60, type: "tir", order: 5, label: "Conclut avant d'être rattrapé" });
  } else {
    actions.push({ from: "A1", tox: 96, toy: 48, type: "tir", order: 3, label: "Frappe avant d'être rattrapé" });
  }
  return { players, markers, actions,
    steps: duo
      ? ["Départs simultanés depuis leurs repères", "Le partenaire accompagne en largeur", "Échanger si nécessaire et conclure avant le retour du défenseur"]
      : ["Départs simultanés depuis leurs repères", "Le défenseur revient en poursuite", "Frapper avant d'être rattrapé"] };
}

/* 1 CONTRE 1 EN ZONES SUCCESSIVES — « zone 1 : éliminer le défenseur ; s'il
   franchit la ligne, il enchaîne en zone 2 contre un défenseur frais, puis
   frappe » : deux duels CHAÎNÉS, le franchissement déclenche le second. */
export function buildChained1v1Zones(text, sport) {
  const players = [], actions = [], markers = [], zones = [];
  zones.push({ x: 2, y: 6, w: 47, h: 88, label: "Zone 1", style: "dashed" });
  zones.push({ x: 51, y: 6, w: 47, h: 88, label: "Zone 2", style: "dashed" });
  players.push({ id: "A1", num: 1, role: "Attaquant", team: "A", color: "#0072B2", x: 10, y: 50 });
  players.push({ id: "D1", num: 1, role: "Défenseur 1", team: "B", color: "#D55E00", x: 30, y: 50 });
  players.push({ id: "D2", num: 2, role: "Défenseur 2", team: "B", color: "#D55E00", x: 68, y: 50 });
  markers.push({ type: "ballon", x: 13, y: 46 });
  actions.push({ from: "A1", tox: 40, toy: 42, type: "dribble", order: 1, label: "1c1 dans la zone 1" });
  actions.push({ from: "A1", tox: 56, toy: 46, type: "dribble", order: 2, label: "Franchit la ligne : zone 2" });
  actions.push({ from: "D2", tox: 62, toy: 48, type: "course", order: 3, label: "Le défenseur frais sort" });
  actions.push({ from: "A1", tox: 78, toy: 42, type: "dribble", order: 4, label: "2e duel" });
  actions.push({ from: "A1", tox: 96, toy: 48, type: "tir", order: 5, label: "Frappe" });
  return { players, markers, actions, zones,
    steps: ["Éliminer le 1er défenseur dans la zone 1", "Le franchissement déclenche le duel de la zone 2", "Conclure au tir"] };
}

/* TRANSITION APRÈS LANCER FRANC — « après son lancer franc, le tireur sprinte
   en repli sur la contre-attaque adverse lancée par le rebondeur » : le tir
   DÉCLENCHE la transition (rebond adverse, relance, repli en sprint). */
export function buildFreeThrowTransition(text) {
  const players = [], actions = [], markers = [];
  players.push({ id: "T1", num: 1, role: "Tireur", team: "A", color: "#0072B2", x: 76, y: 50 });
  players.push({ id: "R1", num: 1, role: "Rebondeur", team: "B", color: "#D55E00", x: 92, y: 42 });
  players.push({ id: "C1", num: 2, role: "Contre-attaquant", team: "B", color: "#D55E00", x: 62, y: 24 });
  markers.push({ type: "ballon", x: 79, y: 46 });
  actions.push({ from: "T1", tox: 96, toy: 50, type: "tir", order: 1, label: "Lancer franc" });
  markers.push({ type: "rebond", x: 94, y: 54 });
  actions.push({ from: "R1", to: "C1", type: "passe", order: 2, label: "Rebond : relance la contre-attaque" });
  actions.push({ from: "C1", tox: 16, toy: 40, type: "dribble", order: 3, label: "Contre-attaque vers le panier opposé" });
  actions.push({ from: "T1", tox: 22, toy: 54, type: "course", order: 4, label: "Après son tir : sprint en repli défensif" });
  return { players, markers, actions,
    steps: ["Lancer franc du tireur", "Le rebond adverse déclenche la contre-attaque", "Le tireur sprinte en repli vers le panier opposé"] };
}

/* TROIS COLONNES / TROIS RÔLES (passeur -> tireur -> rebondeur) — le passeur
   sert le tireur, le rebondeur capte et REDONNE au passeur suivant ; chacun
   avance d'un rôle (P->T, T->R, R->P : trois arcs de rotation). Sans gabarit :
   trois boîtes d'atelier déconnectées. */
export function buildPassShootReboundTrio(text, sport) {
  const t = c0(text || "");
  const per = columnSize(t);
  const basket = /^basket/.test(sport);
  // 3e rôle adapté au sport : REBONDEUR au panier (basket/hand), RÉCUPÉRATEUR
  // au sol (foot/futsal — « récupérateurs derrière le but »).
  const rebounds = basket || sport === "handball";
  const thirdRole = rebounds ? "Rebondeur" : "Récupérateur";
  const players = [], actions = [], markers = [];
  const P = { id: "P1", num: 1, role: "Passeur", team: "A", color: "#0072B2", x: 48, y: 26 };
  const S = { id: "S1", num: 1, role: "Tireur", team: "B", color: "#D55E00", x: 62, y: 50 };
  const R = { id: "R1", num: 1, role: thirdRole, team: "C", color: "#117733", x: 48, y: 74 };
  players.push(P, S, R);
  markers.push({ type: "ballon", x: P.x + 3, y: P.y - 4 });
  let ord = 1;
  actions.push({ from: "P1", to: "S1", type: "passe", order: ord++, label: "Passe au tireur" });
  actions.push({ from: "S1", tox: 96, toy: 50, type: "tir", order: ord++, label: basket ? "Tir au panier" : "Tir au but" });
  actions.push({ from: "R1", tox: 86, toy: 60, type: "course", order: ord++, label: rebounds ? "Va au rebond" : "Récupère le ballon" });
  if (rebounds) markers.push({ type: "rebond", x: 89, y: 56 });
  actions.push({ fromx: 86, fromy: 60, tox: P.x - 8, toy: P.y, type: "passe", order: ord++, label: "Redonne au passeur suivant" });
  pushWaiters(players, P, -8, 0, per);
  pushWaiters(players, S, -8, 0, per);
  pushWaiters(players, R, -8, 0, per);
  actions.push({ from: "P1", tox: S.x - 8 * (per - 1) - 3, toy: S.y, type: "rotation", order: ord++, bulge: 4, label: "Le passeur devient tireur" });
  actions.push({ from: "S1", tox: R.x - 8 * (per - 1) - 3, toy: R.y, type: "rotation", order: ord++, bulge: 4, label: rebounds ? "Le tireur va au rebond" : "Le tireur devient récupérateur" });
  actions.push({ from: "R1", tox: P.x - 8 * (per - 1) - 3, toy: P.y, type: "rotation", order: ord++, bulge: 8, label: `Le ${rebounds ? "rebondeur" : "récupérateur"} rejoint les passeurs` });
  return {
    players, markers, actions,
    steps: ["Le passeur sert le tireur qui tire", `Le ${rebounds ? "rebondeur capte le rebond" : "récupérateur récupère le ballon"} et redonne au passeur suivant`, `Rotation des rôles : passeur → tireur → ${rebounds ? "rebondeur" : "récupérateur"} → passeur`],
  };
}

/* VOLLEY — COLONNES RÉCEPTION/PASSE DANS LE MÊME CAMP : les réceptionneurs au
   fond, les passeurs au filet ; manchette vers le passeur, passe haute en
   retour, chacun change de colonne. Sans gabarit : boîtes d'atelier. */
export function buildVolleySetterColumns(text) {
  const t = c0(text || "");
  const per = columnSize(t);
  const players = [], actions = [], markers = [];
  const R = { id: "R1", num: 1, role: "Réceptionneur", team: "A", color: "#0072B2", x: 14, y: 58 };
  const P = { id: "P1", num: 1, role: "Passeur", team: "B", color: "#D55E00", x: 40, y: 44 };
  players.push(R, P);
  markers.push({ type: "ballon", x: R.x + 3, y: R.y - 4 });
  actions.push({ from: "R1", to: "P1", type: "passe", order: 1, label: "Manchette" });
  actions.push({ from: "P1", tox: 10, toy: 64, type: "passe", order: 2, label: "Passe haute vers le suivant" });
  pushWaiters(players, R, -5, 6, per);
  pushWaiters(players, P, 0, -10, per);           // file de passeurs le long du filet
  actions.push({ from: "R1", tox: 40, toy: 44 - 10 * (per - 1) - 4, type: "rotation", order: 3, bulge: 4, label: "Devient passeur" });
  actions.push({ from: "P1", tox: 14 - 5 * (per - 1) - 3, toy: 58 + 6 * (per - 1) + 3, type: "rotation", order: 4, bulge: 4, label: "Rejoint les réceptionneurs" });
  return { players, markers, actions, steps: ["Manchette du fond vers le passeur au filet", "Passe haute en retour vers le suivant", "Chacun change de colonne après son passage"] };
}

/* ULTIMATE — COLONNES LANCEUR/RECEVEUR : le receveur court en PROFONDEUR, le
   lanceur (immobile : pas de marcher) lance le disque dans la course, puis les
   rôles s'échangent. Sans gabarit : « conduite + tir au but » (double contresens). */
export function buildDeepThrowColumns(text) {
  const t = c0(text || "");
  const per = columnSize(t);
  const players = [], actions = [], markers = [];
  const L = { id: "L1", num: 1, role: "Lanceur", team: "A", color: "#0072B2", x: 24, y: 40 };
  const R = { id: "R1", num: 1, role: "Receveur", team: "B", color: "#D55E00", x: 24, y: 60 };
  players.push(L, R);
  markers.push({ type: "disque", x: L.x + 3, y: L.y - 4 });
  actions.push({ from: "R1", tox: 62, toy: 54, type: "course", order: 1, label: "Course en profondeur" });
  actions.push({ from: "L1", tox: 62, toy: 54, type: "passe", order: 2, label: "Lance dans la course" });
  pushWaiters(players, L, -7, 0, per);
  pushWaiters(players, R, -7, 0, per);
  actions.push({ from: "L1", tox: 24 - 7 * (per - 1) - 3, toy: 60, type: "rotation", order: 3, bulge: 4, label: "Devient receveur" });
  actions.push({ from: "R1", tox: 24 - 7 * (per - 1) - 3, toy: 40, type: "rotation", order: 4, bulge: 4, label: "Revient lancer" });
  return { players, markers, actions, steps: ["Le receveur part en profondeur", "Le lanceur lance le disque dans la course", "Les rôles s'échangent à chaque passage"] };
}

/* GARDIEN TOURNANT — colonne de tireurs face au but : après son tir, le tireur
   REMPLACE le gardien, le gardien rejoint la colonne. Sans gabarit : 1 tireur
   isolé dans une boîte d'atelier, gardien absent. */
export function buildShooterKeeperRotation(text) {
  const t = c0(text || "");
  const per = columnSize(t);
  const players = [], actions = [], markers = [];
  const T = { id: "T1", num: 1, role: "Tireur", team: "A", color: "#0072B2", x: 62, y: 50 };
  players.push(T);
  players.push({ id: "G", role: "Gardien", team: "k", color: "#F2C200", x: 94, y: 50 });
  markers.push({ type: "ballon", x: T.x + 3, y: T.y - 4 });
  actions.push({ from: "T1", tox: 95, toy: 50, type: "tir", order: 1, label: "Tir au but" });
  pushWaiters(players, T, -8, 0, per);
  actions.push({ from: "T1", tox: 92, toy: 58, type: "rotation", order: 2, bulge: 4, label: "Remplace le gardien" });
  actions.push({ from: "G", tox: 62 - 8 * (per - 1) - 3, toy: 50, type: "rotation", order: 3, bulge: 6, label: "Rejoint la colonne des tireurs" });
  return { players, markers, actions, steps: ["Tir au but face au gardien", "Le tireur remplace le gardien", "Le gardien rejoint la colonne des tireurs"] };
}

/* LA TOURNANTE (volley, jeu scolaire) — une colonne derrière chaque ligne de
   fond : le premier frappe la balle par-dessus le filet puis court autour du
   terrain rejoindre la colonne opposée ; le premier d'en face enchaîne. */
export function buildVolleyTournante() {
  const players = [], actions = [], markers = [];
  for (let i = 0; i < 3; i++) {
    players.push({ id: "A" + (i + 1), num: i + 1, role: i === 0 ? "Frappeur" : "Colonne A", team: "A", color: "#0072B2",
      x: Math.max(3, 10 - i * 3), y: 52 + i * 9 });
    players.push({ id: "B" + (i + 1), num: i + 1, role: i === 0 ? "Frappeur" : "Colonne B", team: "B", color: "#D55E00",
      x: Math.min(97, 90 + i * 3), y: 48 - i * 9 });
  }
  markers.push({ type: "ballon", x: 14, y: 52 });
  actions.push({ from: "A1", tox: 70, toy: 48, type: "renvoi", order: 1, label: "Frappe par-dessus le filet" });
  // Course AUTOUR du terrain (par l'extérieur, en deux segments le long du bord).
  actions.push({ from: "A1", tox: 50, toy: 8, type: "course", style: "dashed", order: 2, label: "Court autour du terrain" });
  actions.push({ fromx: 50, fromy: 8, tox: 90, toy: 30, type: "course", style: "dashed", order: 3, label: "Rejoint la colonne opposée" });
  actions.push({ from: "B1", tox: 30, toy: 52, type: "renvoi", order: 4, label: "Le premier d'en face enchaîne" });
  return {
    players, markers, actions,
    steps: ["Frapper la balle par-dessus le filet", "Courir autour du terrain rejoindre la colonne opposée", "Éliminé à la faute — le premier d'en face enchaîne"],
  };
}

/* ÉCHANGE PAR BINÔMES (échauffement de lancers/passes FACE À FACE, tous
   sports) : deux partenaires à distance cotée se renvoient l'engin en
   alternant les gestes (revers / coup droit…). Ni une cible à faire tomber,
   ni un relais — deux binômes empilés pour figurer le travail de groupe. */
export function buildPairExchange(text, sport) {
  const t = c0(text || "");
  const ballType = sport === "ultimate" ? "disque" : "ballon";
  const dm = t.match(/[àa]\s*(\d{1,2})\s*m(?:[èe]tres?)?\b/);
  const rm = t.match(/(\d{1,2})\s*(?:r[ée]ussites?|répétitions?|repetitions?|fois|lancers?)/);
  const reps = rm ? ` (×${rm[1]})` : "";
  // Libellés des deux gestes alternés, lus dans le texte (défaut : lancer).
  const g1 = /revers/.test(t) ? "Lancer revers" : "Lancer";
  const g2 = /coup droit/.test(t) ? "Lancer coup droit" : "Retour";
  const players = [], actions = [], markers = [], zones = [];
  [[30], [70]].forEach(([y], k) => {
    const a = { id: "A" + (k + 1), num: k + 1, role: "Lanceur", team: "A", color: "#0072B2", x: 26, y };
    const b = { id: "B" + (k + 1), num: k + 1, role: "Partenaire", team: "B", color: "#D55E00", x: 74, y };
    players.push(a, b);
    markers.push({ type: ballType, x: 30, y });
    actions.push({ from: a.id, to: b.id, type: "renvoi", order: k * 2 + 1, label: k === 0 ? g1 + reps : null });
    actions.push({ from: b.id, to: a.id, type: "renvoi", order: k * 2 + 2, label: k === 0 ? g2 + reps : null });
  });
  if (dm) zones.push({ x: 26, y: 22, w: 48, h: 0, style: "dashed", dim: `${dm[1]} m` });
  return {
    players, markers, actions, zones,
    steps: [g1 + " vers le partenaire", g2 + " au retour", "Compter les réussites de chaque geste"],
  };
}

/* TOUCHE (rugby) : remise en jeu — lanceur sur la ligne de touche (bord haut),
   alignement de sauteurs perpendiculaire à la touche, lancer au sauteur désigné
   (soulevé par ses soutiens), redescente et transmission au demi de mêlée. */
export function buildRugbyLineout(text) {
  const t = c0(text || "");
  const NUMW = { deux: 2, trois: 3, quatre: 4, cinq: 5 };
  const cm = t.match(/(\d+|deux|trois|quatre|cinq)\s+sauteurs\b/);
  const nS = clampN(cm ? (NUMW[cm[1]] || parseInt(cm[1], 10)) : 3, 2, 5);
  const tm = t.match(/(\d+)\s*(?:e|ème|eme|er)\s+sauteur/);
  const target = clampN(tm ? parseInt(tm[1], 10) : 2, 1, nS);
  const LX = 30, y0 = 22, step = Math.min(14, 56 / nS);
  const players = [{ id: "L1", num: 9, role: "Lanceur", team: "A", color: "#0072B2", x: LX, y: 8 }];
  for (let i = 1; i <= nS; i++) {
    const y = Math.round(y0 + (i - 1) * step);
    players.push({ id: "S" + i, num: i, role: i === target ? "Sauteur soulevé" : "Sauteur", team: "A", color: "#0072B2", x: LX, y });
  }
  const yT = y0 + (target - 1) * step;
  // Soutiens qui soulèvent le sauteur cible, de part et d'autre.
  players.push({ id: "P1", role: "Soutien", team: "A", color: "#0072B2", x: LX - 6, y: Math.round(yT + step / 2) });
  players.push({ id: "P2", role: "Soutien", team: "A", color: "#0072B2", x: LX + 6, y: Math.round(yT + step / 2) });
  // Demi de mêlée EN RETRAIT de l'alignement (côté camp) : la transmission
  // descend vers l'arrière — jamais d'en-avant sur la sortie de touche.
  players.push({ id: "M1", role: "Demi de mêlée", team: "A", color: "#0072B2", x: LX - 14, y: Math.round(yT + 16) });
  const actions = [
    { from: "L1", to: "S" + target, type: "passe", order: 1, label: "Lancer en touche" },
    { from: "S" + target, to: "M1", type: "passe", order: 2, label: "Transmission au demi" },
  ];
  const markers = [{ type: "ballon", x: LX + 4, y: 10 }];
  const steps = ["Lancer du talonneur le long de l'alignement", `Prise de balle du ${target}e sauteur soulevé par ses soutiens`, "Redescente et transmission au demi de mêlée"];
  // CONTINUATION DU LANCEMENT — « le demi de mêlée fait une longue passe au
  // premier centre qui court s'empaler sur la défense au milieu du terrain » :
  // la sortie de touche ne s'arrête plus au demi. Le centre est EN RETRAIT du
  // demi (passe vers l'arrière), sa percussion (portage, tracé ondulé) monte
  // vers le milieu du terrain, face à la défense décrite.
  if (/(?:longue\s+)?passe\s+au\s+(?:premier\s+|1er\s+)?centre/.test(t)) {
    const cy = Math.round(clampN(yT + 38, 40, 88));
    players.push({ id: "C1", role: "Premier centre", team: "A", color: "#0072B2", x: 10, y: cy });
    actions.push({ from: "M1", to: "C1", type: "passe", order: 3, label: "Longue passe au premier centre" });
    steps.push("Longue passe du demi au premier centre");
    if (/s['’]empal|percu(?:ssion|te)|\bfixe\b|fixation|dans la d[ée]fense|au contact|perfore/.test(t)) {
      if (/d[ée]fense|d[ée]fenseurs/.test(t)) {
        [-14, 0, 14].forEach((dy, i) =>
          players.push({ id: "D" + (i + 1), num: i + 1, role: "Défenseur " + (i + 1), team: "B", color: "#D55E00",
            x: 56, y: Math.round(clampN(cy - 6 + dy, 12, 88)) }));
      }
      actions.push({ from: "C1", tox: 48, toy: Math.round(clampN(cy - 6, 12, 88)), type: "dribble", order: 4, label: "Percussion au milieu du terrain" });
      steps.push("Percussion du centre au milieu du terrain (point de fixation)");
    }
  }
  return { players, markers, actions, steps };
}

/* DOUBLE TENNIS DE TABLE : l'ALTERNANCE des frappeurs est obligatoire
   (règlement) — séquence A -> C -> B -> D sur un cycle complet, chaque frappeur
   PIVOTE après sa frappe pour laisser la place à son partenaire (échange de
   positions dessiné). Les noms de coups viennent de l'analyse IA (service,
   remise, top spin…), complétés par « Échange ». */
export function buildTTDouble(text, shots) {
  const B = "#0072B2", R = "#D55E00";
  const players = [
    { id: "A", num: 1, role: "Serveur", team: "A", color: B, x: 8, y: 38 },
    { id: "B", num: 2, role: "Partenaire", team: "A", color: B, x: 3, y: 62 },
    { id: "C", num: 1, role: "Relanceur", team: "B", color: R, x: 92, y: 62 },
    { id: "D", num: 2, role: "Partenaire", team: "B", color: R, x: 97, y: 38 },
  ];
  const markers = [{ type: "ballon", x: 12, y: 38 }];
  const actions = [];
  const seq = ["A", "C", "B", "D"];                       // alternance réglementaire
  const targets = [[62, 66], [34, 38], [66, 62], [30, 64]];   // points de chute (diagonales)
  const given = (shots || []).filter(Boolean);
  for (let i = 0; i < 4; i++) {
    const s = given[i];
    const name = s ? s.name + (s.seg ? ` → ${s.seg}` : "") : "Échange";
    const type = s && s.type === "tir" ? "tir" : "renvoi";
    const [tx, ty] = targets[i];
    actions.push({ from: seq[i], tox: tx, toy: ty, type, order: i + 1, label: name });
    markers.push({ type: "rebond", x: tx, y: ty });
    // PIVOT après la frappe : le frappeur laisse la place, les partenaires
    // échangent leurs positions (dessiné pour la première paire de chaque camp).
    if (i === 0) {
      actions.push({ from: "A", tox: 4, toy: 64, type: "course", style: "dashed", order: 1.4, label: "Pivote, laisse la place" });
      actions.push({ from: "B", tox: 9, toy: 37, type: "course", style: "dashed", order: 1.6 });
    } else if (i === 1) {
      actions.push({ from: "C", tox: 97, toy: 37, type: "course", style: "dashed", order: 2.4 });
      actions.push({ from: "D", tox: 92, toy: 63, type: "course", style: "dashed", order: 2.6 });
    }
  }
  return {
    players, markers, actions,
    steps: ["Service (joueur 1) puis pivot : le partenaire prend la place", "Remise adverse et pivot de la paire adverse",
      "Chaque joueur frappe CHACUN SON TOUR (alternance obligatoire)"],
  };
}

/* LE KUNG-FU (handball) : le passeur aux 9 m envoie une passe EN CLOCHE
   au-dessus de la zone ; le partenaire s'élance, attrape le ballon EN
   SUSPENSION dans l'espace aérien de la zone et marque avant de retomber. */
export function buildHandKungfu() {
  const B = "#0072B2", R = "#D55E00";
  const players = [
    { id: "A1", num: 1, role: "Passeur", team: "A", color: B, x: 70, y: 32 },
    { id: "A2", num: 2, role: "Réceptionneur", team: "A", color: B, x: 68, y: 68 },
    { id: "D1", num: 1, role: "Défenseur", team: "B", color: R, x: 76, y: 50 },
    { id: "G", role: "Gardien", team: "k", color: "#F2C200", x: 94, y: 50 },
  ];
  const markers = [{ type: "ballon", x: 73, y: 32 }];
  const actions = [
    { from: "A1", tox: 84, toy: 60, type: "passe", order: 1, label: "Passe en cloche au-dessus de la zone" },
    { from: "A2", tox: 84, toy: 60, type: "course", order: 2, label: "S'élance dans l'espace aérien" },
    { from: "A2", tox: 96, toy: 50, type: "tir", order: 3, label: "Marque en suspension avant de retomber" },
  ];
  return {
    players, markers, actions,
    steps: ["Passe en cloche au-dessus de la zone des 6 m", "Le partenaire s'élance et attrape en suspension", "Tir en l'air, avant de retomber dans la zone"],
  };
}

/* TIR RÉGLEMENTAIRE ARRÊTÉ (penalty football/futsal, jet de 7 m handball,
   lancer franc basket) : le tireur au POINT réglementaire face au but/panier,
   gardien sur sa ligne (sauf basket), file de tireurs en attente si « N tireurs
   se succèdent », rebondeurs alternés le long de la raquette au basket, autres
   joueurs derrière les 9 m au handball. Sans ce gabarit, ces situations
   tombaient sur un atelier de passes en couloirs (contresens total). */
export function buildPenaltyShot(text, sport) {
  const t = c0(text || "");
  const isBasket = /^basket/.test(sport);
  const SPOT = { football: 88, futsal: 88, handball: 86, basketball: 80, basket3x3: 80 };
  const spot = SPOT[sport] || 88;
  const label = sport === "handball" ? "Jet de 7 mètres" : isBasket ? "Lancer franc" : "Penalty";
  const players = [{ id: "T1", num: 1, role: "Tireur", team: "A", color: "#0072B2", x: spot - 4, y: 50 }];
  const markers = [{ type: "ballon", x: spot, y: 50 }];
  const actions = [{ from: "T1", tox: 96, toy: 50, type: "tir", order: 1, label }];
  if (!isBasket) players.push({ id: "G", role: "Gardien", team: "k", color: "#F2C200", x: 94, y: 50 });
  // « N tireurs se succèdent » : les suivants ATTENDENT en colonne derrière le point.
  const nm = t.match(/(\d+|deux|trois|quatre|cinq|six)\s*tireurs?/);
  const n = clampN(nm ? frenchNumWord(nm[1]) : 1, 1, 6);
  for (let i = 1; i < n; i++)
    players.push({ id: "T" + (i + 1), num: i + 1, role: "En attente", team: "A", color: "#0072B2",
      x: spot - 16, y: Math.round(clampN(50 + (i % 2 ? -1 : 1) * Math.ceil(i / 2) * 9, 12, 88)) });
  // BASKET : rebondeurs ALTERNÉS le long de la raquette (défenseur au plus près
  // du cercle, comme le règlement l'impose).
  if (isBasket && /rebond|raquette/.test(t)) {
    const nd = clampN(parseInt((t.match(/(\d+)\s*d[ée]fenseurs?/) || [])[1], 10) || 3, 1, 4);
    const na = clampN(parseInt((t.match(/(\d+)\s*attaquants?/) || [])[1], 10) || 2, 0, 3);
    const slots = [[92, 38], [92, 62], [87, 36], [87, 64], [83, 34], [83, 66], [80, 38]];
    let iD = 0, iA = 0;
    for (let k = 0; k < nd + na && k < slots.length; k++) {
      const useD = (k % 2 === 0 && iD < nd) || iA >= na;
      const [x, y] = slots[k];
      players.push({ id: "R" + (k + 1), num: k + 2, role: "Rebondeur", team: useD ? "B" : "A", color: useD ? "#D55E00" : "#0072B2", x, y });
      if (useD) iD++; else iA++;
    }
  }
  // HANDBALL : les autres joueurs attendent DERRIÈRE la ligne de jet franc (9 m).
  if (sport === "handball" && /autres joueurs|9\s*m|neuf m[èe]tres/.test(t)) {
    [[68, 22], [66, 38], [66, 62], [68, 78]].forEach(([x, y], i) =>
      players.push({ id: "J" + (i + 1), role: "Derrière les 9 m", team: i < 2 ? "A" : "B", color: i < 2 ? "#0072B2" : "#D55E00", x, y }));
  }
  return {
    players, markers, actions,
    steps: [`${label} : le tireur se présente au point face ${isBasket ? "au panier" : "au gardien"}`,
      ...(n > 1 ? ["Les tireurs suivants attendent leur tour en retrait"] : [])],
  };
}

/* TOUCHE (football) : remise en jeu à deux mains depuis la LIGNE DE TOUCHE
   (bord bas du terrain), pas depuis le rond central. Deux organisations :
   touche COURTE (remise en une touche dans la course du lanceur qui rentre) et
   touche LONGUE (lancer dans la surface, prolongation de la tête, conclusion). */
export function buildFootballThrowIn(text) {
  const t = c0(text || "");
  const long = /touche longue|dans la surface|au (?:premier|second|deuxi[èe]me) poteau/.test(t);
  const B = "#0072B2", R = "#D55E00";
  const players = [], actions = [], markers = [];
  const nd = clampN(parseInt((t.match(/(\d+)\s*d[ée]fenseurs?/) || [])[1], 10) || (long ? 2 : (/d[ée]fenseur|adversaire/.test(t) ? 1 : 0)), 0, 4);
  if (long) {
    players.push({ id: "L1", num: 1, role: "Lanceur", team: "A", color: B, x: 78, y: 96 });
    players.push({ id: "A2", num: 2, role: "Prolongeur", team: "A", color: B, x: 83, y: 66 });
    players.push({ id: "A3", num: 3, role: "Finisseur", team: "A", color: B, x: 82, y: 36 });
    markers.push({ type: "ballon", x: 78, y: 92 });
    actions.push({ from: "L1", to: "A2", type: "passe", order: 1, label: "Touche longue à deux mains" });
    actions.push({ from: "A2", tox: 88, toy: 40, type: "passe", order: 2, label: "Prolonge de la tête" });
    actions.push({ from: "A3", tox: 88, toy: 40, type: "course", order: 3, label: "Appel au second poteau" });
    actions.push({ from: "A3", tox: 96, toy: 50, type: "tir", order: 4, label: "Conclut" });
    [[87, 54], [82, 46], [86, 62], [80, 58]].slice(0, nd).forEach(([x, y], i) =>
      players.push({ id: "D" + (i + 1), num: i + 1, role: "Défenseur " + (i + 1), team: "B", color: R, x, y }));
    return { players, markers, actions, steps: ["Touche longue lancée dans la surface", "Prolongation de la tête au second poteau", "Conclusion du partenaire"] };
  }
  players.push({ id: "L1", num: 1, role: "Lanceur", team: "A", color: B, x: 58, y: 96 });
  players.push({ id: "A2", num: 2, role: "Appui", team: "A", color: B, x: 64, y: 80 });
  markers.push({ type: "ballon", x: 58, y: 92 });
  actions.push({ from: "L1", to: "A2", type: "passe", order: 1, label: "Rentrée de touche à deux mains" });
  actions.push({ from: "L1", tox: 72, toy: 86, type: "course", order: 2, label: "Rentre et enchaîne dans le couloir" });
  actions.push({ from: "A2", tox: 72, toy: 86, type: "passe", order: 3, label: "Remise dans la course" });
  if (nd) players.push({ id: "D1", num: 1, role: "Défenseur", team: "B", color: R, x: 69, y: 76 });
  return { players, markers, actions, steps: ["Remise en jeu à deux mains depuis la ligne de touche", "Remise en une touche du partenaire démarqué", "Le lanceur rentre et reçoit dans le couloir"] };
}

/* REMISE EN JEU (basket) : le passeur remet HORS des limites (ligne de fond sous
   le panier attaqué, ou ligne de touche), un partenaire se démarque — avec écran
   éventuel — pour recevoir. Sans ce gabarit, « remise en jeu … écran » était
   avalé par le pick-and-roll (le passeur disparaissait). */
export function buildBasketInbound(text) {
  const t = c0(text || "");
  const fond = /ligne de fond|de fond\b|baseline|sous (?:le|son|notre) panier/.test(t);
  const ecran = /[ée]cran/.test(t);
  const three = /3 points|trois points|corner/.test(t);
  const B = "#0072B2", R = "#D55E00";
  const players = [], actions = [], markers = [];
  const P = { id: "P1", num: 1, role: "Passeur", team: "A", color: B, x: fond ? 97 : 66, y: fond ? 28 : 3 };
  players.push(P);
  markers.push({ type: "ballon", x: P.x, y: fond ? P.y + 4 : P.y + 4 });
  const nd = clampN(parseInt((t.match(/(\d+)\s*d[ée]fenseurs?/) || [])[1], 10) || (ecran ? 2 : 1), 0, 4);
  if (ecran) {
    players.push({ id: "S1", num: 2, role: "Arrière", team: "A", color: B, x: 85, y: 44 });
    players.push({ id: "E1", num: 3, role: "Intérieur", team: "A", color: B, x: 83, y: 58 });
    if (nd >= 1) players.push({ id: "D1", num: 1, role: "Déf. arrière", team: "B", color: R, x: 88, y: 47 });
    if (nd >= 2) players.push({ id: "D2", num: 2, role: "Déf. intérieur", team: "B", color: R, x: 80, y: 62 });
    let ord = 1;
    if (nd >= 1) actions.push({ from: "E1", to: "D1", type: "ecran", order: ord++, label: "Écran pour l'arrière" });
    const out = three ? [91, 78] : [76, 40];
    actions.push({ from: "S1", tox: out[0], toy: out[1], type: "course", order: ord++, label: three ? "Sort dans le corner" : "Sort de l'écran" });
    actions.push({ from: "P1", to: "S1", type: "passe", order: ord++, label: "Remise en jeu" });
    if (three) actions.push({ from: "S1", tox: 96, toy: 50, type: "tir", order: ord++, label: "Tir à 3 points" });
    return { players, markers, actions, steps: ["Remise en jeu protégée par l'écran", three ? "Sortie dans le corner et tir à 3 points" : "Sortie d'écran et réception"] };
  }
  players.push({ id: "S1", num: 2, role: "Receveur", team: "A", color: B, x: fond ? 82 : 74, y: fond ? 46 : 20 });
  if (nd >= 1) players.push({ id: "D1", num: 1, role: "Défenseur", team: "B", color: R, x: fond ? 86 : 78, y: fond ? 50 : 24 });
  actions.push({ from: "S1", tox: fond ? 74 : 66, toy: fond ? 34 : 14, type: "course", order: 1, label: "Se démarque vers le ballon" });
  actions.push({ from: "P1", to: "S1", type: "passe", order: 2, label: "Remise en jeu" });
  return { players, markers, actions, steps: ["Le receveur se démarque vers le ballon", "Remise en jeu et enchaînement de l'attaque"] };
}

/* Ateliers en rotation : plusieurs stations réparties en anneau sur l'aire, un
   groupe par atelier, reliés par des flèches de rotation (boucle fermée). */
function buildRotationStations(text) {
  const t = c0(text);
  let n = 4;
  const m = t.match(/(\d+|trois|quatre|cinq|six)\s*(?:ateliers?|stations?|postes?)/);
  if (m) n = clampN(frenchNumWord(m[1]), 2, 6);
  const cx = 50, cy = 50, rx = 34, ry = 30;
  const stations = [];
  for (let i = 0; i < n; i++) {
    const ang = -Math.PI / 2 + (i / n) * Math.PI * 2;   // départ en haut, sens horaire
    stations.push({ x: Math.round(cx + rx * Math.cos(ang)), y: Math.round(cy + ry * Math.sin(ang)) });
  }
  const zones = [], markers = [], players = [], actions = [];
  stations.forEach((s, i) => {
    zones.push({ x: s.x - 9, y: s.y - 9, w: 18, h: 18, label: "Atelier " + (i + 1), style: "dashed" });
    markers.push({ type: "plot", x: s.x, y: s.y - 7 });
    players.push({ id: "G" + (i + 1), role: "Groupe " + (i + 1), team: "A", x: s.x, y: s.y, color: "#0072B2" });
  });
  for (let i = 0; i < n; i++) {
    const a = stations[i], b = stations[(i + 1) % n];
    actions.push({ fromx: a.x, fromy: a.y, tox: b.x, toy: b.y, type: "course", style: "dashed", order: i + 1, label: i === 0 ? "Rotation" : "" });
  }
  return { zones, markers, players, actions, steps: ["Chaque groupe pratique à son atelier", "Au signal, rotation vers l'atelier suivant"] };
}

/* ATELIER « PROGRESSION PAR PASSES » : deux joueurs progressent vers une ZONE
   CIBLE (à droite) en se RELAYANT par des passes courtes (leapfrog : le passeur
   se démarque DEVANT, reçoit à son tour…), en évitant des plots-obstacles
   (adversaires symboliques) ; le reste du groupe ATTEND au départ. Terrain du
   sport d'équipe (football par défaut). Progression gauche -> droite ; le modèle
   de rendu fait évoluer les positions, donc chaque passe est un trait DISTINCT. */
function buildPassingProgression(text, sport) {
  const sp = INVASION_SPORTS.includes(sport) ? sport : "football";
  const players = [], markers = [], actions = [], zones = [];
  const dim = extractDim(text);
  zones.push({ x: 78, y: 26, w: 18, h: 48, label: "Zone cible", style: "dashed", ...(dim ? { dim } : {}) });
  const A1 = { id: "A1", num: 1, role: "Passeur", x: 16, y: 58, color: "#0072B2", team: "A" };
  const A2 = { id: "A2", num: 2, role: "Receveur", x: 30, y: 42, color: "#0072B2", team: "A" };
  players.push(A1, A2);
  markers.push({ type: "ballon", x: A1.x + 4, y: A1.y });
  // Adversaires symboliques à ÉVITER : des plots dans le couloir central (le
  // zigzag de passes les contourne par le haut/bas).
  [[40, 50], [56, 50], [72, 50]].forEach(([x, y]) => markers.push({ type: "plot", x, y }));
  // File d'ATTENTE : les autres élèves au départ (colonne à gauche) + cône Départ.
  const waiting = Math.max(1, Math.min(extractWaitingCount(text), 8));
  for (let i = 0; i < waiting; i++)
    players.push({ id: "W" + (i + 1), role: "En attente", x: 5, y: Math.round(clampN(14 + i * (72 / Math.max(1, waiting - 1)), 8, 92)), color: "#0072B2", team: "A" });
  markers.push({ type: "plot", x: 11, y: 92 });
  // Leapfrog : passes courtes + entrée dans la zone, positions qui AVANCENT.
  let ord = 1;
  actions.push({ from: "A1", to: "A2", type: "passe", order: ord++, label: "Passe courte" });
  actions.push({ from: "A1", tox: 46, toy: 58, type: "course", order: ord++, label: "Se démarque devant" });
  actions.push({ from: "A2", to: "A1", type: "passe", order: ord++, label: "Passe courte" });
  actions.push({ from: "A2", tox: 60, toy: 42, type: "course", order: ord++ });
  actions.push({ from: "A1", to: "A2", type: "passe", order: ord++, label: "Passe courte" });
  actions.push({ from: "A2", tox: 82, toy: 50, type: "dribble", order: ord++, label: "Entre dans la zone cible" });
  return {
    players, markers, actions, zones, sport: sp,
    steps: ["Passe courte vers le partenaire", "Le passeur se démarque devant (relais)", "Progression par passes jusqu'à la zone cible"],
  };
}
/* Nombre d'élèves EN ATTENTE déduit du texte (« 6 en attente », « groupes de 8…
   2 actifs » -> 6) ; 6 par défaut. */
function extractWaitingCount(text) {
  const t = String(text || "").toLowerCase();
  const a = t.match(/(\d+)\s*(?:joueurs?|[ée]l[èe]ves?)?\s*en\s*attente/);
  if (a) return parseInt(a[1], 10);
  const g = t.match(/groupes?\s*de\s*(\d+)/), act = t.match(/(\d+)\s*joueurs?\s*(?:actifs?|sur l'exercice)/);
  if (g && act) return Math.max(1, parseInt(g[1], 10) - parseInt(act[1], 10));
  return 6;
}

/* ============================================================================
   REGISTRE DES TEMPLATES (familles : parcours · filet · invasion · atelier)
   Chaque template : { id, family, deterministic, test(ctx), build(text,ctx) }.
   Premier test satisfait gagne -> ordre = du plus SPÉCIFIQUE au plus générique.
   « deterministic » = placement fiable -> utilisé en priorité (template-first).
   ========================================================================== */
const TEMPLATES = [
  // Plusieurs ateliers NOMMÉS : prioritaire sur tout routage de contenu (le
  // texte d'un atelier — sprint, lancer… — ne doit pas capturer la scène).
  { id: "ateliers_nommes", family: "atelier", deterministic: true, kindForce: "exercise",
    test: (c) => extractNamedAteliers(c.t).length >= 2,
    build: (text, c) => ({ ...ateliersFromList(extractNamedAteliers(c.t), /rotation|tourne[nz]?|au signal/.test(c.t)), title: "Ateliers" }) },

  { id: "acrosport_figure", family: "parcours", deterministic: true, kindForce: "exercise",
    // FIGURE D'ACROSPORT (pyramide statique porteur/voltigeur/pareur) : rendue
    // comme une FIGURE sur tapis, pas comme un enchaînement en diagonale du
    // praticable (contresens : un seul « Gymnaste » qui se déplace). Les listes
    // d'ateliers gardent leur moteur (l'atelier « pyramide » y a sa mini-scène).
    test: (c) => c.sport === "gymnastique"
      && /acrosport|pyramides?\b|voltigeurs?/.test(c.t)
      && extractNamedAteliers(c.t).length < 2
      && !/(\d+|deux|trois|quatre|cinq|six)\s*ateliers?/.test(c.t),
    build: (text) => ({ ...buildAcrosportFigure(text), title: "Figure d'acrosport" }) },

  { id: "gym_parcours", family: "parcours", deterministic: true, kindForce: "exercise",
    test: (c) => c.sport === "gymnastique",
    // Le titre d'un travail SUR AGRÈS est déjà calculé par buildGymnastics
    // (« Enchaînement à la poutre », « Saut de cheval »…) : l'écraser par
    // « Enchaînement au sol » annonçait le contraire de ce que la scène montre.
    // Le défaut touchait tous les agrès linéaires et de franchissement.
    build: (text) => { const s = buildGymnastics(text); return { ...s, title: s.title || "Enchaînement au sol" }; } },

  { id: "athle_field_event", family: "atelier", deterministic: true, kindForce: "exercise",
    // SAUT / LANCER ISOLÉ (athlétisme) -> dessine l'AGRÈS (barre+tapis, planche+sable,
    // aire+cible) via une mini-scène d'atelier unique. Exclut les ateliers EN ROTATION
    // et les drills à files/cibles (déjà gérés ailleurs).
    test: (c) => c.sport === "athletisme"
      && /saut en hauteur|saut en longueur|triple saut|multi.?bonds?|pentabond|\blancer\b/.test(c.t)
      && !/(\d+|deux|trois|quatre|cinq)\s*(?:ateliers?|stations?|postes?)/.test(c.t)
      && !/\brotation\b|cibles?\b/.test(c.t),   // une « cible » -> drill de files (géré ailleurs)
    build: (text) => {
      const t = c0(text);
      let nom = "Saut";
      if (/hauteur/.test(t)) nom = "Saut en hauteur";
      else if (/longueur/.test(t)) nom = "Saut en longueur";
      else if (/triple|multi.?bond|pentabond/.test(t)) nom = "Triple saut";
      else if (/lancer/.test(t)) {
        const m = t.match(/lancer\s+(?:de\s+|du\s+)?([a-zà-ÿ]+)/); nom = "Lancer de " + (m ? m[1] : "vortex");
        // ZONES DE PERFORMANCE cotées (« zones à 10, 15 et 20 mètres ») :
        // transmises au dessin de l'atelier via le nom.
        const zm = t.match(/(?:zones?|lignes?)[^.]{0,60}?[àa]\s*((?:\d+\s*(?:,|et)\s*)*\d+)\s*m/);
        if (zm) nom += " — zones à " + zm[1].replace(/\s+/g, " ") + " m";
      }
      const r = ateliersFromList([{ nom }], false);
      return { zones: r.zones, markers: r.markers, players: r.players, actions: r.actions, title: nom };
    } },

  { id: "tirs_multi_postes", family: "atelier", deterministic: true, kindForce: "exercise",
    // TIRS DEPUIS N POSTES MARQUÉS (sports à but) : « 5 plots à des distances
    // variées, tirs successifs depuis ces positions ». Sans ce gabarit, l'IA
    // classait en « cible » -> cible d'archerie et « Lanceurs » (contresens).
    test: (c) => ["football", "futsal", "handball", "basketball", "basket3x3"].includes(c.sport)
      && !/d[ée]fenseurs?|\bcontre\b|\bvs\b|slalom|dribble|conduite|portes?\b|relais/.test(c.t)
      && ((/tirs?\b|\btirent?\b|\btire[sz]?\b|frappes?\b|\bfrappent?\b|shoots?\b/.test(c.t)
        && /(?:\d+|deux|trois|quatre|cinq|six|plusieurs)\s*(?:plots?|c[ôo]nes?|coupelles?|postes?|positions?|spots?|cerceaux?)/.test(c.t)
        && /successi|encha[iî]ne? (?:les tirs|des frappes|des tirs)|[àa] partir de ces|depuis chaque|partant [^.]{0,16}d['e ]?un|diff[ée]rentes? positions?|distances? (?:vari[ée]+s?|diff[ée]rentes?)|(?:plot|poste|c[ôo]ne|cerceau|position) suivant/.test(c.t))
        // NIVEAUX DE TIR PROGRESSIFS (« Niveau 1 : tir à 2 m … Niveau 3 : lancer
        // franc ») : un poste par niveau, à la distance dite — le mot « lancer
        // franc » du dernier niveau volait la scène au gabarit tir_penalty.
        || (/niveaux?\s*[2-9]/.test(c.t) && (c.t.match(/\btirs?\b/g) || []).length >= 2
          && /\d+\s*m[èe]?|lancer franc|penalty|7 m/.test(c.t))),
    build: (text, c) => ({ ...buildMultiSpotShooting(text, c.sport), title: /niveaux?\s*[2-9]/.test(c0(text)) ? "Tirs par niveaux" : "Tirs multi-postes" }) },

  { id: "volley_tournante", family: "atelier", deterministic: true, kindForce: "exercise",
    // LA TOURNANTE : jeu scolaire de volley (frapper puis courir autour du
    // terrain) — sans gabarit, il devenait une boîte d'atelier à flèche nulle.
    // Reconnue aussi SANS le mot « tournante » quand la structure est décrite :
    // deux colonnes de part et d'autre du filet + on rejoint la colonne opposée.
    test: (c) => c.sport === "volleyball"
      && (/\btournante\b/.test(c.t)
        || ((/de part et d['’]autre du filet|de chaque c[ôo]t[ée] du filet|(?:deux|2)\s*colonnes?/.test(c.t))
          && /rejoin[dt] la colonne (?:oppos|d['’]en face)|colonne oppos[ée]e|passe sous le filet|court autour du terrain/.test(c.t))),
    build: () => ({ ...buildVolleyTournante(), title: "La tournante" }) },

  { id: "rugby_touche", family: "atelier", deterministic: true, kindForce: "exercise",
    // TOUCHE rugby (remise en jeu) : lanceur + alignement de sauteurs. Sans ce
    // gabarit, l'IA la classait en « file » -> duel de plaquage sans rapport.
    test: (c) => c.sport === "rugby" && /\btouche\b/.test(c.t)
      && /lanceur|sauteur|alignement|remise en jeu|talonneur/.test(c.t),
    build: (text) => ({ ...buildRugbyLineout(text), title: "Remise en jeu en touche" }) },

  { id: "tir_penalty", family: "invasion", deterministic: true, kindForce: "exercise",
    // TIR RÉGLEMENTAIRE ARRÊTÉ : penalty (foot/futsal), jet de 7 m (hand), lancers
    // francs (basket). « au point de penalty » / « la ligne des lancers francs »
    // employés comme simples REPÈRES de position sont neutralisés avant le test
    // (un centre au point de penalty n'est pas une séance de penaltys).
    test: (c) => {
      const t = c.t.replace(/point de penalty|surface de r[ée]paration|ligne (?:de |des? )?lancers? francs?/g, "");
      // NIVEAUX DE TIR (« Niveau 1 : tir à 2 m … Niveau 3 : lancer franc ») : un
      // drill PROGRESSIF multi-postes, pas une séance au point réglementaire.
      if (/niveaux?\s*[2-9]/.test(t)) return false;
      if (["football", "futsal"].includes(c.sport)) return /p[ée]nalt(?:ys?|ies?)\b|p[ée]nos?\b/.test(t);
      if (c.sport === "handball") return /jets? de 7\s*m|jets? [àa] 7\s*m|7 m[èe]tres|p[ée]nalt(?:ys?|ies?)\b/.test(t) && !/jet franc/.test(t);
      // TRANSITION APRÈS le lancer franc (« après son lancer franc, le tireur
      // sprinte en repli sur la contre-attaque ») : la leçon est la TRANSITION,
      // pas la séance de LF — on laisse l'analyse invasion la séquencer.
      if (["basketball", "basket3x3"].includes(c.sport)) return /lancers? francs?/.test(t) && !/repli|contre.?attaque|transition/.test(t);
      return false;
    },
    build: (text, c) => ({ ...buildPenaltyShot(text, c.sport),
      title: c.sport === "handball" ? "Jet de 7 mètres" : /^basket/.test(c.sport) ? "Lancers francs" : "Séance de penaltys" }) },

  { id: "football_touche", family: "invasion", deterministic: true, kindForce: "exercise",
    // TOUCHE football (remise en jeu à la main) : « ligne de touche » (repère de
    // terrain) et « en une touche » (contrôle) sont neutralisés avant le test —
    // une conduite le long de la ligne de touche n'est pas une remise en jeu.
    test: (c) => c.sport === "football"
      && /\btouche\b/.test(c.t.replace(/lignes? de touche|en une touche|touche de balle/g, ""))
      && /remise en jeu|rentr[ée]e de touche|deux mains|touche longue|touche rapide|effectue (?:la|une|sa) touche|tire (?:la|une) touche/.test(c.t),
    build: (text) => ({ ...buildFootballThrowIn(text), title: "Remise en jeu à la touche" }) },

  { id: "basket_remise", family: "invasion", deterministic: true, kindForce: "exercise",
    // REMISE EN JEU basket (fond / latérale). Prioritaire sur le pick-and-roll :
    // « remise en jeu … écran » perdait son passeur. Le CHECK-BALL 3x3, lui, est
    // une phase de jeu que l'analyse IA séquence bien (échange avec le défenseur).
    test: (c) => ["basketball", "basket3x3"].includes(c.sport)
      && /remise en jeu|remet en jeu/.test(c.t) && !/check[- ]?ball|\bcheck\b/.test(c.t),
    build: (text) => ({ ...buildBasketInbound(text), title: "Remise en jeu" }) },

  { id: "portes_passes", family: "atelier", deterministic: true, kindForce: "exercise",
    // PASSES DANS DES PORTES (2 plots/cônes espacés) réparties sur une aire : la
    // passe doit TRAVERSER une porte, une porte différente à chaque fois.
    // EXCLU : colonnes FACE À FACE qui suivent leur passe (passe-et-suit avec UNE
    // porte à mi-distance) -> gabarit passe_et_suit_colonnes, l'overlay
    // addPassGates matérialise la porte sur la passe.
    test: (c) => /\bportes?\b/.test(c.t) && /\bpasses?\b/.test(c.t) && /plots?|c[ôo]nes?|coupelles?/.test(c.t)
      && !/\bbuts?\b|panier|essai|gardien/.test(c.t)
      && !(/face [àa] face/.test(c.t) && /suit sa passe|rejoi\w+ la colonne/.test(c.t)),
    build: (text) => ({ ...buildGatesPassing(text), title: "Passes dans les portes" }) },

  { id: "conduite_reactive", family: "atelier", deterministic: true, kindForce: "exercise",
    // CONDUITE RÉACTIVE : repères de COULEUR aux coins, au SIGNAL annoncé le
    // porteur conduit son ballon vers la couleur appelée.
    test: (c) => /couleurs?\b/.test(c.t) && /carr[ée]s?|plots?|c[ôo]nes?|zones?|coins?/.test(c.t)
      && /conduite?|conduis|dribble|am[èe]ne/.test(c.t) && /signal|annonc|appel[ée]|\btop\b/.test(c.t),
    build: (text) => ({ ...buildReactiveDribble(text), title: "Conduite réactive aux couleurs" }) },

  { id: "deplacement_drill", family: "atelier", deterministic: true, kindForce: "exercise",
    // DRILL DE DÉPLACEMENT / APPUIS (toucher des plots/coins, « en étoile », revenir au
    // centre) — SANS échange ni opposition. Prioritaire sur le rally de filet (qui
    // matche tout sport de filet) : un travail d'appuis n'est pas un échange.
    test: (c) => /d[ée]placement|jeu d['’ ]appuis|travail des appuis|\b[ée]toile\b|\bappuis\b/.test(c.t)
      // PLUSIEURS plots à parcourir (étoile / aux coins / N≥3) — pas un simple plot de
      // départ d'un échange coopératif.
      && (/[ée]toile|aux coins|\bcoins\b|autour/.test(c.t) || /(?:[3-9]|trois|quatre|cinq|six|huit)\s*(?:plots?|c[ôo]nes?)/.test(c.t))
      // ni un échange / une frappe / un partenaire (= un rally, pas un drill
      // d'appuis), ni un TIR au but/panier (« course d'élan de 3 appuis, tir aux
      // 9 m en visant les coins du but » est un atelier de tir, pas une étoile).
      && !/contre|\bvs\b|\bmatch\b|oppos|[ée]change|\brally\b|adversaire|partenaire|envoi|frappe|renvoi|\btirs?\b|\btirer\b|\bbuts?\b|panier|marqu/.test(c.t),
    build: (text) => {
      const t = c0(text);
      const star = /[ée]toile|coins?|autour/.test(t);
      const nm = t.match(/(\d+|deux|trois|quatre|cinq|six)\s*(?:plots?|c[ôo]nes?|coins?)/);
      const nP = clampN(nm ? frenchNumWord(nm[1]) : 4, 2, 6);
      const cx = 50, cy = 50;
      const players = [{ id: "A1", num: 1, role: "Joueur", x: star ? cx : 12, y: cy, color: "#0072B2", team: "A" }];
      const markers = [], actions = [];
      const pts = [];
      if (star) for (let k = 0; k < nP; k++) { const a = -Math.PI / 2 + (k / nP) * 2 * Math.PI; pts.push([Math.round(clampN(cx + 34 * Math.cos(a), 6, 94)), Math.round(clampN(cy + 34 * Math.sin(a), 10, 90))]); }
      else for (let k = 0; k < nP; k++) pts.push([Math.round(clampN(28 + k * (64 / (nP - 1 || 1)), 10, 92)), 50]);
      const bx = star ? cx : 12;
      pts.forEach(([x, y], i) => { markers.push({ type: "plot", x, y }); actions.push({ fromx: bx, fromy: cy, tox: x, toy: y, type: "course", style: "dashed", order: i + 1, label: i === 0 ? (star ? "Touche chaque plot et revient au centre" : "Touche chaque plot") : null }); });
      return { players, markers, actions, title: "Travail de déplacement", terrainOpts: { plain: true } };
    } },

  { id: "panier_de_balles", family: "filet", deterministic: true, kindForce: "exercise",
    // PANIER DE BALLES : l'enseignant-distributeur alimente l'élève qui enchaîne
    // ses coups — ni un échange 1c1 (le distributeur ne joue pas le point), ni
    // le drill « joueur seul face à N distributeurs » (élèves) déjà couvert.
    test: (c) => NET_SPORTS.includes(c.sport) && feederBasketSignal(c.text),
    build: (text, c) => buildFeederBasket(text, c.sport) },

  { id: "volley_passe_colonnes", family: "atelier", deterministic: true, kindForce: "exercise",
    // COLONNES RÉCEPTION/PASSE dans le MÊME CAMP (manchette vers le passeur au
    // filet, passe haute en retour, changement de colonne). AVANT filet_echange :
    // le rally générique volait ce drill à deux colonnes du même côté.
    test: (c) => c.sport === "volleyball"
      && /colonnes?|files?/.test(c.t) && /manchette/.test(c.t) && /passeurs?/.test(c.t)
      && /change|rejoi|[ée]change|devient/.test(c.t),
    build: (text) => ({ ...buildVolleySetterColumns(text), title: "Réception-passe en colonnes" }) },

  { id: "filet_service_dominant", family: "filet", deterministic: true, kindForce: "exercise",
    // ENTRER DANS L'ÉCHANGE COMME DOMINANT GRÂCE AU SERVICE (retour prod 👎) :
    // le rally générique montrait un échange neutre — ni la MISE EN DIFFICULTÉ
    // du relanceur, ni le POINT conclu par le serveur. Ici la leçon est la
    // chaîne « service qui écarte -> relanceur en retard -> remise faible ->
    // frappe gagnante du serveur ». AVANT filet_echange (catch-all).
    test: (c) => NET_SPORTS.includes(c.sport)
      && /dominant|domination|dominer|prendre l['’]ascendant|initiative/.test(c.t)
      && /\bservices?\b|\bsert\b|\bserveur\b|mise en jeu/.test(c.t),
    build: (text, c) => ({ ...buildDominantServe(text, c.sport), title: "Entrer dans l'échange comme dominant" }) },

  { id: "filet_echange", family: "filet", deterministic: true, kindForce: "exercise",
    test: (c) => NET_SPORTS.includes(c.sport),
    build: (text, c) => {
      const seq = buildRacketSequence(text, c.sport);
      if (seq) return { ...seq, title: "Séquence d'échange" };
      // MATCH À THÈME « N contre N » au VOLLEY sans échange décrit (« match à
      // thème volley 4c4 ») : une FORMATION d'équipes avec l'échange canonique
      // (service -> réception -> passe -> attaque) — pas un 1c1 générique qui
      // contredit l'effectif annoncé.
      if (c.sport === "volleyball") {
        const m = c.t.match(/\b([3-6])\s*(?:contre|c\.?|vs|x)\s*\1\b/);
        if (m) {
          const eq = buildRacketSequence(`volley ${m[1]} contre ${m[1]} : service adverse, réception, passe du passeur, attaque`, "volleyball");
          if (eq) return { ...eq, title: `Match à thème ${m[1]} contre ${m[1]}` };
        }
      }
      return { ...buildRacketRally(text, c.sport), title: /contre.?pied/.test(c0(text)) ? "Déplacement et contre-pied" : "Échange et déplacement" };
    } },

  { id: "zones_cibles", family: "cible", deterministic: true, kindForce: "exercise",
    // ZONES CIBLES visées par UN exécutant, lues du TEXTE (« le botteur vise
    // 3 zones cibles », « le lanceur envoie le disque dans 4 zones cibles ») :
    // placement local fiable quel que soit l'étiquetage IA. Prioritaire sur
    // lancer_cible (qui rendait une cible d'archerie + files). EXCLUSIONS :
    // opposition (NcM, défenseur, réceptionneur), tir AU BUT (marquer/but/
    // panier -> modèle « tirer depuis les zones »), distribution per-lane
    // (« un X par couloir »), progressions par passes (zone cible = arrivée).
    // Le sujet peut être UN exécutant (« le serveur ») OU un DÉFI/DUEL entre N
    // joueurs (« duel de précision entre deux joueurs »). Un « partenaire » qui
    // ne fait que DÉPLACER le matériel (aide) n'est pas une opposition.
    test: (c) => /zones? cibles?/.test(c.t)
      && (/\b(?:le|la|un|une)\s+(?:serveur|serveuse|lanceur|lanceuse|tireur|tireuse|botteur|buteur|passeur|frappeur|joueur|joueuse|[ée]l[èe]ve)\b/.test(c.t)
        || ((/d[ée]fis?|duel|concours/.test(c.t)) && /joueurs|serveurs|tireurs|frappeurs|[ée]l[èe]ves/.test(c.t)))
      && (c.t.match(/atelier/gi) || []).length < 2   // un multi-ateliers relève de la rotation, pas d'une cible unique
      && zonesCiblesCount(c.t) != null
      && !MATCHUP_RE.test(c.t)
      && !/d[ée]fenseurs?|r[ée]ceptionneurs?|adversaires?/.test(c.t)
      && !/partenaires?\b(?![^.]{0,30}(?:d[ée]place|replace|repositionne|change|ramasse))/.test(c.t)
      && !/marqu(?:er|e|ent)\b|\bbuts?\b|panier|\bcage\b/.test(c.t)
      && !/(?:un|une|1)\s+(?:serveur|tireur|lanceur|joueur|[ée]l[èe]ve)\s+(?:par|dans chaque)|chaque\s+(?:serveur|tireur|lanceur)/.test(c.t)
      && !/progress|franchir|slalom|relais|file/.test(c.t),
    build: (text, c) => buildZonesCiblesFromText(text, c.sport) },

  { id: "tir_depuis_zones", family: "invasion", deterministic: true, kindForce: "exercise",
    // TIR AU BUT DEPUIS LES ZONES construit du texte (« marquer un panier depuis
    // chaque zone », défi à N joueurs) : robuste à un étiquetage IA « atelier /
    // rotation ». Les zones sont des ESPACES d'où l'on tire, la cible est le
    // but/panier. Exclusions : « un tireur par zone » (postes //), NcM, files.
    test: (c) => ["football", "futsal", "handball", "basketball", "basket3x3"].includes(c.sport)
      && /marqu|\bbuts?\b|panier/.test(c.t)
      && /(?:depuis|de|dans) (?:chaque|chacune|les \d+|les (?:deux|trois|quatre|cinq|six)) zones?|zones? diff[ée]rentes?|depuis chacune/.test(c.t)
      && zonesCiblesCount(c.t) != null
      && !MATCHUP_RE.test(c.t)
      && !/(?:un|une|1)\s+(?:tireur|joueur|[ée]l[èe]ve)\s+(?:par|dans chaque)/.test(c.t)
      && !/file|relais|slalom|d[ée]fenseur/.test(c.t),
    build: (text, c) => buildShootingZonesFromText(text, c.sport) },

  { id: "lancer_cible", family: "cible", deterministic: true, kindForce: "exercise",
    // LANCER SUR CIBLE (faire tomber des cônes/quilles à distance, frisbee/vortex/
    // balle…) : atelier de visée en files, pas une phase de jeu. Prioritaire pour
    // ne pas être pris pour une situation d'invasion (« lancer/passe »).
    test: (c) => !["tir_a_l_arc", "petanque"].includes(c.sport)
      // EXCLUSION : une PHASE DE JEU (passes vers un partenaire, progression,
      // zone d'en-but) n'est pas un atelier de visée — même si le texte contient
      // « cible » (direction) ou « sans le faire tomber ». Évite que « passer le
      // frisbee à un partenaire pour faire progresser » soit pris pour un lancer.
      && !/partenaire|co[ée]quipier|faire progresser|progress(?:er|ion)|zone d['e ]?en.?but|en-?but|marquer en zone/.test(c.t)
      // EXCLUSION : un DISPOSITIF MULTI-ATELIERS (« Atelier A… Atelier B », plusieurs
      // ateliers énumérés) n'est pas un simple lancer sur cible — le lancer n'est
      // qu'UNE des cellules ; la rotation (analyse/gabarit) doit toutes les rendre.
      && (c.t.match(/atelier/gi) || []).length < 2
      && ((/faire tomber|renverser|abattre/.test(c.t) && /c[ôo]nes?|quilles?|cibles?|plots?|bo[îi]tes?/.test(c.t))
        // FRONTIÈRE DE MOT au début : « reLANCE » (une passe de relance) ne doit
        // pas déclencher le lancer de visée (colonnes qui slaloment + relancent).
        || (/\blanc(?:er|e|ent|é|ait|ons|ez)\b/.test(c.t) && /\bcibles?\b|\bc[ôo]nes?\b|\bquilles?\b/.test(c.t))),
    build: (text, c) => ({ ...buildTargetThrow(text, c.sport), title: "Lancer sur cible" }) },

  { id: "cible", family: "cible", deterministic: true,
    test: (c) => c.sport === "tir_a_l_arc" || c.sport === "petanque"
      || /tir à l'arc|tir a l'arc|archerie|sarbacane|p[ée]tanque|cochonnet/.test(c.t),
    build: (text, c) => (c.sport === "petanque" || /p[ée]tanque|cochonnet|\bboule/.test(c.t))
      ? { ...buildPetanque(text), sport: "petanque", title: /\btire?(?:nt|r)?\b|d[ée]gage|carreau/.test(c0(text)) ? "Pétanque — pointer puis tirer" : "Pétanque — pointer vers le cochonnet" }
      : /postes? de tir|(?:\d+|deux|trois|quatre|cinq|six)\s*(?:postes?|cibles?|ateliers?|archers?|tireurs?)|plusieurs (?:postes?|cibles?|archers?)/.test(c.t)
        ? { ...buildArcheryStations(text), sport: "tir_a_l_arc", title: "Tir à l'arc — ateliers" }
        : { ...buildArchery(text), sport: "tir_a_l_arc", title: "Tir à l'arc — visée et groupement" } },

  { id: "sprint_balise", family: "athletisme", deterministic: true, kindForce: "exercise",
    // SPRINT SOLO BALISÉ (plots aux distances + chrono/signal + relevé), à
    // distinguer de la montante-descendante (vagues). Prioritaire sur
    // course_vitesse, et EXCLUT explicitement les indices de vagues/duel/relais.
    test: (c) => c.sport === "athletisme"
      && !/montante|descendante|vagues?\b|duel|concours|relais|t[ée]moin|4\s?x/.test(c.t)
      && !/dos [àa] dos/.test(c.t)   // course de réaction OPPOSÉE : gabarit dédié
      && /sprint|vitesse|course|d[ée]part/.test(c.t)
      && /jalonn|tous les\s*\d+\s*m|plot[^.]{0,14}\d+\s*m|signal sonore|rel[èe]ve[^.]{0,16}position|distance parcourue|mesurer la distance/.test(c.t),
    build: (text) => ({ ...buildSprintBalise(text), sport: "athletisme", title: "Sprint balisé — mesure de distance" }) },

  { id: "course_vitesse", family: "athletisme", deterministic: true, kindForce: "exercise",
    // EXCLUSION : une course de vitesse RÉPARTIE en plusieurs ateliers en rotation
    // (« course de vitesse en 4 ateliers », « rotation des ateliers ») n'est pas une
    // montante-descendante en vagues : c'est un circuit d'ateliers (capté plus bas).
    // Sinon le mot « course de vitesse » volait toute la session multi-ateliers.
    // EXCLUSIONS supplémentaires : une course de HAIES et un travail du départ en
    // STARTING-BLOCKS ne sont pas des vagues — leur analyse « dispositif couloir »
    // (haies cotées, rôles starter/chronométreur) dessine le bon aménagement.
    test: (c) => !/(?:\d+|deux|trois|quatre|cinq|six)\s*ateliers?\b|ateliers?\s+(?:en|de)\s+rotation|rotation\s+(?:des?\s+)?ateliers?|circuit\s+(?:d'|de\s+)?ateliers?/.test(c.t)
      && !/\bhaies?\b|starting.?blocks?|dos [àa] dos/.test(c.t)
      && (/course de vitesse|montante.?descendante/.test(c.t)
        || (c.sport === "athletisme" && /sprint|vitesse|vagues?\b|d[ée]part/.test(c.t) && !/t[ée]moin|relais|4\s?x\s?\d/.test(c.t))),
    build: (text) => ({ ...buildSprintWaves(text), sport: "athletisme", title: "Course de vitesse en vagues" }) },

  { id: "relais_vitesse", family: "athletisme", deterministic: true, kindForce: "exercise",
    // VRAI relais uniquement (témoin / 4x100 / transmission). PAS de fourre-tout
    // « sport athlétisme » : sinon il vole toute situation athlé non captée avant
    // (course de haies, dispositif balisé…) et la rend en transmission de témoin.
    // « 4x100 / 4x60 » = un relais ; « 4 X 4 » = un EFFECTIF (retour prod 👎 :
    // un 4 contre 4 partait en transmission de témoin) -> distance à 2 chiffres.
    test: (c) => /t[ée]moin|4\s?x\s?\d{2,3}\b|relais.?vitesse|relais.?4\b|course de relais|zone de transmission|passage de t[ée]moin/.test(c.t),
    build: () => ({ ...buildRelay(), sport: "athletisme", title: "Relais-vitesse — transmission du témoin" }) },

  // DRILLS À COLONNES DE RÔLES AVEC ROTATION CROISÉE (échauffements) — prioritaires
  // sur les templates d'invasion/atelier génériques (invasion_attaque, 2c1,
  // couloirs) qui, sinon, réduisaient ces drills à une phase de jeu ou à deux
  // boîtes d'atelier déconnectées. Placés APRÈS les gabarits à géométrie
  // spécialisée (tirs_multi_postes, penalty…) qui restent prioritaires.
  // FAMILLE « TEMPORALITÉ » — déclencheurs, retards, vagues, tâches intercalées.
  { id: "defense_differee", family: "invasion", deterministic: true, kindForce: "exercise",
    // « Le passeur donne PUIS contourne un plot avant de pouvoir défendre. »
    test: (c) => INVASION_SPORTS.includes(c.sport)
      && /(?:contourne\w*|fait le tour|touche\w*)\s*(?:d[e'’]\s*)?(?:un|le|la)?\s*(?:plot|poteau|c[ôo]ne)/.test(c.t)
      && /avant de (?:pouvoir )?d[ée]fendre|puis (?:vient |revient )?d[ée]fend/.test(c.t),
    build: (text, c) => ({ ...buildDelayedDefense(text, c.sport), title: "Défense différée (détour par le plot)" }) },

  { id: "vagues_attaquants", family: "invasion", deterministic: true, kindForce: "exercise",
    // « La deuxième paire s'élance dès que la première a frappé. »
    test: (c) => INVASION_SPORTS.includes(c.sport)
      && /(?:deuxi[èe]me|2e|seconde)\s*(?:paire|vague)/.test(c.t)
      && /s'?[ée]lance|part(?:ent)?\b/.test(c.t)
      && /d[èe]s que|quand|apr[èe]s|une fois/.test(c.t)
      && !/d[ée]fenseur/.test(c.t),
    build: (text, c) => ({ ...buildAttackWaves(text, c.sport), title: "Vagues d'attaque successives" }) },

  { id: "reaction_dos_a_dos", family: "athletisme", deterministic: true, kindForce: "exercise",
    // Course de réaction : dos à dos au centre, sprint opposé au signal.
    test: (c) => /dos [àa] dos/.test(c.t) && /sifflet|signal|au top/.test(c.t) && /sprint|cour/.test(c.t),
    build: (text) => ({ ...buildBackToBackReaction(text), title: "Course de réaction dos à dos" }) },

  { id: "beret_ballon", family: "dispositif", deterministic: true, kindForce: "exercise",
    // BÉRET(-BALLON) : jeu traditionnel d'appel par numéro.
    test: (c) => /b[ée]ret/.test(c.t),
    build: (text) => ({ ...buildBeretBallon(text), title: "Béret-ballon" }) },

  { id: "ca_relance_gardien", family: "invasion", deterministic: true, kindForce: "exercise",
    // Contre-attaque DÉCLENCHÉE par la possession du gardien (relance longue).
    // SOLO uniquement (un ailier lancé) : une contre-attaque À PLUSIEURS
    // (« 3 contre 1, gardien -> arrière -> pivot -> ailier ») reste à l'analyse
    // IA qui séquence la chaîne complète (verdict épinglé).
    test: (c) => ["handball", "football", "futsal"].includes(c.sport)
      && /d[èe]s que le gardien|gardien a le ballon|relance (?:longue )?du gardien|le gardien (?:capte|r[ée]cup[èe]re)/.test(c.t)
      && /s'?[ée]lance|contre.?attaque|part en/.test(c.t)
      && !/\d\s*contre\s*\d|pivot|arri[èe]re|demi.?centre/.test(c.t),
    build: (text, c) => ({ ...buildKeeperLaunchCounter(text, c.sport), title: "Contre-attaque sur relance du gardien" }) },

  { id: "repli_apres_tir", family: "invasion", deterministic: true, kindForce: "exercise",
    // TRANSITION APRÈS LANCER FRANC / TIR : le tir déclenche la contre-attaque
    // adverse et le REPLI en sprint du tireur (le gabarit lancers francs pur
    // est exclu par sa propre garde /repli|contre-attaque/).
    test: (c) => ["basketball", "basket3x3"].includes(c.sport)
      && /lancer franc|apr[èe]s (?:son|le) tir/.test(c.t)
      && /repli|revient d[ée]fendre/.test(c.t)
      && /contre.?attaque|transition|relance/.test(c.t),
    build: (text) => ({ ...buildFreeThrowTransition(text), title: "Transition après lancer franc" }) },

  { id: "duel_poursuite", family: "invasion", deterministic: true, kindForce: "exercise",
    // Duel avec POURSUITE à départs nommés (médiane / poteau de corner / retard).
    // « en retard » SEUL reste au moteur historique (fixture épinglée) : il faut
    // le mot POURSUITE/RATTRAPER, un duel simple et pas de colonnes.
    test: (c) => INVASION_SPORTS.includes(c.sport)
      && /poursuite?|rattrap[ée]|repris par le d[ée]fenseur/.test(c.t)
      && /d[ée]fenseur/.test(c.t)
      // DUEL 1c1 uniquement : un « 2 contre 1 poursuivi » reste au set-play
      // deux_contre_un (verdict épinglé) — le « 1 contre 1 », lui, EST le cas.
      && !/colonnes?|vagues?|[2-9]\s*contre\s*\d|\d\s*contre\s*[2-9]/.test(c.t),
    build: (text, c) => ({ ...buildPursuitDuel(text, c.sport), title: "Duel de poursuite" }) },

  { id: "zones_1c1_successifs", family: "invasion", deterministic: true, kindForce: "exercise",
    // 1c1 en ZONES SUCCESSIVES : le franchissement déclenche le duel suivant.
    test: (c) => INVASION_SPORTS.includes(c.sport)
      && /zone 1/.test(c.t) && /zone 2/.test(c.t)
      && /1 contre 1|un contre un|duel|[ée]limine/.test(c.t)
      && /encha[iî]ne|franchit|puis/.test(c.t),
    build: (text, c) => ({ ...buildChained1v1Zones(text, c.sport), title: "1 contre 1 en zones successives" }) },

  { id: "defenseurs_entrants_portes", family: "invasion", deterministic: true, kindForce: "exercise",
    // DÉFENSEURS ENTRANTS PAR VAGUES : colonnes de défenseurs en touche
    // (derrière une porte de plots) qui entrent en jeu un à un — au départ,
    // puis au franchissement d'un repère. Supériorité provisoire classique.
    test: (c) => INVASION_SPORTS.includes(c.sport)
      && ((/colonnes? de .{0,12}?d[ée]fenseurs?/.test(c.t)
        && /entre(?:nt)?\s+en\s+jeu/.test(c.t)
        && /portes?\b|lignes? de touche/.test(c.t))
        // Variante SANS colonnes : « un premier défenseur entre depuis la touche
        // quand il franchit les 22 m ; un second entre à la médiane » (seuils).
        || (/entre (?:depuis|de) la touche|entre du c[ôo]t[ée] oppos[ée]/.test(c.t)
          && /franchit|passage de|d[èe]s (?:que|la)/.test(c.t)
          && /d[ée]fenseur/.test(c.t))),
    build: (text, c) => ({ ...buildDefendersEntryWaves(text, c.sport), title: "Attaque contre défenseurs entrants" }) },

  { id: "duel_plaquage_colonnes", family: "atelier", deterministic: true, kindForce: "exercise",
    // DUEL DE PLAQUAGE EN COLONNES (rugby & jeux de combat debout) : colonnes
    // face à face, le porteur attaque, le plaqueur monte au contact, rôles
    // échangés. Sans gabarit, le repli hors-ligne classait ce texte en « match »
    // (formation 11 avec gardien !). Réutilise le moteur de duel d'opposition.
    test: (c) => /colonnes?|files?/.test(c.t) && /plaqu/.test(c.t)
      && !/[2-9]\s*contre\s*\d/.test(c.t)
      && extractNamedAteliers(c.t).length < 2,
    build: (text, c) => ({ ...buildDribbleShootRelay(text, c.sport || "rugby", {}), title: "Duel de plaquage en colonnes" }) },

  { id: "trio_colonnes_roles", family: "atelier", deterministic: true, kindForce: "exercise",
    // TROIS COLONNES / TROIS RÔLES (passeur -> tireur -> rebondeur, rotation
    // circulaire) : testé AVANT passe_tir_colonnes (qui ne connaît que 2 rôles).
    test: (c) => ["basketball", "basket3x3", "handball", "football", "futsal"].includes(c.sport)
      && /(?:trois|3)\s*(?:colonnes?|files?)/.test(c.t)
      && /passeurs?/.test(c.t) && /tireurs?|sh?ooteu?rs?|frappeurs?/.test(c.t)
      && /rebond|r[ée]cup[ée]r/.test(c.t)
      && extractNamedAteliers(c.t).length < 2
      && !/\bcontre\b|\bvs\b|d[ée]fenseur/.test(c.t),
    build: (text, c) => ({ ...buildPassShootReboundTrio(text, c.sport), title: "Trois colonnes — rotation des rôles" }) },

  { id: "passe_tir_colonnes", family: "atelier", deterministic: true, kindForce: "exercise",
    // PASSEUR -> FINISSEUR posté avec ÉCHANGE des rôles : centre-reprise (foot),
    // passe-remise-tir au pivot (hand), poste matérialisé (cerceau à N m).
    test: (c) => ["handball", "football", "futsal", "basketball", "basket3x3"].includes(c.sport)
      && /colonnes?|files?/.test(c.t)
      && ((/centr/.test(c.t) && /repren|reprise|vol[ée]e|de la t[êe]te/.test(c.t))
        || (/pivot/.test(c.t) && /remise|remet|redonne/.test(c.t))
        || (/cerceau/.test(c.t) && /passe/.test(c.t) && /\btir/.test(c.t)))
      && /rejoi\w+ (?:la |l['’]autre |la colonne des |les )?(?:colonne|file)|devient|changement de r[ôo]le|change[nt]* de (?:r[ôo]le|colonne|file)|prend (?:sa|la) place|[ée]change/.test(c.t)
      && !/rebond|r[ée]cup[èe]re/.test(c.t)
      && extractNamedAteliers(c.t).length < 2
      && !/\bcontre\b|\bvs\b|d[ée]fenseurs?\b|plaqu/.test(c.t),
    build: (text, c) => ({ ...buildPassShootColumns(text, c.sport), title: "Passe et tir — échange des rôles" }) },

  { id: "lancer_profondeur_colonnes", family: "atelier", deterministic: true, kindForce: "exercise",
    // ULTIMATE : colonnes lanceur/receveur, course en profondeur, échange des
    // rôles — jamais de conduite ni de tir au but (logique de l'activité).
    test: (c) => c.sport === "ultimate"
      && /colonnes?|files?/.test(c.t) && /lanceur|lance/.test(c.t) && /receveur|r[ée]cept/.test(c.t)
      && /devient|rejoi|[ée]change|inverse|revient/.test(c.t),
    build: (text) => ({ ...buildDeepThrowColumns(text), title: "Lancer en profondeur — échange des rôles" }) },

  { id: "gardien_tournant", family: "atelier", deterministic: true, kindForce: "exercise",
    // COLONNE DE TIREURS + GARDIEN TOURNANT : le tireur remplace le gardien
    // après son tir. Sports à gardien uniquement.
    test: (c) => ["handball", "football", "futsal"].includes(c.sport)
      && /colonnes?|files?/.test(c.t)
      && /gardien tournant|remplace le gardien|devient gardien/.test(c.t),
    build: (text) => ({ ...buildShooterKeeperRotation(text), title: "Tirs au but — gardien tournant" }) },

  { id: "tir_rebond_colonnes", family: "atelier", deterministic: true, kindForce: "exercise",
    // TIR-REBOND À DEUX COLONNES (échauffement classique) : deux colonnes face au
    // panier/but, un tireur + un rebondeur qui relance, rotation CROISÉE. Le CÔTÉ
    // des tireurs est lu du texte (« colonne de droite (les tireurs) » -> bas du
    // schéma, face au but). Rugby inclus : la conclusion devient un aplatissement
    // (chokepoint finalizeScene). Exclut les vrais ateliers NOMMÉS (« Atelier 1 …
    // Atelier 2 … ») et les phases 2c1.
    test: (c) => ["basketball", "basket3x3", "handball", "football", "futsal", "rugby"].includes(c.sport)
      && extractNamedAteliers(c.t).length < 2
      && !/\bcontre\b|\bvs\b|d[ée]fenseur/.test(c.t)
      && (((/(?:deux|2)\s*(?:colonnes?|files?|lignes?|rang[ée]es?)/.test(c.t))
        // Colonne d'appui : REBOND (basket/hand) OU récupération/relance (foot/futsal/rugby).
        && /rebond|r[ée]cup[èe]re|\brelance/.test(c.t)
        && /\btirs?\b|\btire[nrz]?t?\b|shoot|lay.?up|double.?(?:pas|appui)|marque|\bdrive\b|au but|au panier|au cercle|aplati|essai|\bfrappes?\b|\bfrappent\b/.test(c.t))
        // PROMPT VAGUE : « colonnes » + « changement de rôle / on tourne sur les
        // rôles » sans autre structure décrite -> l'échauffement tir-rebond est
        // le drill canonique par défaut (sports à but/panier).
        || (/colonnes?|files?/.test(c.t)
          && /changement de r[ôo]les?|change[nt]* de r[ôo]le|tourne[nrz]? sur les r[ôo]les|[ée]change\w* (?:des?|les) r[ôo]les/.test(c.t)
          && !/passe|centr|plaqu|manchette|gardien|lanceur|cerceau|slalom|porte/.test(c.t)
          && c.sport !== "rugby")),
    build: (text, c) => ({ ...buildShootRebound(text, c.sport), title: "Tir-rebond en deux colonnes" }) },

  { id: "passe_et_suit_colonnes", family: "atelier", deterministic: true, kindForce: "exercise",
    // PASSE-ET-SUIT À DEUX COLONNES FACE À FACE : passe à la colonne d'en face
    // puis on suit sa passe. Exclut les sports de filet (le filet coupe le jeu ->
    // tournante), les vrais ateliers nommés et les phases 2c1. Le « rebond » bascule
    // vers tir_rebond_colonnes (testé avant).
    test: (c) => !NET_SPORTS.includes(c.sport) && !detectFormation(c.text)
      && /(?:deux|2)\s*(?:colonnes?|files?|lignes?)/.test(c.t)
      && /\bpasses?\b|passe[nrz]?t?\b/.test(c.t)
      && /suit (?:sa passe|le ballon|son ballon)|va (?:se (?:re)?placer |se replacer )?(?:au bout|derri[èe]re|[àa] la fin)|colonne (?:d['’]en face|oppos[ée]e|adverse)|file (?:d['’]en face|oppos[ée]e|adverse)|passe.?et.?suit|passe et va|passe et suis|rejoin[dt] (?:la (?:colonne|file)|l['’]autre)/.test(c.t)
      && !/rebond/.test(c.t)
      && extractNamedAteliers(c.t).length < 2
      && !/\bcontre\b|\bvs\b|d[ée]fenseur/.test(c.t),
    build: (text, c) => ({ ...buildPassAndFollow(text, c.sport), title: "Passe et suit en deux colonnes" }) },

  { id: "progression_passes", family: "atelier", deterministic: true, kindForce: "exercise",
    // ATELIER de PROGRESSION PAR PASSES vers une zone cible (2 joueurs actifs qui
    // se relaient, le groupe attend) : ce n'est PAS une phase de jeu N-c-M ni un
    // 2v1 avec but. Prioritaire sur les templates d'invasion (jeu à deux, 2c1…).
    test: (c) => INVASION_SPORTS.includes(c.sport)
      && /par passes|passes? courtes|cha[îi]ne de passes|relais de passes/.test(c.t)
      && /progress(?:er|ion|e)/.test(c.t)
      && /zone cible|vers (?:la|une|leur) (?:zone|cible)|atteindre la cible|zone d['e ]?arriv/.test(c.t)
      && !MATCHUP_RE.test(c.t),
    build: (text, c) => ({ ...buildPassingProgression(text, c.sport), title: "Progression par passes" }) },

  { id: "croisement_pivot", family: "invasion", deterministic: true,
    // Croisement de deux extérieurs devant un joueur posté (pivot / dos au
    // panier) qui remet à l'un d'eux. Set-play spécifique -> placement dédié.
    test: (c) => ["basketball", "basket3x3", "handball"].includes(c.sport)
      && /crois(?:e|ent|er|ement)/.test(c.t)
      && /dos au panier|\bpivot\b|poste\b|\bint[ée]rieur\b|devant (?:le|lui|l['’]|a\b|son)/.test(c.t),
    build: () => ({ ...buildCrossOverPivot(), title: "Croisement devant le pivot" }) },

  { id: "pick_and_roll", family: "invasion", deterministic: true,
    // Pick & roll = écran SUR LE PORTEUR (on-ball) suivi d'un roll / d'une
    // pénétration / d'une remise au rouleur. Un « pose un écran » POUR UN
    // PARTENAIRE (écran indirect, ex. écran du pivot pour l'ailier qui reçoit et
    // tire) n'en est PAS un : on exige la signature du roll, sinon on laisse la
    // main à l'analyse IA (invasion_attaque -> invasionFromAnalysis).
    test: (c) => INVASION_SPORTS.includes(c.sport)
      && (/pick.?and.?roll|pick'?n'?roll/.test(c.t)
        || (/pose un [ée]cran|[ée]cran (?:et|puis|,)/.test(c.t)
          // « remise » = remise AU ROULEUR — pas la « remise en jeu » (set-piece dédié).
          && /rouleur|\broll\b|roule(?:r|nt)?\b|remise(?!s? en jeu)|plonge|double[- ]?pas|p[ée]n[éèe]tr/.test(c.t))),
    build: () => ({ ...buildPickAndRoll(), title: "Pick and roll" }) },

  { id: "fixation_passe", family: "invasion", deterministic: true,
    // « fixe un défenseur » dans une CONTRE-ATTAQUE à N contre M est un jeu
    // d'équipe (invasion_attaque), pas une fixation 1-c-1 : on cède la priorité.
    // EXCLUT le basket 3x3 (demi-terrain sur UN panier) : ce set-play est codé
    // en PLEIN TERRAIN — l'analyse IA (invasionFromAnalysis + normalize3x3) le
    // stage correctement en demi-terrain.
    test: (c) => INVASION_SPORTS.includes(c.sport) && c.sport !== "basket3x3"
      && !(MATCHUP_RE.test(c.t) || /contre.?attaque|surnombre|sup[ée]riorit|mont[ée]e de balle/.test(c.t))
      // Un UNE-DEUX décrit (« part dans le dos et reprend la remise ») n'est
      // pas une fixation-passe : le porteur CONCLUT — jeu_a_deux prime.
      && !/\bune[- ]?deux\b|reprend la remise/.test(c.t)
      && /fixation|fixe(r)?\s+(le|son|un|la)\s+d[ée]fenseu|fixe.*(puis|et).*(passe|donne|sert|d[ée]cale)/.test(c.t),
    build: () => ({ ...buildFixationPasse(), title: "Fixation et passe" }) },

  { id: "jeu_a_deux", family: "invasion", deterministic: true,
    // « passe et va » (= passe-et-suit dans la course) est un jeu à deux : set-play
    // local SANS dribble (à ne pas confondre avec « passe et suit EN COULOIR »,
    // qui est un atelier — capté plus haut).
    test: (c) => INVASION_SPORTS.includes(c.sport) && c.sport !== "basket3x3"   // 3x3 : demi-terrain -> analyse IA
      && /jeu (?:à|a) deux|\bune[- ]?deux\b|donne et va|donne et suit|give.?and.?go|passe[- ]?et[- ]?va/.test(c.t),
    build: () => ({ ...buildGiveAndGo(), title: "Jeu à deux (passe et va)" }) },

  { id: "hand_kungfu", family: "invasion", deterministic: true, kindForce: "exercise",
    // LE KUNG-FU (handball) : passe en cloche AU-DESSUS de la zone, réception
    // EN SUSPENSION dans l'espace aérien et tir avant de retomber. Prioritaire
    // sur le 2c1 générique (qui rendait une fixation-passe sans rapport).
    test: (c) => c.sport === "handball"
      && /kung[- ]?fu|passe en cloche[^.]{0,60}(?:zone|suspension|a[ée]rien)|attrape[^.]{0,40}suspension|marque avant de retomber/.test(c.t),
    build: () => ({ ...buildHandKungfu(), title: "Le kung-fu (jeu en suspension)" }) },

  { id: "deux_contre_un", family: "invasion", deterministic: true,
    // EXCLUT l'ultimate : un 2c1 ultimate est un give-and-go vers l'en-but (pas de
    // portage) -> on laisse l'analyse IA primer (invasion_attaque -> moteur
    // ultimate dédié), sinon le set-play générique « fixe + dribble » s'imposerait.
    // EXCLUT la DÉFENSE DIFFÉRÉE (« …avant de pouvoir défendre ») : la tâche
    // intercalée du défenseur est la leçon -> gabarit defense_differee.
    test: (c) => INVASION_SPORTS.includes(c.sport) && c.sport !== "ultimate" && c.sport !== "basket3x3" && /2\s*contre\s*1|deux contre un/.test(c.t)
      && !/avant de (?:pouvoir )?d[ée]fendre/.test(c.t),
    build: (text, ctx) => ({ ...build2v1(ctx.sport, text), title: "Deux contre un" }) },

  { id: "conservation_losange", family: "invasion", deterministic: true,
    // « jeu des passes à 10 » (à N) = une CONSERVATION (cercle de porteurs +
    // récupérateurs au centre), pas une chaîne de passes vers l'avant. Le
    // lookahead écarte « passe à 10 m » (une distance, pas un jeu).
    // …et la conservation DÉCRITE sans son nom : « se font des passes » face à
    // un récupérateur/intercepteur, SANS but ni tir (sinon c'est une attaque).
    test: (c) => INVASION_SPORTS.includes(c.sport)
      && (/conservation|possession|\blosange\b|rondo|\btoro\b|\btaureau\b|carr[ée] magique|conserver le ballon|garder le ballon|passes?\s+[àa]\s+\d+(?!\d|\s*m)|jeu des \d+ passes/.test(c.t)
        || (/(?:se font|se fait|s'[ée]changent|[ée]changent) des passes|passes?\s+[àa] travers/.test(c.t)
          && /intercept|r[ée]cup[ée]rat/.test(c.t) && !/\bbuts?\b|\btirs?\b|tirer|frapp|marqu/.test(c.t))),
    // Le titre du builder prime : une conservation à ÉQUIPES ÉGALES n'est pas
    // un losange (elle se nomme « Conservation N contre N »).
    build: (text) => { const r = buildPossessionDiamond(text); return { ...r, title: r.title || "Conservation en losange" }; } },

  { id: "repli_defensif", family: "invasion", deterministic: true,
    // Le mot « repli » apparaît souvent comme OBJECTIF/repère temporel d'une
    // action offensive (« montée de balle … avant le repli ») : si une action
    // d'attaque claire est décrite, c'est elle la leçon, pas le repli.
    test: (c) => INVASION_SPORTS.includes(c.sport)
      && /repli|transition d[ée]fensive|d[ée]fense de transition|revenir d[ée]fendre|retour d[ée]fensif/.test(c.t)
      && !/mont[ée]e de balle|contre.?attaque|surnombre|sup[ée]riorit/.test(c.t),
    build: () => ({ ...buildDefensiveRecovery(), title: "Repli défensif" }) },

  { id: "sortie_de_balle", family: "invasion", deterministic: true,
    // « relance du gardien » est souvent le DÉCLENCHEUR d'une contre-attaque /
    // d'un jeu en surnombre qui se conclut par un tir : dans ce cas la leçon est
    // la phase offensive (montée de balle → tir), pas l'exercice de construction
    // courte depuis l'arrière. On laisse alors l'analyse IA primer.
    test: (c) => ["football", "futsal", "handball"].includes(c.sport)
      && /sortie de balle|relance courte|relance du gardien|sortie de la d[ée]fense|ressortir le ballon|jouer depuis (?:l'arri[èe]re|le gardien|derri[èe]re)|construire depuis l'arri[èe]re|relancer (?:proprement|court|de derri[èe]re)/.test(c.t)
      && !/contre.?attaque|surnombre|sup[ée]riorit|mont[ée]e de balle|\d\s*c(?:ontre)?\s*\d|conclure|finir|\btir(?:er)?\b|but\b/.test(c.t),
    build: () => ({ ...buildBuildUp(), title: "Sortie de balle" }) },

  { id: "defi_tir_dribble", family: "atelier", deterministic: true, kindForce: "exercise",
    // Atelier-DÉFI : tir en course après dribble, en file(s) d'attente / duel —
    // ce n'est PAS une phase d'attaque (pas de passe ni d'opposition). Prioritaire
    // sur invasion_attaque (qui se déclenche sur « 3 ou 4 joueurs »).
    test: (c) => ["basketball", "basket3x3", "handball", "futsal", "football"].includes(c.sport)
      && /tirs? en course|lay.?up|tir(?:er)? (?:en|apr[èe]s) (?:course|le dribble|un dribble)/.test(c.t)
      && /file d['’ ]attente|en file|chacun (?:son|[àa] son|leur) tour|\brelais\b|\bduel\b|(?:2|deux) [ée]quipes|s['’ ]affrontent/.test(c.t),
    build: (text, c) => ({ ...buildDribbleShootRelay(text, c.sport), title: "Défi de tir en course" }) },

  { id: "slalom_conduite", family: "atelier", deterministic: true, kindForce: "exercise",
    // SLALOM EN CONDUITE (« slalom entre 5 cerceaux/plots, puis frappe ») :
    // filet de sécurité LOCAL — quand l'IA rend `analyse: null`, le repli
    // générique dessinait un échange de passes hors-sujet. Le moteur de files
    // (buildDribbleShootRelay) lit slalom/plots/cerceaux/espacement du texte.
    // EXCLUSIONS : les formats COLLECTIFS (concours entre équipes, duels, files,
    // casiers-navettes) ont leurs moteurs dédiés via l'analyse — ce filet ne
    // capte que le slalom individuel simple.
    test: (c) => ["basketball", "basket3x3", "handball", "futsal", "football", "rugby", "ultimate"].includes(c.sport)
      && /slalom/.test(c.t) && /conduite|dribble|balle|ballon|drible/.test(c.t)
      && !/[ée]quipes?|concours|casiers?|files?\b|colonnes?|duel|s['’ ]affrontent|adversaires?|relais/.test(c.t),
    build: (text, c) => ({ ...buildDribbleShootRelay(text, c.sport), title: "Slalom en conduite" }) },

  { id: "multibuts", family: "invasion", deterministic: true,
    // JEU À MULTIBUTS (retour prod 👎 « pas de multibut ! pas de repère de
    // distance ») : plusieurs mini-buts par côté, sur une aire RÉDUITE et cotée.
    // Sans ce gabarit, le texte partait en attaque générique (un seul grand but,
    // aucune cote). Exige des buts DE CHAQUE CÔTÉ : un « vers un mini-but »
    // isolé reste une situation d'attaque ordinaire.
    test: (c) => INVASION_SPORTS.includes(c.sport)
      && (/multi.?buts?/.test(c.t)
        || (/mini.?buts?|petits? buts?/.test(c.t)
          && /de chaque c[ôo]t[ée]|par c[ôo]t[ée]|de part et d['’]autre|[àa] chaque extr[ée]mit[ée]/.test(c.t))),
    build: (text, c) => ({ ...buildMultiGoals(c.sport, text), title: "Jeu à multibuts" }) },

  { id: "invasion_attaque", family: "invasion", deterministic: true,
    // Un DUEL DE FORMATIONS (« 4-4-2 contre 4-3-3 », « 442 contre 433 ») est
    // un schéma d'équipes complètes : il revient au gabarit formation (les
    // deux équipes en miroir), pas à l'attaque générique.
    test: (c) => INVASION_SPORTS.includes(c.sport) && hasStrongLayoutCues(c.text)
      && !/\b\d(?:-?\d){1,3}\s*(?:contre|vs\.?|face [àa])/.test(c.t),
    build: (text, c) => {
      const t = c0(text);
      const title = /contre.?attaque/.test(t) ? `Contre-attaque à ${countAttackers(t)} contre ${countDefenders(t)}`
        : /mont[ée]e de balle/.test(t) ? "Montée de balle"
        : /surnombre|sup[ée]riorit/.test(t) ? "Jeu en surnombre" : "Situation d'attaque";
      return { ...buildTeamAttack(text, c.sport), title };
    } },

  { id: "phase_jeu_nommee", family: "invasion", deterministic: true,
    test: (c) => INVASION_SPORTS.includes(c.sport) && countNamedActors(c.t) >= 3,
    build: (text, c) => {
      const r = buildNamedPlay(text, c.sport);
      return r ? { ...r, title: "Phase de jeu" } : { ...buildTeamAttack(text, c.sport), title: "Situation d'attaque" };
    } },

  { id: "ateliers_rotation", family: "atelier", deterministic: true, kindForce: "exercise",
    test: (c) => /ateliers?\s+(?:en|de)\s+rotation|rotation\s+(?:des?\s+)?(?:ateliers?|stations?|postes?)|circuit\s+(?:d'|de\s+)?ateliers?|(?:\d+|trois|quatre|cinq|six)\s*ateliers?\b/.test(c.t),
    build: (text) => ({ ...buildRotationStations(text), title: "Ateliers en rotation" }) },

  { id: "atelier_couloir", family: "atelier", deterministic: true, kindForce: "exercise",
    // « couloir » seul ne suffit pas (un couloir d'athlétisme ou le couloir d'une
    // phase de jeu n'est pas un atelier de passes) : il faut un indice de
    // circulation de balle ET pas une phase de jeu à acteurs nommés. Et SURTOUT pas
    // un schéma de match à FORMATION (« 4-3-3 … ferment les couloirs ») -> ce dernier
    // est un dispositif d'équipe, pas un atelier de passes en couloirs.
    test: (c) => !detectFormation(c.text)
      && (/passe.?et.?suit|passe et va|passe et suis/.test(c.t)
        || (/couloir/.test(c.t) && /passe|ballon|balle\b/.test(c.t) && c.sport !== "athletisme" && countNamedActors(c.t) < 3)),
    build: (text) => {
      const r = buildExerciseLayout(text);
      return { zones: r.zones, markers: r.markers, players: r.players, actions: r.actions,
        title: /passe.?et.?suit|passe et va/.test(c0(text)) ? "Passe et suit" : "Atelier en couloirs" };
    } },

  { id: "formation", family: "invasion", deterministic: true, kindForce: "match",
    // Une formation (4-4-2, 4-3-3…) est par nature un dispositif d'ÉQUIPE : on la
    // reconnaît quel que soit le type détecté/forcé (évite qu'un retour IA
    // « exercice » prive le schéma de sa formation complète).
    test: (c) => ["football", "futsal", "rugby"].includes(c.sport) && !!detectFormation(c.text),
    build: (text, c) => {
      if (c && c.sport === "futsal") return { players: buildFutsalFormation(text), title: "Schéma futsal" };
      const f = detectFormation(text) || "4-4-2";
      const players = buildFootballFormation(f);
      // DUEL DE FORMATIONS (« 4-4-2 contre 4-3-3 », « 442 contre 433 ») : les
      // DEUX équipes complètes — la seconde en MIROIR (elle défend le but droit).
      const duelM = c0(text).match(/\b(\d(?:-?\d){1,3})\b\s*(?:contre|vs\.?|face [àa]u?n?e?)\s*\b(\d(?:-?\d){1,3})\b/);
      const normF = (s) => s.includes("-") ? s : s.split("").join("-");
      const f2 = duelM && FORMATIONS[normF(duelM[2])] ? normF(duelM[2]) : null;
      if (f2) {
        for (const p of buildFootballFormation(f2))
          players.push({ ...p, id: "Q" + p.num, team: p.tags && p.tags.includes("gardien") ? "k" : "B",
            color: p.tags && p.tags.includes("gardien") ? "#F2C200" : "#D55E00", x: 100 - p.x });
        return { players, markers: [{ type: "ballon", x: 50, y: 50 }],
          actions: [], title: `${f} contre ${f2}`, kind: "match" };
      }
      let actions = buildPlayChain(text, players, c.sport) || detectMatchActions(text, players);
      let markers;
      // FORMATION + PHASE décrite sans joueur nommé (« transition après
      // récupération », « relance courte », « pressing ») : ni la chaîne ni les
      // mots-clés ne produisent d'action -> on illustre la phase a minima
      // plutôt que de rendre un squelette sans aucune flèche.
      if (!actions || !actions.length) {
        const ph = buildFormationPhase(text, players);
        if (ph) { actions = ph.actions; markers = ph.markers; }
      }
      return { players, actions: actions || [], ...(markers ? { markers } : {}), title: "Schéma " + f };
    } },

  { id: "exercice_generique", family: "atelier", deterministic: false, kindForce: "exercise",
    test: (c) => c.kind === "exercise",
    build: (text) => {
      const r = buildExerciseLayout(text);
      return { zones: r.zones, markers: r.markers, players: r.players, actions: r.actions };
    } },

  { id: "match_generique", family: "invasion", deterministic: false, kindForce: "match",
    test: () => true,
    build: (text, c) => {
      let players;
      if (c.sport === "futsal") players = buildFutsalFormation(text);   // 5 joueurs, pas 11
      else if (["football", "rugby"].includes(c.sport)) players = buildFootballFormation(detectFormation(text) || "4-4-2");
      else {
        // PHASE D'ATTAQUE décrite sans formation ni effectif (« attaquer une
        // défense de zone en passant par le poste ») : une vraie phase minimale
        // (buildTeamAttack : porteur, partenaires, défense, actions) plutôt
        // qu'un semis de joueurs SANS AUCUNE action (défaut relevé par le banc
        // de fidélité).
        if (INVASION_SPORTS.includes(c.sport) && /attaqu|d[ée]fense|marquer|conclure|tir/.test(c.t))
          return buildTeamAttack(text, c.sport);
        const g = /(\d+)\s*(?:joueurs|contre)/.exec(text);
        players = buildGenericPlayers(g ? Math.min(parseInt(g[1], 10) * 2, 10) : 5);
      }
      return { players, actions: buildPlayChain(text, players, c.sport) || detectMatchActions(text, players) };
    } },
];
/* Normalisation partagée du texte : minuscules + effectifs ABRÉGÉS « 2c1 »,
   « 5v2 », « 3vs3 » réécrits en « N contre M » — tous les lecteurs locaux
   (gabarits, effectifs, cohérence) comprennent ainsi les abréviations sans
   dupliquer les variantes dans chaque regex. (« 3x3 » et « 4x100 » intacts.) */
function c0(text) { return String(text || "").toLowerCase().replace(/(\d)\s*(?:vs|[cv])\s*(\d)/g, "$1 contre $2"); }

/* Sports à but AVEC gardien (le basket n'en a pas). */
const GK_SPORTS = new Set(["football", "futsal", "handball"]);
/* GARDIEN D'OFFICE : si une action se conclut par un TIR vers le but (à droite)
   et qu'aucun gardien n'est déjà présent, on en place un devant le but. Règle
   PARTAGÉE — appliquée aux set-plays locaux (via finalizeScene) comme à l'analyse
   IA (invasionFromAnalysis) : un tir au but sans gardien n'a pas de sens. */
function ensureGoalkeeper(players, actions, sport, text) {
  if (!GK_SPORTS.has(sport)) return players;
  // « sans gardien / mini-buts / but vide » : le texte INTERDIT le gardien —
  // on n'en ajoute pas d'office (et le détecteur signale ceux déjà dessinés).
  if (/sans gardien|pas de gardien|but vide|petits? buts?|mini.?buts?/i.test(coreSituationText(String(text || "")))) return players;
  if (players.some((p) => p.team === "k" || /gardien/i.test(p.role || ""))) return players;
  if (!(actions || []).some((a) => a.type === "tir" && (a.tox == null || a.tox >= 88))) return players;
  return [...players, { id: "G", role: "Gardien", team: "k", color: "#F2C200", x: 94, y: 50 }];
}

/* BALLON D'OFFICE : le renderer ne dessine le ballon QUE depuis un marqueur. Une
   phase de jeu (passe/conduite/centre/tir/renvoi) sans marqueur de balle ne montre
   donc aucun ballon (cas d'une formation foot où l'on enchaîne une passe). Si la
   scène joue le ballon et qu'aucune balle/disque n'est présente, on le pose sur le
   PREMIER porteur (le « from » de la 1re action de balle). Chokepoint partagé. */
function ensureBall(players, actions, markers, sport) {
  const mk = markers || [];
  // Sports de CIBLE : le projectile n'est JAMAIS un ballon. En tir à l'arc /
  // sarbacane c'est une FLÈCHE (déjà figurée par la flèche de « tir » vers la
  // cible) ; en pétanque c'est une BOULE (déjà posée). Le « tir » d'un archer ou
  // d'un pétanqueur ne doit pas faire apparaître un ballon fantôme sur le joueur.
  if (sport === "tir_a_l_arc" || sport === "petanque") return mk;
  if (mk.some((m) => /^(ballon|ball|balle|disque|frisbee)$/i.test(m.type || ""))) return mk;
  const ordered = (actions || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
  const first = ordered.find((a) => /^(passe|centre|dribble|tir|renvoi)$/.test(a.type || "") && a.from);
  if (!first) return mk;
  const carrier = (players || []).find((p) => p.id === first.from);
  if (!carrier) return mk;
  return [...mk, { type: sport === "ultimate" ? "disque" : "ballon", x: carrier.x, y: carrier.y }];
}

function finalizeScene(s, T, ctx) {
  const sport = s.sport || ctx.sport;
  // RUGBY : pas de tir au but. Un « tir » produit par un set-play local générique
  // (codé pour les sports à but) devient une conclusion par APLATISSEMENT : le
  // porteur porte la balle en-but + un repère « essai ». Chokepoint unique.
  // (Ultimate : le « tir » local légitime est le LANCER sur cible — on n'y touche
  // pas ; l'invasion ultimate passe par l'analyse, qui gère déjà l'essai.)
  // EXCEPTION : un JEU AU PIED décrit (botteur, dégagement, chandelle…) est un
  // vrai envoi aérien — la flèche de frappe reste (viser des zones cibles au
  // pied n'est pas un aplatissement).
  if (sport === "rugby" && !/jeu au pied|coup de pied|bott|d[ée]gag|chandelle|transformation|p[ée]nalit[ée]/.test(ctx.t)) {
    const acts = s.actions || [];
    const marks = s.markers || (s.markers = []);
    for (const a of acts) {
      if (a.type !== "tir") continue;
      a.type = "dribble";
      a.label = "Aplatit l'essai";
      const ex = a.tox != null ? Math.min(a.tox, 95) : 92;
      const ey = a.toy != null ? a.toy : 50;
      if (a.tox != null) a.tox = ex;
      if (!marks.some((m) => m.type === "essai")) marks.push({ type: "essai", x: ex, y: ey });
    }
  }
  return {
    kind: T.kindForce || ctx.kind,
    sport,
    title: s.title || T.title || (ctx.kind === "exercise" ? "Situation d'apprentissage" : "Schéma de match"),
    pedagogy: extractPedagogy(ctx.text),
    zones: s.zones || [], markers: ensureBall(s.players || [], s.actions || [], s.markers || [], sport), players: ensureGoalkeeper(s.players || [], s.actions || [], sport, ctx.text), actions: s.actions || [],
    ...(s.steps ? { steps: s.steps } : {}),
    // Analyse synthétisée par le moteur (panneau « Ce que j'ai compris »).
    ...(s.analyse ? { analyse: s.analyse } : {}),
    ...(s.terrainOpts ? { terrainOpts: s.terrainOpts } : {}),
  };
}

export function localParse(text, opts = {}) {
  const ctx = makeCtx(text, opts);
  const T = TEMPLATES.find((x) => x.test(ctx)) || TEMPLATES[TEMPLATES.length - 1];
  return finalizeScene(T.build(text, ctx), T, ctx);
}
